// Admin-console translation catalogs (W39). English is the complete
// reference. The four other official SA languages are listed so the picker
// exists, but their catalogs are EMPTY on purpose: the admin console is an
// internal tool and translation is parked backlog (see
// _memory/2026-09-02-w39-admin-ui-i18n.md). Missing keys never throw: they
// render the English string, then the raw key.
//
// Key convention: `page.section.label` (e.g. `tenants.columns.name`,
// `settings.integrations.toast.saved`). Shared vocabulary lives under
// `common.*`, `action.*` and `status.*`: reuse those before adding a
// page-local duplicate. Dynamic values use `{{name}}` placeholders and
// `t(key, { name: value })`.

export type Locale = 'en' | 'af' | 'zu' | 'xh' | 'st';

export const LOCALES: { code: Locale; label: string; english: string }[] = [
  { code: 'en', label: 'English', english: 'English' },
  { code: 'af', label: 'Afrikaans', english: 'Afrikaans' },
  { code: 'zu', label: 'isiZulu', english: 'Zulu' },
  { code: 'xh', label: 'isiXhosa', english: 'Xhosa' },
  { code: 'st', label: 'Sesotho', english: 'Sotho' }
];

export type Catalog = Record<string, string>;

const en: Catalog = {
  // ── common ──────────────────────────────────────────────────────────────
  'common.language': 'Language',
  'common.loading': 'Loading…',
  'common.unknownError': 'Unknown error',
  'common.none': 'None',
  'common.notAvailable': 'n/a',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.all': 'All',
  'common.name': 'Name',
  'common.email': 'Email',
  'common.status': 'Status',
  'common.type': 'Type',
  'common.description': 'Description',
  'common.created': 'Created',
  'common.updated': 'Updated',
  'common.actions': 'Actions',
  'common.id': 'ID',
  'common.code': 'Code',
  'common.value': 'Value',
  'common.enabled': 'Enabled',
  'common.disabled': 'Disabled',
  'common.unknown': 'Unknown',
  'common.optional': 'Optional',
  'common.required': 'Required',
  'common.search': 'Search',
  'common.searchPlaceholder': 'Search…',
  'common.noResults': 'No results.',
  'common.notFound': 'Not found',
  'common.tenant': 'Tenant',
  'common.user': 'User',
  'common.date': 'Date',
  'common.amount': 'Amount',
  'common.currency': 'Currency',
  'common.total': 'Total',
  'common.details': 'Details',
  'common.overview': 'Overview',
  'common.settings': 'Settings',
  'common.filters': 'Filters',
  'common.clearFilters': 'Clear filters',
  'common.view.table': 'Table',
  'common.view.grid': 'Grid',
  'common.view.list': 'List',
  'common.demo': 'Demo data',
  'common.comingSoon': 'Coming soon',
  'common.pendingBackend': 'Pending backend',
  'common.pendingBackendFor': 'Pending backend ({{workstream}})',
  'common.reserved': 'This page is reserved. Wiring lands when the backing endpoints ship.',
  'common.copied': 'Copied to clipboard.',
  'common.failedToLoad': 'Failed to load.',
  'common.never': 'Never',
  'common.today': 'Today',
  'common.lastNDays': 'Last {{days}} days',
  'common.showingRange': 'Showing {{from}}–{{to}} of {{total}}',
  // ── actions ─────────────────────────────────────────────────────────────
  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.create': 'Create',
  'action.delete': 'Delete',
  'action.edit': 'Edit',
  'action.close': 'Close',
  'action.refresh': 'Refresh',
  'action.retry': 'Retry',
  'action.back': 'Back',
  'action.apply': 'Apply',
  'action.clear': 'Clear',
  'action.export': 'Export',
  'action.exportCsv': 'Export CSV',
  'action.copy': 'Copy',
  'action.view': 'View',
  'action.confirm': 'Confirm',
  'action.open': 'Open',
  'action.add': 'Add',
  'action.remove': 'Remove',
  'action.enable': 'Enable',
  'action.disable': 'Disable',
  'action.activate': 'Activate',
  'action.deactivate': 'Deactivate',
  'action.approve': 'Approve',
  'action.reject': 'Reject',
  'action.submit': 'Submit',
  'action.send': 'Send',
  'action.test': 'Test',
  'action.run': 'Run',
  'action.download': 'Download',
  'action.signOut': 'Sign out',
  'action.signIn': 'Sign in',
  'action.continue': 'Continue',
  'action.next': 'Next',
  'action.previous': 'Previous',
  'action.viewAll': 'View all',
  'action.showMore': 'Show more',
  'action.loadMore': 'Load more',
  'action.reset': 'Reset',
  'action.select': 'Select',
  'action.saveChanges': 'Save changes',
  'action.discard': 'Discard',
  // ── status labels ────────────────────────────────────────────────────────
  'status.active': 'Active',
  'status.inactive': 'Inactive',
  'status.pending': 'Pending',
  'status.approved': 'Approved',
  'status.rejected': 'Rejected',
  'status.failed': 'Failed',
  'status.sent': 'Sent',
  'status.delivered': 'Delivered',
  'status.read': 'Read',
  'status.draft': 'Draft',
  'status.submitted': 'Submitted',
  'status.paused': 'Paused',
  'status.running': 'Running',
  'status.completed': 'Completed',
  'status.cancelled': 'Cancelled',
  'status.queued': 'Queued',
  'status.healthy': 'Healthy',
  'status.degraded': 'Degraded',
  'status.down': 'Down',
  'status.warning': 'Warning',
  'status.ok': 'OK',
  'status.error': 'Error',
  'status.unknown': 'Unknown',
  'status.enabled': 'Enabled',
  'status.disabled': 'Disabled',
  'status.expired': 'Expired',
  'status.open': 'Open',
  'status.closed': 'Closed',
  'status.locked': 'Locked',
  'status.verified': 'Verified',
  'status.unverified': 'Unverified',
  'status.succeeded': 'Succeeded',
  'status.skipped': 'Skipped',
  'status.configured': 'Configured',
  'status.notConfigured': 'Not configured',
  // ── shell (locale picker) ────────────────────────────────────────────────
  'shell.localePicker': 'Display language',
  // <page-groups>: page-specific keys are merged below this line by the W39 codemod.
  // ── auth ──
  'auth.layout.badge': 'Admin',
  'auth.layout.subtitle': 'Platform staff sign-in. Tenant users belong on the tenant console.',
  'auth.layout.wrongDomainTitle': 'Wrong domain',
  'auth.layout.wrongDomainBody': 'That account is a tenant account. Switch to the tenant console.',
  'auth.backToSignIn': 'Back to sign-in',
  'auth.login.forgotLink': 'Forgot password?',
  'auth.login.form.password': 'Password',
  'auth.login.wrongDomain': 'That account belongs to a tenant. Use the tenant console.',
  'auth.login.failed': 'Sign-in failed.',
  'auth.login.twoFactor.hint': 'Enter the 6-digit code from your authenticator, or a recovery code.',
  'auth.login.twoFactor.code': 'Authentication code',
  'auth.login.twoFactor.verify': 'Verify',
  'auth.login.twoFactor.mismatch': 'That code did not match.',
  'auth.forgot.hint': 'Enter the email on your platform-admin account.',
  'auth.forgot.submit': 'Send reset link',
  'auth.forgot.sentTitle': 'Check your inbox',
  'auth.forgot.sentLead': 'If an account exists for',
  'auth.forgot.sentTrail': ', a reset link has been sent.',
  'auth.reset.hint': 'Choose a new password for your platform-admin account.',
  'auth.reset.form.password': 'New password',
  'auth.reset.form.confirm': 'Confirm password',
  'auth.reset.submit': 'Reset password',
  'auth.reset.errors.tooShort': 'Password must be at least 12 characters.',
  'auth.reset.errors.mismatch': 'Passwords do not match.',
  'auth.reset.errors.invalidLink': 'This reset link is invalid. Request a new one.',
  'auth.reset.errors.failed': 'Could not reset the password.',
  'auth.reset.toast.success': 'Your password has been reset. Sign in with your new password.',
  'auth.reset.toast.successTitle': 'Password reset',
  'auth.mfa.title': 'Multi-factor sign-in',
  'auth.mfa.pending': 'Enter the six-digit code from your authenticator app to finish signing in.',
  // ── broadcasts ──
  'broadcasts.title': 'Broadcasts (platform-wide)',
  'broadcasts.description': 'Cross-tenant feed of broadcast jobs, so a tenant blasting the platform, a job stuck mid-send or an anomalous failure rate is visible before a customer reports it.',
  'broadcasts.tableTitle': 'All broadcasts',
  'broadcasts.totalLabel': '{{shown}} of {{total}}',
  'broadcasts.searchPlaceholder': 'Filter this page by name or organisation…',
  'broadcasts.kpi.all': 'All broadcasts',
  'broadcasts.kpi.finished': 'Finished',
  'broadcasts.kpi.withFailures': 'With failures',
  'broadcasts.filters.template': 'Template',
  'broadcasts.filters.templatePlaceholder': 'Any template',
  'broadcasts.filters.organisation': 'Organisation',
  'broadcasts.filters.organisationPlaceholder': 'Any org',
  'broadcasts.empty': 'No broadcasts match',
  'broadcasts.emptyFiltered': 'Try clearing your filters.',
  'broadcasts.emptyHint': 'No broadcast jobs in the recent window.',
  'broadcasts.tableEmpty': 'No broadcasts yet.',
  'broadcasts.loadError': 'Failed to load broadcasts.',
  'broadcasts.columns.org': 'Org',
  'broadcasts.columns.template': 'Template',
  'broadcasts.columns.tps': 'TPS',
  'broadcasts.columns.sentTotal': 'Sent / Total',
  'broadcasts.columns.filtered': 'Filtered',
  'broadcasts.columns.finished': 'Finished',
  'broadcasts.status.running': 'Running',
  'broadcasts.status.finished': 'Finished',
  'broadcasts.status.pending': 'Pending',
  'broadcasts.status.withFailures': 'With failures',
  'broadcasts.failedCount': '{{count}} failed',
  'broadcasts.list.meta': '{{sent}} / {{total}} sent · {{pct}}% · created {{when}}',
  'broadcasts.createdRelative': 'Created {{when}}',
  // ── conversations ──
  'conversations.title': 'Conversations (platform-wide)',
  'conversations.description': 'Cross-tenant inbox feed for support oversight. The preview is a sample of the last message; the thread itself is readable only from the tenant\'s own inbox.',
  'conversations.tableTitle': 'All conversations',
  'conversations.totalLabel': '{{shown}} of {{total}}',
  'conversations.searchPlaceholder': 'Filter by MSISDN, contact name or preview…',
  'conversations.kpi.all': 'All conversations',
  'conversations.kpi.unread': 'With unread',
  'conversations.kpi.unassigned': 'Unassigned',
  'conversations.kpi.recent': 'Active (24h)',
  'conversations.filters.organisation': 'Organisation',
  'conversations.filters.organisationPlaceholder': 'Any org',
  'conversations.filters.assignment': 'Assignment',
  'conversations.filters.assignmentPlaceholder': 'Any',
  'conversations.filters.assignment.assigned': 'Assigned',
  'conversations.filters.assignment.unassigned': 'Unassigned',
  'conversations.empty': 'No conversations match',
  'conversations.emptyFiltered': 'Try clearing your filters.',
  'conversations.emptyHint': 'No conversations yet.',
  'conversations.loadError': 'Failed to load conversations.',
  'conversations.columns.org': 'Org',
  'conversations.columns.msisdn': 'Contact MSISDN',
  'conversations.columns.contactName': 'Contact name',
  'conversations.columns.agent': 'Agent',
  'conversations.columns.unread': 'Unread',
  'conversations.columns.preview': 'Preview',
  'conversations.unassigned': 'unassigned',
  'conversations.noPreview': '(no preview)',
  'conversations.unreadCount': '{{count}} unread',
  // ── flows ──
  'flows.title': 'Flow runs',
  'flows.description': 'Cross-tenant view of every flow execution from the flows-llm service.',
  'flows.kpi.all': 'All runs',
  'flows.filters.statusPlaceholder': 'Any status',
  'flows.filters.flowPlaceholder': 'Any flow',
  'flows.columns.startedAt': 'Started',
  'flows.columns.finishedAt': 'Finished',
  'flows.columns.org': 'Org',
  'flows.columns.flow': 'Flow',
  'flows.columns.contact': 'Contact',
  'flows.columns.duration': 'Duration',
  'flows.table.title': 'Recent runs',
  'flows.table.total': '{{shown}} of {{total}} · last {{range}}',
  'flows.table.empty': 'No flow runs in window.',
  'flows.empty': 'No flow runs match',
  'flows.emptyHint': 'Trigger a flow or clear the active filters.',
  'flows.loadError': 'Failed to load flow runs.',
  'flows.list.for': 'for {{msisdn}}',
  'flows.list.started': 'Started {{at}}',
  'flows.list.finished': 'Finished {{at}}',
  'flows.view.loadError': 'Failed to load flow runs: {{message}}',
  'flows.view.notFound': 'Flow run not found in the last {{days}} days. The run may have been pruned or the id is wrong.',
  'flows.view.tabs.trace': 'Trace',
  'flows.view.tabs.related': 'Related runs',
  'flows.view.tabs.raw': 'Raw',
  'flows.view.run.title': 'Flow run',
  'flows.view.run.live': 'live',
  'flows.view.run.runLabel': 'run',
  'flows.view.run.flowLabel': 'flow',
  'flows.view.run.orgLabel': 'org',
  'flows.view.run.startedAt': 'started {{at}} ({{relative}})',
  'flows.view.run.finishedAt': 'finished {{relative}} · duration {{duration}}',
  'flows.view.run.runningFor': 'running {{duration}}',
  'flows.view.run.openTenant': 'Open tenant →',
  'flows.view.run.allRuns': '← All runs',
  'flows.view.overview.runDetails': 'Run details',
  'flows.view.overview.runId': 'Run ID',
  'flows.view.overview.flowHint': 'Flow definitions are tenant-scoped: open via the tenant console for the spec.',
  'flows.view.overview.conversation': 'Conversation',
  'flows.view.overview.noConversation': 'Not linked to a conversation',
  'flows.view.overview.timing': 'Timing',
  'flows.view.overview.stillRunning': 'still running',
  'flows.view.trace.title': 'Trace',
  'flows.view.trace.notice': 'Step-by-step trace persistence ships with W12 (flow builder). For now this view shows the run boundaries inferred from',
  'flows.view.trace.started': 'Flow started',
  'flows.view.trace.finished': 'Run ended ({{status}})',
  'flows.view.trace.running': 'Still running ({{status}})',
  'flows.view.related.sameFlow': 'Other runs of this flow',
  'flows.view.related.sameFlowEmpty': 'No other runs of this flow in the recent window.',
  'flows.view.related.sameConversation': 'Other runs in this conversation',
  'flows.view.related.sameConversationEmpty': 'No other runs in this conversation.',
  'flows.view.related.conversationLabel': 'conversation',
  'flows.view.related.noConversation': 'no conversation linked',
  'flows.view.related.notLinked': 'This run isn\'t tied to a conversation.',
  'flows.view.raw.title': 'Raw payload',
  'flows.view.raw.copy': 'Copy JSON',
  'flows.view.raw.shapeLead': 'The shape returned by',
  'flows.view.raw.usage': 'Use this when filing an issue or reproducing in a notebook.',
  'flows.view.raw.toast.copied': 'Run JSON copied to clipboard.',
  'flows.view.raw.toast.copiedTitle': 'Copied',
  // ── logs ──
  'logs.title': 'Logs',
  'logs.description': 'W27 logs consumer: query the reach_logs database across API, query (SQL), app, lifecycle and frontend families. Click a KPI to switch family.',
  'logs.family.api': 'API',
  'logs.family.query': 'Query (SQL)',
  'logs.family.app': 'App',
  'logs.family.lifecycle': 'Lifecycle',
  'logs.family.frontend': 'Frontend',
  'logs.kpi.api': 'API',
  'logs.kpi.query': 'Query',
  'logs.kpi.app': 'App',
  'logs.kpi.lifecycle': 'Lifecycle',
  'logs.kpi.frontend': 'Frontend',
  'logs.stats.rowsHint': 'rows',
  'logs.stats.apiLogs': 'API logs',
  'logs.stats.queryLogs': 'Query logs',
  'logs.stats.appLogs': 'App logs',
  'logs.stats.lifecycle': 'Lifecycle',
  'logs.stats.frontend': 'Frontend',
  'logs.statsLoadError': 'Failed to load /v1/admin/logs/stats; is the logs service running?',
  'logs.loadError': 'Failed to load logs; is the logs service running?',
  'logs.tableTitle': '{{family}} logs',
  'logs.tableDescription': 'Newest first. Level, service, environment and search are applied by the server, so the rows reflect the whole family.',
  'logs.totalLabel': '{{shown}} of {{total}} rows',
  'logs.searchPlaceholder': 'Search message, path, SQL, etc…',
  'logs.filters.level': 'Level',
  'logs.filters.levelPlaceholder': 'Any level',
  'logs.filters.service': 'Service',
  'logs.filters.servicePlaceholder': 'Any service',
  'logs.empty': 'No log rows match',
  'logs.emptyFiltered': 'Try clearing your filters or switching family.',
  'logs.emptyHint': 'No rows yet for this family.',
  'logs.tableEmpty': 'No rows match those filters.',
  'logs.summary.noSql': '(no sql)',
  'logs.summary.noMessage': '(no message)',
  'logs.columns.when': 'When',
  'logs.columns.service': 'Service',
  'logs.columns.level': 'Level',
  'logs.columns.method': 'Method',
  'logs.columns.path': 'Path',
  'logs.columns.ms': 'ms',
  'logs.columns.sql': 'SQL',
  'logs.columns.rows': 'Rows',
  'logs.columns.message': 'Message',
  'logs.columns.event': 'Event',
  'logs.columns.detail': 'Detail',
  'logs.columns.kind': 'Kind',
  'logs.query.title': 'Log query',
  'logs.query.description': 'Every filter the log endpoint accepts, in one form: family, level, service, environment, correlation id, organisation, time range, status class and free text. The query is mirrored into the URL so it can be shared verbatim.',
  'logs.query.form.family': 'Family',
  'logs.query.form.level': 'Level',
  'logs.query.form.levelAny': 'Any level',
  'logs.query.form.service': 'Service',
  'logs.query.form.servicePlaceholder': 'identity, channel-wa, …',
  'logs.query.form.searchPlaceholder': 'path / message substring',
  'logs.query.form.searchPlaceholderSql': 'SQL substring',
  // ── templates ──
  'templates.title': 'Templates',
  'templates.description': 'WhatsApp message templates and where each one stands in Meta review.',
  'templates.kpi.all': 'All templates',
  'templates.kpi.submitted': 'In review',
  'templates.filters.statusPlaceholder': 'Any status',
  'templates.filters.categoryPlaceholder': 'Any category',
  'templates.filters.languagePlaceholder': 'Any language',
  'templates.columns.category': 'Category',
  'templates.columns.language': 'Lang',
  'templates.columns.rejectionReason': 'Reject reason',
  'templates.table.title': 'All templates',
  'templates.table.total': '{{shown}} of {{total}}',
  'templates.table.empty': 'No templates yet.',
  'templates.searchPlaceholder': 'Filter by code or category…',
  'templates.empty': 'No templates match',
  'templates.emptyHint': 'No templates yet. This view lists the platform organisation\'s own templates; a tenant\'s catalogue is visible from their console.',
  'templates.loadError': 'Failed to load templates.',
  'templates.list.submitted': 'Submitted {{at}}',
  'templates.list.approved': 'Approved {{at}}',
  'templates.list.rejected': 'Rejected: {{reason}}',
  'templates.view.notFound': 'Template not found.',
  'templates.view.allTemplates': '← All templates',
  'templates.view.tabs.variables': 'Variables',
  'templates.view.tabs.components': 'Components',
  'templates.view.tabs.meta': 'Meta sync',
  'templates.view.tabs.lifecycle': 'Lifecycle',
  'templates.view.overview.preview': 'Preview',
  'templates.view.overview.previewHint': 'Closest render to what Meta will deliver. Variable positions are highlighted in amber.',
  'templates.view.overview.header': 'HEADER',
  'templates.view.overview.headerWithFormat': 'HEADER ({{format}})',
  'templates.view.overview.emptyBody': '(empty body)',
  'templates.view.overview.metadata': 'Metadata',
  'templates.view.variables.toast.copied': 'Filled body copied to clipboard.',
  'templates.view.variables.toast.copiedTitle': 'Copied',
  'templates.view.variables.emptyLead': 'This template has no',
  'templates.view.variables.emptyTrail': 'variables: it sends a static body.',
  'templates.view.variables.detected': 'Variables detected ({{count}})',
  'templates.view.variables.hint': 'Enter sample values to preview how the rendered body will look. Values are kept client-side; nothing is sent.',
  'templates.view.variables.form.label': 'Variable {{token}}',
  'templates.view.variables.form.placeholder': 'Sample value for {{token}}',
  'templates.view.variables.filledPreview': 'Filled preview',
  'templates.view.variables.noBody': '(no body text)',
  'templates.view.components.title': 'Components',
  'templates.view.meta.title': 'Meta sync',
  'templates.view.meta.templateId': 'Meta template id',
  'templates.view.meta.templateName': 'Meta template name',
  'templates.view.meta.submittedAt': 'Submitted at',
  'templates.view.meta.approvedAt': 'Approved at',
  'templates.view.meta.rejectionReason': 'Rejection reason',
  'templates.view.lifecycle.title': 'Lifecycle',
  'templates.view.lifecycle.steps.drafted': 'Drafted',
  'templates.view.lifecycle.steps.submitted': 'Submitted to Meta',
  'templates.view.lifecycle.steps.review': 'Meta review',
  // ── webhooks ──
  'webhooks.title': 'Webhook subscriptions',
  'webhooks.description': 'Registered webhook subscriptions across every tenant. The streak is the consecutive-failure count; at {{threshold}} the service disables the subscription itself.',
  'webhooks.tableTitle': 'All subscriptions',
  'webhooks.totalLabel': '{{shown}} of {{total}}',
  'webhooks.searchPlaceholder': 'Filter by URL or org…',
  'webhooks.kpi.all': 'All subscriptions',
  'webhooks.kpi.failing': 'Failing streak',
  'webhooks.filters.organisation': 'Organisation',
  'webhooks.filters.organisationPlaceholder': 'Any org',
  'webhooks.filters.event': 'Event type',
  'webhooks.filters.eventPlaceholder': 'Any event',
  'webhooks.filters.statusPlaceholder': 'Any',
  'webhooks.empty': 'No subscriptions match',
  'webhooks.emptyFiltered': 'Try clearing your filters.',
  'webhooks.emptyHint': 'No webhook subscriptions yet.',
  'webhooks.loadError': 'Failed to load webhook subscriptions.',
  'webhooks.columns.org': 'Org',
  'webhooks.columns.url': 'URL',
  'webhooks.columns.events': 'Events',
  'webhooks.columns.streak': 'Streak',
  'webhooks.list.metaOne': '{{count}} event · created {{when}}',
  'webhooks.list.metaOther': '{{count}} events · created {{when}}',
  'webhooks.failingCount': '{{count}} failing',
  'webhooks.createdRelative': 'Created {{when}}',
  'webhooks.deliveries.title': 'Webhook deliveries',
  'webhooks.deliveries.description': 'Delivery attempts across every tenant. An attempt the SSRF guard refused never leaves the platform, so it is shown as Blocked with its reason rather than as a pending attempt.',
  'webhooks.deliveries.tableTitle': 'Recent deliveries',
  'webhooks.deliveries.totalLabel': '{{shown}} of {{total}} · {{range}}',
  'webhooks.deliveries.kpi.all': 'All attempts',
  'webhooks.deliveries.kpi.success': 'Success',
  'webhooks.deliveries.kpi.slow': 'Slow (>1s)',
  'webhooks.deliveries.filters.eventType': 'Event type',
  'webhooks.deliveries.filters.eventTypePlaceholder': 'Any event',
  'webhooks.deliveries.filters.statusClass': 'Status class',
  'webhooks.deliveries.filters.statusClassPlaceholder': 'Any status',
  'webhooks.deliveries.filters.statusClass.success': '2xx Success',
  'webhooks.deliveries.filters.statusClass.redirect': '3xx Redirect',
  'webhooks.deliveries.filters.statusClass.clientError': '4xx Client error',
  'webhooks.deliveries.filters.statusClass.serverError': '5xx Server error',
  'webhooks.deliveries.empty': 'No deliveries match',
  'webhooks.deliveries.emptyHint': 'No delivery attempts in this window.',
  'webhooks.deliveries.tableEmpty': 'No webhook deliveries in window.',
  'webhooks.deliveries.loadError': 'Failed to load deliveries.',
  'webhooks.deliveries.columns.scheduled': 'Scheduled',
  'webhooks.deliveries.columns.org': 'Org',
  'webhooks.deliveries.columns.event': 'Event',
  'webhooks.deliveries.columns.attempt': 'Attempt',
  'webhooks.deliveries.columns.http': 'HTTP',
  'webhooks.deliveries.columns.latency': 'Latency',
  'webhooks.deliveries.latencyMs': '{{value}} ms',
  'webhooks.deliveries.list.attempt': 'attempt {{number}}',
  'webhooks.deliveries.list.org': 'Org',
  'webhooks.deliveries.list.scheduled': 'Scheduled {{when}}',
  // ── settings (recovered) ──
  'settings.description': 'Platform-admin profile, security and platform configuration.',
  'settings.appearance.theme.title': 'Theme',
  'settings.appearance.theme.description': 'Switch between light, dark or follow your OS preference.',
  'settings.appearance.accent.title': 'Accent',
  'settings.appearance.accent.description': 'Drives the primary color across charts, buttons, focus rings, and the active nav item.',
  'settings.appearance.density.title': 'Density',
  'settings.appearance.density.description': 'Comfortable for big screens · Cozy for laptops · Compact for power users.',
  'settings.appearance.density.comfortable': 'Comfortable',
  'settings.appearance.density.cozy': 'Cozy',
  'settings.appearance.density.compact': 'Compact',
  'settings.appearance.textScale.title': 'Text scale',
  'settings.appearance.textScale.description': 'Adjusts the base font size to taste: useful on high-DPI monitors.',
  'settings.appearance.textScale.small': 'Small',
  'settings.appearance.textScale.default': 'Default',
  'settings.appearance.textScale.large': 'Large',
  'settings.appearance.sidebar.title': 'Sidebar',
  'settings.appearance.sidebar.description': 'Expanded for label clarity, collapsed for more workspace.',
  'settings.appearance.sidebar.iconOnly': 'Icon only',
  'settings.appearance.sidebar.full': 'Full',
  'settings.appearance.motion.title': 'Motion',
  'settings.appearance.motion.description': 'How much of the console animates. Auto follows the reduce-motion setting of your operating system.',
  'settings.appearance.motion.reduceMotion': 'Reduce motion',
  'settings.appearance.motion.auto': 'Auto (OS)',
  'settings.appearance.motion.always': 'Always',
  'settings.appearance.motion.highContrast': 'High contrast',
  'settings.apiKeys.kpi.all': 'All keys',
  'settings.apiKeys.status.revoked': 'Revoked',
  'settings.apiKeys.kpi.idle': 'Idle 90d+',
  'settings.apiKeys.kpi.recent': 'Used 7d',
  'settings.apiKeys.filters.organisation': 'Organisation',
  'settings.apiKeys.filters.organisationPlaceholder': 'Any org',
  'settings.apiKeys.filters.scope': 'Scope',
  'settings.apiKeys.filters.scopePlaceholder': 'Any scope',
  'settings.apiKeys.modal.revoke.title': 'Revoke API key?',
  'settings.apiKeys.title': 'API keys (platform-wide)',
  'settings.apiKeys.description': 'Cross-tenant view of issued API keys. Tenants mint their own keys via the tenant console; this page lets platform staff audit and revoke any key.',
  'settings.apiKeys.actions.includeRevoked': 'Include revoked',
  'settings.apiKeys.table.title': 'All API keys',
  'settings.apiKeys.searchPlaceholder': 'Filter by name, prefix or org…',
  'settings.apiKeys.loadError': 'Failed to load API keys.',
  'settings.apiKeys.empty': 'No API keys issued yet.',
  'settings.apiKeys.columns.prefix': 'Prefix',
  'settings.apiKeys.columns.scopes': 'Scopes',
  'settings.layout.description': 'Tune the console to your preference. Changes save instantly.',
  'settings.backup.title': 'Backup & restore',
  'settings.backup.description': 'Snapshot policy + restore procedure for the dev stack today. Production-side automation lives in Phase 8 (Terraform + RDS PITR).',
  'settings.backup.procedure.title': 'Procedure',
  'settings.backup.procedure.step1Before': 'bodySmall',
  'settings.backup.procedure.step1After': 'Run',
  'settings.backup.procedure.step2Before': 'bodySmall',
  'settings.backup.procedure.step2After': 'Verify the resulting',
  'settings.backup.procedure.step4Before': 'bodySmall',
  'settings.backup.procedure.step4After': 'After restore, run',
  // ── tenants (recovered) ──
  'tenants.kpi.all': 'All tenants',
  'tenants.kpi.large': '5+ users',
  'tenants.kpi.empty': 'No users',
  'tenants.filters.country': 'Country',
  'tenants.filters.countryPlaceholder': 'Any country',
  'tenants.filters.timezone': 'Timezone',
  'tenants.filters.timezonePlaceholder': 'Any timezone',
  'tenants.filters.statusPlaceholder': 'Any status',
  'tenants.title': 'Tenants',
  'tenants.description': 'Manage organisations on the platform.',
  'tenants.table.title': 'All tenants',
  'tenants.searchPlaceholder': 'Filter by name, slug, country or tenant id…',
  'tenants.actions.clearAllFilters': 'Clear all filters',
  'tenants.empty': 'No tenants match',
  'tenants.emptyHint': 'Try clearing the KPI filter or drawer filters.',
  'tenants.loadError': 'Failed to load tenants.',
  'tenants.view.notFound': 'Tenant not found.',
  'tenants.view.tabs.users': 'Users',
  'tenants.view.tabs.billing': 'Billing',
  'tenants.view.tabs.integrations': 'Integrations',
  'tenants.view.tabs.audit': 'Audit',
  'tenants.view.overview.kpi.users': 'Users',
  'tenants.view.overview.kpi.spend': 'Spend (30d)',
  'tenants.view.overview.kpi.topUps': 'Top-ups (30d)',
  'tenants.view.overview.kpi.creditEntries': 'credit entries',
  'tenants.view.overview.kpi.integrations': 'Integrations',
  'tenants.view.overview.profile': 'Profile',
  'tenants.columns.slug': 'Slug',
  'tenants.columns.country': 'Country',
  'tenants.columns.timezone': 'Timezone',
  'tenants.view.overview.latestActivity': 'Latest activity',
  'tenants.view.overview.noActivity': 'No audit events in the last 30 days.',
  'tenants.view.overview.auditHint': 'Open the Audit tab for the full 7-day feed across all sources.',
  'tenants.view.settings.modal.suspend.body': 'Suspending blocks all API access and locks every user out of the tenant console. Outbound jobs already in flight will complete.',
  'tenants.view.settings.modal.reactivate.body': 'Re-activating restores API access and unlocks user sign-in.',
  'tenants.view.settings.audited': 'Action is audited.',
  'tenants.view.settings.toast.lifecyclePending': 'Tenant lifecycle endpoints ship with W23.2.',
  'tenants.view.settings.toast.pendingTitle': 'Action pending',
  'tenants.view.settings.toast.deletePending': 'Hard-delete tenant ships with W23.2.',
  'tenants.view.settings.lifecycle': 'Lifecycle',
  'tenants.view.settings.actions.suspend': 'Suspend tenant',
  'tenants.view.settings.actions.reactivate': 'Re-activate tenant',
  'tenants.view.settings.toast.rotatePending': 'Key rotation ships with W23.2.',
  'tenants.view.settings.dangerZone': 'Danger zone',
  'tenants.view.impersonate.toast.failed': 'Could not mint the impersonation token.',
  'tenants.view.impersonate.toast.failedTitle': 'Impersonation failed',
  'tenants.view.impersonate.tooltip': 'Open the tenant console as this organisation (audited)',
  'tenants.view.users.title': 'Tenant users',
  'tenants.view.users.searchPlaceholder': 'Search by email or name…',
  'tenants.view.users.noMatch': 'No users match that search.',
  'tenants.view.users.empty': 'No users yet for this tenant.',
  'tenants.view.users.columns.scope': 'Scope',
  'tenants.view.audit.title': 'Audit trail',
  'tenants.view.audit.description': 'Last 7 days, across transfers, opt-ins, deliveries, flow runs and ledger.',
  'tenants.view.audit.searchPlaceholder': 'Filter by summary text…',
  'tenants.view.audit.empty': 'No audit events for this tenant in the last 7 days.',
  'tenants.view.audit.columns.source': 'Source',
  'tenants.view.audit.columns.summary': 'Summary',
  'tenants.view.integrations.title': 'Tenant-scoped integrations',
  'tenants.view.integrations.empty': 'No tenant-scoped integrations yet.',
  'tenants.view.billing.title': 'Recent ledger entries',
  'tenants.view.billing.empty': 'No ledger entries for this tenant.',
  'tenants.view.billing.columns.amountMinor': 'Amount (minor)',
  'tenants.view.billing.columns.note': 'Note',
  'tenants.table.empty': 'No tenants yet.',
  'tenants.columns.users': 'Users',
  'tenants.list.users': 'users',

  // ── settings (authored: W39 completion pass) ──
  'settings.cards.open': 'Open →',
  'settings.cards.profile.title': 'Profile',
  'settings.cards.profile.hint': 'Update your platform-admin profile and locale.',
  'settings.cards.security.title': 'Security',
  'settings.cards.security.hint': 'Rotate your password and review active sessions.',
  'settings.cards.featureFlags.title': 'Feature flags',
  'settings.cards.featureFlags.hint': 'Toggle platform-wide flags and per-scope overrides.',
  'settings.layout.nav.you': 'You',
  'settings.layout.nav.platform': 'Platform',
  'settings.layout.nav.profile': 'Profile',
  'settings.layout.nav.appearance': 'Appearance',
  'settings.layout.nav.security': 'Security',
  'settings.layout.nav.apiKeys': 'API keys',
  'settings.layout.nav.integrations': 'Integrations',
  'settings.layout.nav.featureFlags': 'Feature flags',
  'settings.layout.nav.backup': 'Backup & restore',

  // ── settings › API keys (authored: W39 completion pass) ──
  'settings.apiKeys.actions.revoke': 'Revoke',
  'settings.apiKeys.columns.org': 'Org',
  'settings.apiKeys.columns.rpm': 'RPM',
  'settings.apiKeys.columns.lastUsed': 'Last used',
  'settings.apiKeys.filters.statusPlaceholder': 'Any',
  'settings.apiKeys.emptyMatch': 'No API keys match',
  'settings.apiKeys.emptyHint': 'Try clearing your filters.',
  'settings.apiKeys.table.total': '{{shown}} of {{total}}',
  'settings.apiKeys.neverUsed': 'never',
  'settings.apiKeys.list.meta': '{{rpm}} rpm · last used {{lastUsed}}',
  'settings.apiKeys.card.rpm': '{{rpm}} rpm',
  'settings.apiKeys.card.lastUsed': 'Last used {{lastUsed}}',
  'settings.apiKeys.modal.revoke.body': 'Revoke API key “{{name}}”? Active sessions keep working until the JWT expires.',
  'settings.apiKeys.toast.revoked': 'Key {{name}} revoked.',
  'settings.apiKeys.toast.revokedTitle': 'API key revoked',
  'settings.apiKeys.toast.revokeFailedTitle': 'Revoke failed',

  // ── settings › backup & restore (authored: W39 completion pass) ──
  // The local-snapshot sentence is split around inline <code> spans, so each
  // fragment is its own key; keep them translatable as clause-sized units.
  'settings.backup.local.title': 'Local snapshot',
  'settings.backup.local.uses': 'uses',
  'settings.backup.local.whichRuns': 'which runs',
  'settings.backup.local.againstEvery': 'against every',
  'settings.backup.local.databaseAndEmits': 'database and emits a manifest in',
  'settings.backup.local.seeSystemBackups': 'See the System → Backups page for the most recent snapshot timestamps.',
  'settings.backup.procedure.step3Before': 'To restore:',
  'settings.backup.procedure.note': 'Every script in scripts/dr/ is idempotent and safe to re-run. reconcile-ledger.sh enforces five invariants and exits non-zero on any drift.',
  'settings.backup.drillNotice': 'The production-side restore drill (cross-host PITR + smoke verification) is gating before onboarding the first paying tenant. Tracked in Phase 8 T8.2.',

  // ── tenants (authored: W39 completion pass) ──
  'tenants.createdAt': 'Created {{date}}',
  'tenants.totalLabel': '{{count}} of {{total}}',
  'tenants.view.header.users': '{{count}} users',
  'tenants.view.header.created': 'created {{date}}',
  'tenants.view.header.id': 'id',
  'tenants.view.header.backToAll': '← All tenants',
  'tenants.view.overview.kpi.tenantScope': 'tenant scope',
  'tenants.view.overview.kpi.platformAdminsOne': '{{count}} platform admin',
  'tenants.view.overview.kpi.platformAdminsOther': '{{count}} platform admins',
  'tenants.view.overview.kpi.ledgerEntries': '{{count}} ledger entries',
  'tenants.view.overview.kpi.configured': '{{count}} configured',
  'tenants.view.users.columns.displayName': 'Display name',
  'tenants.view.users.createdAt': 'Created {{date}}',
  'tenants.view.users.createdRelative': 'created {{when}}',
  'tenants.view.audit.columns.when': 'When',
  'tenants.view.billing.columns.when': 'When',
  'tenants.view.billing.columns.sku': 'SKU',
  'tenants.view.billing.openLedger': 'Open full ledger →',
  'tenants.view.integrations.manageAll': 'Manage all integrations →',
  'tenants.view.integrations.hint': 'Only credentials with {{scope}} are listed here. Platform-wide providers (Meta, billing gateways) are managed globally.',
  'tenants.view.integrations.loadError': 'Failed to load integrations: {{message}}',
  'tenants.view.integrations.status.enabled': 'enabled',
  'tenants.view.integrations.status.disabled': 'disabled',
  'tenants.view.impersonate.button': 'Impersonate',
  'tenants.view.impersonate.modal.title': 'Impersonate {{name}}?',
  'tenants.view.impersonate.modal.body': 'This mints a session as {{name}} that lasts at most 15 minutes, and opens the tenant console in a new tab.',
  'tenants.view.impersonate.modal.confirm': 'Open as tenant',
  'tenants.view.settings.modal.suspend.title': 'Suspend {{name}}?',
  'tenants.view.settings.modal.reactivate.title': 'Re-activate {{name}}?',
  'tenants.view.settings.modal.delete.title': 'Delete {{name}}?',
  'tenants.view.settings.modal.delete.body': 'Permanently removes the tenant and all associated data (users, contacts, broadcasts, ledger, integrations). Cannot be undone: export critical data first.',
  'tenants.view.settings.modal.delete.confirm': 'Delete tenant',
  'tenants.view.settings.actions.rotateKeys': 'Rotate API keys',
  'tenants.view.settings.actions.delete': 'Delete tenant…',
  'tenants.view.settings.dangerWarning': 'Deleting a tenant is permanent. Make sure you have an export of the ledger and any critical templates before continuing.',

  // ── common (W39 completion pass) ──
  'common.optionalPlaceholder': 'optional',
  'common.tryClearingFilters': 'Try clearing your filters.',
  'common.validation': 'Validation',

  // ── billing › overview ──
  'billing.overview.title': 'Billing',
  'billing.overview.description': 'Platform-wide wallets, ledger volume and billing operations.',
  'billing.overview.open': 'Open →',
  'billing.overview.operations': 'Operations',
  'billing.overview.kpi.wallets': 'Wallets',
  'billing.overview.kpi.ledgerVolume': 'Ledger volume (7d)',
  'billing.overview.kpi.entries': 'entries',
  'billing.overview.kpi.totalWallets': 'Total wallet count',
  'billing.overview.kpi.acrossCurrencies': 'across all currencies',
  'billing.overview.cards.skuRates.title': 'SKU rates',
  'billing.overview.cards.skuRates.hint': 'Edit per-event pricing for messaging, LLM, broadcasts.',
  'billing.overview.cards.ledger.title': 'Ledger',
  'billing.overview.cards.ledger.hint': 'Search and export ledger entries by organisation, currency and SKU.',
  'billing.overview.cards.subscriptions.title': 'Subscriptions',
  'billing.overview.cards.subscriptions.hint': 'Per-org plan, renewal date and monthly recurring revenue.',
  'billing.overview.cards.plans.title': 'Plans',
  'billing.overview.cards.plans.hint': 'Manage the platform\'s published plans.',
  'billing.overview.cards.gateways.title': 'Payment gateways',
  'billing.overview.cards.gateways.hint': 'PayFast, Stripe and Paystack credential status.',

  // ── billing › payment gateways (ITN) ──
  'billing.itn.title': 'Payment gateways',
  'billing.itn.description': 'Credential and connection status for each payment provider, read live from the integrations registry. This page shows configuration, not ITN traffic.',
  'billing.itn.footnote': 'Per-gateway success rate and latency surface here once the ITN telemetry endpoint ships.',
  'billing.itn.gateway.payfast': 'South African EFT + cards.',
  'billing.itn.gateway.stripe': 'International cards.',
  'billing.itn.gateway.paystack': 'Africa-first cards + USSD.',
  'billing.itn.test.passed': 'Last test passed',
  'billing.itn.test.failed': 'Last test failed',
  'billing.itn.test.untested': 'Not yet tested',
  'billing.itn.config.checking': 'Checking…',
  'billing.itn.config.connected': 'Connected',
  'billing.itn.config.needsAttention': 'Needs attention',

  // ── billing › ledger ──
  'billing.ledger.title': 'Ledger',
  'billing.ledger.description': 'Search and export ledger entries by organisation and SKU.',
  'billing.ledger.tableTitle': 'Ledger entries',
  'billing.ledger.totalLabel': '{{shown}} of {{total}}',
  'billing.ledger.orgSuffix': 'organisation {{org}}',
  'billing.ledger.searchPlaceholder': 'Filter by description, SKU, org id…',
  'billing.ledger.kpi.all': 'All entries',
  'billing.ledger.kpi.credits': 'Credits',
  'billing.ledger.kpi.debits': 'Debits',
  'billing.ledger.kpi.sku': 'SKU entries',
  'billing.ledger.kpi.adjustments': 'Adjustments',
  'billing.ledger.filters.anyCurrency': 'Any currency',
  'billing.ledger.filters.skuCode': 'SKU code',
  'billing.ledger.filters.anySku': 'Any SKU',
  'billing.ledger.filters.orgId': 'Org ID',
  'billing.ledger.filters.limit': 'Rows fetched',
  'billing.ledger.empty': 'No ledger entries match',
  'billing.ledger.emptyHint': 'Adjust the filters, choose a larger row limit, or clear the active filters.',
  'billing.ledger.emptyRows': 'No ledger entries matched.',
  'billing.ledger.loadError': 'Failed to load ledger.',
  'billing.ledger.columns.when': 'When',
  'billing.ledger.columns.org': 'Org',
  'billing.ledger.columns.sku': 'SKU',
  'billing.ledger.columns.note': 'Note',
  'billing.ledger.columns.idempotency': 'Idempotency',
  'billing.ledger.list.org': 'Org',
  'billing.ledger.list.idem': 'idem {{key}}',
  'billing.ledger.badge.sku': 'SKU',
  'billing.ledger.badge.adjust': 'Adjust',

  // ── billing › invoices ──
  'billing.invoices.title': 'Invoices',
  'billing.invoices.description': 'One row per billing cycle, across every tenant. PDFs are cached in object storage; Regenerate deletes the cached blob so the next download re-runs the template.',
  'billing.invoices.generate': 'Generate invoice',
  'billing.invoices.regenerate': 'Regenerate',
  'billing.invoices.loadError': 'Failed to load invoices.',
  'billing.invoices.empty': 'No invoices match',
  'billing.invoices.footnote': 'Validation is the SARS (South Africa) or FIRS (Nigeria) verdict stored on the invoice. Validate re-runs the check and saves the result.',
  'billing.invoices.columns.number': 'Number',
  'billing.invoices.columns.org': 'Org',
  'billing.invoices.columns.period': 'Period',
  'billing.invoices.columns.pdf': 'PDF',
  'billing.invoices.pdf.rendered': 'Rendered {{when}}',
  'billing.invoices.pdf.notYet': 'Not yet',
  'billing.invoices.toast.regenQueued': 'Invoice {{number}} marked for regeneration.',
  'billing.invoices.toast.regenQueuedTitle': 'Regenerate queued',
  'billing.invoices.toast.regenFailed': 'Regenerate failed',
  'billing.invoices.toast.generated': '{{number}}, {{amount}} incl. {{vatLabel}} {{vatPercent}}%. Validation: {{validation}}.',
  'billing.invoices.toast.generatedTitle': 'Invoice generated',
  'billing.invoices.toast.generateFailed': 'Generate failed',
  'billing.invoices.form.title': 'Generate invoice',
  'billing.invoices.form.vatHint': 'VAT is computed from the selected country\'s tax profile (ZA 15% · NG 7.5% · KE 16%) and the label + percent are snapshotted into the invoice so the PDF stays correct if the rate later changes.',
  'billing.invoices.form.orgId': 'Organisation ID',
  'billing.invoices.form.orgName': 'Organisation name',
  'billing.invoices.form.orgNamePlaceholder': 'Shown on the PDF',
  'billing.invoices.form.vatNumber': 'VAT number',
  'billing.invoices.form.country': 'Country',
  'billing.invoices.form.country.za': 'South Africa: VAT 15% (SARS)',
  'billing.invoices.form.country.ng': 'Nigeria: VAT 7.5% (FIRS)',
  'billing.invoices.form.country.ke': 'Kenya: VAT 16% (KRA)',
  'billing.invoices.form.periodStart': 'Period start',
  'billing.invoices.form.periodEnd': 'Period end',
  'billing.invoices.form.lineDescription': 'Line description',
  'billing.invoices.form.amount': 'Amount (minor units, VAT-exclusive)',
  'billing.invoices.form.amountHint': 'e.g. 99900 = 999.00 in the major unit',
  'billing.invoices.form.issueNow': 'Issue immediately',
  'billing.invoices.form.issueNowHint': 'Off = save as draft.',
  'billing.invoices.form.submit': 'Generate',
  'billing.invoices.form.errors.orgRequired': 'Organisation ID is required.',
  'billing.invoices.form.errors.amountPositive': 'Amount (minor units) must be a positive integer.',

  // ── billing › plans ──
  'billing.plans.title': 'Plans',
  'billing.plans.description': 'The platform\'s plan catalogue, with the active subscribers and the monthly revenue each plan carries. New plans are added by migration today; operator-driven creation is a follow-up.',
  'billing.plans.loadError': 'Failed to load plans or subscriptions.',
  'billing.plans.tableTitle': 'Plans',
  'billing.plans.totalLabel': '{{shown}} of {{total}}',
  'billing.plans.searchPlaceholder': 'Filter by code or name…',
  'billing.plans.kpi.all': 'All plans',
  'billing.plans.kpi.free': 'Free',
  'billing.plans.kpi.paid': 'Paid',
  'billing.plans.archived': 'Archived',
  'billing.plans.empty': 'No plans match',
  'billing.plans.emptyRows': 'No plans defined.',
  'billing.plans.columns.monthly': 'Monthly',
  'billing.plans.columns.includedCredits': 'Included credits',
  'billing.plans.columns.activeSubs': 'Active subs',
  'billing.plans.credits': 'Credits',
  'billing.plans.list.meta': '{{monthly}} / month · {{credits}} credits included',
  'billing.plans.subsCount': '{{count}} subs',
  'billing.plans.activeSubsCount': '{{count}} active subs',
  'billing.plans.subs.searchPlaceholder': 'Filter by org…',
  'billing.plans.subs.listMeta': 'Started {{started}} · ends {{ends}}',

  // ── billing › subscriptions (shared with the plans page section) ──
  'billing.subscriptions.title': 'Subscriptions',
  'billing.subscriptions.description': 'Cross-tenant subscription roster: which organisation is on which plan, and its billing-cycle state.',
  'billing.subscriptions.totalLabel': '{{shown}} of {{total}}',
  'billing.subscriptions.searchPlaceholder': 'Filter by org id, plan, status…',
  'billing.subscriptions.kpi.all': 'All subscriptions',
  'billing.subscriptions.kpi.pastDue': 'Past due',
  'billing.subscriptions.filters.anyStatus': 'Any status',
  'billing.subscriptions.filters.plan': 'Plan',
  'billing.subscriptions.filters.anyPlan': 'Any plan',
  'billing.subscriptions.filters.organisation': 'Organisation',
  'billing.subscriptions.filters.anyOrg': 'Any org',
  'billing.subscriptions.empty': 'No subscriptions match',
  'billing.subscriptions.emptyHint': 'Try clearing the KPI filter or drawer filters.',
  'billing.subscriptions.emptyRows': 'No subscriptions yet.',
  'billing.subscriptions.loadError': 'Failed to load subscriptions.',
  'billing.subscriptions.columns.org': 'Org',
  'billing.subscriptions.columns.plan': 'Plan',
  'billing.subscriptions.columns.started': 'Started',
  'billing.subscriptions.columns.cycleEnds': 'Cycle ends',
  'billing.subscriptions.list.org': 'org {{id}}',
  'billing.subscriptions.list.meta': 'Started {{started}} · Cycle {{cycle}}',
  'billing.subscriptions.list.cancelled': 'Cancelled {{when}}',

  // ── billing › SKU rates ──
  'billing.skuRates.title': 'SKU rates',
  'billing.skuRates.description': 'The price book every tenant is charged from: what each unit costs the platform, what it is listed at and what each plan pays for it. No unit is listed under its floor, the cost plus the free plan\'s markup. Changing a rate starts a new version today and keeps the old one for reconciliation.',
  'billing.skuRates.tableTitle': 'All SKUs',
  'billing.skuRates.totalLabel': '{{shown}} of {{total}}',
  'billing.skuRates.searchPlaceholder': 'Filter by SKU code or name…',
  'billing.skuRates.kpi.all': 'All rates',
  'billing.skuRates.historical': 'Historical',
  'billing.skuRates.empty': 'No SKU rates match',
  'billing.skuRates.emptyHint': 'Add SKUs through the billing service migration, or clear the active filters.',
  'billing.skuRates.emptyRows': 'No SKU rates configured.',
  'billing.skuRates.loadError': 'Failed to load SKU rates.',
  'billing.skuRates.columns.sku': 'SKU',
  'billing.skuRates.columns.effectiveFrom': 'Effective from',
  'billing.skuRates.list.effectiveFrom': 'Effective from {{date}}',
  'billing.skuRates.edit.amountAria': 'New amount in minor units',
  'billing.skuRates.toast.updated': '{{sku}}: {{from}} → {{to}}, effective today. The previous version is retired, not overwritten.',
  'billing.skuRates.toast.updatedTitle': 'Rate updated',
  'billing.skuRates.toast.updateFailed': 'Update failed',

  // ── billing › usage ──
  'billing.usage.title': 'Usage',
  'billing.usage.description': 'Per-tenant usage rolled up from the billing ledger. It samples the most recent 1 000 ledger entries, so on a busy platform the sample can be narrower than the range you choose.',
  'billing.usage.tableTitle': 'Tenant usage',
  'billing.usage.totalLabel': '{{shown}} of {{total}}',
  'billing.usage.searchPlaceholder': 'Filter by organisation id…',
  'billing.usage.kpi.all': 'All tenants',
  'billing.usage.filters.activity': 'Activity',
  'billing.usage.filters.anyActivity': 'Any',
  'billing.usage.filters.hasDebits': 'Has debits',
  'billing.usage.filters.creditsOnly': 'Credits only',
  'billing.usage.empty': 'No usage in this window',
  'billing.usage.emptyHint': 'No ledger activity in the sampled range. Clear the active filters or choose a shorter range.',
  'billing.usage.emptyRows': 'No usage in this window.',
  'billing.usage.columns.organisation': 'Organisation',
  'billing.usage.columns.events': 'Events',
  'billing.usage.columns.debit': 'Debit',
  'billing.usage.columns.credit': 'Credit',
  'billing.usage.list.eventsOne': '{{count}} ledger event in window',
  'billing.usage.list.eventsOther': '{{count}} ledger events in window',
  'billing.usage.list.debit': 'debit',
  'billing.usage.list.credit': 'credit',
  'billing.usage.window.aria': 'Window',
  'billing.usage.window.24h': 'Last 24 hours',
  'billing.usage.window.7d': 'Last 7 days',
  'billing.usage.window.30d': 'Last 30 days',

  // ── users ──
  'users.title': 'Users',
  'users.description': 'Every account on the platform, across tenants and staff.',
  'users.tableTitle': 'All users',
  'users.totalLabel': '{{shown}} of {{total}} users',
  'users.searchPlaceholder': 'Filter by name, email, organisation or id…',
  'users.kpi.all': 'All users',
  'users.kpi.tenant': 'Tenant users',
  'users.kpi.platform': 'Platform staff',
  'users.kpi.recent': 'Joined 30d',
  'users.filters.scope': 'Scope',
  'users.filters.anyScope': 'Any scope',
  'users.filters.anyOrganisation': 'Any organisation',
  'users.scope.tenant': 'Tenant',
  'users.scope.platform': 'Platform',
  'users.empty': 'No users match',
  'users.emptyHint': 'Try clearing the active filters.',
  'users.emptyRows': 'No users yet.',
  'users.loadError': 'Failed to load users.',
  'users.columns.displayName': 'Display name',
  'users.columns.org': 'Org',
  'users.list.created': 'Created {{date}}',
  'users.view.actionPending': 'Action pending',
  'users.view.account.title': 'Account',
  'users.view.account.userId': 'User ID',
  'users.view.activity.title': 'Recent tenant activity',
  'users.view.activity.hintLead': 'Audit is keyed by organisation. Showing the last 30 days for',
  'users.view.activity.thisTenant': 'this tenant',
  'users.view.activity.empty': 'No tenant activity in the last 30 days.',
  'users.view.scope.button.toPlatform': 'Change scope to platform',
  'users.view.scope.button.toTenant': 'Change scope to tenant',
  'users.view.scope.modal.titleToPlatform': 'Move {{email}} to platform scope?',
  'users.view.scope.modal.titleToTenant': 'Move {{email}} to tenant scope?',
  'users.view.scope.modal.bodyToPlatform': 'Granting platform scope gives this user admin access across every tenant. This action is audited.',
  'users.view.scope.modal.bodyToTenant': 'Demoting to tenant scope removes platform-admin access. This action is audited.',
  'users.view.scope.modal.confirmToPlatform': 'Move to platform',
  'users.view.scope.modal.confirmToTenant': 'Move to tenant',
  'users.view.scope.toast.nowPlatform': '{{email}} is now platform-scoped.',
  'users.view.scope.toast.nowTenant': '{{email}} is now tenant-scoped.',
  'users.view.scope.toast.updatedTitle': 'Scope updated',
  'users.view.scope.toast.failed': 'Failed to update scope.',
  'users.view.security.title': 'Security posture',
  'users.view.security.emailVerified': 'Email verified',
  'users.view.security.twoFactor': 'Two-factor',
  'users.view.security.activeSessions': 'Active sessions',
  'users.view.security.lastSignIn': 'Last sign-in',
  'users.view.security.hint.emailVerified': 'admin.getUser projection lands with W23.1',
  'users.view.security.hint.twoFactor': 'users can manage their own 2FA in the tenant console',
  'users.view.security.hint.lastSignIn': 'captured on /v1/auth/login: W23.1 backfill',
  'users.view.security.actions.title': 'Admin actions',
  'users.view.security.actions.reset': 'Force password reset',
  'users.view.security.actions.resetPending': 'Forced password-reset link will email {{email}} once W23.1 ships.',
  'users.view.security.actions.revoke': 'Revoke all sessions',
  'users.view.security.actions.revokePending': 'Session revocation lands with W23.1.',
  'users.view.security.actions.disable': 'Switch off',
  'users.view.security.actions.disablePending': 'Disable user lands with W23.1.',
  'users.view.security.actions.auditNote': 'Impersonation is recorded with the operator who started it, and appears on the Audit log and the Security events feed. Other admin actions on this page are not yet attributed.',
  'users.view.danger.warning': 'Destructive operations. Confirm before continuing. Only impersonation records who acted; the rest leave no actor on the audit trail yet.',
  'users.view.danger.transfer.title': 'Transfer organisation',
  'users.view.danger.transfer.body': 'Move this user to a different tenant. Their audit trail in the prior tenant is preserved.',
  'users.view.danger.transfer.action': 'Transfer to another tenant…',
  'users.view.danger.transfer.pending': 'Transfer-organisation lands with W23.1.',
  'users.view.danger.delete.title': 'Delete user',
  'users.view.danger.delete.action': 'Delete user…',
  'users.view.danger.delete.bodyLead': 'Permanently removes',
  'users.view.danger.delete.bodyTrail': 'from the platform. Their tenant\'s data is unaffected.',
  'users.view.danger.delete.modalTitle': 'Delete {{email}}?',
  'users.view.danger.delete.modalBody': 'This permanently removes the user account, their refresh tokens, and any pending verification codes. Org-owned data (contacts, broadcasts, ledger) stays with the tenant.',
  'users.view.danger.delete.modalNote': 'This action is audited and cannot be undone.',
  'users.view.danger.delete.confirm': 'Delete user',
  'users.view.danger.delete.pending': 'Hard-delete endpoint lands with W23.1.',

  // ── audit ──
  'audit.title': 'Audit log',
  'audit.description': 'Fan-in of transfers, opt-in, deliveries, flow runs and ledger entries across every tenant.',
  'audit.tableTitle': 'Recent events',
  'audit.totalLabel': '{{count}} events · {{range}}',
  'audit.kpi.all': 'All events',
  'audit.kpi.transfers': 'Transfers',
  'audit.kpi.optIn': 'Opt-in',
  'audit.kpi.deliveries': 'Deliveries',
  'audit.kpi.flowRuns': 'Flow runs',
  'audit.kpi.ledger': 'Ledger',
  'audit.filters.anySource': 'Any source',
  'audit.empty': 'No events match',
  'audit.emptyHint': 'No events match these filters. Widen the range or clear them.',
  'audit.emptyRows': 'No audit events.',
  'audit.columns.when': 'When',
  'audit.columns.source': 'Source',
  'audit.columns.org': 'Org',
  'audit.columns.summary': 'Summary',

  // ── settings › profile ──
  'settings.profile.noDisplayName': 'No display name',
  'settings.profile.locale.title': 'Locale',
  'settings.profile.locale.description': 'Locale drives currency formatting, dates, and number grouping across the console.',
  'settings.profile.toast.saved': 'Your display name and locale are saved.',
  'settings.profile.toast.savedTitle': 'Profile updated',
  'settings.profile.displayNameNotice.title': 'Display name editing',
  'settings.profile.displayNameNotice.lead': 'Endpoint',
  'settings.profile.displayNameNotice.trail': '(PATCH) is pending: drop-in once it lands.',

  // ── settings › security ──
  'settings.security.password.title': 'Password',
  'settings.security.password.lead': 'Password rotation lives behind',
  'settings.security.password.trail': '(W23). Until that ships, rotate by signing out and using the forgot-password flow.',
  'settings.security.sessions.body': 'Multi-session enumeration is pending W23 (refresh-token table per session).',
  'settings.security.mfa.title': 'MFA enrolment',
  'settings.security.mfa.body': 'TOTP enrolment for platform admins is W24 (Phase 8 hardening).',

  // ── settings › feature flags ──
  'settings.flags.description': 'The rollout-switch registry in the settings database. No service evaluates these yet, so flipping one has no runtime effect until the evaluation client ships; this page keeps the registry, its owners and its per-scope overrides in order ahead of that.',
  'settings.flags.showArchived': 'Show archived',
  'settings.flags.new': 'New flag',
  'settings.flags.loadError': 'Failed to load feature flags.',
  'settings.flags.empty': 'No feature flags defined yet.',
  'settings.flags.createFirst': 'Create the first flag',
  'settings.flags.archived': 'archived',
  'settings.flags.owner': 'Owner: {{owner}}',
  'settings.flags.noOwner': 'No owner set',
  'settings.flags.rollout': 'Rollout: {{percent}}%',
  'settings.flags.overridesOne': '{{count}} override',
  'settings.flags.overridesOther': '{{count}} overrides',
  'settings.flags.on': 'ON',
  'settings.flags.off': 'OFF',
  'settings.flags.defaultState': '{{state}} default',
  'settings.flags.toggleDefault': 'Toggle default',
  'settings.flags.scopedOverrides': 'Scoped overrides',
  'settings.flags.noOverrides': 'No overrides defined.',
  'settings.flags.addOverride': 'Add override',
  'settings.flags.delete.title': 'Delete feature flag?',
  'settings.flags.delete.bodyLead': 'Delete flag',
  'settings.flags.delete.bodyTrail': 'and all its overrides? Evaluations fall back to off.',
  'settings.flags.toast.toggledOn': '{{key}} → on',
  'settings.flags.toast.toggledOff': '{{key}} → off',
  'settings.flags.toast.toggledTitle': 'Default toggled',
  'settings.flags.toast.toggleFailed': 'Toggle failed',
  'settings.flags.toast.saved': '{{key}} saved.',
  'settings.flags.toast.savedTitle': 'Feature flag updated',
  'settings.flags.toast.saveFailed': 'Save failed',
  'settings.flags.toast.overrideSaved': 'Override saved.',
  'settings.flags.scope.global': 'Global',
  'settings.flags.scope.country': 'Country (ISO-2)',
  'settings.flags.scope.org': 'Organisation (UUID)',
  'settings.flags.scope.subAccount': 'Sub-account (UUID)',
  'settings.flags.scope.user': 'User (UUID)',
  'settings.flags.form.newTitle': 'New feature flag',
  'settings.flags.form.editTitle': 'Edit {{key}}',
  'settings.flags.form.key': 'Key',
  'settings.flags.form.keyHint': 'Stable identifier referenced from code. Lowercase + dots, e.g. `inbox.experimental_assist`.',
  'settings.flags.form.owner': 'Owner',
  'settings.flags.form.ownerPlaceholder': 'Workstream tag or team name (e.g. W22-meta)',
  'settings.flags.form.defaultEnabled': 'Default enabled',
  'settings.flags.form.rollout': 'Rollout %',
  'settings.flags.form.rolloutHint': '0-100. Stored for the future evaluator; no service buckets on it today.',
  'settings.flags.form.tags': 'Tags',
  'settings.flags.form.tagsHint': 'Comma-separated; UI uses for grouping (`killswitch`, `experiment`, `ops`).',
  'settings.flags.form.archived': 'Archived',
  'settings.flags.form.archivedHint': 'Hide from default lists. Existing overrides keep evaluating until you delete them.',
  'settings.flags.form.target': 'Target',
  'settings.flags.form.targetPlaceholder': 'UUID or country code',
  'settings.flags.form.reason': 'Reason',
  'settings.flags.form.reasonPlaceholder': 'e.g. \'beta tenant\' or \'kill switch: incident #123\'',
  'settings.flags.form.errors.keyRequired': 'Flag key is required.',
  'settings.flags.form.errors.rolloutRange': 'Rollout percent must be 0–100.',
  'settings.flags.form.errors.targetRequired': 'Scope target is required.',

  // ── settings › integrations ──
  'settings.integrations.description': 'Connect external services (Meta WhatsApp, AWS Bedrock, PayFast, PagerDuty …). Credentials live in the settings DB and are read by services through IntegrationResolver.',
  'settings.integrations.fireTestAlert': 'Fire test alert',
  'settings.integrations.loadError': 'Failed to load integrations.',
  'settings.integrations.noDescription': 'No description.',
  'settings.integrations.testedOk': 'Tested ok',
  'settings.integrations.configure': 'Configure',
  'settings.integrations.connect': 'Connect',
  'settings.integrations.docs': 'Docs ↗',
  'settings.integrations.providerDocs': 'Provider docs ↗',
  'settings.integrations.remove.title': 'Remove integration?',
  'settings.integrations.remove.body': 'Remove the configured {{name}} integration? Services using it will report not_configured.',
  'settings.integrations.toast.alertFired': 'Fired test alert to {{channels}}.',
  'settings.integrations.toast.alertFiredTitle': 'Alert dispatched',
  'settings.integrations.toast.noAlertChannel': 'No PagerDuty or Slack integration is enabled with valid credentials.',
  'settings.integrations.toast.nothingDispatched': 'Nothing dispatched',
  'settings.integrations.toast.testAlertFailed': 'Test alert failed',
  'settings.integrations.toast.saved': '{{name}} saved.',
  'settings.integrations.toast.savedTitle': 'Integration updated',
  'settings.integrations.form.title': 'Configure {{name}}',
  'settings.integrations.form.loadError': 'Failed to load current values.',
  'settings.integrations.form.loading': 'Loading current values…',
  'settings.integrations.form.operational': 'Operational',
  'settings.integrations.form.enabledHint': 'Services only read this integration when enabled. Disable to take it offline without deleting credentials.',
  'settings.integrations.form.notes': 'Notes',
  'settings.integrations.form.notesPlaceholder': 'Internal memo: e.g. \'rotating creds Q3\' or \'pending vendor approval\'.',
  'settings.integrations.form.missingRequired': 'Missing required: {{fields}}. You can still save as draft (Enabled off) until creds arrive.',

  // ── system ──
  'system.title': 'System',
  'system.description': 'The operational surface: service health, alerting, backups, the message broker, logs and API traffic.',
  'system.cards.health.title': 'Health',
  'system.cards.health.hint': 'Live /health/ready probe across every service, refreshed every 15 s.',
  'system.cards.alerts.title': 'Alerts',
  'system.cards.alerts.hint': 'Send a one-off alert or a test page through the configured PagerDuty, Slack and Linear channels.',
  'system.cards.backups.title': 'Backups',
  'system.cards.backups.hint': 'Backup, restore and drill commands. Snapshot history is not centralised yet.',
  'system.health.title': 'System health',
  'system.health.description': 'Each service reports its /health/ready, probed from inside the cluster.',
  'system.health.loadError': 'Failed to query service health.',
  'system.backups.title': 'Backups',
  'system.backups.description': 'Snapshot history for the platform databases, with the backup, restore and drill commands beside it. The scripts run on the host that holds the volumes.',
  'system.backups.loadError': 'Failed to query backup metadata.',
  'system.backups.noMetadata': 'Backup metadata stream not yet provisioned.',
  'system.backups.takeSnapshot': 'Take a snapshot',
  'system.backups.restoreLatest': 'Restore from a snapshot',
  'system.alerts.title': 'Alerts',
  'system.alerts.description': 'Compose and send a one-off alert through the Integrations registry. Everything sent from here reaches a person; per-rule silencing and the on-call roster are not here yet.',
  'system.alerts.wiring.label': 'Wiring:',
  'system.alerts.wiring.lead': 'alerts fan out via',
  'system.alerts.wiring.trail': 'in the settings service. A disabled or missing PagerDuty, Slack or Linear integration row is skipped, so only the channels marked enabled below will fire.',
  'system.alerts.configureLink': 'Configure integrations →',
  'system.alerts.compose': 'Compose alert',
  'system.alerts.fireTest': 'Fire test',
  'system.alerts.dispatch': 'Dispatch',
  'system.alerts.lastDispatch': 'Last dispatch',
  'system.alerts.severity.info': 'Info',
  'system.alerts.severity.critical': 'Critical',
  'system.alerts.form.summary': 'Summary',
  'system.alerts.form.severity': 'Severity',
  'system.alerts.form.sourceHint': 'Free-form: appears in PagerDuty payload + Slack message.',
  'system.alerts.form.details': 'Details (key=value, one per line)',
  'system.alerts.toast.dispatched': 'Dispatched to {{channels}}.',
  'system.alerts.toast.dispatchedTitle': 'Alert sent',
  'system.alerts.toast.dispatchFailed': 'Dispatch failed',
  'system.alerts.toast.noChannelDispatch': 'No channel accepted: check integration creds on Settings → Integrations.',
  'system.alerts.toast.testFired': 'Test fired to {{channels}}.',
  'system.alerts.toast.testTitle': 'Test alert',
  'system.alerts.toast.noChannelTest': 'No channel accepted: check Settings → Integrations.',

  // ── roles ──
  'roles.title': 'Platform staff',
  'roles.description': 'Platform-staff versus tenant-user assignment. Promoting to platform grants cross-tenant admin access.',
  'roles.totalLabel': '{{shown}} of {{total}} accounts',
  'roles.searchPlaceholder': 'Filter by name, email or organisation…',
  'roles.filters.tenantUser': 'Tenant user',
  'roles.columns.joined': 'Joined',
  'roles.list.joined': 'Joined {{date}}',
  'roles.promote': 'Promote',
  'roles.demote': 'Demote',
  'roles.promote.modal.title': 'Promote {{email}}?',
  'roles.promote.modal.bodyLead': 'Promote {{email}} to',
  'roles.promote.modal.bodyTrail': '. They\'ll gain cross-tenant admin access.',
  'roles.demote.modal.title': 'Demote {{email}}?',
  'roles.demote.modal.bodyLead': 'Demote {{email}} to',
  'roles.demote.modal.bodyTrail': '. They\'ll lose admin access on next login.',
  'roles.toast.updated': '{{email}} → {{scope}}.',

  // ── channels / gateways ──
  'gateways.title': 'Channels',
  'gateways.description': 'Routing, attached numbers, and Embedded Signup health for the messaging side of the platform.',
  'gateways.cards.numbers.title': 'Numbers',
  'gateways.cards.numbers.description': 'Cross-tenant WhatsApp phone-number inventory, quality ratings, and routing.',
  'gateways.cards.numbers.loadError': 'Failed to load numbers: retry below',
  'gateways.cards.numbers.attached': '{{count}} attached',
  'gateways.cards.numbers.active': '{{count}} active',
  'gateways.cards.wba.title': 'WhatsApp Business',
  'gateways.cards.wba.description': 'Meta Embedded Signup completion health: per-tenant onboarding status and WBA approval.',
  'gateways.cards.wba.countLabel': 'Not wired yet',
  'gateways.wba.title': 'WhatsApp Business signup',
  'gateways.wba.description': 'Meta Embedded Signup completion tracking surfaces here: per-tenant flow status, drop-off step, and WBA approval status.',
  'gateways.numbers.title': 'Phone numbers',
  'gateways.numbers.description': 'Cross-tenant inventory of WhatsApp phone numbers attached via Meta. Quality rating mirrors what Meta reports on the WBA; LOW or RED ratings need immediate operator attention before throttle escalations.',
  'gateways.numbers.tableTitle': 'All phone numbers',
  'gateways.numbers.totalLabel': '{{shown}} of {{total}}',
  'gateways.numbers.searchPlaceholder': 'Filter by number, display name or tenant…',
  'gateways.numbers.kpi.all': 'All numbers',
  'gateways.numbers.kpi.unrated': 'Unrated',
  'gateways.numbers.filters.anyRating': 'Any rating',
  'gateways.numbers.empty': 'No phone numbers match',
  'gateways.numbers.emptyRows': 'No phone numbers attached yet.',
  'gateways.numbers.loadError': 'Failed to load phone numbers.',
  'gateways.numbers.columns.quality': 'Quality',
  'gateways.numbers.columns.metaId': 'Meta number ID',
  'gateways.numbers.columns.registered': 'Registered',
  'gateways.numbers.list.registered': 'registered {{when}}',

  // ── media ──
  'media.title': 'Media storage',
  'media.description': 'Blob storage by tenant: how many files each organisation holds, how many bytes, and how many of those files have text extracted from them.',
  'media.tableTitle': 'By tenant',
  'media.totalLabel': '{{shown}} of {{total}}',
  'media.withExtractedText': 'With extracted text',
  'media.kpi.empty': 'Empty',
  'media.filters.size': 'Storage size',
  'media.filters.anySize': 'Any size',
  'media.metrics.totalFiles': 'Total files',
  'media.metrics.totalBytes': 'Total bytes',
  'media.empty': 'No tenants match',
  'media.emptyRows': 'No media uploads yet.',
  'media.loadError': 'Failed to load media stats.',
  'media.columns.files': 'Files',
  'media.columns.bytes': 'Bytes',
  'media.columns.extracted': 'Extracted',
  'media.list.meta': '{{files}} files · {{withText}} with extracted text',
  'media.list.storage': 'storage',
  'media.card.large': 'large',
  'media.card.withText': 'With text',
  'media.kb.title': 'Knowledge base',
  'media.kb.description': 'Per-tenant document indexing status and per-flow LLM token burn are still pending: neither has a cross-tenant endpoint yet.',

  // ── API monitoring ──
  'apiMonitoring.title': 'API monitoring',
  'apiMonitoring.description': 'The API log family, aggregated by the logs service over the whole window rather than a sample of it. Counts, error rate and latency are computed where the rows are, and paths with an id in them are grouped as {id}.',
  'apiMonitoring.loadError': 'Failed to query logs. Is the logs service running?',
  'apiMonitoring.window.15m': 'Last 15 minutes',
  'apiMonitoring.window.1h': 'Last hour',
  'apiMonitoring.kpi.requests': 'Requests',
  'apiMonitoring.kpi.errorRate': 'Error rate',
  'apiMonitoring.p95Latency': 'P95 latency',
  'apiMonitoring.drillDown': 'Drill down',
  'apiMonitoring.openLogQuery': 'Open log query →',
  'apiMonitoring.empty': 'No API requests in this window',
  'apiMonitoring.columns.path': 'Path',
  'apiMonitoring.columns.errors': 'Errors',
  'apiMonitoring.columns.errRate': 'Err rate',
  'apiMonitoring.columns.avgMs': 'Avg ms',
  'apiMonitoring.columns.maxMs': 'Max ms',

  // ── queue monitoring ──
  'queues.title': 'Queue monitoring',
  'queues.description': 'Depth and consumer counts from the RabbitMQ management API, refreshed every {{seconds}} s. A queue holding messages with no consumer is a stuck consumer; a ready backlog above {{threshold}} is flagged.',
  'queues.unreachable': 'RabbitMQ management API not reachable ({{error}}). Is rabbit:15672 up?',
  'queues.totalLabel': '{{shown}} of {{total}}',
  'queues.searchPlaceholder': 'Filter by queue name…',
  'queues.kpi.all': 'All queues',
  'queues.kpi.backlogged': 'Backlog >{{threshold}}',
  'queues.kpi.noConsumers': 'No consumers',
  'queues.filters.anyState': 'Any state',
  'queues.metrics.queues': 'Queues',
  'queues.metrics.readyBacklog': 'Ready backlog',
  'queues.metrics.unackedTotal': 'Unacked total',
  'queues.empty': 'No queues match',
  'queues.emptyRows': 'No queues reported. RabbitMQ may still be starting.',
  'queues.loadError': 'Failed to query queues.',
  'queues.columns.queue': 'Queue',
  'queues.columns.state': 'State',
  'queues.columns.ready': 'Ready',
  'queues.columns.unacked': 'Unacked',
  'queues.columns.consumers': 'Consumers',
  'queues.list.consumersOne': '{{count}} consumer',
  'queues.list.consumersOther': '{{count}} consumers',
  'queues.list.total': '{{count}} total',

  // ── not found ──
  'notFound.description': 'That URL doesn\'t match any page in the admin console.',
  'notFound.backToDashboard': 'Back to dashboard →',

  // ── dashboard (W39 completion pass) ──
  'dashboard.welcome': 'Welcome back',
  'dashboard.description': 'Here\'s a snapshot of what\'s happening across every tenant.',
  'dashboard.openAuditLog': 'Open audit log →',
  'dashboard.metrics.tenants': 'Active organisations',
  'dashboard.metrics.tenantsHint': 'Active organisations',
  'dashboard.metrics.usersHint': 'Across all tenants',
  'dashboard.metrics.messages': 'Messages',
  'dashboard.metrics.messagesHint': '{{count}} conversations · spark = billed/day',
  'dashboard.metrics.numbersHint': '{{count}} healthy',
  'dashboard.metrics.billedUsage': 'Billed usage (ZAR)',
  'dashboard.metrics.billedUsageHint': 'from the last 1,000 ledger entries',
  'dashboard.metrics.deliveryRate': 'Delivery rate',
  'dashboard.billedActivity': 'Billed activity',
  'dashboard.billedActivityHint': 'last {{days}} days · ledger-derived',
  'dashboard.series.llmEvents': 'LLM events',
  'dashboard.channelHealth': 'Channel health',
  'dashboard.channelHealthHint': 'phone-number quality ratings',
  'dashboard.noNumbers': 'No numbers yet',
  'dashboard.numbersLabel': 'numbers',

  // ── users › detail shell (W39 completion pass) ──
  'users.view.notFound': 'User not found.',
  'users.view.openTenant': 'Open tenant →',
  'users.view.header.id': 'id',
  'users.view.header.created': 'created {{date}} ({{relative}})',
  'users.view.tabs.activity': 'Activity',
  'users.view.tabs.danger': 'Danger',

  // ── shell chrome: sidebar nav, breadcrumbs, header, profile menu ──
  'nav.section.core': 'Core',
  'nav.section.communications': 'Communications',
  'nav.section.monitoring': 'Monitoring',
  'nav.section.billing': 'Billing',
  'nav.section.platform': 'Platform',
  'nav.item.dashboard': 'Dashboard',
  'nav.item.tenants': 'Tenants',
  'nav.item.users': 'Users',
  'nav.item.roles': 'Platform staff',
  'nav.item.gatewaysNumbers': 'Numbers',
  'nav.item.gateways': 'Channels',
  'nav.item.templates': 'Templates',
  'nav.item.webhooks': 'Webhooks',
  'nav.item.media': 'Media & KB',
  'nav.item.flows': 'Flows',
  'nav.item.broadcasts': 'Broadcasts',
  'nav.item.conversations': 'Conversations',
  'nav.item.audit': 'Audit',
  'nav.item.logs': 'Logs',
  'nav.item.systemHealth': 'System Health',
  'nav.item.apimonitoring': 'API Monitoring',
  'nav.item.queuemonitoring': 'Queue Monitoring',
  'nav.item.billing': 'Overview',
  'nav.item.billingPlans': 'Plans',
  'nav.item.billingSubscriptions': 'Subscriptions',
  'nav.item.billingInvoices': 'Invoices',
  'nav.item.billingUsage': 'Usage',
  'nav.item.billingSkuRates': 'SKU rates',
  'nav.item.billingLedger': 'Ledger',
  'nav.item.billingItn': 'Payment gateways',
  'nav.item.settings': 'Settings',
  'nav.item.settingsApiKeys': 'API Keys',
  'nav.item.settingsIntegrations': 'Integrations',
  'nav.item.settingsSecurity': 'Security',
  'nav.item.settingsBackup': 'Backup & Restore',
  'shell.breadcrumb.admin': 'Admin',
  'shell.breadcrumb.dashboard': 'Dashboard',
  'shell.breadcrumb.tenants': 'Tenants',
  'shell.breadcrumb.users': 'Users',
  'shell.breadcrumb.billing': 'Billing',
  'shell.breadcrumb.skuRates': 'SKU rates',
  'shell.breadcrumb.ledger': 'Ledger',
  'shell.breadcrumb.subscriptions': 'Subscriptions',
  'shell.breadcrumb.plans': 'Plans',
  'shell.breadcrumb.invoices': 'Invoices',
  'shell.breadcrumb.usage': 'Usage',
  'shell.breadcrumb.paymentGateways': 'Payment gateways',
  'shell.breadcrumb.templates': 'Templates',
  'shell.breadcrumb.channels': 'Channels',
  'shell.breadcrumb.numbers': 'Numbers',
  'shell.breadcrumb.whatsappBusiness': 'WhatsApp Business',
  'shell.breadcrumb.media': 'Media',
  'shell.breadcrumb.knowledgeBase': 'Knowledge base',
  'shell.breadcrumb.webhooks': 'Webhooks',
  'shell.breadcrumb.deliveries': 'Deliveries',
  'shell.breadcrumb.flows': 'Flows',
  'shell.breadcrumb.audit': 'Audit',
  'shell.breadcrumb.logs': 'Logs',
  'shell.breadcrumb.query': 'Query',
  'shell.breadcrumb.system': 'System',
  'shell.breadcrumb.health': 'Health',
  'shell.breadcrumb.alerts': 'Alerts',
  'shell.breadcrumb.backups': 'Backups',
  'shell.breadcrumb.settings': 'Settings',
  'shell.breadcrumb.profile': 'Profile',
  'shell.breadcrumb.appearance': 'Appearance',
  'shell.breadcrumb.security': 'Security',
  'shell.breadcrumb.apiKeys': 'API keys',
  'shell.breadcrumb.integrations': 'Integrations',
  'shell.breadcrumb.featureFlags': 'Feature flags',
  'shell.breadcrumb.backup': 'Backup',
  'shell.breadcrumb.roles': 'Platform staff',
  'shell.breadcrumb.broadcasts': 'Broadcasts',
  'shell.breadcrumb.conversations': 'Conversations',
  'shell.breadcrumb.apiMonitoring': 'API Monitoring',
  'shell.breadcrumb.queueMonitoring': 'Queue Monitoring',
  'shell.brand.badge': 'Admin',
  'shell.brand.subtitle': 'Admin console',
  'shell.nav.open': 'Open navigation',
  'shell.nav.close': 'Close navigation',
  'shell.nav.toggle': 'Toggle navigation',
  'shell.nav.collapse': 'Collapse menu',
  'shell.nav.expand': 'Expand menu',
  'shell.help.tooltip': 'Help & docs',
  'shell.help.aria': 'Help and documentation',
  'shell.palette.open': 'Open command palette',
  'shell.palette.searchPlaceholder': 'Search anything…',
  'shell.palette.goTo': 'Go to {{label}}',
  'shell.profile.open': 'Open profile menu',
  'shell.profile.menu': 'Profile menu',
  'shell.profile.guest': 'Guest',
  'shell.profile.notifications': 'Notifications',
  'shell.profile.allSettings': 'All settings',
  'shell.profile.quickActions': 'Quick actions',
  'shell.profile.commandPalette': 'Command palette',
  'shell.profile.statusPage': 'Status page',
  'shell.profile.whatsNew': 'What\'s new',

  // ── shell components: activity feed, health grid, error boundary, bell, accent picker ──
  'common.accentColor': 'Accent color',
  'common.setAccentTo': 'Set accent to {{name}}',
  'common.color.amber': 'Amber',
  'common.color.orange': 'Orange',
  'common.color.red': 'Red',
  'common.color.pink': 'Pink',
  'common.color.violet': 'Violet',
  'common.color.indigo': 'Indigo',
  'common.color.blue': 'Blue',
  'common.color.cyan': 'Cyan',
  'common.color.teal': 'Teal',
  'common.color.green': 'Green',
  'common.color.lime': 'Lime',
  'common.color.yellow': 'Yellow',
  'activity.title': 'Recent activity',
  'activity.subtitle': 'across every tenant · last 24 hours',
  'activity.empty': 'Nothing has been recorded across the platform in the last 24 hours.',
  'activity.kind.submitted': 'submitted',
  'activity.kind.approved': 'approved',
  'activity.kind.rejected': 'rejected',
  'healthGrid.demoHint': 'probed live',
  'healthGrid.detail.identity': 'auth, users, tenants',
  'healthGrid.detail.billing': 'wallets, ledger',
  'healthGrid.detail.inbox': 'conversations + SignalR',
  'healthGrid.detail.channelWa': 'templates, send/receive',
  'healthGrid.detail.webhooks': 'elevated retry rate',
  'healthGrid.detail.flowsLlm': 'flow runs + LLM',
  'healthGrid.detail.media': 'MinIO + extraction',
  'healthGrid.detail.logs': 'W27 consumer',
  'errorBoundary.title': 'Something went wrong on this page',
  'errorBoundary.body': 'An unexpected rendering error occurred.',
  'errorBoundary.retry': 'Try again',
  'notifications.bellAria': 'Recent realtime events',
  'notifications.template.submitted': 'Template submitted',
  'notifications.template.approved': 'Template approved',
  'notifications.template.rejected': 'Template rejected',
  'notifications.template.noReason': 'no reason supplied',
  'settings.integrations.summary.title': '{{tested}} of {{total}} providers tested green ({{enabled}} enabled)',
  'settings.integrations.summary.hint': 'Test runs the real call for each provider. Tenants can message, upload and pay only once the required ones are green.',
  'settings.integrations.summary.ready': 'Ready for tenants',
  'settings.integrations.summary.notReady': 'Not ready for tenants',
  'settings.integrations.summary.missing': 'Still needed: {{list}}',
  'settings.integrations.summary.aPaymentProvider': 'a payment provider (PayFast, Stripe or Paystack)',
  'settings.integrations.requiredForTenants': 'Required for tenants',
  'settings.integrations.oneOfPayments': 'One of these for top-ups',
  'settings.integrations.usedBy': 'Used by:',
  'settings.integrations.usedBy.meta_whatsapp': 'sending and receiving WhatsApp messages, template import, number attach',
  'settings.integrations.usedBy.aws_s3': 'media uploads, knowledge-base documents, invoice PDFs',
  'settings.integrations.usedBy.smtp': 'sign-up verification, password resets, invoices, a flow\'s Notify your team step',
  'settings.integrations.usedBy.aws_bedrock': 'AI replies and the knowledge base in flows (the offline stub answers until this is on)',
  'settings.integrations.usedBy.twilio': 'SMS from a flow\'s Notify your team step, and SMS for customers when chosen as the active or backup SMS provider',
  'settings.integrations.usedBy.payfast': 'wallet top-ups in rand (ITN webhook, fail-closed)',
  'settings.integrations.usedBy.stripe': 'international card top-ups',
  'settings.integrations.usedBy.paystack': 'Nigeria and wider Africa top-ups',
  'settings.integrations.usedBy.sars_einvoice': 'South African tax-invoice validation on billing',
  'settings.integrations.usedBy.firs_einvoice': 'Nigerian tax-invoice validation on billing',
  'settings.integrations.usedBy.pagerduty': 'paging on-call for firing alerts (Alertmanager route and Fire test alert)',
  'settings.integrations.usedBy.slack_alerts': 'non-paging alert messages',
  'settings.integrations.usedBy.linear_issues': 'an issue filed per firing alert',
  'settings.integrations.testedAt': 'Tested {{when}}',
  'settings.integrations.toast.testOk': '{{name}}: test passed',
  'settings.integrations.toast.testFailed': '{{name}}: test failed',
  'common.copy': 'Copy',
  'tenantName.tooltip': 'Tenant: {{slug}}. Click the id beside it to copy.',
  'tenantName.unknown': 'This tenant is no longer on the platform (id {{id}}).',
  'tenantName.unknownShort': 'Removed tenant',
  'tenantName.copyId': 'Copy the tenant id',
  'conversations.totalLabelCapped': '{{shown}} of the newest {{total}}',
  'conversations.capHint': 'This feed loads the 200 most recent conversations. Older threads are not counted here.',
  'conversations.columns.tenant': 'Tenant',
  'conversations.columns.contact': 'Contact',
  'conversations.assigned': 'Assigned',
  'conversations.drawer.title': 'Conversation',
  'conversations.drawer.hint': 'The thread itself is readable only from the tenant inbox. These are the identifiers a support ticket needs.',
  'conversations.drawer.tenant': 'Tenant',
  'conversations.drawer.contact': 'Contact',
  'conversations.drawer.conversationId': 'Conversation id',
  'conversations.drawer.phoneNumberId': 'Number id',
  'conversations.drawer.agent': 'Assigned agent',
  'conversations.drawer.agentId': 'Agent id',
  'conversations.drawer.opened': 'Opened',
  'conversations.drawer.lastMessage': 'Last message',
  'conversations.drawer.unread': 'Unread',
  'conversations.drawer.openTenant': 'Open tenant',
  'conversations.copy.conversationId': 'Copy the conversation id',
  'conversations.copy.phoneNumberId': 'Copy the number id',
  'conversations.copy.agentId': 'Copy the agent id',
  'broadcasts.totalLabelCapped': '{{shown}} of the newest {{total}}',
  'broadcasts.capHint': 'This feed loads the 200 most recent broadcasts. Older jobs are not counted here.',
  'broadcasts.columns.tenant': 'Tenant',
  'broadcasts.columns.scheduled': 'Scheduled',
  'broadcasts.columns.progress': 'Progress',
  'broadcasts.notScheduled': 'Not scheduled',
  'broadcasts.progressAria': 'Recipients done',
  'broadcasts.progressLabel': '{{sent}} of {{total}} sent, {{pct}}%',
  'broadcasts.scheduledFor': 'Scheduled for {{when}}',
  'broadcasts.startedRelative': 'Started {{when}}',
  'broadcasts.drawer.title': 'Broadcast',
  'broadcasts.drawer.hint': 'Job identifiers, for cross-referencing a number or a segment while an incident is open.',
  'broadcasts.drawer.tenant': 'Tenant',
  'broadcasts.drawer.template': 'Template',
  'broadcasts.drawer.broadcastId': 'Broadcast id',
  'broadcasts.drawer.phoneNumberId': 'Number id',
  'broadcasts.drawer.segmentId': 'Segment',
  'broadcasts.drawer.noSegment': 'Explicit recipient list',
  'broadcasts.drawer.timeline': 'Timeline',
  'broadcasts.drawer.recipients': 'Recipients',
  'broadcasts.drawer.throttle': 'Throttle',
  'broadcasts.drawer.throttleValue': '{{tps}} per second',
  'broadcasts.drawer.openTenant': 'Open tenant',
  'broadcasts.drawer.openNumbers': 'Open numbers',
  'broadcasts.copy.broadcastId': 'Copy the broadcast id',
  'broadcasts.copy.phoneNumberId': 'Copy the number id',
  'broadcasts.copy.segmentId': 'Copy the segment id',
  'templates.emptyFiltered': 'Try clearing your filters.',
  'templates.devApproved': 'Dev auto-approved',
  'templates.devApprovedTooltip': 'A non-production fallback approved this template. Meta never reviewed it, so treat it as unapproved for any real send.',
  'templates.metaStatus': 'Meta: {{status}}',
  'templates.metaStatusTooltip': 'Meta\'s own review word, as last read from the Graph API. Where it disagrees with our status, Meta is the one that decides whether a send goes out.',
  'templates.rejectionTooltip': 'Meta\'s rejection reason: {{reason}}',
  'templates.view.loadError': 'Failed to load this template.',
  'templates.view.notFoundHint': 'It may have been deleted, or it belongs to a tenant this console cannot read.',
  'templates.view.backToList': 'All templates',
  'templates.view.meta.metaStatus': 'Meta status',
  'templates.view.meta.devFallback': 'Dev fallback',
  'templates.view.meta.importedAt': 'Last imported from Meta',
  'templates.view.meta.neverImported': 'Never imported',
  'templates.view.meta.copyTemplateId': 'Copy the Meta template id',
  'templates.view.meta.hint': 'Meta status, dev fallback and the import time are Meta\'s side of the record; the timestamps below are ours.',
  'templates.view.lifecycle.steps.paused': 'Paused by Meta',
  'templates.view.lifecycle.devFallbackBadge': 'Dev auto-approve',
  'templates.view.lifecycle.devFallbackWarning': 'Meta never reviewed this template. A non-production fallback approved it, so it must be treated as unapproved for a real send.',
  'templates.view.lifecycle.pausedDetail': 'Meta has paused this template. Sends are refused until it is resumed.',
  'templates.view.overview.parseError': 'The components JSON on this template could not be read, so there is nothing to preview. The raw value is on the Components tab.',
  'templates.view.overview.emptyPreview': 'This template has no components to preview.',
  'templates.view.variables.reset': 'Clear values',
  'templates.view.variables.toast.copyFailed': 'Could not copy to the clipboard.',
  'templates.view.components.copied': 'Components JSON copied to clipboard.',
  'media.columns.tenant': 'Tenant',
  'media.searchPlaceholder': 'Filter by tenant name…',
  'media.totalLabelCapped': '{{shown}} of the largest {{total}}',
  'media.capHint': 'This view loads the 200 tenants holding the most bytes.',
  'media.kpi.large': 'At least 100 MB',
  'media.kpi.noText': 'No extracted text',
  'media.filters.sizeSmall': 'Under 10 MB',
  'media.filters.sizeMedium': '10 MB to 100 MB',
  'media.filters.sizeLarge': 'Over 100 MB',
  'media.metrics.uploads24h': 'Uploads (24h)',
  'media.metrics.bytes24h': 'Uploaded (24h)',
  'media.mime.title': 'By file type',
  'media.mime.description': 'Stored objects grouped by MIME type, largest first.',
  'media.mime.columns.type': 'File type',
  'media.mime.empty': 'No files stored yet.',
  'media.kb.provider.title': 'LLM provider',
  'media.kb.provider.description': 'Which model this deployment is actually talking to, and where its credentials came from.',
  'media.kb.provider.chat': 'Chat',
  'media.kb.provider.embedding': 'Embedding',
  'media.kb.provider.dimension': 'Dimension',
  'media.kb.provider.source': 'Credentials',
  'media.kb.provider.sourceRegistry': 'Settings, Integrations',
  'media.kb.provider.sourceConfig': 'Service configuration',
  'media.kb.provider.sourceNone': 'Offline stub',
  'media.kb.provider.stubWarning': 'No provider is configured, so the offline stub is answering every prompt. Add credentials under Settings, Integrations.',
  'media.kb.provider.loadError': 'Failed to load the LLM provider.',
  'shell.breadcrumb.details': 'Details',
  'shell.realtime.live': 'Live',
  'shell.realtime.connecting': 'Connecting…',
  'shell.realtime.offline': 'Offline',
  'shell.realtime.aria': 'Realtime connection',
  'shell.loading.title': 'Loading the admin console…',
  'notifications.title': 'Notifications',
  'notifications.empty': 'Nothing yet. Template lifecycle events land here as they happen.',
  'notifications.markAllRead': 'Mark all as read',
  'notifications.unknownTenant': 'an unnamed tenant',
  'notifications.toast.submitted': '{{code}} sent to Meta for {{tenant}}',
  'notifications.toast.approved': '{{code}} approved for {{tenant}}',
  'notifications.toast.rejected': '{{code}} rejected for {{tenant}}: {{reason}}',
  'activity.viewAll': 'Open the audit log →',
  'activity.live': 'live',
  'activity.loading': 'Reading the audit feed…',
  'healthGrid.subtitle': 'probed live',
  'healthGrid.loadError': 'Could not read service health.',
  'healthGrid.empty': 'No services reported a health probe.',
  'healthGrid.viewAll': 'Open system health →',
  'healthGrid.probe': 'HTTP {{status}} · {{ms}} ms',
  'errorBoundary.details': 'Technical detail',
  'errorBoundary.home': 'Back to dashboard',
  'auth.layout.wrongDomainLink': 'Go to the tenant console →',
  'auth.login.invalidCredentials': 'Email or password is incorrect.',
  'auth.login.lockoutHint': 'Several wrong attempts in a row lock the account for a few minutes.',
  'auth.login.networkError': 'Could not reach the sign-in service. Check your connection and try again.',
  'auth.login.twoFactor.back': 'Back to sign in',
  'auth.login.twoFactor.recoveryHint': 'Lost your authenticator? A recovery code works in this field too.',
  'auth.mfa.description': 'Enter the six-digit code from your authenticator app to finish signing in.',
  'auth.mfa.expiry': 'The challenge expires five minutes after you entered your password.',
  'auth.mfa.noChallenge': 'No sign-in is waiting for a code. Start again from the sign-in page.',
  'auth.forgot.sent.body': 'If an account exists for {{email}}, a reset link is on its way. The link expires in 1 hour.',
  'auth.forgot.resend': 'Send it again',
  'auth.forgot.resendIn': 'Send again in {{seconds}}s',
  'auth.forgot.failed': 'Could not send the reset link. Try again in a moment.',
  'auth.forgot.emailDescription': 'The address you sign in with.',
  'auth.reset.errors.invalidLinkAction': 'Request a new reset link',
  'auth.reset.errors.invalidToken': 'That reset link is invalid or has expired. Request a new one.',
  'auth.reset.errors.weakPassword': 'That password does not meet the policy. Use at least 12 characters.',
  'auth.reset.form.passwordDescription': 'At least 12 characters. A short sentence of your own works well; no symbols needed.',
  'auth.reset.form.confirmDescription': 'Type the same password again.',
  'auth.reset.otherSessions': 'Resetting your password signs you out on every other device.',
  'notFound.title': 'Page not found',
  'notFound.path': 'No page is registered at this address:',
  'notFound.goBack': 'Go back',
  'notFound.openPalette': 'Search the console',
  'notFound.suggestions': 'Closest matches',
  'dashboard.error.title': 'Some of this page could not load',
  'dashboard.error.body': 'These feeds failed: {{feeds}}. Anything they carry is missing from the numbers below.',
  'dashboard.feeds.billing': 'Billing stats',
  'dashboard.feeds.inbox': 'Inbox stats',
  'dashboard.feeds.tenants': 'Tenants',
  'dashboard.feeds.users': 'Users',
  'dashboard.feeds.ledger': 'Ledger',
  'dashboard.feeds.numbers': 'Phone numbers',
  'dashboard.updated': 'Updated {{when}}',
  'dashboard.metrics.tenantsHintActive': 'active of {{total}} organisations',
  'dashboard.metrics.usersCapped': 'the API returns at most 500, so this is a floor',
  'dashboard.noValue': '–',
  'settings.layout.navLabel': 'Settings sections',
  'settings.cards.appearance.title': 'Appearance',
  'settings.cards.appearance.hint': 'Theme, accent colour, density and accessibility helpers for this browser.',
  'settings.cards.apiKeys.title': 'API keys',
  'settings.cards.apiKeys.hint': 'Audit and revoke every API key issued across all tenants.',
  'settings.cards.integrations.title': 'Integrations',
  'settings.cards.integrations.hint': 'Supply and rotate credentials for Meta, AWS, payment providers and alerting.',
  'settings.cards.integrations.status': '{{configured}} of {{total}} providers configured',
  'settings.cards.featureFlags.status': '{{count}} flags in the registry',
  'settings.cards.backup.title': 'Backup & restore',
  'settings.cards.backup.hint': 'Snapshot, restore and drill procedure for the platform databases.',
  'settings.secret.onlyChance': 'This is the only time this value is shown. Nobody, including platform staff, can retrieve it afterwards.',
  'settings.secret.download': 'Download',
  'settings.secret.copyFailed': 'Copy failed',
  'settings.secret.acknowledge': 'I have saved this somewhere safe.',
  'settings.secret.done': 'Done',
  'audit.range.label': 'Time range',
  'audit.range.1h': 'Last hour',
  'audit.range.24h': 'Last 24 hours',
  'audit.range.7d': 'Last 7 days',
  'audit.range.30d': 'Last 30 days',
  'audit.searchPlaceholder': 'Search the summary or tenant…',
  'audit.lastUpdated': 'Updated {{when}}',
  'audit.emptyUnfiltered': 'No activity recorded in this window.',
  'audit.totalLabelCapped': 'Newest {{count}} events · {{range}}',
  'audit.partialError.title': 'Part of the audit trail is missing',
  'audit.partialError.body': 'Could not load: {{sources}}. The counts and rows below are incomplete, so treat an absence here as unknown rather than as nothing having happened.',
  'audit.denied.title': 'You do not have permission to read the full audit trail',
  'audit.denied.body': 'At least one audit source answered 403. Ask a platform administrator to grant the audit permission; this is not an empty log.',
  'audit.toast.exportedTitle': 'Export ready',
  'audit.toast.exported': 'Wrote {{count}} events, with the tenant name resolved, to a CSV file.',
  'settings.apiKeys.table.description': 'Every key issued across every tenant, newest activity first. Revoking a key here stops it immediately, wherever it is used.',
  'settings.apiKeys.copyPrefix': 'Copy the key prefix',
  'settings.apiKeys.columns.rpmConfigured': 'RPM (configured)',
  'settings.apiKeys.columns.rpmTooltip': 'A stored per-key rate limit. The gateway does not enforce it yet, so this number describes intent rather than behaviour.',
  'settings.apiKeys.columns.created': 'Created',
  'settings.apiKeys.revokedOn': 'Revoked {{when}}',
  'settings.apiKeys.card.created': 'Created {{created}}',
  'settings.apiKeys.modal.revoke.bodyWithOrg': 'Revoke “{{name}}” ({{prefix}}…) belonging to {{org}}? Anything signing requests with this key starts failing immediately; existing JWTs keep working until they expire.',
  'settings.apiKeys.actions.new': 'New key',
  'settings.apiKeys.create.title': 'Issue an API key',
  'settings.apiKeys.create.intro': 'This mints a key for {{org}}, the platform organisation. Tenant keys are minted by tenants in their own console.',
  'settings.apiKeys.create.nameHint': 'What will use this key, so the next operator can tell whether revoking it is safe.',
  'settings.apiKeys.create.scopesHint': 'Comma-separated, at least one, from: messages:send, messages:read, contacts:read, contacts:write, templates:read, broadcasts:read, broadcasts:write, media, webhooks. Any other name is refused.',
  'settings.apiKeys.create.oneSightNotice': 'The key is shown once, on the screen that follows. It is stored hashed and cannot be recovered afterwards.',
  'settings.apiKeys.create.submit': 'Create key',
  'settings.apiKeys.create.errors.nameRequired': 'Give the key a name.',
  'settings.apiKeys.create.failedTitle': 'Could not create the key',
  'settings.apiKeys.created.title': 'Your new API key',
  'settings.apiKeys.created.intro': '“{{name}}” is live. Copy or download it now and store it wherever the calling system reads its credentials from.',
  'settings.security.password.current': 'Current password',
  'settings.security.password.new': 'New password',
  'settings.security.password.confirm': 'Confirm new password',
  'settings.security.password.hint': 'At least 12 characters. A short sentence of your own works well; no symbols needed.',
  'settings.security.password.submit': 'Change password',
  'settings.security.password.rules.length': 'At least 12 characters',
  'settings.security.password.rules.upper': 'An uppercase letter',
  'settings.security.password.rules.lower': 'A lowercase letter',
  'settings.security.password.rules.digit': 'A number',
  'settings.security.password.rules.symbol': 'A symbol',
  'settings.security.password.errors.weak': 'That password does not meet all the rules below.',
  'settings.security.password.errors.mismatch': 'The two new passwords do not match.',
  'settings.security.password.errors.failed': 'Could not change the password.',
  'settings.security.password.toast.changed': 'Password changed. Other sessions keep working until their tokens expire.',
  'settings.security.twoFactor.title': 'Two-factor authentication',
  'settings.security.twoFactor.notEnrolled': 'Not enrolled',
  'settings.security.twoFactor.unprotectedWarning': 'This account administers every tenant on the platform. Without a second factor, a leaked password is the whole attack surface.',
  'settings.security.twoFactor.intro': 'Enrol an authenticator app. You will be asked for a six-digit code at every sign-in, and you get ten single-use recovery codes for when the phone is not to hand.',
  'settings.security.twoFactor.actions.setup': 'Set up two-factor',
  'settings.security.twoFactor.actions.enable': 'Turn on two-factor',
  'settings.security.twoFactor.actions.disable': 'Turn off two-factor',
  'settings.security.twoFactor.actions.newCodes': 'New recovery codes',
  'settings.security.twoFactor.setup.step1': 'Add this secret to your authenticator app.',
  'settings.security.twoFactor.setup.orType': 'Shared secret',
  'settings.security.twoFactor.setup.step2': 'Enter the six-digit code the app shows now.',
  'settings.security.twoFactor.code': 'Authenticator code',
  'settings.security.twoFactor.manageHint': 'A current code from your authenticator is required to change either setting below.',
  'settings.security.twoFactor.codesRemaining': '{{count}} recovery codes left.',
  'settings.security.twoFactor.disableConsequence': 'Turning two-factor off leaves this platform-admin account protected by its password alone, and invalidates the recovery codes.',
  'settings.security.twoFactor.codeMismatch': 'That code was not accepted. Codes expire after 30 seconds.',
  'settings.security.twoFactor.alreadySetUp': 'This account already has two-factor set up.',
  'settings.security.twoFactor.toast.enabled': 'Two-factor is on',
  'settings.security.twoFactor.toast.enabledBody': 'You will be asked for a code the next time you sign in.',
  'settings.security.twoFactor.toast.disabled': 'Two-factor is off',
  'settings.security.twoFactor.toast.disabledBody': 'This account now signs in with a password alone.',
  'settings.security.twoFactor.codes.title': 'Your recovery codes',
  'settings.security.twoFactor.codes.intro': 'Each code signs you in once when your authenticator is unavailable. Store them somewhere you can reach without this console.',
  'settings.security.sessions.title': 'Active sessions',
  'settings.security.sessions.hint': 'Every device holding a refresh token for this account. Ending one signs that device out at its next refresh.',
  'settings.security.sessions.empty': 'No other sessions are recorded for this account.',
  'settings.security.sessions.loadError': 'Failed to load sessions.',
  'settings.security.sessions.unknownDevice': 'Unrecognised device',
  'settings.security.sessions.meta': 'Started {{started}} · expires {{expires}}',
  'settings.security.sessions.revoke.title': 'End this session?',
  'settings.security.sessions.revoke.body': 'End the session on {{device}}? That device is signed out and has to authenticate again.',
  'settings.security.sessions.revoke.confirm': 'End session',
  'settings.security.sessions.toast.revoked': 'Session ended.',
  'settings.security.sessions.toast.revokeFailed': 'Could not end the session',
  'settings.security.credentials.title': 'Platform credentials',
  'settings.security.credentials.hint': 'API keys are the other credential surface for this platform, and they live on their own page.',
  'settings.security.credentials.link': 'Manage API keys →',
  'logs.level.verbose': 'Verbose',
  'logs.level.debug': 'Debug',
  'logs.level.information': 'Information',
  'logs.level.warning': 'Warning',
  'logs.level.error': 'Error',
  'logs.level.fatal': 'Fatal',
  'logs.columns.environment': 'Env',
  'logs.columns.correlation': 'Correlation id',
  'logs.columns.outcome': 'Outcome',
  'logs.copyCorrelation': 'Copy the correlation id; support asks a customer to quote this',
  'logs.filters.env': 'Environment',
  'logs.filters.envPlaceholder': 'Any environment',
  'logs.lastUpdated': 'Updated {{when}}',
  'logs.limitLabel': 'Rows per query',
  'logs.limitOption': '{{count}} rows',
  'logs.openQueryBuilder': 'Query builder →',
  'logs.capReached': 'Showing the newest {{limit}} rows; the server had more. Narrow the query.',
  'logs.stats.scopeCaption': 'These totals count every row in the logs database. The table below shows one window of one family.',
  'logs.denied.title': 'You do not have permission to read the logs',
  'logs.denied.body': 'The logs service answered 403. Ask a platform administrator to grant log access; this is not an empty result.',
  'logs.query.backToLogs': '← All logs',
  'logs.query.form.envPlaceholder': 'production, staging, …',
  'logs.query.form.correlationPlaceholder': '0192f3a1-…',
  'logs.query.form.correlationHint': 'Follows one request across every service that handled it.',
  'logs.query.form.orgId': 'Organisation id',
  'logs.query.form.orgIdPlaceholder': '01a0631f-…',
  'logs.query.form.orgIdHint': 'A tenant UUID. Copy it from the tenant page.',
  'logs.query.form.orgIdInvalid': 'That is not a UUID.',
  'logs.query.form.since': 'From',
  'logs.query.form.sinceHint': 'Your local time; sent as UTC.',
  'logs.query.form.until': 'To',
  'logs.query.form.untilHint': 'Leave empty for up to now.',
  'logs.query.form.statusClass': 'Status class (API family only)',
  'logs.query.unapplied': 'Filters changed. Press Apply.',
  'logs.query.resultCount': '{{count}} of at most {{limit}} rows',
  'logs.query.notRun': 'No query run yet',
  'logs.query.notRunHint': 'Pick a family and any filters, then press Apply. The query is mirrored into the URL so you can paste it to a colleague.',
  'settings.profile.identity.title': 'Account',
  'settings.profile.identity.email': 'Email',
  'settings.profile.identity.organisation': 'Organisation',
  'settings.profile.identity.scope': 'Sign-in domain',
  'settings.profile.scope.platform': 'Platform admin',
  'settings.profile.scope.tenant': 'Tenant user',
  'settings.profile.scope.platformHint': 'Can read and act across every tenant on the platform.',
  'settings.profile.scope.tenantHint': 'Limited to this organisation.',
  'settings.profile.details.title': 'Your details',
  'settings.profile.displayName.label': 'Display name',
  'settings.profile.displayName.hint': 'Shown on audit entries and impersonation notices. Leave empty to be shown by email address.',
  'settings.profile.displayName.placeholder': 'Your name',
  'settings.profile.locale.options.en-ZA': 'English (South Africa)',
  'settings.profile.locale.options.en-GB': 'English (United Kingdom)',
  'settings.profile.locale.options.en-US': 'English (United States)',
  'settings.profile.locale.options.en-NG': 'English (Nigeria)',
  'settings.profile.toast.saveFailed': 'Could not save your profile',
  'apiMonitoring.window.24h': 'Last 24 hours',
  'apiMonitoring.window.7d': 'Last 7 days',
  'apiMonitoring.windowLabel': 'Time window',
  'apiMonitoring.kpi.ok': '2xx Success',
  'apiMonitoring.kpi.clientError': '4xx Client error',
  'apiMonitoring.kpi.serverError': '5xx Server error',
  'apiMonitoring.columns.method': 'Method',
  'apiMonitoring.ms': '{{value}} ms',
  'apiMonitoring.updated': 'Updated {{when}} · auto-refresh {{seconds}}s',
  'apiMonitoring.autoRefresh': 'Auto-refresh every {{seconds}}s',
  'apiMonitoring.sampleSpan': 'Sample: {{count}} requests, {{from}} to {{to}}.',
  'apiMonitoring.sampleTruncated': 'The logs service returned its maximum of {{limit}} rows, so this window is truncated and the figures above describe the sample, not the window. Pick a shorter window for a complete picture.',
  'apiMonitoring.emptyHint': 'No API requests were recorded in this window. Widen it, or clear the service and status filters.',
  'apiMonitoring.denied.title': 'You do not have permission to read the API logs',
  'apiMonitoring.denied.body': 'The logs service answered 403. Ask a platform administrator to grant log access; this is not a quiet platform.',
  'settings.appearance.localOnlyNotice': 'These preferences are stored in this browser only. They do not follow you to another machine or another browser profile.',
  'settings.appearance.accent.previewButton': 'Primary button',
  'settings.appearance.accent.previewSecondary': 'Secondary',
  'settings.appearance.accent.previewBadge': 'Badge',
  'settings.appearance.sidebar.narrowNote': 'Expanded for label clarity, collapsed for more workspace. Below 768px the sidebar goes off-canvas and this setting has no effect.',
  'settings.appearance.contrast.title': 'High contrast',
  'settings.appearance.contrast.description': 'Stronger borders and text contrast throughout the console.',
  'settings.appearance.contrast.switchHint': 'Useful on glare-heavy screens and for anyone who finds the default borders too faint.',
  'settings.appearance.reset.action': 'Reset to defaults',
  'settings.appearance.reset.title': 'Reset appearance?',
  'settings.appearance.reset.body': 'Theme accent, density, text scale, sidebar, motion and contrast all go back to the shipped defaults in this browser. Nothing else about your account changes.',
  'settings.appearance.reset.confirm': 'Reset',
  'flows.status.handedOff': 'Handed off',
  'flows.flowDeleted': 'Deleted flow',
  'flows.searchPlaceholder': 'Filter by contact number, flow or run id…',
  'flows.columns.channel': 'Channel',
  'flows.columns.tenant': 'Tenant',
  'flows.copyRunId': 'Copy the run id',
  'flows.copyFlowId': 'Copy the flow id',
  'flows.copyConversationId': 'Copy the conversation id',
  'flows.capNotice': 'The service returns at most {{cap}} runs per request and this window filled it. Narrow the range to be sure you are seeing every run.',
  'flows.table.totalCapped': '{{shown}} of {{total}} · last {{range}} · server limit reached',
  'flows.empty.filtered': 'No runs match these filters. Widen the time range or clear them.',
  'flows.empty.none': 'Nothing here yet. A run appears as soon as a flow is triggered in any tenant.',
  'flows.emptyTitleNone': 'No flow runs yet',
  'flows.filters.channelPlaceholder': 'Any channel',
  'flows.view.loadErrorTitle': 'Could not load the flow runs',
  'flows.view.run.tenantLabel': 'tenant',
  'flows.view.run.channelLabel': 'channel',
  'flows.view.run.errorTitle': 'This run failed',
  'flows.view.overview.references': 'References',
  'flows.view.overview.flowId': 'Flow ID',
  'flows.view.overview.runIdHint': 'Quote this when reporting the run to support or engineering.',
  'flows.view.trace.errorLabel': 'Reported error',
  'flows.view.related.bound': 'Up to {{count}} runs from the last {{days}} days.',
  'flows.view.raw.toast.copyFailed': 'The browser blocked clipboard access. Select the JSON and copy it by hand.',
  'flows.view.raw.toast.copyFailedTitle': 'Copy failed',
  'gateways.metrics.attached': 'Numbers attached',
  'gateways.metrics.active': 'Active numbers',
  'gateways.metrics.red': 'Rated RED',
  'gateways.metrics.redHint': 'Meta throttles a RED number before it suspends it.',
  'gateways.workstreamTooltip': 'Internal delivery reference {{workstream}}. It names the piece of the build this surface belongs to.',
  'gateways.cards.wba.openHint': 'Opens a page explaining what is still missing, with the closest signal the platform records today.',
  'gateways.numbers.kpi.other': 'Other rating',
  'gateways.numbers.columns.tenant': 'Tenant',
  'gateways.numbers.columns.displayName': 'Display name',
  'gateways.numbers.columns.waba': 'Business account',
  'gateways.numbers.columns.attached': 'Attached',
  'gateways.numbers.filters.tenant': 'Tenant',
  'gateways.numbers.filters.anyTenant': 'Any tenant',
  'gateways.numbers.filters.anyWaba': 'Any business account',
  'gateways.numbers.copyMetaId': 'Copy the Meta number ID',
  'gateways.numbers.copyWaba': 'Copy the business account ID',
  'gateways.numbers.qualityStale': 'Meta reports this rating on the business account. The platform does not record when it was last refreshed, so treat it as indicative.',
  'gateways.numbers.neverRegistered': 'Never registered',
  'gateways.numbers.unnamed': 'Unnamed number',
  'gateways.numbers.capNotice': 'The service returns at most {{cap}} numbers per request and it filled that. These counts describe what was returned, not the whole platform.',
  'gateways.numbers.totalCapped': '{{shown}} of {{total}} · server limit reached',
  'gateways.wba.pageDescription': 'Every attempt a customer made to connect WhatsApp themselves, finished or not, so you can see exactly where one got stuck.',
  'gateways.wba.workaround': 'Until the signup feed exists, impersonate the tenant from its Tenants page to see the onboarding wizard as they see it.',
  'gateways.wba.interim.title': 'Attached but never registered',
  'gateways.wba.interim.description': 'Numbers that reached a business account and never completed registration. It is the closest signal to a stalled signup the platform records today, and it comes from the numbers inventory rather than from a signup feed.',
  'gateways.wba.interim.empty': 'Every attached number has completed registration.',
  'gateways.wba.interim.emptyTitle': 'Nothing stalled',
  'gateways.wba.interim.openNumbers': 'Open all numbers',
  'settings.backup.local.body': 'scripts/dr/backup.sh runs pg_dump -Fc against every reach_* database and writes a timestamped folder under backups/ with a manifest.json beside the dumps.',
  'settings.backup.steps.snapshot.title': 'Take a snapshot',
  'settings.backup.steps.snapshot.body': 'Dumps every reach_* database into backups/<timestamp>/.',
  'settings.backup.steps.verify.title': 'Verify the snapshot',
  'settings.backup.steps.verify.body': 'The manifest lists each database, its dump size and a checksum. A missing database here is the failure you want to find now rather than during a restore.',
  'settings.backup.steps.restore.title': 'Restore',
  'settings.backup.steps.restore.body': 'Restores the chosen snapshot into the running Postgres. Pass the folder, not the individual dump files.',
  'settings.backup.steps.reconcile.title': 'Reconcile the ledger',
  'settings.backup.steps.reconcile.body': 'Checks the money tables after a restore and exits non-zero on any drift, so a partial restore cannot pass quietly.',
  'settings.backup.steps.drill.title': 'Rehearse the whole thing',
  'settings.backup.steps.drill.body': 'The automated drill takes a fresh backup, restores it into an isolated second Postgres, reconciles the ledger, cross-checks the money tables, prints RPO and RTO against a 30-minute budget, then tears itself down. Add --reuse-latest to drill against the newest snapshot, or KEEP_BACKUP=1 to keep the one it takes. It exits non-zero on any failed step.',
  'settings.backup.drill.title': 'Production drill is still outstanding',
  'settings.backup.drill.body': 'The local drill above is shipped and runnable today. What remains before the first paying tenant is the production-side rehearsal: cross-host point-in-time recovery with smoke verification, tracked in Phase 8 T8.2.',
  'queues.columns.vhost': 'Vhost',
  'queues.filters.anyVhost': 'Any vhost',
  'queues.noConsumersBadge': 'No consumers',
  'queues.backlogTooltip': 'More than {{threshold}} messages waiting to be read.',
  'queues.lastUpdated': 'Updated {{when}} · auto-refresh {{seconds}}s',
  'queues.autoRefresh': 'Auto-refresh every {{seconds}}s',
  'queues.unreachableTitle': 'RabbitMQ is not answering',
  'queues.unreachableDetail': 'HTTP {{status}} · {{message}}',
  'queues.metrics.filteredHint': 'Filtered view',
  'queues.denied.title': 'You do not have permission to read the broker',
  'queues.denied.body': 'The ops endpoint answered 403. Ask a platform administrator to grant ops access; the broker is not necessarily idle.',
  'auditSource.transfers': 'Transfers',
  'auditSource.opt-in': 'Opt-ins',
  'auditSource.deliveries': 'Deliveries',
  'auditSource.flow-runs': 'Flow runs',
  'auditSource.ledger': 'Ledger',
  'users.unnamed': 'Unnamed user',
  'users.noEmail': 'No email on record',
  'tenants.totalLabelTenants': '{{count}} of {{total}} tenants',
  'tenants.emptyPlatform': 'No tenants yet',
  'tenants.emptyPlatformHint': 'Organisations appear here as soon as the first one signs up.',
  'tenants.view.overview.kpi.ledgerCapped': 'At least this much: the ledger page is capped at 200 entries',
  'tenants.view.users.totalLabel': '{{count}} of {{total}} users',
  'tenants.view.audit.totalLabel': '{{count}} of {{total}} events',
  'tenants.view.billing.adjustment': 'adjustment',
  'tenants.view.integrations.hintOrgScope': 'Credentials stored against this organisation. Platform-wide defaults live under Settings.',
  'tenants.view.integrations.neverTested': 'Never tested',
  'tenants.view.impersonations.title': 'Impersonation history',
  'tenants.view.impersonations.description': 'Who has opened the tenant console as this organisation, newest first.',
  'tenants.view.impersonations.empty': 'Nobody has impersonated this tenant.',
  'tenants.view.impersonations.loadError': 'Could not load the impersonation history.',
  'tenants.view.impersonations.live': 'Live',
  'tenants.view.impersonate.modal.capability': 'While the session lasts you can read every conversation, send messages and change settings as this tenant.',
  'tenants.view.impersonate.modal.audited': 'Your email address is written to the audit log, and the session cannot be refreshed or extended.',
  'tenants.view.impersonate.toast.opened': 'Impersonating {{name}} until {{until}}. The tenant console opened in a new tab.',
  'tenants.view.impersonate.toast.openedTitle': 'Impersonation started',
  'tenants.view.impersonate.toast.popupBlocked': 'Your browser blocked the tenant console tab. Allow pop-ups for this site, then try again.',
  'tenants.view.settings.notAvailable.suspend': 'Suspending and re-activating a tenant is not available yet.',
  'tenants.view.settings.notAvailable.rotate': 'Rotating a tenant\'s API keys is not available yet.',
  'tenants.view.settings.notAvailable.delete': 'Deleting a tenant is not available yet.',
  'tenants.view.settings.notAvailable.explainer': 'Suspension and key rotation need admin endpoints that have not shipped. The controls stay disabled until they do, rather than confirming an action that cannot happen.',
  'tenants.view.settings.notAvailable.deleteExplainer': 'Tenant deletion needs an admin endpoint that has not shipped. Until it does, export the ledger and any critical templates through the Billing and Templates pages.',
  'users.filters.organisation': 'Organisation',
  'users.actions.clearAllFilters': 'Clear all filters',
  'users.emptyPlatform': 'No users yet',
  'users.emptyPlatformHint': 'Accounts appear here as soon as the first person signs up.',
  'users.truncated': 'Showing the newest {{cap}} accounts. The endpoint returns no more than that, so every count on this page is a floor, not a total.',
  'users.view.backToAll': 'All users',
  'users.view.copyUserId': 'Copy the user id',
  'users.view.tabs.tenantActivity': 'Tenant activity',
  'users.view.activity.scopeWarning': 'These are the tenant\'s events, not this person\'s. The audit sources record the organisation, not the actor, so a per-user history is not available yet.',
  'users.view.scope.toast.failedTitle': 'Scope change failed',
  'users.view.scope.modal.effectiveGain': 'They gain admin access the next time they sign in.',
  'users.view.scope.modal.effectiveLose': 'They lose admin access the next time they sign in. A session already open keeps its access until its token expires.',
  'users.view.scope.selfDemotionBlocked': 'You cannot remove your own platform scope: it would lock you out of this console.',
  'users.view.security.unknownExplainer': 'The admin user projection carries no security columns yet, so these four values are genuinely unknown to the console rather than empty.',
  'users.view.security.notAvailable.reset': 'Forcing a password reset is not available yet.',
  'users.view.security.notAvailable.revoke': 'Revoking a user\'s sessions is not available yet.',
  'users.view.security.notAvailable.disable': 'Disabling a user is not available yet.',
  'users.view.security.notAvailable.explainer': 'Forcing a password reset and revoking sessions need admin endpoints that have not shipped. They stay disabled until then.',
  'users.view.danger.notAvailable.transfer': 'Moving a user to another tenant is not available yet.',
  'users.view.danger.notAvailable.delete': 'Deleting a user is not available yet.',
  'users.view.danger.notAvailable.explainer': 'Deletion needs an admin endpoint that has not shipped. When it does, this will ask you to type the address before it will run.',
  'roles.you': 'You',
  'roles.emptyHint': 'Try clearing the active filters.',
  'roles.filters.organisation': 'Organisation',
  'roles.toast.failedTitle': 'Scope change failed',
  'roles.toast.promoted': '{{email}} now has platform admin access across every tenant.',
  'roles.toast.demoted': '{{email}} no longer has admin access. It takes effect at their next sign-in.',
  'roles.promote.modal.body': 'Promote {{email}} to platform scope. They gain admin access to every tenant on the platform.',
  'roles.promote.modal.effective': 'They gain that access the next time they sign in. This change is not recorded in the audit trail.',
  'roles.demote.modal.body': 'Demote {{email}} to tenant scope. They lose admin access to the platform console.',
  'roles.demote.modal.effective': 'They lose that access the next time they sign in. A session already open keeps its access until its token expires.',
  'roles.selfDemotionBlocked': 'You cannot remove your own platform scope: it would lock you out of this console.',
  'settings.flags.scope.phone': 'Phone number (UUID)',
  'settings.flags.lastChanged': 'Last changed {{when}}',
  'settings.flags.lastChangedBy': 'Last changed {{when}} by {{who}}',
  'settings.flags.toggle.title': 'Change the default for {{key}}?',
  'settings.flags.toggle.bodyOn': 'Turn the default for {{key}} on. Every scope without an override starts seeing it enabled.',
  'settings.flags.toggle.bodyOff': 'Turn the default for {{key}} off. Every scope without an override starts seeing it disabled.',
  'settings.flags.toggle.overridesKeep': '{{count}} override(s) keep their own value and are unaffected.',
  'settings.flags.toggle.noOverrides': 'No overrides exist, so this applies everywhere.',
  'settings.flags.toggle.confirmOn': 'Turn on',
  'settings.flags.toggle.confirmOff': 'Turn off',
  'settings.flags.delete.body': 'Delete the flag {{key}} and its {{count}} override(s)? Any evaluation of this key falls back to off, and the registry entry is gone.',
  'settings.flags.toast.deleted': '{{key}} deleted.',
  'settings.flags.toast.deletedTitle': 'Feature flag deleted',
  'settings.flags.toast.deleteFailed': 'Delete failed',
  'settings.flags.toast.createdTitle': 'Feature flag created',
  'settings.flags.toast.overrideRemoved': 'Override removed.',
  'settings.flags.removeOverride.title': 'Remove this override?',
  'settings.flags.removeOverride.body': 'Remove the {{scope}} override on {{target}} for {{key}}? That scope falls back to the flag default, currently {{state}}.',
  'settings.flags.form.blastRadius': 'Saving sets the default to {{state}} at {{percent}}% rollout.',
  'settings.flags.form.blastRadiusOverrides': '{{count}} override(s) keep their own value regardless.',
  'system.hub.allHealthy': 'All {{count}} healthy',
  'system.hub.servicesDown': '{{count}} down',
  'system.hub.servicesUnhealthy': '{{count}} degraded',
  'system.hub.healthUnavailable': 'Could not read service health, so the Health tile below shows no status. That is a failure to ask, not a healthy platform.',
  'system.cards.queues.title': 'Queues',
  'system.cards.queues.hint': 'RabbitMQ depth, unacked messages and consumer counts, refreshed every 10 s.',
  'system.cards.logs.title': 'Logs',
  'system.cards.logs.hint': 'Query the logs database across the API, SQL, app, lifecycle and frontend families.',
  'system.cards.api.title': 'API monitoring',
  'system.cards.api.hint': 'Request volume, error rate and latency per endpoint, from the API log family.',
  'system.health.lastChecked': 'Last checked {{when}}',
  'system.health.neverChecked': 'Not checked yet',
  'system.health.showingStale': 'The cards below are the last good result, from {{when}}. They are not current.',
  'system.health.noResult': 'No health result to show. The probe has not succeeded yet.',
  'system.health.probeResult': 'HTTP {{status}} · {{ms}} ms',
  'system.health.noResponse': 'No response after {{ms}} ms',
  'system.health.empty': 'No services reported',
  'system.health.emptyHint': 'The probe returned an empty list. Check that the settings service knows which services to probe.',
  'system.health.denied.title': 'You do not have permission to read service health',
  'system.health.denied.body': 'The ops endpoint answered 403. Ask a platform administrator to grant ops access; no service has been reported as down.',
  'system.backups.denied.title': 'You do not have permission to read backup metadata',
  'system.backups.denied.body': 'The ops endpoint answered 403. Ask a platform administrator to grant ops access.',
  'system.backups.runDrill': 'Run a disaster-recovery drill',
  'system.backups.drillHint': 'Backs up, restores into an isolated second Postgres, reconciles the ledger and prints RPO and RTO. Exits non-zero on any failed step, so CI can gate on it. Add --reuse-latest to skip the fresh backup.',
  'system.backups.drillCommand': './scripts/dr/drill.sh',
  'system.backups.reconcile': 'Reconcile the ledger after a restore',
  'system.backups.reconcileHint': 'Compares ledger totals against the subscription state and reports any drift the restore introduced.',
  'system.backups.reconcileCommand': './scripts/dr/reconcile-ledger.sh',
  'system.alerts.channel.pagerduty': 'PagerDuty',
  'system.alerts.channel.slack': 'Slack',
  'system.alerts.channel.linear': 'Linear',
  'system.alerts.channelEnabled': '{{channel}}: enabled',
  'system.alerts.channelDisabled': '{{channel}}: not configured',
  'system.alerts.notifiesHumansTitle': 'Both buttons on this page notify whoever is on call',
  'system.alerts.notifiesHumans': 'There is no dry run here. Every send reaches the enabled channels below, and the PagerDuty call opens an incident that has to be acknowledged and closed by hand in PagerDuty.',
  'system.alerts.sendTestPage': 'Send test page',
  'system.alerts.buttonsCaption': 'Both buttons page a real person.',
  'system.alerts.defaults.summary': 'Manual alert from the RepletoryReach admin console',
  'system.alerts.defaults.details': 'reason=',
  'system.alerts.testPayloadSummary': 'Test alert from the RepletoryReach admin console',
  'system.alerts.form.source': 'Source',
  'system.alerts.form.summaryHint': 'This becomes the PagerDuty incident title and the bold line in Slack, so write it for the person who will be woken by it.',
  'system.alerts.form.summaryRequired': 'A summary is required.',
  'system.alerts.form.detailsHint': 'One key=value per line. Lines without an = are dropped.',
  'system.alerts.form.detailsIgnored': '{{count}} line(s) have no = and will be dropped.',
  'system.alerts.toast.testFailedTitle': 'Test page failed',
  'system.alerts.confirm.noChannels': 'no channel (nothing is enabled)',
  'system.alerts.confirm.payload': 'What will be sent',
  'system.alerts.confirm.severity': 'Severity: {{severity}}',
  'system.alerts.confirm.pagerDutyWarning': 'If PagerDuty is among the channels, this opens an incident. Nothing in this console can resolve it; somebody has to close it in PagerDuty.',
  'system.alerts.confirm.dispatch.title': 'Send this alert to on-call?',
  'system.alerts.confirm.dispatch.body': 'This notifies whoever is on call through {{channels}}. It is not a rehearsal.',
  'system.alerts.confirm.dispatch.confirm': 'Send the alert',
  'system.alerts.confirm.test.title': 'Send a test page to on-call?',
  'system.alerts.confirm.test.body': 'A test page is a real page. It reaches {{channels}} exactly like a genuine alert; only the wording differs.',
  'system.alerts.confirm.test.confirm': 'Send the test page',
  'settings.integrations.plaintextNotice': 'Credentials are stored unencrypted in the settings database and are readable by anyone with platform-admin access. Column-level encryption is still outstanding, so treat this page as the credential store it is and keep that access tight.',
  'settings.integrations.confirmTestAlert.title': 'Send a real alert?',
  'settings.integrations.confirmTestAlert.body': 'This fires a genuine alert through every enabled channel: PagerDuty raises an incident and pages whoever is on call, and Slack posts to the alert channel. It is not a dry run.',
  'settings.integrations.confirmTestAlert.confirm': 'Send the alert',
  'settings.integrations.toast.removed': '{{name}} removed. Services asking for it now report not_configured.',
  'settings.integrations.toast.removedTitle': 'Integration removed',
  'settings.integrations.toast.removeFailed': 'Could not remove the integration',
  'settings.integrations.toast.saveFailed': 'Could not save the integration',
  'settings.integrations.status.connected': 'Connected',
  'settings.integrations.status.needsAttention': 'Needs attention',
  'settings.integrations.status.enabledUntested': 'Enabled, untested',
  'settings.integrations.lastChanged': 'Changed {{when}}',
  'settings.integrations.lastChangedBy': 'Changed {{when}} by {{who}}',
  'settings.integrations.usedByUnknown': 'nothing yet: configuring this has no effect until a service reads it',
  'settings.integrations.empty.title': 'No providers in the catalogue',
  'settings.integrations.empty.description': 'The settings database has no integration providers seeded, so there is nothing to configure. Run the settings migrations and reload.',
  'settings.integrations.category.compliance': 'COMPLIANCE',
  'settings.integrations.category.llm': 'AI',
  'settings.integrations.category.messaging': 'MESSAGING',
  'settings.integrations.category.observability': 'ALERTING',
  'settings.integrations.category.payments': 'PAYMENTS',
  'settings.integrations.category.storage': 'STORAGE',
  'settings.integrations.category.email': 'EMAIL',
  'settings.integrations.form.revealNotice': 'Opening this form loads the stored secrets in the clear so you can check and edit them. Close it when you are done.',
  'settings.integrations.form.discard.title': 'Discard these credentials?',
  'settings.integrations.form.discard.body': 'You have typed changes that are not saved. Closing loses them, and credentials usually have to be fetched from the provider’s own console again.',
  'settings.integrations.form.discard.confirm': 'Discard',
  'settings.integrations.form.discard.cancel': 'Keep editing',
  'billing.overview.loadError': 'Failed to load billing stats.',
  'billing.overview.kpi.noWallets': 'No wallets yet',
  'billing.overview.kpi.invalidInvoices': 'Invoices failing validation',
  'billing.overview.kpi.invalidInvoicesHint': 'Issued, but rejected by the tax rules of their country.',
  'billing.overview.kpi.pastDue': 'Subscriptions past due',
  'billing.overview.kpi.pastDueHint': 'The renewal worker could not cover the plan charge.',
  'billing.overview.cards.invoices.title': 'Invoices',
  'billing.overview.cards.invoices.hint': 'Find an invoice, check its tax validation and download the PDF.',
  'billing.overview.cards.usage.title': 'Usage',
  'billing.overview.cards.usage.hint': 'Per-tenant spend rolled up from the ledger.',
  'billing.ledger.adjustment': 'adjustment',
  'billing.ledger.copyIdempotency': 'Copy the idempotency key',
  'billing.ledger.filters.organisation': 'Organisation',
  'billing.ledger.filters.newestRows': 'Newest {{count}} rows',
  'billing.ledger.truncatedSuffix': 'newest {{limit}} entries',
  'billing.ledger.toast.exported': 'Exported {{count}} ledger entries.',
  'billing.ledger.toast.exportedTitle': 'Export ready',
  'billing.invoices.tableTitle': 'Invoices',
  'billing.invoices.totalLabel': '{{shown}} of {{total}}',
  'billing.invoices.truncatedSuffix': 'newest {{limit}}',
  'billing.invoices.searchPlaceholder': 'Filter by invoice number or organisation…',
  'billing.invoices.emptyHint': 'Invoices are written by the subscription renewal worker at cycle close. You can also generate one here.',
  'billing.invoices.emptyRows': 'No invoices matched.',
  'billing.invoices.columns.due': 'Due',
  'billing.invoices.columns.validation': 'Validation',
  'billing.invoices.copyNumber': 'Copy the invoice number',
  'billing.invoices.kpi.all': 'All invoices',
  'billing.invoices.kpi.invalid': 'Failing validation',
  'billing.invoices.filters.anyValidation': 'Any verdict',
  'billing.invoices.validate': 'Validate',
  'billing.invoices.status.draft': 'Draft',
  'billing.invoices.status.issued': 'Issued',
  'billing.invoices.status.paid': 'Paid',
  'billing.invoices.status.void': 'Void',
  'billing.invoices.status.past_due': 'Past due',
  'billing.invoices.validation.valid': 'Valid',
  'billing.invoices.validation.invalid': 'Invalid',
  'billing.invoices.validation.unvalidated': 'Unvalidated',
  'billing.invoices.validation.skipped': 'Not applicable',
  'billing.invoices.invalidBanner.title': '{{count}} invoices fail tax validation',
  'billing.invoices.invalidBanner.body': 'These invoices do not satisfy the VAT rules of the country they were issued in. Validate a row to see which rule failed.',
  'billing.invoices.list.meta': '{{period}} · {{total}}',
  'billing.invoices.modal.regenerate.title': 'Regenerate the PDF for {{number}}?',
  'billing.invoices.modal.regenerate.body': 'The cached PDF for {{number}} is deleted from object storage. The next download renders the invoice again from the template.',
  'billing.invoices.modal.validate.title': 'Validation for {{number}}',
  'billing.invoices.modal.validate.none': 'No issues found.',
  'billing.invoices.modal.validate.validator': 'Checked against the {{validator}} rules.',
  'billing.invoices.modal.validate.severityError': 'Error',
  'billing.invoices.modal.validate.severityWarning': 'Warning',
  'billing.invoices.toast.validated': '{{number}} passes validation.',
  'billing.invoices.toast.validatedTitle': 'Validation passed',
  'billing.invoices.toast.validateFailed': 'Validation failed',
  'billing.invoices.form.organisation': 'Organisation',
  'billing.invoices.form.orgPlaceholder': 'Choose a tenant',
  'billing.invoices.form.lineDescriptionDefault': 'Subscription: monthly plan',
  'billing.plans.viewRoster': 'View the subscription roster',
  'billing.plans.subsLoadError': 'Failed to load subscriber counts.',
  'billing.plans.columns.mrr': 'Revenue / month',
  'billing.plans.columns.created': 'Created',
  'billing.plans.archivedWithSubs': 'Archived plan still has active subscribers.',
  'billing.plans.cardMrr': '{{mrr}} per month',
  'billing.plans.list.created': 'created {{when}}',
  'billing.plans.metric.mrr': 'Monthly recurring revenue',
  'billing.plans.metric.mrrHint': 'Plan price times active subscriptions.',
  'billing.plans.metric.activePlans': 'Active plans',
  'billing.plans.metric.activePlansHint': 'of {{total}} in the catalogue',
  'billing.plans.metric.stranded': 'Archived with subscribers',
  'billing.plans.metric.strandedHint': 'Archived plans that tenants are still billed on.',
  'billing.subscriptions.unknownPlan': 'Unknown plan',
  'billing.subscriptions.columns.cycle': 'Billing cycle',
  'billing.subscriptions.pastDueSince': 'Past due, cycle ended {{when}}',
  'billing.subscriptions.status.active': 'Active',
  'billing.subscriptions.status.past_due': 'Past due',
  'billing.subscriptions.status.cancelled': 'Cancelled',
  'billing.subscriptions.pastDue.title': '{{count}} subscriptions are past due',
  'billing.subscriptions.pastDue.banner': 'The renewal worker could not cover the plan charge from the tenant\'s wallet. Sending is not suspended: top the wallet up and the next renewal tick recovers the subscription on its own.',
  'billing.subscriptions.plansLoadError': 'Failed to load plans, so plan codes are missing.',
  'billing.subscriptions.metric.mrr': 'Monthly recurring revenue',
  'billing.subscriptions.metric.mrrHint': 'From {{count}} active subscriptions.',
  'billing.subscriptions.metric.noRevenue': 'No revenue yet',
  'billing.subscriptions.metric.active': 'Active subscriptions',
  'billing.subscriptions.metric.activeHint': 'of {{total}} on the roster',
  'billing.subscriptions.metric.pastDueHint': 'Wallet short at renewal. Nothing is suspended.',
  'billing.skuRates.action.newVersion': 'Change rate',
  'billing.skuRates.edit.amountLabel': 'Charge ({{currency}})',
  'billing.skuRates.edit.amountHint': 'What the tenant pays per unit, for example {{example}}.',
  'billing.skuRates.modal.confirm.title': 'Change the {{sku}} rate?',
  'billing.skuRates.modal.confirm.change': '{{sku}} moves from {{from}} to {{to}} per event.',
  'billing.skuRates.modal.confirm.everyTenant': 'This inserts a new rate version effective today and retires the current one. Every tenant on the platform is charged the new rate from now on.',
  'billing.skuRates.modal.confirm.usage': 'Used {{events}} times by {{orgs}} organisations in the sampled ledger over the last 7 days.',
  'billing.skuRates.modal.confirm.confirm': 'Change the rate',
  'billing.usage.loadError': 'Failed to load usage.',
  'billing.usage.toast.exported': 'Exported {{count}} usage rows.',
  'billing.usage.toast.exportedTitle': 'Export ready',
  'billing.usage.truncated.title': 'The rollup is capped',
  'billing.usage.truncated.body': 'The endpoint returned its maximum of {{limit}} ledger entries, so the selected range may be wider than the rows behind these totals.',
  'billing.usage.truncated.covered': 'The endpoint returned its maximum of {{limit}} ledger entries, covering {{from}} to {{to}}. Anything older than that is not counted here.',
  'billing.itn.loadError': 'Failed to load gateway configuration.',
  'billing.itn.webhookLabel': 'ITN endpoint',
  'billing.itn.copyWebhook': 'Copy the webhook path',
  'billing.itn.configure': 'Configure credentials',
  'billing.itn.updatedAt': 'Updated {{when}}',
  'billing.itn.updatedBy': 'Updated {{when}} by {{who}}',
  'activity.loadError': 'Some activity sources could not be read, so this list is incomplete.',
  'shell.impersonation.one': 'Impersonating {{tenant}} · ends {{when}}',
  'shell.impersonation.many': '{{count}} impersonations active',
  'shell.impersonation.tooltip': 'A platform admin is signed in as a tenant right now. Open the audit log.',
  'shell.impersonation.unknownTenant': 'a tenant',
  'dashboard.wallets.title': 'Platform float',
  'dashboard.wallets.hint': 'wallet balances by currency',
  'dashboard.wallets.count': '{{count}} wallets',
  'dashboard.wallets.empty': 'No wallets yet.',
  'webhooks.autoDisabled': 'Auto-disabled',
  'webhooks.streakOf': '{{count}} / {{threshold}}',
  'webhooks.streakTooltip': 'Consecutive failures. At {{threshold}} the service disables the subscription.',
  'webhooks.moreEvents': '+{{count}} more',
  'webhooks.copyUrl': 'Copy the endpoint URL',
  'webhooks.lastUpdated': 'Updated {{when}}',
  'webhooks.openDeliveries': 'Deliveries →',
  'webhooks.viewDeliveries': 'Deliveries',
  'webhooks.totalLabelCapped': '{{shown}} of the newest {{total}}',
  'webhooks.denied.title': 'You do not have permission to read webhook subscriptions',
  'webhooks.denied.body': 'The webhooks service answered 403. Ask a platform administrator to grant webhook access; no tenant has been shown to have zero subscriptions.',
  'webhooks.deliveries.blocked': 'Blocked',
  'webhooks.deliveries.blockedReason': 'Refused by the SSRF guard: {{reason}}',
  'webhooks.deliveries.blockedNotice': '{{count}} attempt(s) in this window were refused before leaving the platform because the target resolves into private address space. They are counted as Blocked, not as Pending.',
  'webhooks.deliveries.attemptedAt': 'tried {{when}}',
  'webhooks.deliveries.neverAttempted': 'not yet attempted',
  'webhooks.deliveries.searchPlaceholder': 'Search by event, delivery id or tenant…',
  'webhooks.deliveries.emptyFiltered': 'No deliveries match these filters. Widen the window or clear them.',
  'webhooks.deliveries.totalLabelCapped': '{{shown}} of the newest {{total}} · {{range}}',
  'webhooks.deliveries.openSubscriptions': '← Subscriptions',
  'webhooks.deliveries.scopedToSubscription': 'Showing one subscription only.',
  'webhooks.deliveries.copySubscriptionId': 'Copy the subscription id',
  'webhooks.deliveries.showAllSubscriptions': 'Show every subscription',
  'conversations.columns.state': 'State',
  'conversations.state.open': 'Open',
  'conversations.state.pending': 'Pending',
  'conversations.state.resolved': 'Resolved',
  'conversations.state.archived': 'Archived',
  'conversations.filters.state': 'State',
  'conversations.filters.statePlaceholder': 'Any state',
  'conversations.kpi.open': 'Open',
  'conversations.drawer.state': 'State',
  'broadcasts.status.draft': 'Draft',
  'broadcasts.status.scheduled': 'Scheduled',
  'broadcasts.status.paused': 'Paused',
  'broadcasts.status.completed': 'Completed',
  'broadcasts.status.cancelled': 'Cancelled',
  'broadcasts.status.failed': 'Failed',
  'broadcasts.filters.status': 'Status',
  'broadcasts.filters.statusPlaceholder': 'Any status',
  'broadcasts.drawer.status': 'Status',
  'broadcasts.drawer.scheduled': 'Scheduled for',
  'broadcasts.drawer.started': 'Started',
  'broadcasts.drawer.finished': 'Finished',
  'nav.section.overview': 'Overview',
  'nav.section.tenantManagement': 'Tenant management',
  'nav.section.messaging': 'Messaging',
  'nav.section.operations': 'Operations',
  'nav.item.system': 'System',
  'filters.active': 'Active filters',
  'filters.remove': 'Remove the {{field}} filter',
  'filters.clearAll': 'Clear all',
  'table.footer.range': 'Showing {{from}} to {{to}} of {{total}} results',
  'table.footer.rangeCapped': 'Showing {{from}} to {{to}} of the newest {{total}} the server returns',
  'table.footer.perPage': 'Rows per page',
  'table.rowActions': 'Row actions',
  'flows.actions.view': 'View run details',
  'flows.actions.copyContact': 'Copy contact number',
  'flows.actions.openTenant': 'Open tenant',
  'flows.cell.channel': 'via {{channel}}',
  'filters.drawer.title': 'Filter results',
  'filters.drawer.description': 'Apply one or more filters. Combinations narrow the list.',
  'filters.any': 'Any',
  'tenants.columns.organisation': 'Organisation',
  'tenants.actions.new': 'New tenant',
  'tenants.actions.newUnavailable': 'Creating an organisation from the console is not available yet. A tenant is created when someone signs up.',
  'tenants.row.menuLabel': 'Actions for {{name}}',
  'tenants.row.viewDetails': 'View details',
  'tenants.row.manageUsers': 'Manage users',
  'tenants.row.viewBilling': 'View billing',
  'tenants.row.suspend': 'Suspend tenant',
  'tenants.row.reactivate': 'Re-activate tenant',
  'tenants.row.delete': 'Delete tenant',
  'tenants.row.lifecycleUnavailable': 'Suspending, re-activating and deleting a tenant need admin endpoints that have not shipped.',
  'tenants.view.header.tenantId': 'Tenant ID',
  'tenants.view.header.viewUsers': 'View users',
  'tenants.view.header.menuLabel': 'More actions for {{name}}',
  'tenants.view.header.openUsers': 'Open the users tab',
  'tenants.view.header.openBilling': 'Open the billing tab',
  'tenants.view.header.openSettings': 'Open the settings tab',
  'tenants.view.overview.link.users': 'View users',
  'tenants.view.overview.link.ledger': 'View ledger',
  'tenants.view.overview.link.integrations': 'View integrations',
  'users.columns.user': 'User',
  'users.columns.organisation': 'Organisation',
  'users.columns.joined': 'Joined',
  'users.row.menuLabel': 'Actions for {{name}}',
  'users.row.view': 'View user',
  'users.row.openTenant': 'Open tenant',
  'users.row.reset': 'Reset password',
  'users.row.disable': 'Disable user',
  'users.row.unavailable': 'Resetting a password and disabling an account need admin endpoints that have not shipped.',
  'users.view.header.menuLabel': 'More actions for {{name}}',
  'roles.actions.invite': 'Invite platform staff',
  'roles.actions.inviteUnavailable': 'Inviting staff from the console is not available yet. Promote an existing account instead.',
  'roles.columns.user': 'User',
  'roles.auditNote': 'A scope change takes effect at the person’s next sign-in. It is not written to the audit trail yet.',
  'filters.field.show': 'Show',
  'billing.range.label': 'Date range',
  'billing.range.all': 'All time',
  'billing.range.today': 'Today',
  'billing.range.last7': 'Last 7 days',
  'billing.range.last30': 'Last 30 days',
  'billing.ledger.type.credit': 'Credit',
  'billing.ledger.type.debit': 'Debit',
  'billing.ledger.type.adjustment': 'Adjustment',
  'settings.hub.you.title': 'Your account',
  'settings.hub.you.description': 'Your profile, how this console looks and how you sign in.',
  'settings.hub.platform.title': 'Platform configuration',
  'settings.hub.platform.description': 'Credentials and switches that apply to every tenant.',
  'settings.hub.access.title': 'Access and recovery',
  'settings.hub.access.description': 'Keys issued across the platform, and the procedure that brings the databases back.',
  'settings.hub.meta.integrations': '{{configured}}/{{total}} set',
  'settings.hub.meta.flags': '{{count}} flags',
  'settings.layout.nav.access': 'Access and recovery',
  'system.hub.health.title': 'Service health',
  'system.hub.health.description': 'Whether every service is answering, and how a person gets paged when one is not.',
  'system.hub.throughput.title': 'Traffic and queues',
  'system.hub.throughput.description': 'Request volume, error rate and broker depth across the platform.',
  'system.hub.records.title': 'Records and recovery',
  'system.hub.records.description': 'What the platform wrote down, and what to run when it has to be restored.',
  'system.cards.audit.title': 'Audit trail',
  'view.list': 'List view',
  'view.table': 'Table view',
  'flows.view.eyebrow': 'Flow run',
  'flows.view.dataAsOf': 'Data as of {{time}}',
  'flows.view.summary.state': 'State',
  'flows.view.summary.timing': 'Timing',
  'flows.view.summary.references': 'References',
  'flows.view.summary.routing': 'Routing',
  'flows.view.summary.inProgress': 'In progress',
  'flows.view.summary.updating': 'Updating…',
  'flows.view.summary.finished': 'Finished',
  'flows.view.summary.duration': 'Duration',
  'flows.view.summary.contact': 'Contact',
  'flows.view.summary.conversation': 'Conversation',
  'billing.ledger.stat.all': 'All entries',
  'billing.ledger.stat.credits': 'Credits',
  'billing.ledger.stat.debits': 'Debits',
  'billing.ledger.stat.adjustments': 'Adjustments',
  'billing.ledger.stat.skus': 'By SKU',
  'billing.ledger.stat.activeSkus': 'Distinct SKU codes',
  'billing.ledger.stat.totalHint': 'Total {{total}}',
  'billing.ledger.stat.noAmounts': 'No amounts in this window',
  'billing.ledger.showingNewest': 'Showing the newest {{limit}} entries',
  'billing.ledger.showingCount': 'Showing {{shown}} of {{total}} entries',
  'billing.ledger.cappedHelp': 'The ledger endpoint returns the newest entries only and reports no grand total, so this is the size of the window and not the length of the journal. Raise the row limit to look further back.',
  'billing.ledger.sortedBy': 'Sorted by',
  'billing.ledger.sort.newest': 'Created at (newest)',
  'billing.ledger.sort.oldest': 'Created at (oldest)',
  'billing.ledger.sort.amountHigh': 'Amount (highest)',
  'billing.ledger.sort.amountLow': 'Amount (lowest)',
  'billing.ledger.columns.createdAt': 'Created at',
  'billing.ledger.columns.organisation': 'Organisation',
  'billing.ledger.columns.entryType': 'Entry type',
  'billing.ledger.columns.skuCode': 'SKU code',
  'billing.ledger.columns.description': 'Description',
  'billing.ledger.columns.reference': 'Reference',
  'billing.ledger.columns.entryId': 'Entry ID',
  'billing.ledger.copyReference': 'Copy the idempotency reference',
  'billing.ledger.copyEntryId': 'Copy the entry id',
  'billing.ledger.filters.entryType': 'Entry type',
  'billing.ledger.filters.anyType': 'Any entry type',
  'billing.ledger.search': 'Search organisation, SKU code, description, reference…',
  'billing.ledger.eyebrow': 'Billing',
  'settings.profile.title': 'Profile',
  'settings.profile.description': 'Your platform-admin identity, and the locale this console formats dates and numbers in.',
  'settings.appearance.title': 'Appearance',
  'settings.appearance.description': 'Theme, accent, density and accessibility helpers. These are stored in this browser.',
  'settings.security.title': 'Security',
  'settings.security.description': 'Rotate your password, manage two-factor authentication and review where you are signed in.',
  'auth.panel.badge': 'Admin console',
  'auth.panel.tagline': 'Secure access for platform operators.',
  'auth.panel.theme': 'Theme',
  'auth.panel.theme.light': 'Light',
  'auth.panel.theme.dark': 'Dark',
  'auth.panel.theme.system': 'System',
  'auth.footer.docs': 'Documentation',
  'auth.footer.status': 'System status',
  'auth.staffOnly': 'For authorised RepletoryReach staff.',
  'auth.login.title': 'Sign in',
  'auth.login.emailPlaceholder': 'operator@example.com',
  'auth.login.passwordPlaceholder': 'Enter your password',
  'auth.login.rememberEmail': 'Remember my email address',
  'auth.mfa.expiresIn': 'Code expires in',
  'auth.mfa.digitLabel': 'Digit {{position}} of {{total}}',
  'auth.mfa.useRecovery': 'Use a recovery code',
  'auth.mfa.useAuthenticator': 'Use the authenticator code',
  'auth.mfa.recoveryLabel': 'Recovery code',
  'auth.mfa.recoveryDescription': 'One of the codes you saved when you set two-factor sign-in up.',
  'auth.mfa.expiredTitle': 'This challenge has expired',
  'auth.mfa.expiredBody': 'The code was valid for five minutes. Sign in again to start a new challenge.',
  'auth.forgot.title': 'Forgot password',
  'auth.forgot.resendPrompt': 'Didn\'t get it?',
  'auth.reset.title': 'Reset password',
  'auth.reset.fromLink': 'From link',
  'auth.reset.notYou': 'Not you? Start a new reset',
  'auth.reset.rules.title': 'Password must contain:',
  'auth.reset.rules.length': 'At least 12 characters',
  'auth.reset.rules.upper': 'At least one uppercase letter (A-Z)',
  'auth.reset.rules.lower': 'At least one lowercase letter (a-z)',
  'auth.reset.rules.digit': 'At least one number (0-9)',
  'auth.reset.rules.symbol': 'At least one special character (!@#$%^&*)',
  'auth.reset.strength.weak': 'Weak',
  'auth.reset.strength.fair': 'Fair',
  'auth.reset.strength.good': 'Good',
  'auth.reset.strength.strong': 'Strong',
  'auth.reset.match': 'Passwords match',
  'auth.reset.errors.policy': 'That password does not meet every rule below.',
  'webhooks.stats.allHint': 'Across {{count}} organisations',
  'webhooks.stats.percentOfTotal': '{{percent}}% of all subscriptions',
  'webhooks.stats.inactiveHint': 'Paused by an operator or auto-disabled',
  'webhooks.stats.failingLabel': 'Failing streak ≥ 1',
  'webhooks.columns.failures': 'Failures (threshold {{threshold}})',
  'webhooks.columns.created': 'Created',
  'webhooks.meter.label': '{{count}} of {{threshold}} consecutive failures',
  'webhooks.detail.title': 'Webhook subscription',
  'webhooks.detail.endpoint': 'Endpoint',
  'webhooks.detail.url': 'URL',
  'webhooks.detail.events': 'Events ({{count}})',
  'webhooks.detail.health': 'Status and health',
  'webhooks.detail.status': 'Status',
  'webhooks.detail.lastDelivery': 'Last delivery',
  'webhooks.detail.created': 'Created at',
  'webhooks.detail.viewDeliveries': 'View this endpoint’s deliveries',
  'webhooks.detail.readOnly': 'Disable, replay and delete exist in the webhooks service but only on tenant-scoped routes, so this console cannot offer them yet. Read-only for now.',
  'webhooks.detail.noLastDelivery': 'The admin projection does not carry a last-delivery timestamp. Open the deliveries page for this endpoint.',
  'webhooks.openDetail': 'Open subscription details',
  'webhooks.viewTenant': 'Open the tenant',
  'common.lastUpdated': 'Last updated {{time}}',
  'common.readOnly': 'Read-only overview',
  'conversations.columns.phoneNumberId': 'Phone number ID',
  'conversations.actions.view': 'View details',
  'conversations.actions.openTenant': 'Open tenant',
  'conversations.columns.lastActivity': 'Last activity',
  'billing.overview.eyebrow': 'Billing',
  'billing.overview.updated': 'Last updated {{time}}',
  'billing.overview.refresh': 'Refresh',
  'billing.overview.operationsHint': 'Billing health across every tenant, and the two conditions worth acting on today.',
  'billing.overview.kpi.currencies': 'Wallet currencies',
  'billing.overview.kpi.currenciesHint': 'No wallet currency in use yet',
  'billing.overview.kpi.ledgerEntries': 'entries written in the last 7 days',
  'billing.overview.link.currencies': 'View wallets',
  'billing.overview.link.ledger': 'View ledger',
  'billing.overview.link.invoices': 'View invoices',
  'billing.overview.link.subscriptions': 'View subscriptions',
  'billing.overview.kpi.invalidTotal': 'Total value {{total}}',
  'billing.overview.kpi.noInvalid': 'Every issued invoice passed its tax check',
  'billing.overview.kpi.walletsHint': 'Across {{count}} wallets',
  'billing.overview.openCard': 'Open {{name}}',
  'common.view': 'View',
  'broadcasts.failedPct': '{{count}} ({{pct}}%)',
  'broadcasts.actions.view': 'View details',
  'broadcasts.actions.openTenant': 'Open tenant',
  'broadcasts.actions.copyId': 'Copy the broadcast id',
  'broadcasts.columns.recipients': 'Recipients',
  'broadcasts.cell.failedRate': '{{pct}}% failed',
  'tenants.view.overview.viewAllActivity': 'View all activity',
  'webhooks.deliveries.columns.attempted': 'Attempted',
  'webhooks.deliveries.columns.endpoint': 'Endpoint',
  'webhooks.deliveries.columns.tenant': 'Tenant',
  'webhooks.deliveries.stats.successRate': 'Success rate',
  'webhooks.deliveries.stats.ofAttempts': '{{count}} of {{total}} attempts',
  'webhooks.deliveries.stats.failedHint': 'Answered 4xx or 5xx, or never answered',
  'webhooks.deliveries.stats.blockedHint': 'Refused before leaving the platform',
  'webhooks.deliveries.percentOfAttempts': '{{percent}}% of attempts',
  'webhooks.deliveries.detail.title': 'Delivery details',
  'webhooks.deliveries.detail.overview': 'Overview',
  'webhooks.deliveries.detail.identifiers': 'Identifiers',
  'webhooks.deliveries.detail.subscription': 'Subscription',
  'webhooks.deliveries.detail.tenant': 'Tenant',
  'webhooks.deliveries.detail.endpoint': 'Endpoint',
  'webhooks.deliveries.detail.eventType': 'Event type',
  'webhooks.deliveries.detail.result': 'Result',
  'webhooks.deliveries.detail.attempt': 'Attempt',
  'webhooks.deliveries.detail.scheduledAt': 'Scheduled at',
  'webhooks.deliveries.detail.attemptedAt': 'Attempted at',
  'webhooks.deliveries.detail.latency': 'Latency',
  'webhooks.deliveries.detail.deliveryId': 'Delivery ID',
  'webhooks.deliveries.detail.resultId': 'Result ID',
  'webhooks.deliveries.detail.noResponseBody': 'The admin projection carries no response body. The status code and latency above are everything the platform recorded for this attempt.',
  'webhooks.deliveries.detail.readOnly': 'Replay and delete exist in the webhooks service but only on tenant-scoped routes, so this console cannot offer them yet. Read-only for now.',
  'webhooks.deliveries.unknownEndpoint': 'Not in the loaded subscriptions',
  'webhooks.deliveries.openDetail': 'Open delivery details',
  'webhooks.deliveries.maxRows': 'maximum {{limit}} rows',
  'webhooks.deliveries.viewSubscription': 'Open the subscription',
  'billing.invoices.eyebrow': 'Billing',
  'billing.invoices.search': 'Search invoice number or organisation…',
  'billing.invoices.columns.issue': 'Issue date',
  'billing.invoices.columns.totalInclVat': 'Total (incl. VAT)',
  'billing.invoices.showingCount': 'Showing {{shown}} of {{total}} invoices',
  'billing.invoices.showingNewest': 'Showing the newest {{limit}} invoices',
  'billing.invoices.actions.download': 'Download PDF',
  'billing.invoices.actions.validate': 'Validate invoice',
  'billing.invoices.actions.regenerate': 'Regenerate PDF',
  'billing.invoices.toast.exported': 'Exported {{count}} invoices.',
  'billing.invoices.toast.exportedTitle': 'Export ready',
  'billing.invoices.range.issued': 'Issue date',
  'billing.invoices.period': '{{from}} to {{to}}',
  'dashboard.range.days': '{{days}}d',
  'dashboard.range.window': '{{from}} – {{to}}',
  'dashboard.delta.newInDays': 'new in the last {{days}} days',
  'dashboard.delta.previousDays': 'vs previous {{days}} days',
  'dashboard.error.count': '{{count}} data feeds failed to refresh',
  'dashboard.error.countOne': '1 data feed failed to refresh',
  'dashboard.error.details': 'View details',
  'dashboard.links.tenants': 'View organisations',
  'dashboard.links.users': 'View users',
  'dashboard.links.conversations': 'View conversations',
  'dashboard.links.numbers': 'Manage numbers',
  'dashboard.links.ledger': 'View ledger',
  'dashboard.links.usage': 'View full report',
  'dashboard.metrics.billedUsageRange': 'Billed usage ({{days}}d)',
  'dashboard.wallets.currencies': '{{count}} currencies',
  'dashboard.wallets.link': 'View billing overview',
  'dashboard.legend.value': '{{count}} · {{share}}%',
  'notFound.dashboardAction': 'Back to dashboard',
  'notFound.suggestionsTitle': 'You might be looking for',
  'notFound.suggestionsHint': 'The closest matches in the admin console.',
  'notFound.help.title': 'Still cannot find what you need?',
  'notFound.help.body': 'Search for it, or ask another platform administrator.',
  'notFound.help.searchTitle': 'Search the console',
  'notFound.help.searchBody': 'Open the command palette and search every page.',
  'notFound.help.statusTitle': 'Check system health',
  'notFound.help.statusBody': 'See whether a service behind this page is degraded.',
  'notFound.help.statusAction': 'Open system health',
  'notFound.occurred': 'Occurred {{when}}',
  'templates.columns.template': 'Template',
  'templates.columns.metaStatus': 'Meta status',
  'templates.columns.updatedAt': 'Updated at',
  'templates.columns.importedAt': 'Imported at',
  'templates.metaStatusNone': 'Never synced',
  'templates.devFallbackNotice.title': 'Why some templates show dev auto-approved',
  'templates.devFallbackNotice.body': 'A non-production fallback approves a template locally so development can send without waiting for Meta. Meta never reviewed those templates, so they may not reach a real recipient until it does.',
  'templates.actions.view': 'View template',
  'templates.actions.copyCode': 'Copy the template code',
  'templates.view.eyebrow': 'Template',
  'templates.view.lastImported': 'Imported {{when}}',
  'billing.plans.eyebrow': 'Billing',
  'billing.plans.search': 'Search plans by name or code…',
  'billing.plans.columns.planCode': 'Plan code',
  'billing.plans.columns.planName': 'Plan name',
  'billing.plans.copyCode': 'Copy the plan code',
  'billing.plans.metric.activeSubs': 'Active subscriptions',
  'billing.plans.metric.activeSubsHint': 'Subscriptions currently billing across every tenant',
  'billing.plans.metric.arpt': 'Average revenue per tenant',
  'billing.plans.metric.arptHint': 'Monthly recurring revenue divided by active subscriptions',
  'billing.plans.metric.pastDue': 'Past due (unpaid)',
  'billing.plans.metric.pastDueHint': 'Plan value the renewal worker could not charge',
  'billing.plans.metric.noPastDue': 'Every renewal charged',
  'billing.plans.showingCount': 'Showing {{shown}} of {{total}} plans',
  'billing.plans.filters.status': 'Status',
  'billing.plans.filters.anyStatus': 'Any status',
  'billing.plans.filters.price': 'Price',
  'billing.plans.filters.anyPrice': 'Any price',
  'billing.plans.strandedBanner.title': '{{count}} archived plan(s) still have active subscribers',
  'billing.plans.strandedBanner.body': 'An archived plan keeps billing the tenants already on it. Move them to a current plan before the catalogue is tidied.',
  'billing.plans.subsFootnote.title': 'Manage subscriptions',
  'billing.plans.subsFootnote.body': 'View and manage individual tenant subscriptions, billing cycles and payments.',
  'media.metrics.acrossTenants': 'Across {{count}} tenants',
  'media.metrics.extractedShare': '{{pct}}% of stored files',
  'media.metrics.last24hHint': 'In the last 24 hours',
  'media.columns.share': 'Share of storage',
  'media.actions.openTenant': 'Open tenant',
  'media.actions.viewKb': 'Knowledge base',
  'logs.query.section.source': 'Source',
  'logs.query.section.sourceHint': 'Which family, service and environment to read.',
  'logs.query.section.correlate': 'Correlate',
  'logs.query.section.correlateHint': 'Follow one request or one tenant across every service.',
  'logs.query.section.window': 'Window and search',
  'logs.query.section.windowHint': 'Narrow to a period, then to the text in the row.',
  'logs.query.resultsTitle': 'Results',
  'logs.query.appliedLabel': 'Applied query',
  'billing.skuRates.eyebrow': 'Billing',
  'billing.skuRates.search': 'Search by SKU code or name…',
  'billing.skuRates.columns.skuAndName': 'SKU code & name',
  'billing.skuRates.columns.currentRate': 'Current rate',
  'billing.skuRates.minorUnits': '{{count}} minor units',
  'billing.skuRates.showingCount': 'Showing {{shown}} of {{total}} SKU rates',
  'billing.skuRates.perPair': 'Rates are held per SKU and currency. Changing one inserts a new version effective today and retires the current one, and every tenant is charged the new rate from that moment.',
  'billing.skuRates.stat.activeSkus': 'Active SKUs',
  'billing.skuRates.stat.activeSkusHint': 'Distinct SKU codes with a live rate',
  'billing.skuRates.stat.historical': 'Historical versions',
  'billing.skuRates.stat.historicalHint': 'Retired versions, kept so old charges still reconcile',
  'billing.skuRates.stat.currencies': 'Currencies',
  'billing.skuRates.stat.currenciesHint': 'Currencies the price book is quoted in',
  'billing.skuRates.stat.lastChange': 'Last rate change',
  'billing.skuRates.stat.lastChangeHint': 'Newest effective date in the price book',
  'billing.skuRates.stat.never': 'No rates yet',
  'audit.summary.transferred': 'Conversation transferred',
  'audit.summary.transferredWithReason': 'Conversation transferred ({{reason}})',
  'audit.summary.optIn': 'Opted in on {{channel}}',
  'audit.summary.optInVia': 'Opted in on {{channel}} via {{source}}',
  'audit.summary.optOut': 'Opted out on {{channel}}',
  'audit.summary.optOutVia': 'Opted out on {{channel}} via {{source}}',
  'audit.summary.deliveryBlocked': '{{event}} attempt {{attempt}} blocked: {{reason}}',
  'audit.summary.delivery': '{{event}} attempt {{attempt}} returned HTTP {{status}} in {{ms}} ms',
  'audit.summary.deliveryNoStatus': '{{event}} attempt {{attempt}} has no recorded response',
  'audit.summary.flowRun': 'Flow run',
  'audit.summary.flowRunFor': 'Flow run for {{msisdn}}',
  'audit.summary.ledger': '{{sku}} {{amount}}',
  'audit.summary.ledgerWithDescription': '{{sku}} {{amount}}: {{description}}',
  'audit.summary.ledgerAdjustment': 'Adjustment',
  'audit.stats.events': 'Events in range',
  'audit.stats.eventsHint': 'Merged from {{count}} audit feeds',
  'audit.stats.organisations': 'Organisations touched',
  'audit.stats.organisationsHint': 'Distinct tenants in this window',
  'audit.stats.sources': 'Sources reporting',
  'audit.stats.sourcesHint': '{{ok}} of {{total}} feeds answered',
  'audit.stats.newest': 'Newest event',
  'audit.stats.newestNone': 'Nothing in this window',
  'media.kb.eyebrow': 'Media',
  'media.kb.provider.lastChecked': 'Checked {{when}}',
  'media.kb.provider.healthy': 'Configured',
  'media.kb.provider.notConfigured': 'Not configured',
  'gateways.overviewBadge': 'Overview',
  'gateways.health.title': 'WhatsApp channel health',
  'gateways.health.viewAll': 'View service health',
  'gateways.health.unknown': 'Not reported',
  'gateways.health.unknownHint': 'The health endpoint did not report on this service.',
  'gateways.metrics.acrossTenants': 'Across {{count}} tenants',
  'gateways.metrics.shareOfAttached': '{{pct}}% of attached',
  'gateways.cards.numbers.openNumbers': 'Open numbers',
  'gateways.preview.title': 'Numbers needing attention',
  'gateways.preview.description': 'The lowest-rated numbers across every tenant, worst first.',
  'gateways.preview.viewAll': 'All numbers',
  'gateways.preview.emptyTitle': 'Nothing rated below GREEN',
  'gateways.preview.empty': 'Every attached number Meta has rated is GREEN. Unrated numbers are listed on the numbers page.',
  'billing.subscriptions.eyebrow': 'Billing',
  'billing.subscriptions.search': 'Search organisations, plans or status…',
  'billing.subscriptions.columns.organisation': 'Organisation',
  'billing.subscriptions.columns.mrr': 'MRR',
  'billing.subscriptions.columns.nextRenewal': 'Next renewal',
  'billing.subscriptions.showingCount': 'Showing {{shown}} of {{total}} subscriptions',
  'billing.subscriptions.metric.renewals': 'Renewals this month',
  'billing.subscriptions.metric.renewalsHint': 'Cycles ending before the month is out',
  'billing.subscriptions.metric.cancelled': 'Cancelled',
  'billing.subscriptions.metric.cancelledHint': 'Cancelled or carrying a cancellation date',
  'billing.subscriptions.metric.activeSubsHint': 'of {{total}} on the roster',
  'billing.subscriptions.action.viewOrg': 'View the organisation',
  'billing.subscriptions.action.viewLedger': 'View this organisation in the ledger',
  'billing.subscriptions.action.viewInvoices': 'View this organisation\'s invoices',
  'apiMonitoring.tableTitle': 'Busiest endpoints',
  'apiMonitoring.tableDescription': 'Grouped by method and templated path, ordered by request count over the whole window.',
  'apiMonitoring.totalLabel': '{{shown}} of {{total}} endpoints in this window',
  'apiMonitoring.kpi.p95Hint': '95th percentile over the sample',
  'apiMonitoring.kpi.requestsHint': 'Requests recorded in this window',
  'apiMonitoring.searchPlaceholder': 'Filter by path…',
  'apiMonitoring.rowHint': 'Open the log query for this path',
  'queues.metrics.queuesHint': 'Reported by the broker',
  'queues.metrics.readyHint': 'Messages waiting to be read',
  'queues.metrics.unackedHint': 'Delivered but not yet acknowledged',
  'queues.tableTitle': 'Queues',
  'gateways.numbers.columns.number': 'Number',
  'gateways.numbers.actions.openTenant': 'Open tenant',
  'gateways.numbers.actions.copyMsisdn': 'Copy the number',
  'gateways.numbers.filters.quality': 'Quality',
  'apiMonitoring.shareOfSample': '{{percent}}% of the sample',
  'gateways.wba.metrics.attached': 'Reached a business account',
  'gateways.wba.metrics.attachedHint': 'Numbers Meta has linked to a WABA',
  'gateways.wba.metrics.stalled': 'Never registered',
  'gateways.wba.metrics.stalledHint': '{{pct}}% of those attached',
  'gateways.wba.metrics.registered': 'Registered',
  'gateways.wba.metrics.registeredHint': 'Able to send',
  'gateways.wba.goToTenants': 'Go to Tenants to impersonate a signup',
  'billing.usage.eyebrow': 'Billing',
  'billing.usage.search': 'Search organisations…',
  'billing.usage.columns.totalDebit': 'Total debit',
  'billing.usage.columns.totalCredit': 'Total credit',
  'billing.usage.columns.net': 'Net',
  'billing.usage.showingCount': 'Showing {{shown}} of {{total}} tenant rollups',
  'billing.usage.stat.debit': 'Total debit',
  'billing.usage.stat.debitHint': 'Charged to tenant wallets in this window',
  'billing.usage.stat.credit': 'Total credit',
  'billing.usage.stat.creditHint': 'Top-ups, refunds and manual grants',
  'billing.usage.stat.tenants': 'Tenants active',
  'billing.usage.stat.tenantsHint': 'Organisations with at least one entry',
  'billing.usage.stat.events': 'Ledger events',
  'billing.usage.stat.eventsHint': 'Entries in the sample behind these totals',
  'billing.usage.sampleCovers': 'Sample covers {{from}} to {{to}}',
  'billing.usage.topTenants': 'Top tenants by debit',
  'billing.usage.topSkus': 'Top SKUs by debit',
  'billing.usage.topSkusHint': 'Showing the {{shown}} dearest of {{total}} SKUs in the sample',
  'billing.usage.totalsRow': '{{currency}}: {{events}} events · {{debit}} debit · {{credit}} credit · {{net}} net',
  'billing.usage.totals': 'Totals',
  'billing.usage.noSkus': 'No SKU debits in this window',
  'system.health.stats.healthyHint': 'Answering /health/ready',
  'system.health.stats.degradedHint': 'Answering, but not ready',
  'system.health.stats.downHint': 'No answer at all',
  'system.health.stats.totalHint': 'Services in the probe list',
  'system.health.servicesTitle': 'Services',
  'system.alerts.section.compose': 'Compose the alert',
  'system.alerts.section.composeHint': 'What the person who receives this page will read first.',
  'system.alerts.section.routing': 'Where it goes',
  'system.alerts.section.routingHint': 'Only the enabled channels below will fire.',
  'system.backups.section.commands': 'Commands',
  'system.backups.section.commandsHint': 'Run these on the host that holds the volumes. Each one copies to the clipboard.',
  'settings.integrations.stats.providers': 'Providers',
  'settings.integrations.stats.providersHint': 'In the provider catalogue',
  'settings.integrations.stats.enabled': 'Enabled',
  'settings.integrations.stats.enabledHint': 'Credentials supplied and switched on',
  'settings.integrations.stats.verified': 'Verified',
  'settings.integrations.stats.verifiedHint': 'Enabled and their last test passed',
  'settings.integrations.stats.readiness': 'Tenant readiness',
  'billing.itn.eyebrow': 'Billing',
  'billing.itn.autoRefresh': 'Auto-refresh: {{seconds}}s',
  'billing.itn.refreshNow': 'Refresh now',
  'billing.itn.signature.enforcedTitle': 'Signature verification is enforced',
  'billing.itn.signature.enforcedBody': 'Every ITN callback must carry a valid provider signature. A request with a missing or mismatched signature is rejected, and a gateway with no secret configured answers 503 rather than accepting the payment.',
  'billing.itn.signature.permissiveTitle': 'Signature verification is permissive outside production',
  'billing.itn.signature.permissiveBody': 'In this environment an unsigned ITN callback is accepted and only logged. Treat anything observed here as unverified: the fail-closed behaviour applies in production only.',
  'billing.itn.statusLabel': 'Status',
  'billing.itn.lastTestLabel': 'Last test',
  'billing.itn.updatedLabel': 'Updated',
  'billing.itn.updatedByLabel': 'Updated by',
  'billing.itn.notesLabel': 'Notes',
  'billing.itn.noTest': 'Not yet tested',
  'settings.flags.stats.total': 'Flags',
  'settings.flags.stats.totalHint': 'In the registry',
  'settings.flags.stats.on': 'On by default',
  'settings.flags.stats.onHint': 'For every scope without an override',
  'settings.flags.stats.overridden': 'With overrides',
  'settings.flags.stats.overriddenHint': 'Ignoring the default for at least one scope',
  'settings.flags.emptyHint': 'Create the first flag to gate a change behind a switch you can flip without a deploy.',
  'settings.hub.stats.integrations.label': 'Integrations',
  'settings.hub.stats.integrations.hint': '{{count}} connected',
  'settings.hub.stats.integrations.link': 'View integrations',
  'settings.hub.stats.flags.label': 'Feature flags',
  'settings.hub.stats.flags.hint': '{{count}} on by default',
  'settings.hub.stats.flags.link': 'View feature flags',
  'settings.hub.stats.apiKeys.label': 'API keys',
  'settings.hub.stats.apiKeys.hint': '{{count}} revoked',
  'settings.hub.stats.apiKeys.link': 'View API keys',
  'settings.hub.stats.twoFactor.label': 'Two-factor',
  'settings.hub.stats.twoFactor.on': 'On',
  'settings.hub.stats.twoFactor.off': 'Off',
  'settings.hub.stats.twoFactor.hintOn': 'Your sign-in is protected',
  'settings.hub.stats.twoFactor.hintOff': 'Enrol an authenticator app',
  'settings.hub.stats.twoFactor.link': 'Open security',
  'settings.hub.section.title': 'Platform settings',
  'settings.hub.integrations.title': 'Integrations',
  'settings.hub.integrations.viewAll': 'View all integrations',
  'settings.hub.integrations.empty': 'No provider is configured yet.',
  'settings.hub.integrations.error': 'Could not load integrations.',
  'settings.hub.integrations.tested': 'Tested {{when}}',
  'settings.hub.integrations.neverTested': 'Not tested yet',
  'settings.hub.integrations.disabled': 'Disabled',
  'gateways.wba.proxyNote': 'The table below is built from the numbers inventory, so it carries no signup status, no current step and no last-updated time. Those arrive with the signup feed.',
  'gateways.wba.metrics.tenants': 'Customers waiting',
  'gateways.wba.metrics.tenantsHint': 'With at least one attempt that has not connected.',
  'gateways.wba.interim.searchPlaceholder': 'Filter by tenant, number or business account…',
  'gateways.wba.columns.numberStatus': 'Number status',
  'gateways.wba.interim.totalLabel': '{{shown}} of {{total}}',
  'system.health.banner.title': 'Not every service is answering',
  'system.health.banner.body': '{{down}} down and {{degraded}} degraded, out of {{total}} probed services. Investigate before it becomes an incident.',
  'system.health.banner.action': 'Open alerts',
  'system.health.footer.interval': 'Health probes run every {{seconds}} seconds',
  'system.health.footer.live': 'Showing live data',
  'system.health.footer.stale': 'Showing the last probe that answered',
  'system.health.sparkLabel': 'Probe time for each poll since this page was opened',
  'settings.profile.identity.subtitle': 'Your platform account and access.',
  'settings.profile.identity.displayName': 'Display name',
  'settings.profile.details.subtitle': 'Update your display name and formatting preferences.',
  'settings.profile.form.unsaved': 'Unsaved changes are lost if you leave this page.',
  'settings.profile.form.saved': 'All changes saved',
  'settings.profile.form.reset': 'Reset',
  'settings.profile.form.save': 'Save changes',
  'settings.profile.notifications.title': 'Notification preferences',
  'settings.profile.notifications.subtitle': 'Choose what the platform emails you about.',
  'settings.profile.notifications.columns.channel': 'Channel',
  'settings.profile.notifications.columns.when': 'When you are notified',
  'settings.profile.notifications.columns.email': 'Email',
  'settings.profile.notifications.sentTo': 'Sent to {{email}}.',
  'settings.profile.notifications.noDispatcher': 'Preferences are stored against your account. No service reads them yet, so turning one on does not start an email today.',
  'settings.profile.notifications.loadError': 'Could not load your notification preferences.',
  'settings.profile.notifications.saveFailed': 'Could not save that preference',
  'settings.profile.notifications.security.label': 'Security and account',
  'settings.profile.notifications.security.hint': 'Sign-in alerts, password changes and API key activity.',
  'settings.profile.notifications.platform.label': 'Platform updates',
  'settings.profile.notifications.platform.hint': 'Releases, maintenance windows and announcements.',
  'settings.profile.notifications.audit.label': 'Audit and compliance',
  'settings.profile.notifications.audit.hint': 'Audit log exports, data access requests and compliance alerts.',
  'settings.profile.notifications.billing.label': 'Billing and usage',
  'settings.profile.notifications.billing.hint': 'Invoice notices, payment failures and usage threshold alerts.',
  'settings.profile.locale.label': 'Locale (formatting)',
  'settings.profile.locale.hint': 'Sets how dates, times, numbers and currency read. The console itself stays in English.',
  'system.alerts.banner': 'Every alert goes by email, and critical ones by WhatsApp, to the recipients set below. PagerDuty, Slack and Linear are optional extra channels, configured as integrations.',
  'system.alerts.preview.title': 'Preview (dry run)',
  'system.alerts.preview.note': 'Rendered in the browser from the form beside it. Nothing is sent until you dispatch.',
  'system.alerts.preview.pagerdutyTab': 'PagerDuty (JSON)',
  'system.alerts.preview.linearTab': 'Linear (JSON)',
  'system.alerts.preview.slackHint': 'The message text posted to the webhook, including the channel override when the integration sets one.',
  'system.alerts.preview.pagerdutyHint': 'Posted to the Events v2 API as event_action trigger, which opens an incident a person has to close.',
  'system.alerts.preview.linearHint': 'Opened as an issue in the configured team, with the severity as the priority.',
  'system.alerts.preview.noSummary': '(no summary yet)',
  'system.alerts.history.title': 'Sent from this page',
  'system.alerts.history.hint': 'This browser tab only. Dispatches are not stored, so this list starts empty on every visit.',
  'system.alerts.history.empty': 'Nothing sent yet',
  'system.alerts.history.emptyHint': 'Dispatch an alert or send a test page and the per-channel outcome appears here.',
  'system.alerts.history.testPage': 'test page',
  'system.alerts.rules.title': 'Alert rules and silences',
  'system.alerts.rules.body': 'Rules that fire on their own, and the silences that mute them, live in Prometheus and Alertmanager. The console has no read path to either yet, so this page can send an alert but cannot show you what is already firing.',
  'settings.security.password.subtitle': 'Use a strong password you do not use anywhere else.',
  'settings.security.twoFactor.subtitle': 'A second factor at sign-in, from an authenticator app.',
  'settings.security.safeguards.title': 'Account safeguards',
  'settings.security.safeguards.subtitle': 'What the identity service enforces on this account.',
  'settings.security.safeguards.password.title': 'Password requirements',
  'settings.security.safeguards.password.hint': 'At least 12 characters, with no rule about capitals, numbers or symbols. The most common leaked passwords are refused. Existing passwords keep working until they are next changed.',
  'settings.security.safeguards.password.state': 'Enforced',
  'settings.security.safeguards.twoFactor.title': 'Two-factor authentication',
  'settings.security.safeguards.twoFactor.hintOn': 'A code from your authenticator is required at every sign-in.',
  'settings.security.safeguards.twoFactor.hintOff': 'Not enrolled. A leaked password is the whole attack surface for this account.',
  'settings.security.safeguards.recovery.title': 'Recovery codes',
  'settings.security.safeguards.recovery.hintOn': '{{count}} single-use codes remain for signing in without your authenticator.',
  'settings.security.safeguards.recovery.hintOff': 'Issued when you turn two-factor on.',
  'settings.security.safeguards.recovery.state': '{{count}} left',
  'settings.security.credentials.question': 'Need to manage programmatic access?',
  'system.backups.procedure.title': 'Restore procedure',
  'system.backups.procedure.hint': 'The order matters: a restore that runs while the services are still writing leaves the ledger inconsistent.',
  'system.backups.procedure.step1': 'Stop the writers',
  'system.backups.procedure.step1Detail': 'Take the API and the workers down so nothing writes while the databases are replaced.',
  'system.backups.procedure.step2': 'Restore the snapshot',
  'system.backups.procedure.step2Detail': 'Point restore.sh at the snapshot directory you want. Add --check first to see what it would replace.',
  'system.backups.procedure.step2Command': 'bash scripts/dr/restore.sh backups/<timestamp>',
  'system.backups.procedure.step3': 'Reconcile the ledger',
  'system.backups.procedure.step3Detail': 'Enforces the money invariants across the restored databases and exits non-zero on drift.',
  'system.backups.procedure.step3Command': 'bash scripts/dr/reconcile-ledger.sh',
  'system.backups.procedure.step4': 'Start the services and verify',
  'system.backups.procedure.step4Detail': 'Bring the stack back up, then run the drill against the newest snapshot to prove the restore path still works.',
  'system.backups.procedure.step4Command': 'bash scripts/dr/drill.sh --reuse-latest',
  'system.backups.destructive.title': 'A restore replaces the current data',
  'system.backups.destructive.body': 'Every reach_ database is dropped and recreated from the snapshot. Confirm the snapshot is the one you want, and tell whoever is on call, before you start.',
  'system.backups.history.title': 'Snapshot history',
  'system.backups.history.empty': 'No snapshots yet',
  'system.backups.history.emptyHint': 'The store is configured and holds no completed snapshot. backup.sh writes a manifest beside each snapshot and uploads it last, so a prefix without one is a half-finished upload rather than a backup.',
  'system.backups.commandsIntro': 'Run these on the host that holds the volumes. They use the environment configuration that is already there.',
  'system.backups.takeSnapshotHint': 'Dumps every reach_ database into backups/<timestamp> with a manifest beside it.',
  'settings.appearance.density.comfortableHint': 'More open',
  'settings.appearance.density.cozyHint': 'Balanced',
  'settings.appearance.density.compactHint': 'More content',
  'settings.appearance.textScale.smallHint': '90%',
  'settings.appearance.textScale.defaultHint': '100%',
  'settings.appearance.textScale.largeHint': '110%',
  'settings.appearance.motion.autoHint': 'Follow system',
  'settings.appearance.motion.alwaysHint': 'Reduce motion',
  'settings.appearance.motion.neverHint': 'Enable all motion',
  'settings.appearance.sidebar.iconOnlyHint': 'More workspace',
  'settings.appearance.sidebar.fullHint': 'Labels visible',
  'settings.appearance.accent.livePreview': 'Live preview',
  'settings.appearance.accent.preview': 'Preview',
  'settings.appearance.autosaved': 'Changes are saved as you make them.',
  'settings.apiKeys.kpi.share': '{{percent}}% of all keys',
  'settings.apiKeys.select.aria': 'Select {{name}}',
  'settings.apiKeys.select.all': 'Select every key on this page',
  'settings.apiKeys.selection.count': '{{count}} selected',
  'settings.apiKeys.selection.clear': 'Clear selection',
  'settings.apiKeys.selection.revoke': 'Revoke {{count}} keys',
  'settings.apiKeys.selection.revokeTitle': 'Revoke {{count}} API keys?',
  'settings.apiKeys.selection.revokeBody': 'Revoke these {{count}} keys? Every integration signing with one stops working immediately, across {{orgs}} organisations. This cannot be undone.',
  'settings.apiKeys.selection.revokedToast': '{{count}} keys revoked.',
  'settings.apiKeys.selection.partialToast': '{{done}} of {{count}} keys revoked. The rest failed and are still active.',
  'settings.apiKeys.footnote.rpm': 'RPM (configured) is the limit stored against the key. The gateway does not throttle per key yet, so nothing enforces it.',
  'settings.apiKeys.export.filename': 'api-keys',
  'settings.apiKeys.toast.exported': '{{count}} keys exported.',
  'settings.apiKeys.toast.exportedTitle': 'Export ready',
  'status.connected': 'Connected',
  'system.hub.stats.healthy': 'Healthy services',
  'system.hub.stats.down': 'Not answering',
  'system.hub.stats.ofServices': 'of {{total}} probed',
  'system.hub.stats.downHint': '{{degraded}} answering but not ready',
  'system.hub.stats.services': 'Services probed',
  'system.hub.stats.servicesHint': 'Every service behind the gateway',
  'system.hub.stats.slowestProbe': 'Slowest probe',
  'system.hub.stats.ms': '{{ms}} ms',
  'system.hub.stats.acrossQueues': 'across {{count}} queues',
  'system.hub.stats.unackedHint': '{{count}} messages unacknowledged',
  'system.hub.noChannels': 'No channel',
  'system.hub.channelsLive': '{{count}} live',
  'system.hub.queuesStarved': '{{count}} without a consumer',
  'system.hub.queuesOk': '{{count}} queues',
  'system.hub.probedAt': 'Probed {{when}}',
  'system.hub.health.unknownRow': 'Service health could not be read',
  'system.hub.health.degradedRow': '{{count}} services need attention',
  'system.hub.health.okRow': 'All systems operational',
  'system.hub.alerts.unknownRow': 'Channel wiring could not be read',
  'system.hub.alerts.noneRow': 'No channel is wired, so nothing would be delivered',
  'system.hub.alerts.liveRow': '{{count}} channels would receive an alert',
  'system.hub.backups.manual': 'Run on the host',
  'system.hub.backups.row': 'Backup, restore and drill commands',
  'system.hub.queues.description': 'Live queue depth and consumer counts from RabbitMQ.',
  'system.hub.queues.row': '{{ready}} ready across {{count}} queues',
  'system.hub.queues.unknownRow': 'The broker could not be reached',
  'system.hub.snapshot.title': 'Health snapshot',
  'system.hub.snapshot.probeMs': 'Probe (ms)',
  'system.hub.snapshot.viewAll': 'View full health',
  'system.hub.snapshot.unavailable': 'The probe did not answer, so there is nothing to show here. That is a failure to ask, not a healthy platform.',
  'system.hub.channels.title': 'Alert channels',
  'system.hub.channels.hint': 'An alert is delivered only to the integration rows that are enabled here.',
  'system.hub.channels.manage': 'Manage channels',
  'system.hub.backupStatus.title': 'Backup status',
  'system.hub.backupStatus.manage': 'Manage backups',
  'system.hub.queueOverview.title': 'Queue overview',
  'system.hub.queueOverview.viewAll': 'View all queues',
  'system.hub.queueOverview.unavailable': 'The RabbitMQ management API did not answer, so queue depth is unknown.',
  'system.hub.footer.updated': 'Last updated {{when}}',
  'system.hub.footer.refresh': 'Auto-refresh: every {{seconds}} seconds',
  'settings.flags.kpi.all': 'All flags',
  'settings.flags.kpi.on': 'ON by default',
  'settings.flags.kpi.off': 'OFF by default',
  'settings.flags.kpi.archived': 'Archived',
  'settings.flags.kpi.overrides': 'With overrides',
  'settings.flags.searchPlaceholder': 'Search flags by key, description or owner…',
  'settings.flags.overrides.title': 'Overrides ({{count}})',
  'settings.flags.overrides.updated': 'Updated {{when}}',
  'settings.flags.overrides.updatedBy': 'Updated {{when}} by {{who}}',
  'settings.flags.overrides.remove': 'Remove this override',
  'settings.flags.overrides.removeTitle': 'Remove this override?',
  'settings.flags.overrides.removeBody': 'Remove the {{scope}} override on {{key}}? That scope falls back to the flag default, which is currently {{state}}.',
  'settings.flags.overrides.toast.removed': 'Override removed.',
  'settings.flags.overrides.toast.removeFailed': 'Could not remove the override',
  'settings.flags.emptyMatch': 'No flags match',
  'settings.flags.emptyMatchHint': 'Try a different search, or clear the filter above.',
  'settings.flags.showing': 'Showing {{from}} to {{to}} of {{total}} flags',
  'audit.detail.transferReason': 'Reason: {{reason}}',
  'audit.detail.optInSource': 'Source: {{source}}',
  'audit.detail.delivery': 'Attempt {{attempt}} · HTTP {{status}} · {{ms}} ms',
  'audit.detail.deliveryNoStatus': 'Attempt {{attempt}} · {{result}}, no HTTP response',
  'audit.detail.deliveryBlocked': 'Attempt {{attempt}} · refused: {{reason}}',
  'audit.detail.flowRun': '{{status}} · {{channel}}',
  'audit.detail.flowRunFor': '{{status}} · {{channel}} · {{msisdn}}',
  'audit.detail.ledger': '{{sku}} · {{amount}} {{direction}}',
  'audit.summary.deliveryEvent': 'Webhook {{event}}',
  'audit.summary.ledgerEntry': 'Ledger {{type}}',
  'audit.sourcesLegend': 'Sources',
  'audit.kpi.share': '{{percent}}% of the window',
  'audit.kpi.unavailable': 'Feed did not answer',
  'audit.kpi.windowTotal': '{{total}} in the window',
  'audit.detail.title': 'Audit event',
  'audit.detail.occurred': 'Occurred',
  'audit.detail.source': 'Source',
  'audit.detail.organisation': 'Organisation',
  'audit.detail.eventId': 'Event ID',
  'audit.detail.summary': 'Summary',
  'audit.detail.record': 'Record',
  'audit.detail.recordHint': 'The row exactly as the feed returned it. There is no separate audit-event read, so this is the whole of what the console knows.',
  'audit.detail.copyJson': 'Copy JSON',
  'audit.detail.viewTenant': 'Open this organisation',
  'audit.detail.previous': 'Previous event',
  'audit.detail.next': 'Next event',
  'audit.detail.open': 'Open this event',
  'audit.columns.detail': 'Detail',
  'audit.showingEvents': 'Showing {{count}} events',
  'audit.showingEventsCapped': 'Showing the newest {{count}} events in the window',
  'common.filtering': 'Filtering',
  'settings.integrations.neverTested': 'Never tested',
  'settings.integrations.orgOverridesOne': '{{count}} org override',
  'settings.integrations.orgOverridesOther': '{{count}} org overrides',
  'settings.integrations.noOrgOverrides': 'No org overrides',
  'settings.backup.service.title': 'What the service reports',
  'settings.backup.service.subtitle': 'Read from the settings service, so this page and System → Backups cannot drift.',
  'settings.backup.service.command': 'Snapshot command',
  'settings.backup.service.restore': 'Restore command',
  'settings.backup.service.loadError': 'Could not read the backup policy from the service.',
  'settings.backup.service.noHistory': 'The service records no snapshot history yet, so there is no last-run time to show. The manifest beside each dump is the record until it does.',
  'settings.backup.service.viewSystem': 'Open System → Backups',
  'filters.filtering': 'Filtering',
  'logs.stats.all': 'All logs',
  'logs.range.15m': '15m',
  'logs.range.1h': '1h',
  'logs.range.24h': '24h',
  'logs.range.7d': '7d',
  'logs.range.all': 'All time',
  'logs.filters.statusClass': 'Status class',
  'logs.filters.anyStatusClass': 'Any status',
  'logs.export.action': 'Export JSON',
  'logs.export.title': 'Export ready',
  'logs.export.done': 'Wrote {{count}} rows, with the query that produced them, to a JSON file.',
  'logs.detail.title': 'Log row',
  'logs.detail.open': 'Open this row',
  'logs.detail.tab.details': 'Details',
  'logs.detail.tab.payload': 'Payload',
  'logs.detail.tab.properties': 'Properties',
  'logs.detail.tab.exception': 'Exception',
  'logs.detail.family': 'Family',
  'logs.detail.host': 'Host',
  'logs.detail.occurred': 'Occurred',
  'logs.detail.noPayload': 'This row carries no payload document.',
  'logs.detail.noProperties': 'This row carries no structured properties.',
  'logs.detail.noException': 'No exception was recorded on this row.',
  'logs.detail.findCorrelated': 'Find every row with this correlation ID',
  'logs.footer.fresh': 'Data as of {{when}}',
  'logs.footer.notLoaded': 'Nothing loaded yet',
  'logs.footer.timezone': 'Times are shown in your own timezone; the exact moment is on every timestamp.',
  'logs.query.copyLink': 'Copy link',
  'logs.query.copiedLinkTitle': 'Link copied',
  'logs.query.copiedLink': 'The URL carries the whole query, so pasting it reproduces these results exactly.',
  'apiMonitoring.allServices': 'All services',
  'apiMonitoring.shareOfTotal': '{{percent}}% of requests',
  'apiMonitoring.kpi.errorRateHint': '4xx and 5xx together',
  'apiMonitoring.latencyHint': 'P50 {{p50}} ms · P99 {{p99}} ms · slowest {{max}} ms',
  'apiMonitoring.viewInLogQuery': 'View in log query',
  'apiMonitoring.chart.title': 'Request volume, errors and latency',
  'apiMonitoring.chart.errors': 'Errors (4xx and 5xx)',
  'apiMonitoring.chart.bucket': '{{bucket}} buckets',
  'apiMonitoring.chart.empty': 'Nothing to plot',
  'apiMonitoring.chart.emptyHint': 'No API requests were recorded in this window, so there is no shape to draw.',
  'apiMonitoring.about.title': 'What this is counting',
  'apiMonitoring.about.window': 'Time range',
  'apiMonitoring.about.bucket': 'Bucket interval',
  'apiMonitoring.about.bucketValue': '{{bucket}}',
  'apiMonitoring.about.source': 'Data source',
  'apiMonitoring.about.sourceValue': 'API log family, aggregated by the logs service',
  'apiMonitoring.about.sampled': 'Sampled',
  'apiMonitoring.about.sampledYes': 'Yes, the newest {{limit}} rows',
  'apiMonitoring.about.sampledNo': 'No, the window fit under the row cap',
  'apiMonitoring.columns.rank': '#',
  'apiMonitoring.columns.ok': '2xx',
  'apiMonitoring.columns.clientErr': '4xx',
  'apiMonitoring.columns.serverErr': '5xx',
  'apiMonitoring.columns.p95Ms': 'P95 ms',
  'apiMonitoring.export.title': 'Export ready',
  'apiMonitoring.export.done': 'Wrote {{count}} endpoint rows to a CSV file.',
  'apiMonitoring.footer.showing': 'Ordered by request count, busiest first.',
  'apiMonitoring.footer.rowHint': 'Open a row to see the log rows behind it.',
  'queues.metrics.queuesFiltered': 'Queues (filtered)',
  'queues.metrics.readyBacklogFiltered': 'Ready backlog (filtered)',
  'queues.metrics.unackedFiltered': 'Unacked (filtered)',
  'queues.metrics.platformTotal': 'platform total {{count}}',
  'queues.trendTooltip': 'Depth at each poll since this page was opened',
  'queues.detail.title': 'Queue',
  'queues.detail.readyTrend': 'Ready messages',
  'queues.detail.readyTrendHint': 'The last {{count}} polls this page has watched. The broker reports the current depth only, so the series starts when the page opens.',
  'queues.detail.starvedHint': 'This queue holds messages and has no consumer attached, so nothing is draining it.',
  'queues.detail.limits': 'Requeue takes messages one at a time and checks the destination exists before it removes anything, so a message it cannot deliver goes straight back. Depths on this page come from the broker\'s sampled statistics and can lag a move by a few seconds.',
  'users.truncatedTotal': 'Showing the newest {{returned}} of {{total}} accounts. Narrow the list with a search or a filter to reach the rest.',
  'queues.columns.rate': 'In / out per second',
  'queues.rateTooltip': 'Messages published to this queue and delivered from it, measured across the polls this page has watched.',
  'queues.detail.throughput': 'Throughput',
  'queues.detail.throughputHint': 'Rates are messages per second, measured across the {{polls}} polls this page has watched. The totals are cumulative since the broker last started, not since the page opened.',
  'queues.detail.publishRate': 'Published per second',
  'queues.detail.deliverRate': 'Delivered per second',
  'queues.detail.memory': 'Memory',
  'queues.detail.ttl': 'Message TTL',
  'queues.detail.idleSince': 'Idle since',
  'queues.rateTooltipBroker': 'Messages published to this queue and delivered from it, as the broker averages them. This page has not polled twice yet, so it cannot measure the rate itself.',
  'queues.detail.throughputHintBroker': 'Rates are the broker\'s own average, over a sample window it does not report. This page measures them itself once it has polled twice.',
  'queues.detail.published': 'Published in total',
  'queues.detail.acked': 'Acknowledged in total',
  'fileViewer.close': 'Close',
  'fileViewer.back': 'Back',
  'fileViewer.download': 'Download',
  'fileViewer.downloadOriginal': 'Download original',
  'fileViewer.print': 'Print',
  'fileViewer.share': 'Share',
  'fileViewer.copyLink': 'Copy link',
  'fileViewer.copyName': 'Copy file name',
  'fileViewer.copied': 'Copied',
  'fileViewer.copyFailed': 'Could not copy',
  'fileViewer.openExternally': 'Open externally',
  'fileViewer.refresh': 'Refresh',
  'fileViewer.refreshed': 'Reloaded. Nothing changed.',
  'fileViewer.refreshedNewer': 'A newer version replaced the one on screen.',
  'fileViewer.details': 'Details',
  'fileViewer.hideDetails': 'Hide details',
  'fileViewer.fullscreen': 'Fullscreen',
  'fileViewer.exitFullscreen': 'Exit fullscreen',
  'fileViewer.keyboardHelp': 'Keyboard shortcuts',
  'fileViewer.more': 'More',
  'fileViewer.previous': 'Previous file',
  'fileViewer.next': 'Next file',
  'fileViewer.position': '{index} of {count}',
  'fileViewer.local': 'Local',
  'fileViewer.remote': 'Remote',
  'fileViewer.readOnly': 'Read only',
  'fileViewer.retry': 'Retry',
  'fileViewer.cancel': 'Cancel',
  'fileViewer.loadedState': 'Loaded',
  'fileViewer.loadingState': 'Loading',
  'fileViewer.inspectingState': 'Inspecting source',
  'fileViewer.processingState': 'Processing',
  'fileViewer.errorState': 'Could not open',
  'fileViewer.progressOf': '{loaded} of {total}',
  'fileViewer.progressLoaded': '{loaded} loaded',
  'fileViewer.viewerTitle': 'File viewer',
  'fileViewer.untitled': 'Untitled file',
  'fileViewer.familyPdf': 'PDF',
  'fileViewer.familyImage': 'Image',
  'fileViewer.familyVideo': 'Video',
  'fileViewer.familyAudio': 'Audio',
  'fileViewer.familySpreadsheet': 'Spreadsheet',
  'fileViewer.familyPresentation': 'Presentation',
  'fileViewer.familyDocument': 'Document',
  'fileViewer.familyCode': 'Code',
  'fileViewer.familyText': 'Text',
  'fileViewer.familyArchive': 'Archive',
  'fileViewer.familyUnknown': 'File',
  'fileViewer.detailFilename': 'File name',
  'fileViewer.detailTitle': 'Title',
  'fileViewer.detailType': 'Type',
  'fileViewer.detailMime': 'MIME type',
  'fileViewer.detailSize': 'Size',
  'fileViewer.detailSource': 'Source',
  'fileViewer.detailHost': 'Host',
  'fileViewer.detailModified': 'Last modified',
  'fileViewer.detailEtag': 'ETag',
  'fileViewer.detailChecksum': 'Checksum (SHA-256)',
  'fileViewer.computeChecksum': 'Compute checksum',
  'fileViewer.computingChecksum': 'Computing…',
  'fileViewer.detailDimensions': 'Dimensions',
  'fileViewer.detailAspect': 'Aspect ratio',
  'fileViewer.detailDuration': 'Duration',
  'fileViewer.detailResolution': 'Resolution',
  'fileViewer.detailLines': 'Lines',
  'fileViewer.detailEncoding': 'Encoding',
  'fileViewer.detailLanguage': 'Language',
  'fileViewer.detailValidation': 'Validation',
  'fileViewer.detailFrames': 'Frames',
  'fileViewer.detailAlpha': 'Transparency',
  'fileViewer.detailRenderer': 'Rendered by',
  'fileViewer.rendererNative': 'This browser',
  'fileViewer.rendererConverted': 'Converted preview',
  'fileViewer.rendererNone': 'Not rendered',
  'fileViewer.yes': 'Yes',
  'fileViewer.no': 'No',
  'fileViewer.unknownValue': 'Unknown',
  'fileViewer.errorNetwork': 'The file could not be fetched. Check the connection and try again.',
  'fileViewer.errorCors': 'The file’s host does not allow this page to read it. Open it externally or download it instead.',
  'fileViewer.errorHttp': 'The server answered {status}.',
  'fileViewer.errorAuth': 'You are not allowed to read this file, or the sign-in has expired.',
  'fileViewer.errorNotFound': 'The file was not found. It may have been deleted or the link may be wrong.',
  'fileViewer.errorExpired': 'This link has expired. Ask for a fresh one.',
  'fileViewer.errorTooLarge': 'This file is {size}, above the {limit} this viewer will load. Download it instead.',
  'fileViewer.errorUnsupported': 'This browser cannot show this kind of file.',
  'fileViewer.errorDecode': 'The file could not be decoded. It may be corrupt.',
  'fileViewer.errorAborted': 'Loading was cancelled.',
  'fileViewer.mismatchWarning': 'The file’s contents do not match its declared type. It is shown as what the bytes say it is.',
  'fileViewer.unsupportedTitle': 'No preview for this file',
  'fileViewer.unsupportedSpreadsheet': 'Spreadsheets cannot be drawn by a browser on its own. Download the original to open it in a spreadsheet application.',
  'fileViewer.unsupportedPresentation': 'Presentations cannot be drawn by a browser on its own. Download the original to open it in a presentation application.',
  'fileViewer.unsupportedDocument': 'Word documents cannot be drawn by a browser on its own. Download the original to open it in a word processor.',
  'fileViewer.unsupportedArchive': 'Archives are not opened here. Download it to extract the files.',
  'fileViewer.unsupportedUnknown': 'This kind of file has no preview. The original is untouched and can be downloaded.',
  'fileViewer.unsupportedPdfInline': 'This browser has no built-in PDF viewer. Download the file or open it externally.',
  'fileViewer.unsupportedMediaCodec': 'This browser cannot decode this media. Download it or open it externally.',
  'fileViewer.unsupportedHls': 'This browser cannot play HLS streams without a player library, and none is installed here.',
  'fileViewer.convertedPreview': 'Converted text preview',
  'fileViewer.convertedPreviewHint': 'Text extracted on the server. Layout, images and formatting are not shown; the original is unchanged.',
  'fileViewer.loadConvertedPreview': 'Show text preview',
  'fileViewer.convertedPreviewFailed': 'The text preview could not be loaded.',
  'fileViewer.hexPreview': 'Hex preview',
  'fileViewer.hexPreviewHint': 'The first bytes of the file, for telling what it really is.',
  'fileViewer.showHexPreview': 'Show hex preview',
  'fileViewer.originalKept': 'Nothing here changes the original file.',
  'fileViewer.imageFit': 'Fit',
  'fileViewer.imageFill': 'Fill',
  'fileViewer.imageActual': '1:1',
  'fileViewer.zoomIn': 'Zoom in',
  'fileViewer.zoomOut': 'Zoom out',
  'fileViewer.rotateLeft': 'Rotate left',
  'fileViewer.rotateRight': 'Rotate right',
  'fileViewer.flipHorizontal': 'Flip horizontally',
  'fileViewer.flipVertical': 'Flip vertically',
  'fileViewer.resetView': 'Reset view',
  'fileViewer.checkerboard': 'Show transparency grid',
  'fileViewer.transformNote': 'View only. Rotating and flipping change nothing in the file.',
  'fileViewer.copyImage': 'Copy image',
  'fileViewer.exportPng': 'Export preview as PNG',
  'fileViewer.animatedNote': 'This format may be animated. The browser plays it as the file dictates.',
  'fileViewer.svgNote': 'Shown as a picture. Scripts and external resources inside the SVG never run.',
  'fileViewer.playbackSpeed': 'Speed',
  'fileViewer.loop': 'Loop',
  'fileViewer.pictureInPicture': 'Picture in picture',
  'fileViewer.fitContain': 'Fit',
  'fileViewer.fitCover': 'Fill',
  'fileViewer.fitOriginal': 'Original',
  'fileViewer.captureFrame': 'Capture frame',
  'fileViewer.quality': 'Quality',
  'fileViewer.qualityAuto': 'Auto',
  'fileViewer.live': 'Live',
  'fileViewer.bufferHealth': 'Buffer',
  'fileViewer.droppedFrames': 'Dropped frames',
  'fileViewer.bandwidth': 'Bandwidth',
  'fileViewer.protocol': 'Protocol',
  'fileViewer.hlsNative': 'HLS (built into this browser)',
  'fileViewer.hlsPlayer': 'HLS (player library)',
  'fileViewer.mediaDiagnostics': 'Diagnostics',
  'fileViewer.mediaPlaybackFailed': 'Playback failed. The browser could not decode this file.',
  'fileViewer.seekBack': 'Back 10 seconds',
  'fileViewer.seekForward': 'Forward 10 seconds',
  'fileViewer.playPause': 'Play or pause',
  'fileViewer.wrapLines': 'Wrap',
  'fileViewer.lineNumbers': 'Line numbers',
  'fileViewer.searchInFile': 'Search in file',
  'fileViewer.searchMatches': '{current} of {total}',
  'fileViewer.searchNone': 'No matches',
  'fileViewer.goToLine': 'Go to line',
  'fileViewer.language': 'Language',
  'fileViewer.viewRaw': 'Raw',
  'fileViewer.viewRendered': 'Preview',
  'fileViewer.viewGrid': 'Grid',
  'fileViewer.prettyPrint': 'Pretty print',
  'fileViewer.validJson': 'Valid JSON',
  'fileViewer.invalidJson': 'Invalid JSON at line {line}, column {column}: {message}',
  'fileViewer.copyCode': 'Copy contents',
  'fileViewer.truncatedNote': 'Showing the first {shown} of {total}. Download the file for the rest.',
  'fileViewer.sandboxNote': 'Rendered in a sandbox: no scripts, forms or outside requests.',
  'fileViewer.noRendererNote': 'No renderer is installed for this format, so it is shown as text.',
  'fileViewer.gridSummary': '{rows} rows, {columns} columns',
  'fileViewer.emptyText': 'This file is empty.',
  'fileViewer.pickerTitle': 'Open a file',
  'fileViewer.pickerFamilies': 'Supported file families',
  'fileViewer.pickerFromDevice': 'Open from device',
  'fileViewer.pickerDrop': 'Drop a file here',
  'fileViewer.pickerOr': 'or',
  'fileViewer.pickerChoose': 'Choose from device',
  'fileViewer.pickerFromLink': 'Open from link',
  'fileViewer.pickerUrl': 'File URL',
  'fileViewer.pickerUrlPlaceholder': 'https://',
  'fileViewer.pickerOpenLink': 'Open link',
  'fileViewer.pickerSendCredentials': 'Send credentials',
  'fileViewer.pickerSendCredentialsHint': 'Include this site’s cookies with the request. Only for links on a host that expects them.',
  'fileViewer.pickerRemember': 'Remember this link',
  'fileViewer.pickerRecentLocal': 'Recent local files',
  'fileViewer.pickerRecentLinks': 'Recent remote links',
  'fileViewer.pickerName': 'Name',
  'fileViewer.pickerSize': 'Size',
  'fileViewer.pickerModified': 'Modified',
  'fileViewer.pickerLink': 'Link',
  'fileViewer.pickerLastOpened': 'Last opened',
  'fileViewer.pickerLocalNote': 'Local files stay on this device unless you share them.',
  'fileViewer.pickerRemoteNote': 'The host of a link may refuse this page, or ask for a sign-in.',
  'fileViewer.pickerSizeNote': 'Files up to {size} open here. For larger files, use a link.',
  'fileViewer.pickerReopenNote': 'A browser cannot reopen a device file by itself. Choose it again to open it.',
  'fileViewer.pickerInvalidUrl': 'Enter a full https:// link.',
  'fileViewer.pickerNoRecent': 'Nothing yet.',
  'fileViewer.pickerClearRecent': 'Clear',
  'fileViewer.helpTitle': 'Keyboard shortcuts',
  'fileViewer.helpEscape': 'Close, or leave fullscreen',
  'fileViewer.helpArrows': 'Previous or next file',
  'fileViewer.helpZoom': 'Zoom in or out',
  'fileViewer.helpFit': 'Fit to view',
  'fileViewer.helpActual': 'Actual size',
  'fileViewer.helpRotate': 'Rotate clockwise',
  'fileViewer.helpSearch': 'Search',
  'fileViewer.helpFullscreen': 'Fullscreen',
  'fileViewer.helpDetails': 'Show or hide details',
  'fileViewer.helpSpace': 'Play or pause',
  'fileViewer.helpSeek': 'Seek back or forward',
  'billing.currency.showIn': 'Show amounts in',
  'billing.currency.aria': 'Currency to show amounts in',
  'billing.currency.othersHint': 'Also in {{list}}. Switch above to see them.',
  'billing.skuRates.columns.cost': 'Cost',
  'billing.skuRates.columns.charge': 'Charge',
  'billing.skuRates.columns.margin': 'Margin',
  'billing.skuRates.columns.category': 'Category',
  'billing.skuRates.filters.anyCategory': 'Any category',
  'billing.skuRates.thinMargin': 'Thin margin',
  'billing.skuRates.noCost': 'No cost recorded',
  'billing.skuRates.markup': '{{pct}}% markup on cost',
  'billing.skuRates.stat.margin': 'Average margin',
  'billing.skuRates.stat.marginHint': 'Across the live price book',
  'billing.skuRates.stat.marginThin': '{{count}} SKUs under 10% margin',
  'billing.skuRates.edit.costLabel': 'Cost ({{currency}})',
  'billing.skuRates.edit.costHint': 'What the platform pays per unit: the Meta, Bedrock, Twilio or storage price.',
  'billing.skuRates.edit.markupLabel': 'Markup %',
  'billing.skuRates.edit.markupHint': 'Sets the charge from the cost.',
  'billing.skuRates.edit.preview': 'Charge {{charge}}, cost {{cost}}, margin {{margin}}',
  'billing.skuRates.modal.confirm.margin': 'Cost stays {{cost}}, so the margin becomes {{margin}} a unit.',
  'billing.skuRates.list.costAndMargin': 'Cost {{cost}} · margin {{margin}}',
  'billing.skuRates.new.action': 'New rate',
  'billing.skuRates.new.title': 'New SKU rate',
  'billing.skuRates.new.body': 'A new billable item, or a price for an existing one in another currency. It is live from today; every tenant on that currency is charged it from now on.',
  'billing.skuRates.new.code': 'SKU code',
  'billing.skuRates.new.codeHint': 'Lower-case, dotted: wa.marketing.ng, notify.sms.ke. A WhatsApp code ends in the destination country (za, ng, ke, row).',
  'billing.skuRates.new.namePlaceholder': 'WhatsApp marketing message (Nigeria)',
  'billing.skuRates.new.nameHint': 'Tenants see this on their usage page.',
  'billing.skuRates.new.category': 'Category',
  'billing.skuRates.new.unit': 'Unit',
  'billing.skuRates.new.submit': 'Create the rate',
  'billing.skuRates.new.created': '{{sku}} is live at {{charge}} a unit.',
  'billing.skuRates.new.createdTitle': 'Rate created',
  'billing.skuRates.new.failed': 'Could not create the rate',
  'billing.skuRates.category.whatsapp': 'WhatsApp',
  'billing.skuRates.category.ai': 'AI',
  'billing.skuRates.category.knowledge': 'Knowledge base',
  'billing.skuRates.category.notifications': 'Notifications',
  'billing.skuRates.category.media': 'Media',
  'billing.skuRates.category.web': 'Website chat',
  'billing.skuRates.unit.message': 'per message',
  'billing.skuRates.unit.1k_tokens': 'per 1 000 tokens',
  'billing.skuRates.unit.document': 'per document',
  'billing.skuRates.unit.question': 'per question',
  'billing.skuRates.unit.file': 'per file',
  'billing.skuRates.unit.gb_day': 'per GB-day',
  'billing.skuRates.unit.call': 'per call',
  'billing.fx.title': 'Exchange rates',
  'billing.fx.body': 'The day\'s mid-market rates, read from public feeds every twelve hours and written only when two feeds agree. Money never converts at the mid-market rate: each currency shows the rate a customer\'s payment converts at and the rate a cost you pay in it (Meta\'s dollars) converts at.',
  'billing.fx.perZar': '{{currency}} per ZAR',
  'billing.fx.updated': 'Updated {{when}}',
  'billing.fx.saved': '{{currency}} set to {{rate}} per ZAR.',
  'billing.usage.stat.revenue': 'Revenue',
  'billing.usage.stat.revenueHint': 'Charged to tenants in the window',
  'billing.usage.stat.cost': 'Cost',
  'billing.usage.stat.costHint': 'What the platform paid, from the snapshot on each entry',
  'billing.usage.stat.margin': 'Margin',
  'billing.usage.stat.marginHint': 'Revenue less cost',
  'billing.usage.margin.title': 'Margin by category',
  'billing.usage.margin.empty': 'No priced usage in the window',
  'billing.usage.margin.row': '{{revenue}} revenue · {{cost}} cost',
  'billing.usage.category.whatsapp': 'WhatsApp',
  'billing.usage.category.ai': 'AI',
  'billing.usage.category.knowledge': 'Knowledge base',
  'billing.usage.category.notifications': 'Notifications',
  'billing.usage.category.media': 'Media',
  'billing.usage.category.web': 'Website chat',
  'billing.usage.category.other': 'Other',
  'nav.item.billingWalletAlerts': 'Wallet alerts',
  'billing.overview.cards.walletAlerts.title': 'Wallet alerts',
  'billing.overview.cards.walletAlerts.hint': 'Every wallet that is low, empty, on auto top-up or was recently alerted.',
  'billing.walletAlerts.title': 'Wallet alerts',
  'billing.walletAlerts.description': 'Tenants running out, tenants topped up by the platform, and what they were told. A wallet that has never crossed a line is not listed; the tenant page shows any one wallet.',
  'billing.walletAlerts.kpi.all': 'Listed',
  'billing.walletAlerts.kpi.auto': 'Auto top-up on',
  'billing.walletAlerts.state.ok': 'OK',
  'billing.walletAlerts.state.low': 'Low',
  'billing.walletAlerts.state.empty': 'Empty',
  'billing.walletAlerts.stat.empty': 'Empty wallets',
  'billing.walletAlerts.stat.emptyHint': 'Sending refused until topped up',
  'billing.walletAlerts.stat.low': 'Low wallets',
  'billing.walletAlerts.stat.lowHint': 'Under their low-balance line',
  'billing.walletAlerts.stat.auto': 'Auto top-up on',
  'billing.walletAlerts.stat.autoHint': 'Wallets the platform tops up from a saved card',
  'billing.walletAlerts.stat.wallets': 'Wallets',
  'billing.walletAlerts.stat.walletsHint': '{{listed}} listed here',
  'billing.walletAlerts.staleHint': '{{count}} wallets have moved since the worker last recorded them; the next sweep announces the change.',
  'billing.walletAlerts.search': 'Search by organisation or alert email…',
  'billing.walletAlerts.showingCount': 'Showing {{shown}} of {{total}} wallets',
  'billing.walletAlerts.empty': 'No wallets match',
  'billing.walletAlerts.emptyHint': 'Nothing is low, empty or on auto top-up, and no alert has gone out.',
  'billing.walletAlerts.loadError': 'Failed to load wallet alerts.',
  'billing.walletAlerts.columns.balance': 'Balance',
  'billing.walletAlerts.columns.line': 'Low line',
  'billing.walletAlerts.columns.auto': 'Auto top-up',
  'billing.walletAlerts.columns.lastAlert': 'Last alert',
  'billing.walletAlerts.columns.notify': 'Alerts go to',
  'billing.walletAlerts.notifyDefault': 'every user on the organisation',
  'billing.walletAlerts.openTenant': 'Open tenant',
  'billing.walletAlerts.kind.low_balance': 'Low balance',
  'billing.walletAlerts.kind.depleted': 'Empty',
  'billing.walletAlerts.kind.recovered': 'Recovered',
  'billing.walletAlerts.kind.auto_topup_succeeded': 'Auto top-up succeeded',
  'billing.walletAlerts.kind.auto_topup_failed': 'Auto top-up failed',
  'billing.walletAlerts.kind.daily_spend': 'Daily spend',
  'billing.walletAlerts.channel.email': 'email',
  'billing.walletAlerts.channel.whatsapp': 'WhatsApp',
  'billing.walletAlerts.channel.webhook': 'webhook',
  'billing.walletAlerts.channel.in_app': 'console',
  'tenants.view.wallet.title': 'Wallet and alerts',
  'tenants.view.wallet.none': 'This organisation has no wallet yet.',
  'tenants.view.wallet.balance': 'Balance',
  'tenants.view.wallet.line': 'Alerts under {{amount}}',
  'tenants.view.wallet.spentMtd': 'Spent this month',
  'tenants.view.wallet.costMtd': 'Our cost this month',
  'tenants.view.wallet.marginMtd': 'Margin this month',
  'tenants.view.wallet.autoOn': 'Auto top-up on: {{amount}} when under {{threshold}}, charged to {{method}}.',
  'tenants.view.wallet.autoOff': 'Auto top-up off. Saved method: {{method}}.',
  'tenants.view.wallet.notify': 'Alerts go to {{email}}; WhatsApp {{msisdn}}',
  'tenants.view.wallet.dailyLine': 'daily spend line {{amount}}',
  'tenants.view.wallet.evaluated': 'last checked {{when}}',
  'tenants.view.wallet.recentAlerts': 'Recent alerts',
  'billing.tax.title': 'VAT',
  'billing.tax.body': 'Off while the platform is not registered: no VAT is charged and statements say so. Prices include VAT: when it goes on, a customer in the registration country pays the listed price and the VAT is the part inside it, from the effective date. The platform keeps the price less VAT, so every rate must still clear its floor with VAT taken out.',
  'billing.tax.switch': 'Charge VAT',
  'billing.tax.switchNeedsNumber': 'Enter the VAT registration number first.',
  'billing.tax.state.on': 'On, {{pct}}%',
  'billing.tax.state.off': 'Off',
  'billing.tax.number': 'VAT registration number',
  'billing.tax.numberHint': 'As issued by the {{country}} tax authority. Printed on every tax invoice. Required while VAT is on; it cannot be cleared without switching VAT off.',
  'billing.tax.from': 'Effective from',
  'billing.tax.fromHint': 'Charges before this date carry no VAT. Blank means the day the switch goes on.',
  'billing.tax.onHint': 'Prices include VAT: {{pct}}% is the part inside every charge to {{country}} customers from {{from}}. Statements are tax invoices.',
  'billing.tax.offHint': 'Statements are plain invoices with the note that no VAT is charged. Enter the number and the date, then switch on.',
  'billing.tax.confirm.onTitle': 'Start charging VAT?',
  'billing.tax.confirm.onBody': 'From the effective date every charge to a {{country}} customer includes {{pct}}% VAT: a {{example}} message still costs the customer {{example}}, and {{exampleVat}} of it is VAT. Customers see the VAT on every ledger entry and on their statements.',
  'billing.tax.confirm.onMargin': 'The platform keeps the price less the VAT inside it, so the margin on South African sales falls by the VAT. A rate that would then be under its cost plus the free plan markup stops the switch. Customers outside the registration country are zero-rated and pay the same listed price with no VAT inside.',
  'billing.tax.confirm.on': 'Charge VAT',
  'billing.tax.confirm.offTitle': 'Stop charging VAT?',
  'billing.tax.confirm.offBody': 'New charges carry no VAT and new statements become plain invoices. Charges already made keep the VAT they carried.',
  'billing.tax.confirm.off': 'Stop charging VAT',
  'billing.tax.toast.on': 'VAT is on at {{pct}}% from {{from}}.',
  'billing.tax.toast.off': 'VAT is off.',
  'billing.skuRates.filters.allCategories': 'All',
  'billing.skuRates.showRetired': 'Show retired versions',
  'billing.skuRates.retired': 'Retired',
  'billing.skuRates.columns.item': 'Item',
  'billing.skuRates.action.change': 'Change',
  'billing.skuRates.modal.title': 'Change the {{sku}} rate',
  'billing.skuRates.modal.current': 'now {{charge}} at a cost of {{cost}}',
  'billing.skuRates.modal.consequence': 'This starts a new version effective today and retires the current one. Every tenant paying in {{currency}} is charged the new rate from that moment.',
  'billing.skuRates.modal.submit': 'Change the rate',
  'billing.fx.saveAll': 'Save changes',
  'billing.fx.savedMany': '{{count}} change(s) saved.',
  'billing.plans.edit.action': 'Edit',
  'billing.plans.edit.title': 'Edit the {{name}} plan',
  'billing.plans.edit.name': 'Name',
  'billing.plans.edit.tagline': 'Tagline',
  'billing.plans.edit.taglineHint': 'One line under the name on the price list.',
  'billing.plans.edit.badge': 'Badge',
  'billing.plans.edit.badgeHint': 'A short label on the price list, for example Most popular. Blank for none.',
  'billing.plans.edit.features': 'What the plan includes',
  'billing.plans.edit.featuresHint': 'One line each, in the order shown on the price list and in the tenant console. Up to 30 lines.',
  'billing.plans.edit.limits': 'Allowances',
  'billing.plans.edit.limitsHint': 'Blank means unlimited. Shown to customers on the price list, and enforced: a customer at the allowance is asked to upgrade before adding another.',
  'billing.plans.limit.numbers': 'WhatsApp numbers',
  'billing.plans.limit.team_members': 'Team members',
  'billing.plans.limit.flows': 'Chatbot flows',
  'billing.plans.limit.knowledge_documents': 'Knowledge base documents',
  'billing.plans.unlimited': 'Unlimited',
  'billing.plans.edit.sortOrder': 'Order on the price list',
  'billing.plans.edit.sortOrderHint': 'Lowest first.',
  'billing.plans.edit.offered': 'Offered to new sign-ups',
  'billing.plans.edit.offeredHint': 'Off hides the plan from the price list and the console. Existing subscribers keep it.',
  'billing.plans.edit.priceNote': '{{monthly}} a month. The fee is not edited here: a different fee is a new plan, so existing subscribers keep what they signed up for. The markup and the included credits can be changed below.',
  'billing.plans.edit.saved': '{{name}} saved. The public price list shows it within a minute.',
  'billing.plans.edit.failed': 'Could not save the plan',
  'billing.plans.includesCount': '{{count}} feature lines',
  'nav.section.content': 'Content',
  'nav.item.contentNews': 'News and updates',
  'content.blog.eyebrow': 'Content',
  'content.blog.title': 'News and updates',
  'content.blog.description': 'What the public site shows under News: product updates, price changes, announcements and guides. A story is public the moment it is published.',
  'content.blog.action.new': 'New story',
  'content.blog.action.publish': 'Publish',
  'content.blog.action.unpublish': 'Unpublish',
  'content.blog.stat.published': 'Published',
  'content.blog.stat.publishedHint': 'Live on the public site',
  'content.blog.stat.drafts': 'Drafts',
  'content.blog.stat.draftsHint': 'Written, not yet public',
  'content.blog.stat.categories': 'Categories',
  'content.blog.stat.categoriesHint': 'Across every story',
  'content.blog.search': 'Search by headline, category or address…',
  'content.blog.filter.all': 'All',
  'content.blog.status.published': 'Published',
  'content.blog.status.draft': 'Draft',
  'content.blog.status.scheduled': 'Scheduled',
  'content.blog.showingCount': 'Showing {{shown}} of {{total}} stories',
  'content.blog.empty': 'No stories match',
  'content.blog.emptyHint': 'Write the first one: a product update, a price change, an announcement or a guide.',
  'content.blog.loadError': 'Could not load the stories.',
  'content.blog.columns.story': 'Story',
  'content.blog.columns.category': 'Category',
  'content.blog.columns.published': 'Published',
  'content.blog.readMinutes': '{{count}} min read',
  'content.blog.notYet': 'Not yet',
  'content.blog.confirm.publishTitle': 'Publish "{{title}}"?',
  'content.blog.confirm.publishBody': 'It appears on the public site\'s News page within a minute and is listed in the sitemap.',
  'content.blog.confirm.unpublishTitle': 'Take "{{title}}" down?',
  'content.blog.confirm.unpublishBody': 'It leaves the public site within a minute and stays here as a draft.',
  'content.blog.confirm.deleteTitle': 'Delete "{{title}}"?',
  'content.blog.confirm.deleteBody': 'The story is removed from the site and from here. Pictures and video it used stay in the library.',
  'content.blog.toast.published': '"{{title}}" is live.',
  'content.blog.toast.unpublished': '"{{title}}" is back to a draft.',
  'content.blog.toast.deleted': '"{{title}}" deleted.',
  'content.blog.editor.newTitle': 'New story',
  'content.blog.editor.editTitle': 'Edit story',
  'content.blog.editor.headline': 'Headline',
  'content.blog.editor.headlinePlaceholder': 'What is new',
  'content.blog.editor.slug': 'Address',
  'content.blog.editor.slugHint': 'The story lives at {{url}}. Lower-case letters, digits and hyphens.',
  'content.blog.editor.category': 'Category',
  'content.blog.editor.categoryHint': 'Product updates, pricing and announcements are highlighted on the site.',
  'content.blog.editor.categoryOther': 'Something else…',
  'content.blog.editor.categoryCustom': 'Category name',
  'content.blog.editor.excerpt': 'Summary',
  'content.blog.editor.excerptHint': 'One or two sentences shown in the list and in search results. Blank uses the first paragraph.',
  'content.blog.editor.author': 'Author',
  'content.blog.editor.cover': 'Cover picture',
  'content.blog.editor.coverHint': 'Upload a picture, or paste an address. Blank shows a colour panel.',
  'content.blog.editor.coverSet': 'Cover picture uploaded.',
  'content.blog.editor.upload': 'Upload',
  'content.blog.editor.write': 'Write',
  'content.blog.editor.preview': 'Preview',
  'content.blog.editor.heading': 'Heading',
  'content.blog.editor.list': 'List',
  'content.blog.editor.quote': 'Quote',
  'content.blog.editor.picture': 'Picture or video',
  'content.blog.editor.video': 'YouTube or Vimeo',
  'content.blog.editor.videoPrompt': 'Paste the YouTube or Vimeo link',
  'content.blog.editor.body': 'Story',
  'content.blog.editor.bodyPlaceholder': 'Write the story. Markdown works: ## for a heading, - for a list, > for a quote, **bold**, [link](https://…).',
  'content.blog.editor.bodyHint': 'Pictures and video go where the cursor is: use the buttons above, or paste a YouTube or Vimeo link on its own line.',
  'content.blog.editor.inserted': '{{name}} uploaded and placed in the story.',
  'content.blog.editor.uploadFailed': 'Could not upload the file',
  'content.blog.editor.previewEmpty': 'Nothing to show yet. Write something under Write.',
  'content.blog.editor.words': '{{count}} words, about {{minutes}} min to read',
  'content.blog.editor.publish': 'Publish on save',
  'content.blog.editor.publishHint': 'On: the story goes live on the site when you save. Off: it is kept as a draft only you can see.',
  'content.blog.editor.publishedAt': 'Publish date',
  'content.blog.editor.publishedAtHint': 'Blank uses the moment you publish. An earlier date places the story earlier on the news page; a later date keeps it off the site until then. Your local time.',
  'content.blog.editor.publishedAtBad': 'That is not a date and time.',
  'content.blog.editor.saveDraft': 'Save draft',
  'content.blog.editor.savePublish': 'Save and publish',
  'content.blog.editor.savedDraft': '"{{title}}" saved as a draft.',
  'content.blog.editor.savedLive': '"{{title}}" is live on the site.',
  'content.blog.editor.saveFailed': 'Could not save the story',
  'media.scan.title': 'Virus scanning',
  'media.scan.description': 'What the scanner decided about each upload. An infected file is refused before anything is stored, so these rows are the only record of it.',
  'media.scan.window.24h': '24 hours',
  'media.scan.window.7d': '7 days',
  'media.scan.window.30d': '30 days',
  'media.scan.window.90d': '90 days',
  'media.scan.loadError': 'The scan figures could not be loaded.',
  'media.scan.kpi.clean': 'Clean',
  'media.scan.kpi.cleanHint': '{{bytes}} scanned in this window',
  'media.scan.kpi.infected': 'Infected',
  'media.scan.kpi.infectedHint': 'Refused on the way in; nothing was stored',
  'media.scan.kpi.unscanned': 'Not scanned',
  'media.scan.kpi.unscannedHint': 'Stored whilst the scanner was unreachable',
  'media.scan.kpi.rate': 'Infected rate',
  'media.scan.kpi.rateHint': 'Of {{count}} scanned uploads',
  'media.scan.since': 'The scan log starts {{when}}. Uploads from before then are in none of these counts.',
  'media.scan.noRecordYet': 'No scans recorded yet. The log fills as files are uploaded.',
  'media.scan.signatures': 'Signatures matched:',
  'media.scan.feed.title': 'Recent scans',
  'media.scan.feed.empty': 'No scans in this window',
  'media.scan.feed.emptyHint': 'Every upload is scanned, so a row appears here the moment one arrives.',
  'media.scan.feed.capped': 'Showing the newest {{shown}} of {{total}} in this window.',
  'media.scan.verdict.clean': 'Clean',
  'media.scan.verdict.infected': 'Infected',
  'media.scan.verdict.unscanned': 'Not scanned',
  'media.scan.notStored': 'Not stored',
  'media.scan.columns.verdict': 'Verdict',
  'media.scan.columns.file': 'File',
  'media.scan.columns.detail': 'Signature or reason',
  'media.scan.columns.stored': 'Stored',
  'media.scan.columns.when': 'Scanned',
  'logs.columns.exception': 'Exception',
  'logs.hasException': 'Trace',
  'billing.ledger.loadMore': 'Load more',
  'billing.ledger.loadedOfWindow': 'Showing {{shown}} of {{loaded}} loaded, {{total}} in this window',
  'billing.ledger.morePagesHelp': 'The journal is read one page at a time, newest first. This window holds more entries than are loaded. Load more to search, sort and export across all of them.',
  'billing.ledger.loadMoreError': 'Could not load the next page.',
  'billing.ledger.filters.pageRows': '{{count}} rows a page',
  'billing.ledger.filters.pageSize': 'Rows per page',
  'settings.integrations.category.auth': 'Sign-in providers',
  'apiMonitoring.windowSpan': '{{count}} requests recorded between {{from}} and {{to}}.',
  'apiMonitoring.notInstrumented.title': 'No service records API requests yet',
  'apiMonitoring.notInstrumented.body': 'No request has been logged in the last seven days. The gateway logs every request that passes it, so this means its log messages are not reaching the logs service: check that the gateway can reach the message broker and that the logs service is running.',
  'apiMonitoring.notInstrumented.shipping': 'Services shipping logs',
  'apiMonitoring.notInstrumented.writing': 'Services recording API requests',
  'apiMonitoring.notInstrumented.rows': 'Rows in the API table',
  'apiMonitoring.notInstrumented.latest': 'Most recent API row',
  'apiMonitoring.notInstrumented.never': 'None written yet',
  'apiMonitoring.empty.notInstrumented': 'Nothing records API requests yet, so there is nothing to list. This is a wiring gap and not an outage.',
  'apiMonitoring.chart.volumeTitle': 'Requests and errors',
  'apiMonitoring.chart.latencyTitle': 'P95 latency per bucket',
  'apiMonitoring.chart.p95': 'P95 latency (ms)',
  'apiMonitoring.chart.latencyHint': 'Latency is drawn apart from the counts because milliseconds and request counts do not share an axis.',
  'apiMonitoring.about.endpoints': 'Distinct endpoints',
  'apiMonitoring.about.endpointsValue': '{{total}} in the window, top {{shown}} listed',
  'apiMonitoring.about.coverage': 'Coverage',
  'apiMonitoring.about.coverageValue': 'Every request in the window',
  'apiMonitoring.about.instrumentation': 'Request logging',
  'apiMonitoring.about.instrumentedYes': 'Registered',
  'apiMonitoring.about.instrumentedNo': 'Not registered',
  'queues.requeue.action': 'Requeue messages',
  'queues.requeue.actionShort': 'Requeue',
  'queues.rowMenuLabel': 'Actions for {{name}}',
  'queues.deadLetterBadge': 'Dead letter',
  'queues.deadLetterTooltip': 'Messages that failed their consumer land here. Requeue puts them back on the queue that should have handled them.',
  'queues.detail.deadLetterTitle': 'This is a dead-letter queue',
  'queues.detail.deadLetterBody': 'Every message here is work that did not happen: a wallet not credited, an inbound message not filed. Requeue sends them back to the queue that should have handled them.',
  'queues.detail.deadLetterEmpty': 'Nothing is waiting on this queue at the moment, so there is nothing to move.',
  'queues.requeue.title': 'Requeue messages from {{queue}}',
  'queues.requeue.summaryTitle': 'What happens when you confirm',
  'queues.requeue.summaryCount': '{{count}} of the {{held}} messages on {{queue}} are taken off it, one at a time.',
  'queues.requeue.summaryCountOverflow': 'You have asked for {{count}}, and the broker last reported {{held}} on {{queue}}. Whatever is there is moved and the rest is not waited for.',
  'queues.requeue.summaryTarget': 'Every one of them is sent to {{target}}.',
  'queues.requeue.summaryFaultAddress': 'Each one is routed back to the queue that originally failed to handle it, worked out from that message\'s own fault headers.',
  'queues.requeue.summaryRetry': 'A message that fails again returns to {{queue}} through its consumer\'s own error path, so nothing is lost and you can try again.',
  'queues.requeue.summaryAudit': 'The move is recorded in the audit trail against {{actor}}, with the reason you give below.',
  'queues.requeue.count': 'Messages to move',
  'queues.requeue.countHint': 'Between 1 and {{max}}. The broker last reported {{held}} on this queue. There is deliberately no option to move everything.',
  'queues.requeue.target': 'Destination queue',
  'queues.requeue.targetPlaceholder': 'Leave blank to route by fault address',
  'queues.requeue.targetHint': 'Leave this blank and each message goes back to the queue named in its own fault headers, which is almost always what you want. Name a queue only to send every message somewhere else.',
  'queues.requeue.reason': 'Reason',
  'queues.requeue.reasonPlaceholder': 'Why these messages are being moved…',
  'queues.requeue.reasonHint': 'Required. Stored on the audit row beside your name, and read by whoever asks later why this queue was drained.',
  'queues.requeue.confirm': 'Move {{count}} messages',
  'queues.requeue.confirmOne': 'Move 1 message',
  'queues.requeue.result.title': 'Moved off {{queue}}',
  'queues.requeue.result.requeued': 'Requeued',
  'queues.requeue.result.restored': 'Restored',
  'queues.requeue.result.failed': 'Failed',
  'queues.requeue.result.remaining': 'Left on the queue',
  'queues.requeue.result.remainingHint': 'As the broker last reported it. It samples this figure on its own interval, so it lags a move by a few seconds.',
  'queues.requeue.result.restoredTitle': '{{count}} could not be delivered and were put back',
  'queues.requeue.result.restoredBody': 'These messages were taken off {{queue}}, found undeliverable, and returned to it unchanged. Nothing was lost. Check that the destination queue exists and has a consumer attached before trying again.',
  'queues.requeue.result.failedTitle': '{{count}} were neither delivered nor put back',
  'queues.requeue.result.failedBody': 'This should not happen. Quote the audit row for this move when you report it, and check the queue\'s depth in the broker before moving anything else.',
  'queues.requeue.result.destinations': 'Where they went',
  'queues.requeue.result.destinationRow': '{{count}} · routed by {{source}}',
  'queues.requeue.result.source.target': 'the queue you named',
  'queues.requeue.result.source.xDeath': 'the broker\'s dead-letter header',
  'queues.requeue.result.source.faultAddress': 'the message\'s own fault address',
  'queues.requeue.result.source.queueName': 'this queue\'s own name',
  'queues.requeue.result.recorded': 'This move is now in the ops audit trail, on this page and scoped to this queue in its detail panel.',
  'queues.requeue.result.again': 'Move more',
  'queues.requeue.error.title': 'Nothing was moved',
  'queues.requeue.error.noSuchQueue': 'The broker has no queue named {{queue}}. It may have been deleted since this page last polled the broker.',
  'queues.requeue.error.noSuchTarget': 'There is no queue named {{target}} to move these messages to, so nothing was taken off {{queue}}. Check the spelling, or leave the destination blank to route by each message\'s own fault address.',
  'queues.requeue.error.targetIsSource': '{{target}} is the queue you are moving messages off, so this would move them nowhere. Choose a different destination, or leave it blank to route by each message\'s own fault address.',
  'queues.requeue.error.invalidCount': 'The count must be a whole number between 1 and {{max}}.',
  'queues.requeue.error.reasonRequired': 'A reason is required before anything moves. It is stored on the audit row.',
  'queues.requeue.error.emptyQueue': '{{queue}} is empty, so there was nothing to move. The depth shown on this page can be a few seconds stale, so the last message was most likely consumed whilst this dialog was open.',
  'queues.requeue.error.brokerUnreachable': 'RabbitMQ did not answer, so nothing was taken off {{queue}}. Every message is still exactly where it was. Check the broker is up, then try again.',
  'queues.requeue.error.auditUnavailable': 'The audit row could not be written, so the move was refused and nothing was touched. This action is never performed unrecorded. Restore the database connection, then try again.',
  'queues.requeue.error.forbidden': 'You do not have permission to move messages on the broker. Ask a platform administrator for ops access; nothing was moved.',
  'queues.requeue.error.generic': 'The broker refused the move ({{code}}): {{message}}',
  'queues.requeue.error.unexpected': 'The move could not be sent: {{message}}. Check whether anything was moved before trying again.',
  'queues.requeue.toast.title': 'Requeue complete',
  'queues.requeue.toast.body': '{{requeued}} messages requeued from {{queue}}.',
  'queues.requeue.toast.partialTitle': 'Requeue only partly delivered',
  'queues.requeue.toast.partialBody': '{{requeued}} requeued from {{queue}}, {{restored}} put back undelivered and {{failed}} failed.',
  'queues.audit.title': 'Ops audit trail',
  'queues.audit.titleScoped': 'Ops audit trail for {{queue}}',
  'queues.audit.description': 'Every write this console has made to the broker, newest first.',
  'queues.audit.totalLabel': '{{shown}} of {{total}}',
  'queues.audit.empty': 'No ops actions recorded yet',
  'queues.audit.emptyScoped': 'Nothing has been moved off {{queue}} yet.',
  'queues.audit.emptyHint': 'Requeueing messages from a queue writes a row here, whether it succeeds or not.',
  'queues.audit.loadError': 'Failed to load the ops audit trail.',
  'queues.audit.denied': 'You do not have permission to read the ops audit trail.',
  'queues.audit.drawerTitle': 'Recent ops actions on this queue',
  'queues.audit.compactLine': '{{when}} · {{who}} · {{destination}}',
  'queues.audit.columns.when': 'When',
  'queues.audit.columns.who': 'Who',
  'queues.audit.columns.queue': 'Queue',
  'queues.audit.columns.destination': 'Destination',
  'queues.audit.columns.moved': 'Moved',
  'queues.audit.columns.restored': 'Restored',
  'queues.audit.columns.outcome': 'Outcome',
  'queues.audit.columns.reason': 'Reason',
  'queues.audit.movedOf': '{{affected}} of {{requested}}',
  'queues.audit.movedTooltip': '{{requested}} were asked for. A queue holding fewer than that gives up what it has.',
  'queues.audit.byFaultAddress': 'By fault address',
  'queues.audit.action.requeue': 'Requeue',
  'queues.audit.action.other': '{{action}}',
  'queues.audit.outcome.pending': 'Pending',
  'queues.audit.outcome.succeeded': 'Succeeded',
  'queues.audit.outcome.partial': 'Partial',
  'queues.audit.outcome.failed': 'Failed',
  'queues.audit.outcome.refused': 'Refused',
  'queues.audit.pendingTooltip': 'Still in flight, or the service stopped part-way through this action.',
  'queues.audit.failedTooltip': '{{count}} messages were neither delivered nor put back.',
  'queues.audit.systemActor': '{{service}} (automated)',
  'queues.audit.unknownActor': 'Unknown operator',
  'system.backups.stats.snapshots': 'Snapshots',
  'system.backups.stats.snapshotsHint': 'In {{location}}',
  'system.backups.stats.snapshotsHintNoStore': 'No snapshot store is configured',
  'system.backups.stats.newest': 'Newest snapshot',
  'system.backups.stats.newestAge': 'Taken {{age}} ago',
  'system.backups.stats.newestStale': 'Taken {{age}} ago, which is past the daily target',
  'system.backups.stats.none': 'None',
  'system.backups.stats.noneHint': 'No snapshot has been taken',
  'system.backups.stats.size': 'Newest snapshot size',
  'system.backups.stats.sizeHint': '{{rows}} live rows dumped',
  'system.backups.stats.databases': 'Databases covered',
  'system.backups.stats.databasesHint': 'In the newest snapshot',
  'system.backups.history.description': 'Newest first, one row per completed snapshot, read from the manifest written beside it.',
  'system.backups.history.total': '{{count}} in {{location}}',
  'system.backups.history.rowHint': 'Open a row for the databases inside the snapshot.',
  'system.backups.history.emptyNotConfigured': 'No snapshot store is configured, so the console has nothing to list. The backup command beside this table writes snapshots on the host.',
  'system.backups.history.emptyUnreachable': 'A snapshot store is configured and could not be read, so the console cannot say what it holds. This is not the same as holding nothing.',
  'system.backups.history.columns.snapshot': 'Snapshot',
  'system.backups.history.columns.taken': 'Taken',
  'system.backups.history.columns.host': 'Host',
  'system.backups.history.columns.databases': 'Databases',
  'system.backups.history.columns.size': 'Size',
  'system.backups.history.columns.encryption': 'Encryption',
  'system.backups.history.columns.verified': 'Verified',
  'system.backups.encryption.none': 'Not encrypted',
  'system.backups.verified.yes': 'Complete',
  'system.backups.verified.no': 'Incomplete',
  'system.backups.verified.unknown': 'Not checked',
  'system.backups.verified.missing': 'Missing or the wrong size: {{databases}}',
  'system.backups.verified.explain': 'Verified means every dump the manifest names is present in the store at the recorded size. It is a completeness check and not a re-hash, so each database carries its recorded SHA-256 for checking by hand.',
  'system.backups.verified.explainNone': 'Nothing was checked on this read, because the store could not be listed. An unchecked snapshot is not a passed one.',
  'system.backups.unreachable.title': 'The snapshot store could not be read',
  'system.backups.unreachable.fallback': 'A snapshot store is configured and the console could not list it, so the history below may be incomplete.',
  'system.backups.detail.title': 'Snapshot contents',
  'system.backups.detail.taken': 'Taken',
  'system.backups.detail.host': 'Host',
  'system.backups.detail.container': 'Container',
  'system.backups.detail.location': 'Location',
  'system.backups.detail.encryption': 'Encryption',
  'system.backups.detail.totalSize': 'Total size',
  'system.backups.detail.liveRows': 'Live rows dumped',
  'system.backups.detail.databases': 'Databases in this snapshot',
  'system.backups.detail.columns.database': 'Database',
  'system.backups.detail.columns.size': 'Size',
  'system.backups.detail.columns.rows': 'Live rows',
  'system.backups.detail.columns.checksum': 'SHA-256',
  'system.backups.detail.columns.present': 'In the store',
  'system.backups.detail.present.yes': 'Present',
  'system.backups.detail.present.no': 'Missing',
  'system.backups.detail.present.unknown': 'Unchecked',
  'system.backups.detail.storedDiffers': '{{bytes}} stored',
  'system.backups.detail.checksumHint': 'Recorded when the dump was written. Copy it to check a downloaded dump by hand.',
  'system.backups.detail.extra': 'Other manifest keys',
  'system.backups.detail.restoreHint': 'Restore this snapshot with the restore command on the page, pointed at this location.',
  'system.cards.security.title': 'Security events',
  'system.cards.security.hint': 'Sign-ins, password and second-factor changes, keys and impersonations.',
  'system.hub.security.row': 'Open the security feed',
  'security.title': 'Security events',
  'security.pageDescription': 'Every security-relevant moment on every account: who signed in, who could not, what changed and who changed it.',
  'security.retentionNote': 'Events are kept for {{days}} days. A quiet period further back is not the same as a clean one.',
  'security.loadError': 'Could not load the security feed',
  'security.selfService': 'The account holder',
  'security.noSuchAccount': 'No account holds this address',
  'security.repeatBadge': '×{{count}}',
  'security.repeatTooltip': '{{count}} attempts of this kind from the same address, folded into one row.',
  'security.outcome.success': 'Succeeded',
  'security.outcome.failure': 'Failed',
  'security.filters.eventType': 'Event',
  'security.filters.anyEventType': 'Any event',
  'security.filters.outcome': 'Outcome',
  'security.filters.anyOutcome': 'Any outcome',
  'security.metrics.events': 'Events in the window',
  'security.metrics.eventsHint': 'Across {{rows}} rows; repeated failures fold into one.',
  'security.metrics.failures': 'Failed attempts',
  'security.metrics.failuresHint': 'From {{addresses}} addresses.',
  'security.metrics.impersonations': 'Impersonations started',
  'security.metrics.impersonationsHint': 'Each one is an operator inside a customer account.',
  'security.metrics.window': 'Rows in the window',
  'security.metrics.windowHint': 'What the platform holds for this period.',
  'security.capped.title': 'This window holds more than one page',
  'security.capped.body': 'Showing the newest {{shown}} of {{total}} rows. Narrow the window or the filters to see the rest.',
  'security.table.title': 'Feed',
  'security.table.description': 'Newest first. Click a row to open the account it belongs to.',
  'security.table.totalLabel': '{{shown}} of {{total}} shown',
  'security.table.searchPlaceholder': 'Search by address, IP, device or detail',
  'security.table.pageOf': 'Page {{page}} of {{pages}}',
  'security.table.perPage': '{{size}} per page',
  'security.empty.title': 'No security events in this window',
  'security.empty.description': 'Nothing was recorded for the period selected. Widen the range to look further back.',
  'security.empty.filteredTitle': 'Nothing matches these filters',
  'security.columns.when': 'When',
  'security.columns.event': 'Event',
  'security.columns.account': 'Account',
  'security.columns.tenant': 'Customer',
  'security.columns.from': 'From',
  'security.columns.actor': 'Done by',
  'security.columns.detail': 'Detail',
  'gateways.wba.metrics.completed': 'Connected',
  'gateways.wba.metrics.completedHint': '{{pct}}% of all attempts.',
  'gateways.wba.metrics.open': 'Still going',
  'gateways.wba.metrics.openHint': 'Opened Meta\'s window and have not finished or given up.',
  'gateways.wba.metrics.failed': 'Failed',
  'gateways.wba.metrics.failedHint': 'Plus {{cancelled}} the customer closed.',
  'gateways.wba.showCompleted': 'Include connected',
  'gateways.wba.hideCompleted': 'Hide connected',
  'gateways.wba.loadError': 'Could not load connection attempts',
  'gateways.wba.numberNotChosen': 'No number chosen yet',
  'gateways.wba.stepPosition': 'Step {{at}} of {{total}}',
  'gateways.wba.dropOff.title': 'Where attempts stop',
  'gateways.wba.dropOff.body': '{{count}} unfinished attempts are sitting at {{step}}. That is the step to look at first.',
  'gateways.wba.filters.status': 'State',
  'gateways.wba.filters.anyStatus': 'Any state',
  'gateways.wba.filters.step': 'Step',
  'gateways.wba.filters.anyStep': 'Any step',
  'gateways.wba.columns.status': 'State',
  'gateways.wba.columns.step': 'Reached',
  'gateways.wba.columns.metaStatus': 'Meta says',
  'gateways.wba.columns.problem': 'Problem',
  'gateways.wba.columns.started': 'Started',
  'gateways.wba.table.title': 'Unfinished attempts',
  'gateways.wba.table.allTitle': 'All attempts',
  'gateways.wba.table.description': 'Newest first. Open a row for the full step-by-step record.',
  'gateways.wba.table.totalLabel': '{{shown}} of {{total}} shown',
  'gateways.wba.table.searchPlaceholder': 'Search by number, account id or error',
  'gateways.wba.empty.title': 'No unfinished attempts',
  'gateways.wba.empty.description': 'Every connection a customer started has either finished or been closed. Include connected to see them all.',
  'gateways.wba.actions.openSession': 'Open the record',
  'gateways.wba.status.started': 'Started',
  'gateways.wba.status.in_progress': 'In progress',
  'gateways.wba.status.completed': 'Connected',
  'gateways.wba.status.failed': 'Failed',
  'gateways.wba.status.cancelled': 'Closed by the customer',
  'gateways.wba.step.launched': 'Meta window opened',
  'gateways.wba.step.meta_returned': 'Account and number chosen',
  'gateways.wba.step.token_exchanged': 'Connection authorised',
  'gateways.wba.step.app_subscribed': 'Messages routed to us',
  'gateways.wba.step.number_registered': 'Number registered for sending',
  'gateways.wba.step.credentials_stored': 'Credentials saved',
  'gateways.wba.step.number_attached': 'Number connected',
  'gateways.wba.drawer.title': 'Connection attempt',
  'gateways.wba.drawer.displayName': 'Name at Meta',
  'gateways.wba.drawer.completed': 'Connected at',
  'gateways.wba.drawer.notCompleted': 'Not connected',
  'gateways.wba.drawer.timeline': 'What happened, step by step',
  'gateways.wba.drawer.noTransitions': 'No steps were recorded for this attempt.',
  'gateways.wba.drawer.noErrorMessage': 'Meta gave no further detail.',
  'settings.apiKeys.columns.tenant': 'Customer',
  'settings.apiKeys.actions.provenance': 'Where this came from',
  'settings.apiKeys.detail.title': 'API key',
  'settings.apiKeys.detail.loadError': 'Could not load this key',
  'settings.apiKeys.detail.whose': 'Whose key this is',
  'settings.apiKeys.detail.scopes': 'Scopes',
  'settings.apiKeys.detail.noScopes': 'No scopes: the key can do nothing.',
  'settings.apiKeys.detail.rateLimit': 'Rate limit',
  'settings.apiKeys.detail.perMinute': '{{n}} a minute',
  'settings.apiKeys.detail.provenance': 'Where it came from',
  'settings.apiKeys.detail.createdAt': 'Created',
  'settings.apiKeys.detail.createdBy': 'Created by',
  'settings.apiKeys.detail.createdIp': 'Created from',
  'settings.apiKeys.detail.unrecorded': 'Not recorded for this key',
  'settings.apiKeys.detail.expiresAt': 'Expires',
  'settings.apiKeys.detail.noExpiry': 'No expiry set',
  'settings.apiKeys.detail.use': 'What has used it',
  'settings.apiKeys.detail.lastUsedIp': 'Last used from',
  'settings.apiKeys.detail.useCount': 'Calls recorded',
  'settings.apiKeys.detail.useCountNote': 'Counted from the usage log, so this is what the log still holds rather than a lifetime total.',
  'settings.apiKeys.detail.revoked': 'This key has been revoked',
  'settings.apiKeys.detail.revokedBy': 'Revoked by {{who}}.',
  'settings.apiKeys.detail.noReason': 'No reason was recorded.',
  'settings.apiKeys.selection.reasonLabel': 'Why',
  'settings.apiKeys.selection.reasonHint': 'Stored on every key and written to the security feed with your name. Say enough that somebody reading it in three months understands.',
  'settings.apiKeys.selection.revokedWithSkipped': '{{count}} revoked. {{skipped}} were already off.',
  'settings.apiKeys.selection.tooManyTitle': 'Too many keys selected',
  'settings.apiKeys.selection.tooManyBody': 'At most 100 keys can be revoked in one go. Narrow the selection and repeat.',
  'audit.kpi.impersonations': 'Impersonations',
  'audit.summary.impersonation': '{{admin}} signed in as this customer',
  'audit.summary.impersonationUnknown': 'An operator',
  'audit.detail.impersonation': 'Into {{tenant}}, valid until {{expires}}',
  'audit.detail.impersonationNoExpiry': 'Into {{tenant}}',
  'queues.requeue.toast.bodyOne': 'One message requeued from {{queue}}.',
  'queues.requeue.result.restoredTitleOne': 'One message could not be delivered and was put back',
  'queues.requeue.result.restoredBodyOne': 'It was taken off {{queue}}, found undeliverable, and returned to it unchanged. Nothing was lost. Check that the destination queue exists and has a consumer attached before trying again.',
  'queues.requeue.result.failedTitleOne': 'One message was neither delivered nor put back',
  'queues.requeue.result.stoppedTitle': 'There is more to do',
  'queues.requeue.result.stoppedCounts': 'The figures above are true: those messages did move. This is what the run did not get to.',
  'queues.requeue.result.stoppedAgain': 'Requeue the rest',
  'queues.requeue.toast.stoppedTitle': 'Requeue stopped before it finished',
  'queues.requeue.toast.stoppedBody': '{{requeued}} requeued from {{queue}}, and the run stopped before it reached the rest. Open the result and run it again to continue.',
  'gateways.wba.readiness.title': 'Account readiness',
  'gateways.wba.readiness.description': 'Asked of Meta right now, not read from our own records. A green Test proves a token can read an account; these three answers are what a customer actually needs.',
  'gateways.wba.readiness.assigned': 'System user can manage this account',
  'gateways.wba.readiness.subscribed': 'App subscribed for incoming messages',
  'gateways.wba.readiness.numbers': 'Numbers on this account',
  'gateways.wba.readiness.yes': 'Yes',
  'gateways.wba.readiness.no': 'No',
  'gateways.wba.readiness.unknown': 'Not known',
  'gateways.wba.readiness.working': 'Ready',
  'gateways.wba.readiness.needsWork': 'Needs attention',
  'gateways.wba.readiness.noNumbers': 'No numbers on this account yet.',
  'gateways.wba.readiness.empty': 'No WhatsApp Business Account has been connected yet, so there is nothing to check.',
  'gateways.wba.readiness.loadError': 'Readiness could not be read',
  'gateways.wba.readiness.numberState.connected': 'Connected',
  'gateways.wba.readiness.numberState.limited': 'Limited',
  'gateways.wba.readiness.numberState.on_business_app': 'In the WhatsApp Business app',
  'gateways.wba.readiness.numberState.unverified': 'Not verified',
  'gateways.wba.readiness.numberState.not_registered': 'Not registered',
  'gateways.wba.step.started': 'Move started',
  'gateways.wba.step.number_on_account': 'Number on the account',
  'gateways.wba.step.app_deleted': 'App account deleted on the phone',
  'gateways.wba.step.code_requested': 'Code sent',
  'gateways.wba.step.number_verified': 'Number verified',
  'settings.integrations.form.remote.fillFirst': 'Fill in {{fields}} first.',
  'settings.integrations.form.remote.loading': 'Loading the list…',
  'settings.integrations.form.remote.search': 'Search, or type a value',
  'settings.integrations.form.remote.useTyped': 'Use what I typed: {{value}}',
  'settings.integrations.form.remote.noMatches': 'Nothing in the list matches. Press Enter to keep what you typed.',
  'settings.integrations.form.remote.notFound': '{{value}} was not found in this region. It stays as typed until you change it.',
  'settings.integrations.form.remote.typedNotListed': 'Not in the list; it will be saved as typed.',
  'settings.integrations.form.remote.loadFailed': 'The list could not be loaded. {{message}}',
  'settings.integrations.form.remote.retry': 'Try again',
  'settings.integrations.form.remote.notSelectable': 'Not selectable',
  'settings.integrations.form.remote.open': 'Open the list',
  'settings.integrations.form.remote.and': 'and',
  'billing.skuRates.category.meta': 'Messenger and Instagram',
  'nav.section.partners': 'Partners',
  'nav.item.partners': 'Partners',
  'nav.item.partnerWithdrawals': 'Withdrawals',
  'nav.item.partnerSettings': 'Programme settings',
  'shell.breadcrumb.partners': 'Partners',
  'shell.breadcrumb.partnerWithdrawals': 'Withdrawals',
  'shell.breadcrumb.partnerSettings': 'Programme settings',
  'partners.eyebrow': 'Partner programme',
  'partners.title': 'Partners',
  'partners.description': 'Who refers business to the platform, what they have earned, and what is owed to them.',
  'partners.loadFailed': 'The partner list could not be loaded.',
  'partners.searchPlaceholder': 'Search by name, business, email or code',
  'partners.total': '{{count}} partners',
  'partners.emptyTitle': 'No partners yet',
  'partners.emptyBody': 'Applications appear here as soon as somebody applies from the customer console.',
  'partners.noneMatch': 'Nothing matches',
  'partners.noneMatchBody': 'Clear the filter or search for something else.',
  'partners.noCode': 'No code yet',
  'partners.ownRate': 'Own rate',
  'partners.stat.active': 'Active partners',
  'partners.stat.activeHint': 'Approved and able to refer.',
  'partners.stat.referred': 'Businesses referred',
  'partners.stat.referredHint': 'Still earning for somebody.',
  'partners.stat.earned': 'Earned this month',
  'partners.stat.earnedHint': 'What partners have earned since the first of the month.',
  'partners.stat.owed': 'Owed to partners',
  'partners.stat.owedHint': 'Cleared and waiting to be withdrawn.',
  'partners.filter.all': 'All',
  'partners.filter.pending': 'Waiting',
  'partners.filter.active': 'Active',
  'partners.filter.suspended': 'On hold',
  'partners.status.pending_review': 'Waiting for review',
  'partners.status.active': 'Active',
  'partners.status.suspended': 'On hold',
  'partners.status.closed': 'Closed',
  'partners.status.revoked': 'Taken away',
  'partners.status.earning': 'Earning',
  'partners.status.ended': 'Ended',
  'partners.column.partner': 'Partner',
  'partners.column.account': 'Their account',
  'partners.column.code': 'Code',
  'partners.column.status': 'Status',
  'partners.column.share': 'Share',
  'partners.column.referrals': 'Referrals',
  'partners.column.pending': 'Still clearing',
  'partners.column.available': 'Available',
  'partners.column.applied': 'Applied',
  'partners.column.company': 'Business',
  'partners.column.source': 'Came from',
  'partners.column.since': 'Since',
  'partners.column.earned': 'Earned',
  'partners.column.flags': 'Worth a look',
  'partners.column.day': 'Day',
  'partners.column.kind': 'What',
  'partners.column.basis': 'What we kept',
  'partners.column.entries': 'Entries',
  'partners.column.amount': 'Amount',
  'partners.column.method': 'Paid how',
  'partners.column.requested': 'Asked for',
  'partners.column.availableAfter': 'Left after this',
  'partners.column.reference': 'Reference',
  'partners.source.link': 'Their link',
  'partners.source.code_at_signup': 'Code at sign-up',
  'partners.source.code_late': 'Code entered later',
  'partners.source.admin_attached': 'Attached by us',
  'partners.kind.commission': 'Their share',
  'partners.kind.adjustment': 'Correction',
  'partners.kind.withdrawal': 'Withdrawal',
  'partners.kind.withdrawal_reversal': 'Withdrawal returned',
  'partners.method.eft': 'Bank payment',
  'partners.method.wallet_credit': 'Credits',
  'partners.flag.same_email_domain': 'Same email domain',
  'partners.flag.shared_payment_method': 'Same card',
  'partners.flag.mutual_referral': 'Referred each other',
  'partners.flags.none': 'Nothing',
  'partners.field.account': 'Account',
  'partners.field.email': 'Email',
  'partners.field.business': 'Business',
  'partners.field.phone': 'Phone',
  'partners.field.website': 'Website',
  'partners.field.applied': 'Applied',
  'partners.field.how': 'How they will promote it',
  'partners.field.lifetime': 'Earned altogether',
  'partners.field.withdrawn': 'Paid out',
  'partners.action.approve': 'Approve',
  'partners.action.approveBody': '{{name}} gets a referral code and can start introducing businesses.',
  'partners.action.suspend': 'Put on hold',
  'partners.action.suspendBody': '{{name}} stops earning and cannot withdraw. Nothing already earned is lost, and reactivating catches up the days in between.',
  'partners.action.reactivate': 'Reactivate',
  'partners.action.reactivateBody': '{{name}} starts earning again, including the days they were on hold.',
  'partners.action.close': 'Close',
  'partners.action.closeBody': '{{name}} leaves the programme and every referral of theirs stops earning. Only possible once they are owed nothing.',
  'partners.action.reason': 'Reason',
  'partners.action.reasonHint': 'Kept on the partner\'s record. This is the only account of why.',
  'partners.action.reasonRequired': 'A reason is required.',
  'partners.action.done': 'Done.',
  'partners.action.failed': 'That could not be done.',
  'partners.accrual.run': 'Work out earnings now',
  'partners.accrual.runOne': 'Work out theirs now',
  'partners.accrual.done': '{{rows}} new lines, {{amount}} earned.',
  'partners.accrual.failed': 'The pass could not be run.',
  'partners.rate.title': 'What they earn',
  'partners.rate.hint': 'A change applies to businesses referred from now on. Existing referrals keep the share they were made at.',
  'partners.rate.percent': 'Share of what we keep, as a percentage',
  'partners.rate.months': 'For how many months',
  'partners.rate.monthsHint': 'Leave it empty for no end date.',
  'partners.rate.notes': 'Notes, for us only',
  'partners.rate.reason': 'Changed on the partner page',
  'partners.rate.saved': 'Saved.',
  'partners.rate.failed': 'That could not be saved.',
  'partners.referrals.title': 'Businesses they referred',
  'partners.referrals.empty': 'No referrals yet',
  'partners.referrals.emptyHint': 'They appear here as soon as somebody signs up with the code.',
  'partners.referrals.revoke': 'Take it away',
  'partners.referrals.revokeTitle': 'Take this referral away?',
  'partners.referrals.revokeBody': 'The partner stops earning on this business from now on. What they have already earned stays theirs; taking that back is a correction of its own.',
  'partners.referrals.revokeConfirm': 'Take it away',
  'partners.referrals.revoked': 'Taken away.',
  'partners.referrals.revokeFailed': 'That referral could not be taken away.',
  'partners.referrals.attachHint': 'Attach a business to this partner by hand. Only possible before that business has paid anything.',
  'partners.referrals.attachCompany': 'Business',
  'partners.referrals.attachPick': 'Choose one',
  'partners.referrals.attach': 'Attach it',
  'partners.referrals.attached': 'Attached.',
  'partners.referrals.attachFailed': 'That business could not be attached.',
  'partners.earnings.title': 'How the money was worked out',
  'partners.earnings.hint': 'One line per business per day. What we kept is the charge less our cost, and the share is taken from that. The partner is shown only their own share.',
  'partners.earnings.empty': 'Nothing earned yet',
  'partners.adjust.amount': 'Correction, in rand',
  'partners.adjust.amountHint': 'Negative takes it away. Available at once.',
  'partners.adjust.action': 'Correct the balance',
  'partners.adjust.done': 'Balance corrected.',
  'partners.adjust.failed': 'That correction could not be made.',
  'partners.events.title': 'Who did what',
  'partners.events.empty': 'Nothing recorded yet',
  'partners.event.applied': 'Applied to the programme',
  'partners.event.approved': 'Approved',
  'partners.event.suspended': 'Put on hold',
  'partners.event.reactivated': 'Reactivated',
  'partners.event.closed': 'Closed',
  'partners.event.rate_changed': 'Share changed',
  'partners.event.profile_updated': 'Details edited',
  'partners.event.referral_attributed': 'Business referred',
  'partners.event.referral_attached': 'Business attached by us',
  'partners.event.referral_revoked': 'Referral taken away',
  'partners.event.adjustment': 'Balance corrected',
  'partners.event.withdrawal_requested': 'Withdrawal asked for',
  'partners.event.withdrawal_cancelled': 'Withdrawal cancelled',
  'partners.event.withdrawal_approved': 'Withdrawal approved',
  'partners.event.withdrawal_rejected': 'Withdrawal refused',
  'partners.event.withdrawal_paid': 'Withdrawal paid',
  'partners.event.payout_account_changed': 'Bank account changed',
  'partners.event.terms_accepted': 'Terms accepted',
  'partners.event.earnings_digest_sent': 'Earnings email sent',
  'partners.actorScope.tenant': 'The partner',
  'partners.actorScope.platform': 'Us',
  'partners.actorScope.system': 'The platform',
  'partners.withdrawals.title': 'Withdrawals',
  'partners.withdrawals.description': 'Money about to leave the company\'s bank account. Approve it, pay it, and record the reference.',
  'partners.withdrawals.total': '{{count}} withdrawals',
  'partners.withdrawals.waiting': 'Waiting for a decision',
  'partners.withdrawals.waitingHint': 'Requested or approved and not yet paid.',
  'partners.withdrawals.paidThisMonth': 'Paid this month',
  'partners.withdrawals.paidThisMonthHint': 'Since the first of the month.',
  'partners.withdrawals.emptyTitle': 'Nothing here',
  'partners.withdrawals.emptyBody': 'Nothing under that status at the moment.',
  'partners.withdrawals.loadFailed': 'The withdrawals could not be loaded.',
  'partners.withdrawals.export': 'Download bank file',
  'partners.withdrawals.exportFailed': 'The bank file could not be downloaded.',
  'partners.withdrawals.reviewTitle': 'This withdrawal',
  'partners.withdrawals.bankTitle': 'Pay to',
  'partners.withdrawals.negative': 'This partner\'s available earnings have gone negative since they asked, so a payment is refused. Correct the balance on their page first.',
  'partners.withdrawals.reference': 'Bank reference',
  'partners.withdrawals.referenceHint': 'What you put on the payment, so the partner can match it to their statement.',
  'partners.withdrawals.referenceRequired': 'The bank reference is required.',
  'partners.withdrawals.note': 'Note',
  'partners.withdrawals.noteHint': 'Quoted to the partner word for word when a withdrawal is refused.',
  'partners.withdrawals.approve': 'Approve it',
  'partners.withdrawals.markPaid': 'Mark it paid',
  'partners.withdrawals.reject': 'Refuse it',
  'partners.withdrawals.approveDone': 'Approved.',
  'partners.withdrawals.paidDone': 'Recorded as paid.',
  'partners.withdrawals.rejectDone': 'Refused. The money is back in their available earnings.',
  'partners.withdrawals.actionFailed': 'That could not be done.',
  'partners.withdrawalStatus.requested': 'Waiting',
  'partners.withdrawalStatus.approved': 'Approved',
  'partners.withdrawalStatus.paid': 'Paid',
  'partners.withdrawalStatus.rejected': 'Refused',
  'partners.withdrawalStatus.cancelled': 'Cancelled',
  'partners.settings.title': 'Programme settings',
  'partners.settings.description': 'What partners earn, when it becomes theirs, and the terms they accept.',
  'partners.settings.loadFailed': 'The settings could not be loaded.',
  'partners.settings.enabled': 'The programme is open',
  'partners.settings.enabledHint': 'Off, nobody can apply and no code works.',
  'partners.settings.autoApprove': 'Approve applications automatically',
  'partners.settings.autoApproveHint': 'Off, somebody reads each application before a code is issued. This programme pays cash, so off is the safer setting.',
  'partners.settings.percent': 'Default share, as a percentage',
  'partners.settings.percentHint': 'Of what the platform keeps on a referred business\'s usage. Can be overridden per partner.',
  'partners.settings.months': 'Default term, in months',
  'partners.settings.monthsHint': 'Leave it empty and a referral earns for as long as the business stays.',
  'partners.settings.clearance': 'Clearing period, in days',
  'partners.settings.clearanceHint': 'How long a day\'s earnings are held before a partner can take them. It exists so a reversed payment can still be undone.',
  'partners.settings.minimum': 'Smallest withdrawal, in rand',
  'partners.settings.minimumHint': 'Below this a partner cannot ask for a payment.',
  'partners.settings.lateDays': 'Days to enter a code afterwards',
  'partners.settings.lateDaysHint': 'How long after signing up a business may still enter a code, and only before its first payment.',
  'partners.settings.terms': 'Partner terms',
  'partners.settings.termsHint': 'What a partner accepts when they apply. Editing the words moves the version, so what each partner accepted stays meaningful.',
  'partners.settings.termsVersion': 'Version {{version}}',
  'partners.settings.updated': 'Last changed {{when}}',
  'partners.settings.saved': 'Saved.',
  'partners.settings.failed': 'Those settings could not be saved.',
  'billing.skuRates.import.action': 'Import Meta rates',
  'billing.skuRates.import.title': 'Import Meta\'s WhatsApp rates',
  'billing.skuRates.import.intro': 'Download the rate card from Meta\'s WhatsApp pricing page (the CSV in US dollars) and upload it here, or paste the table. You see every change before anything is saved.',
  'billing.skuRates.import.source.file': 'Upload a file',
  'billing.skuRates.import.source.paste': 'Paste text',
  'billing.skuRates.import.file.choose': 'Choose a file',
  'billing.skuRates.import.file.replace': 'Choose another file',
  'billing.skuRates.import.file.none': 'No file chosen',
  'billing.skuRates.import.file.hint': 'A CSV, an Excel workbook (.xlsx) or a text file, up to 5 MB.',
  'billing.skuRates.import.paste.label': 'Rate card',
  'billing.skuRates.import.paste.hint': 'Paste the table with its header row, from Meta\'s page, the CSV or the PDF rate card.',
  'billing.skuRates.import.pastedText': 'Pasted text',
  'billing.skuRates.import.effectiveFrom.label': 'Effective from',
  'billing.skuRates.import.effectiveFrom.hint': 'Meta changes rates on 1 January, 1 April, 1 July and 1 October. A later day waits: today\'s rates stay in force until then.',
  'billing.skuRates.import.currency.label': 'Currency of the rate card',
  'billing.skuRates.import.currency.hint': 'Meta publishes a card for each billing currency.',
  'billing.skuRates.import.currency.auto': 'As the file says',
  'billing.skuRates.import.fx.label': 'Rand per {{currency}}',
  'billing.skuRates.import.fx.hint': 'Leave blank to use the price book\'s rate, {{rate}}.',
  'billing.skuRates.import.fx.hintUnknown': 'The price book has no rate for this currency, so enter one.',
  'billing.skuRates.import.preview': 'Preview changes',
  'billing.skuRates.import.back': 'Back',
  'billing.skuRates.import.summary.markets': '{{count}} markets read',
  'billing.skuRates.import.summary.from': 'From {{date}}',
  'billing.skuRates.import.summary.today': 'From today',
  'billing.skuRates.import.notes.service_from_utility': 'This card has no service rates. From 1 October 2026 Meta charges for service messages at each market\'s utility rate, so that is the cost used here. Meta does not charge for the first 1,000 service messages from each number each month, and the platform records those as costing nothing.',
  'billing.skuRates.import.notes.service_not_in_file': 'This card has no service rates and the day is before 1 October 2026, so service rates stay as they are.',
  'billing.skuRates.import.notes.authentication_international_not_applied': 'The card has authentication-international rates. They are not applied: the price book has no separate rate for them yet.',
  'billing.skuRates.import.notes.no_rest_of_world': 'No market is chosen for the rest of the world, so those rates stay as they are.',
  'billing.skuRates.import.missing': 'Not on this card, so they stay as they are: {{countries}}.',
  'billing.skuRates.import.country.za': 'South Africa',
  'billing.skuRates.import.country.ng': 'Nigeria',
  'billing.skuRates.import.country.ke': 'Kenya',
  'billing.skuRates.import.country.row': 'Rest of world',
  'billing.skuRates.import.rest.label': 'Price the rest of the world at',
  'billing.skuRates.import.rest.hint': 'South Africa, Nigeria and Kenya have their own rates. Every other country is charged at the market chosen here.',
  'billing.skuRates.import.rule.label': 'Suggested charges',
  'billing.skuRates.import.rule.keepMarkup': 'Price ladder',
  'billing.skuRates.import.rule.keepCharge': 'Keep my charges',
  'billing.skuRates.import.columns.item': 'Rate',
  'billing.skuRates.import.columns.market': 'Meta market',
  'billing.skuRates.import.columns.metaRate': 'Meta rate',
  'billing.skuRates.import.columns.cost': 'Our cost',
  'billing.skuRates.import.columns.charge': 'Charge (R)',
  'billing.skuRates.import.columns.margin': 'Margin',
  'billing.skuRates.import.chargeFor': 'Charge for {{sku}}',
  'billing.skuRates.import.fromUtility': 'Utility rate',
  'billing.skuRates.import.newRate': 'New rate',
  'billing.skuRates.import.was': 'Was {{amount}}',
  'billing.skuRates.import.belowCost': 'Below cost',
  'billing.skuRates.import.belowCostWarning': '{{count}} charges are below what Meta will charge the platform.',
  'billing.skuRates.import.empty': 'Nothing on this card matches South Africa, Nigeria or Kenya. Choose a market for the rest of the world, or check the file.',
  'billing.skuRates.import.consequence.today': 'Every organisation pays the new charges from today.',
  'billing.skuRates.import.consequence.later': 'Nothing changes until {{date}}. From that day every organisation pays the new charges.',
  'billing.skuRates.import.apply.today': 'Apply {{count}} changes today',
  'billing.skuRates.import.apply.later': 'Schedule {{count}} changes for {{date}}',
  'billing.skuRates.import.toast.appliedTitle': 'Meta rates imported',
  'billing.skuRates.import.toast.applied': '{{changed}} changed, {{created}} added and {{unchanged}} unchanged, effective {{date}}.',
  'billing.skuRates.import.errors.NOT_A_RATE_CARD': 'That is not a rate card: no header row with a market column and Meta\'s categories was found.',
  'billing.skuRates.import.errors.VOLUME_TIER_FILE': 'That is Meta\'s volume tier file. Upload the rate card itself.',
  'billing.skuRates.import.errors.RATE_CARD_EMPTY': 'The file or text is empty.',
  'billing.skuRates.import.errors.NO_MARKETS': 'The header row was found, but no market under it has a rate.',
  'billing.skuRates.import.errors.UNREADABLE_WORKBOOK': 'That workbook could not be read.',
  'billing.skuRates.import.errors.UNSUPPORTED_FILE': 'Upload a CSV, an Excel workbook (.xlsx) or a text file.',
  'billing.skuRates.import.errors.FILE_TOO_LARGE': 'That file is larger than 5 MB. A rate card is a few kilobytes.',
  'billing.skuRates.import.errors.NO_EXCHANGE_RATE': 'The price book has no exchange rate for that currency. Enter the rand value.',
  'billing.skuRates.import.errors.EFFECTIVE_DATE_PAST': 'A rate cannot start in the past. Choose today or a later day.',
  'billing.skuRates.status.upcoming': 'Starts {{date}}',
  'billing.skuRates.status.superseded': 'Replaced',
  'billing.skuRates.upcoming.banner': '{{count}} rates are scheduled. The first starts on {{date}}; until then the rates in force apply.',
  'billing.skuRates.action.cancelUpcoming': 'Cancel',
  'billing.skuRates.cancel.title': 'Cancel the scheduled rate for {{sku}}?',
  'billing.skuRates.cancel.body': 'Nobody has paid {{charge}} yet: it was due to start on {{date}}. Cancelling it keeps the rate in force after that day.',
  'billing.skuRates.cancel.keep': 'Keep it',
  'billing.skuRates.cancel.confirm': 'Cancel the rate',
  'billing.skuRates.cancel.doneTitle': 'Scheduled rate cancelled',
  'billing.skuRates.cancel.done': '{{sku}} will not change on {{date}}.',
  'billing.skuRates.cancel.failed': 'Could not cancel the rate',
  'nav.item.billingMetaUpdates': 'Meta updates',
  'shell.breadcrumb.metaUpdates': 'Meta updates',
  'billing.metaUpdates.eyebrow': 'Billing',
  'billing.metaUpdates.title': 'Meta updates',
  'billing.metaUpdates.description': 'What Meta changes on the WhatsApp Business Platform: pricing and rate cards first, then API versions, deprecations and template rules. Read it here in time to update our prices.',
  'billing.metaUpdates.lastChecked': 'Last checked {{when}}',
  'billing.metaUpdates.neverChecked': 'Not checked yet',
  'billing.metaUpdates.action.checkNow': 'Check now',
  'billing.metaUpdates.action.checking': 'Checking…',
  'billing.metaUpdates.action.markAllRead': 'Mark all as read',
  'billing.metaUpdates.action.markRead': 'Mark as read',
  'billing.metaUpdates.action.markUnread': 'Mark as unread',
  'billing.metaUpdates.stat.unread': 'Unread updates',
  'billing.metaUpdates.stat.unreadHint': 'Across every topic and source.',
  'billing.metaUpdates.stat.pricing': 'Unread about pricing',
  'billing.metaUpdates.stat.pricingHint': 'Rate cards, charges and billing rules.',
  'billing.metaUpdates.stat.sources': 'Sources turned on',
  'billing.metaUpdates.stat.sourcesHint': '{{enabled}} of {{total}} sources are checked.',
  'billing.metaUpdates.stat.sourcesFailing': '{{count}} could not be checked last time. See Sources below.',
  'billing.metaUpdates.stat.lastChecked': 'Last checked',
  'billing.metaUpdates.stat.lastCheckedHint': 'Checked automatically every few hours, or straight away with Check now.',
  'billing.metaUpdates.summaryError': 'The totals could not be loaded.',
  'billing.metaUpdates.callout.title': 'Unread pricing news from Meta: {{count}}',
  'billing.metaUpdates.callout.body': 'Check that our prices still cover what Meta charges. Open Billing, then SKU rates, and use Import Meta rates to bring in the new rate card.',
  'billing.metaUpdates.callout.latest': 'Latest:',
  'billing.metaUpdates.callout.action': 'Go to SKU rates',
  'billing.metaUpdates.topic.all': 'All',
  'billing.metaUpdates.topic.pricing': 'Pricing',
  'billing.metaUpdates.topic.whatsapp': 'WhatsApp',
  'billing.metaUpdates.topic.api': 'API changes',
  'billing.metaUpdates.topic.templates': 'Templates',
  'billing.metaUpdates.topic.policy': 'Policy',
  'billing.metaUpdates.unreadOnly': 'Unread only',
  'billing.metaUpdates.showingCount': 'Showing {{shown}} of {{total}}',
  'billing.metaUpdates.item.unread': 'Unread',
  'billing.metaUpdates.item.meta': '{{source}} · {{date}}',
  'billing.metaUpdates.item.metaFound': '{{source}} · found {{date}}',
  'billing.metaUpdates.item.noSummary': 'Open the page on Meta to see what changed.',
  'billing.metaUpdates.empty': 'No updates here yet',
  'billing.metaUpdates.emptyUnread': 'Nothing unread here',
  'billing.metaUpdates.emptyHint': 'Meta is checked every few hours. Choose Check now to look straight away.',
  'billing.metaUpdates.loadError': 'The updates could not be loaded.',
  'billing.metaUpdates.confirm.markAllTitle': 'Mark every update as read?',
  'billing.metaUpdates.confirm.markAllTopicTitle': 'Mark every update under {{topic}} as read?',
  'billing.metaUpdates.confirm.markAllBody': 'They stay in the list without the unread marker, and you can mark any of them unread again.',
  'billing.metaUpdates.toast.checked': 'Checked {{sources}} sources: {{count}} new updates.',
  'billing.metaUpdates.toast.someFailed': '{{count}} sources could not be checked. The reason is shown under Sources.',
  'billing.metaUpdates.toast.markedAll': 'Marked {{count}} updates as read.',
  'billing.metaUpdates.sources.title': 'Sources',
  'billing.metaUpdates.sources.description': 'The feeds and Meta pages that are checked. A page is read section by section, using the dates in its headings.',
  'billing.metaUpdates.sources.add': 'Add source',
  'billing.metaUpdates.sources.empty': 'No sources yet',
  'billing.metaUpdates.sources.loadError': 'The sources could not be loaded.',
  'billing.metaUpdates.sources.columns.source': 'Source',
  'billing.metaUpdates.sources.columns.kind': 'Kind',
  'billing.metaUpdates.sources.columns.enabled': 'On',
  'billing.metaUpdates.sources.columns.status': 'Last check',
  'billing.metaUpdates.sources.columns.items': 'Updates',
  'billing.metaUpdates.sources.enabledLabel': 'Check {{name}}',
  'billing.metaUpdates.sources.status.working': 'Working',
  'billing.metaUpdates.sources.status.failing': 'Failed',
  'billing.metaUpdates.sources.status.notChecked': 'Not checked yet',
  'billing.metaUpdates.sources.status.off': 'Turned off',
  'billing.metaUpdates.sources.checkedWhen': 'Checked {{when}}',
  'billing.metaUpdates.sources.itemCount': '{{count}} updates, {{unread}} unread',
  'billing.metaUpdates.sources.checkNow': 'Check',
  'billing.metaUpdates.sources.added': 'Added {{name}}.',
  'billing.metaUpdates.sources.updated': 'Saved {{name}}.',
  'billing.metaUpdates.sources.deleted': 'Deleted {{name}} and its updates.',
  'billing.metaUpdates.sources.checked': '{{name}}: {{count}} new updates.',
  'billing.metaUpdates.sources.checkFailed': '{{name}} could not be checked: {{error}}',
  'billing.metaUpdates.sources.confirm.deleteTitle': 'Delete {{name}}?',
  'billing.metaUpdates.sources.confirm.deleteBody': 'The {{count}} updates found in this source are deleted with it. To stop checking it but keep what it found, turn it off instead.',
  'billing.metaUpdates.kind.page': 'Web page',
  'billing.metaUpdates.kind.atom': 'Atom feed',
  'billing.metaUpdates.kind.rss': 'RSS feed',
  'billing.metaUpdates.form.name': 'Name',
  'billing.metaUpdates.form.nameHint': 'How the source is shown in the list, such as Meta: Calling API pricing.',
  'billing.metaUpdates.form.url': 'Address',
  'billing.metaUpdates.form.urlHint': 'A public https address. Addresses inside a private network are refused.',
  'billing.metaUpdates.form.urlPlaceholder': 'https://developers.facebook.com/docs/…',
  'billing.metaUpdates.form.kind': 'Kind',
  'billing.metaUpdates.form.kindHint': 'Web page for a Meta documentation page with dated sections. Atom or RSS for a feed.',
  'billing.metaUpdates.form.save': 'Add source',
  'billing.skuRates.import.rest.none': 'Leave them as they are',
  'billing.skuRates.metaNews.banner': 'Meta has {{count}} pricing updates you have not read.',
  'billing.skuRates.metaNews.bannerLatest': 'Meta has {{count}} pricing updates you have not read. The latest: {{title}}',
  'billing.skuRates.metaNews.open': 'Read Meta updates',
  'billing.skuRates.import.notes.country_from_region': 'A country without its own row on this card is priced at the Meta region it belongs to. Kenya is in Rest of Africa on Meta\'s card, so that is the rate it gets.',
  'billing.plans.limit.bots': 'Chatbots',
  'gateways.wba.step.billing_attached': 'Credit line shared (Meta bills us)',
  'billing.plans.columns.markup': 'Markup',
  'billing.plans.markup.entry': 'Sets the pay-as-you-go price',
  'billing.plans.ceiling.upTo': 'Ceiling {{ceiling}}',
  'billing.plans.ceiling.over': 'Over the ceiling of {{ceiling}}',
  'billing.plans.ceiling.hint': 'The most credits this fee can carry at the plan\'s markup. Above it, a customer who spends them all costs more than they paid.',
  'billing.plans.list.ladder': '{{markup}}% markup over cost · ceiling {{ceiling}} credits',
  'billing.plans.overCeilingBanner.title': '{{count}} plan(s) include more credits than their fee can carry',
  'billing.plans.overCeilingBanner.body': 'A customer who spends every included credit on such a plan costs more than they paid. Edit the plan and lower its credits, or raise its markup.',
  'billing.plans.edit.pricing': 'Message prices and credits',
  'billing.plans.edit.markup': 'Markup over cost (%)',
  'billing.plans.edit.markupHint': 'What a message costs on this plan is its cost plus this percentage. The free plan\'s markup is the pay-as-you-go price, and no paid plan may be above it.',
  'billing.plans.edit.markupInvalid': 'A markup is a whole number from 0 to 1 000.',
  'billing.plans.edit.credits': 'Included credits',
  'billing.plans.edit.creditsHint': 'At {{markup}}% this fee can carry at most {{ceiling}} credits. Above that a customer who spends them all costs more than they paid.',
  'billing.plans.edit.creditsHintFree': 'A free plan has no fee to carry credits, so it can include none.',
  'billing.plans.edit.creditsHintNoMarkup': 'Enter a markup to see how many credits this fee can carry.',
  'billing.plans.edit.creditsOver': '{{credits}} credits is more than this fee can carry at {{markup}}%. The most is {{ceiling}}.',
  'billing.plans.edit.creditsOverFree': 'A free plan can include no credits. Set this to 0.',
  'billing.plans.edit.creditsRequired': 'Enter the credits this plan includes, or 0 for none.',
  'billing.plans.edit.blocked': 'Fix the markup or the included credits above before saving.',
  'billing.plans.edit.refusal.canLoseMoney': 'At {{markup}}% this fee can carry at most {{ceiling}} credits, and {{credits}} were asked for. Lower the credits or raise the markup.',
  'billing.plans.edit.refusal.ladderPaid': 'The free plan sets the pay-as-you-go price at {{entry}}%, and no paid plan may be above it. Set this markup to {{entry}}% or less.',
  'billing.plans.edit.refusal.ladderFree': 'At {{entry}}% the free plan would be cheaper per message than a paid plan. Keep it at or above every paid plan\'s markup, or lower those first.',
  'billing.plans.edit.refusal.ladderOther': 'Another paid plan is above the free plan\'s {{entry}}%, so the ladder was refused as a whole. Bring that plan\'s markup down first, then save this one.',
  'billing.skuRates.columns.byPlan': 'By plan',
  'billing.skuRates.columns.floor': 'Floor',
  'billing.skuRates.floor': 'Floor {{amount}}',
  'billing.skuRates.noFloor': 'No floor',
  'billing.skuRates.ladderBadge': 'Price ladder',
  'billing.skuRates.ladderBadgeHint': 'Priced at its floor: the cost plus the free plan\'s markup. It moves when either does.',
  'billing.skuRates.floorHint': 'The least this can be listed at: the cost plus the free plan\'s markup.',
  'billing.skuRates.noFloorHint': 'A unit that costs the platform nothing has no floor. It is priced from its charge alone.',
  'billing.skuRates.edit.follow': 'Follow the price ladder',
  'billing.skuRates.edit.followHint': 'The charge is the cost plus the free plan\'s markup, and it moves when either changes. Switch off to set a higher charge by hand.',
  'billing.skuRates.edit.followNoCost': 'A unit with no cost has no floor, so its charge is set by hand.',
  'billing.skuRates.edit.ladderChargeHint': 'Set by the price ladder: the cost plus the free plan\'s {{pct}}%.',
  'billing.skuRates.edit.ladderChargeUnknown': 'Set by the price ladder once the plans have been read.',
  'billing.skuRates.edit.floorHint': 'At least {{floor}}: the cost plus the free plan\'s {{pct}}%.',
  'billing.skuRates.edit.belowFloor': '{{charge}} is under the floor of {{floor}}. Raise the charge, or follow the price ladder.',
  'billing.skuRates.edit.refusal.belowFloor': 'A unit that costs {{cost}} cannot be listed under {{floor}}, its cost plus the free plan\'s {{pct}}%. Raise the charge, or follow the price ladder.',
  'billing.skuRates.edit.byPlan': 'What each plan pays',
  'billing.skuRates.edit.plansError': 'The plans could not be read, so the floor cannot be shown here. The billing service still checks it when you save.',
  'billing.skuRates.new.belowFloor': '{{charge}} is under the floor of {{floor}}. Raise the charge.',
  'billing.skuRates.new.refusal.belowFloor': 'A unit that costs {{cost}} cannot be listed under {{floor}}, its cost plus the free plan\'s {{pct}}%. Raise the charge.',
  'billing.skuRates.import.rule.ladderHint': 'Each charge is its new cost plus the free plan\'s {{pct}}%, and every paid plan pays its own markup on the same cost. A charge you had set above that is kept.',
  'billing.skuRates.import.rule.ladderHintNoPct': 'Each charge is its new cost plus the free plan\'s markup, and every paid plan pays its own markup on the same cost. A charge you had set above that is kept.',
  'billing.skuRates.import.rule.keepChargeHint': 'Today\'s charges stay as they are. Where the new cost has overtaken a charge, it is lifted to its floor.',
  'billing.skuRates.import.belowFloor': 'Under the floor of {{floor}}',
  'billing.skuRates.import.belowFloorWarning': '{{count}} charges are under their floor, the new cost plus the free plan\'s markup. Raise them before applying.',
  'billing.skuRates.import.refusal.belowFloor': '{{name}} cannot be listed under {{floor}}: that is its new cost plus the free plan\'s {{pct}}%. Raise its charge and apply again.',
  'billing.plans.edit.confirm.title': 'Change the {{name}} plan\'s pricing?',
  'billing.plans.edit.confirm.markup': 'Messages on {{name}} go from their cost plus {{from}}% to their cost plus {{to}}%. Every customer on the plan pays the new prices from now on.',
  'billing.plans.edit.confirm.markupEntry': '{{name}} sets the pay-as-you-go price. Moving its markup from {{from}}% to {{to}}% re-lists every rate that follows the price ladder and lifts any charge the new floor overtakes. Everyone without a paid plan pays the new prices from now on.',
  'billing.plans.edit.confirm.credits': 'Included credits go from {{from}} to {{to}}. Customers get the new figure at their next monthly charge, and the public price list shows it within a minute.',
  'billing.plans.edit.confirm.submit': 'Change the pricing',
  'billing.fx.feed.on': 'Updates every {{hours}} hours',
  'billing.fx.feed.off': 'Automatic updates are off',
  'billing.fx.feed.stale': 'Not confirmed lately',
  'billing.fx.lastOk': 'Every currency was last confirmed on {{when}}.',
  'billing.fx.refresh': 'Refresh now',
  'billing.fx.refresh.done': '{{feeds}} feeds read, {{changed}} rate(s) moved.',
  'billing.fx.refresh.held': 'Left as they were because the feeds did not agree: {{currencies}}.',
  'billing.fx.refresh.noFeeds': 'No exchange rate feed could be reached. The rates are unchanged.',
  'billing.fx.held.title': 'Some rates were not updated',
  'billing.fx.held.body': '{{detail}}. The rates in force were left as they are; set one by hand if it matters today.',
  'billing.fx.stale.title': 'The rates have not been confirmed for more than a day and a half',
  'billing.fx.stale.body': 'The feeds may be unreachable from the server. Try Refresh now, or set the rates by hand.',
  'billing.fx.each': 'One {{currency}} is R{{mid}} at mid-market. A customer paying in {{currency}} gets R{{customer}} of credits for each; a cost you pay in {{currency}} is R{{cost}} for each.',
  'billing.fx.source.feed': 'Confirmed {{when}} by {{source}}',
  'billing.fx.source.manual': 'Set by hand on {{when}}',
  'billing.fx.source.seed': 'The figure seeded on 5 September 2026; no feed has confirmed it yet',
  'billing.fx.auto': 'Update automatically',
  'billing.fx.spread': 'Spread (%)',
  'billing.fx.spreadHint': 'How far the two working rates sit from mid-market, always in your favour: a gateway settles a customer\'s currency for less than mid-market, and the bank sells you dollars for more. 3% is typical. At 0 every conversion is at mid-market and each one can lose a little.',
  'billing.fx.spreadInvalid': 'Between 0 and 25.',
  'billing.fx.attribution.lead': 'Rates by',
  'billing.fx.attribution.erApi': 'ExchangeRate-API',
  'billing.fx.attribution.currencyApi': 'currency-api',
  'billing.fx.attribution.and': 'and',
  'billing.fx.attribution.frankfurter': 'Frankfurter (European Central Bank)',
  'billing.skuRates.unit.10k_tokens': 'per 10 000 tokens',
  'nav.item.voice': 'Voice',
  'shell.breadcrumb.voice': 'Voice',
  'voice.eyebrow': 'Messaging',
  'voice.title': 'Voice',
  'voice.description': 'Phone calls answered by the assistant. Whether it is on, which parts are on, the ceilings no organisation can exceed, and who has asked for it.',
  'voice.loadFailed': 'The voice settings could not be loaded.',
  'voice.save': 'Save settings',
  'voice.master.on': 'Voice is on',
  'voice.master.off': 'Voice is off',
  'voice.master.badgeOn': 'On',
  'voice.master.badgeOff': 'Off',
  'voice.master.onHint': 'Organisations that are switched on can use the parts that are on below. The kill switch turns everything off at once and ends every call in progress.',
  'voice.master.offHint': 'Nothing of voice is shown to any organisation, every request is refused and every call is refused.',
  'voice.master.changed': 'Last changed {{when}} by {{who}}',
  'voice.master.switchOn': 'Switch voice on',
  'voice.master.switchOff': 'Switch off',
  'voice.master.kill': 'Kill switch',
  'voice.master.needsTerms': 'Voice cannot be switched on yet: publish the voice terms first (a terms version, under Policy below).',
  'voice.switches.title': 'Parts',
  'voice.switches.hint': 'Each part has its own switch, and all of them sit under the master switch. Outgoing calls, international calls and recording start off.',
  'voice.switch.inbound': 'Incoming calls',
  'voice.switch.inboundHint': 'Calls to an organisation\'s number are answered.',
  'voice.switch.ai': 'The assistant',
  'voice.switch.aiHint': 'The assistant may answer calls. Off, calls can only go to people.',
  'voice.switch.outbound': 'Outgoing calls',
  'voice.switch.outboundHint': 'People may call out from their extensions. This is where fraud costs money: leave it off until the limits below are set.',
  'voice.switch.international': 'International calls',
  'voice.switch.internationalHint': 'Calls outside the allowed countries. Each organisation must also be allowed individually.',
  'voice.switch.recording': 'Recording',
  'voice.switch.recordingHint': 'Organisations may choose to record calls. Transcripts are kept either way.',
  'voice.switch.applications': 'Applications for numbers',
  'voice.switch.applicationsHint': 'Organisations may apply for a number from the platform.',
  'voice.switch.self_activation': 'Self-activation',
  'voice.switch.self_activationHint': 'An organisation that connects a number of its own is switched on without waiting for approval.',
  'voice.limits.title': 'Ceilings',
  'voice.limits.hint': 'The most any organisation may have. An organisation\'s own limits, and the ones you set for it, can only be lower. Money is in rand.',
  'voice.limit.tenant_hourly_cap_minor': 'Spend per organisation per hour',
  'voice.limit.tenant_hourly_cap_minorHint': 'Outgoing calls stop for an organisation that reaches this in an hour.',
  'voice.limit.tenant_daily_cap_minor': 'Spend per organisation per day',
  'voice.limit.tenant_daily_cap_minorHint': 'The default daily cap for an organisation.',
  'voice.limit.platform_hourly_cap_minor': 'Spend across the platform per hour',
  'voice.limit.platform_hourly_cap_minorHint': 'All outgoing calls stop when the platform reaches this in an hour.',
  'voice.limit.platform_daily_cap_minor': 'Spend across the platform per day',
  'voice.limit.platform_daily_cap_minorHint': 'The most the platform can owe its carriers for one day of calls.',
  'voice.limit.max_call_minutes': 'Longest call (minutes)',
  'voice.limit.max_call_minutesHint': 'A call is ended when it reaches this length.',
  'voice.limit.min_balance_minutes': 'Minutes of credit to start a call',
  'voice.limit.min_balance_minutesHint': 'A call starts only when the wallet covers this many minutes of it.',
  'voice.limit.billing_increment_seconds': 'Charging step (seconds)',
  'voice.limit.billing_increment_secondsHint': 'Calls are charged in steps of this many seconds, rounded up. 60 charges by the minute.',
  'voice.limit.concurrent_calls_per_tenant': 'Calls at once per organisation',
  'voice.limit.concurrent_calls_per_tenantHint': 'The default for an organisation.',
  'voice.limit.concurrent_calls_platform': 'Calls at once across the platform',
  'voice.limit.concurrent_calls_platformHint': 'New calls are refused politely past this, so the calls in progress keep their quality.',
  'voice.limit.max_extensions_per_tenant': 'Extensions per organisation',
  'voice.limit.max_extensions_per_tenantHint': 'The most extensions an organisation may create.',
  'voice.limit.max_numbers_per_tenant': 'Numbers per organisation',
  'voice.limit.max_numbers_per_tenantHint': 'The most phone numbers an organisation may have.',
  'voice.policy.title': 'Policy',
  'voice.policy.terms': 'Voice terms version',
  'voice.policy.termsHint': 'The version an organisation accepts before it can start, such as 2026-10. Empty means no terms are published and voice cannot be switched on.',
  'voice.policy.approval': 'Approving organisations',
  'voice.policy.approvalManual': 'A person approves each one',
  'voice.policy.approvalAuto': 'Switched on as soon as they accept the terms',
  'voice.policy.approvalHint.manual': 'An organisation that asks for voice waits in the list below until you switch it on.',
  'voice.policy.approvalHint.auto': 'An organisation is active the moment it accepts the terms. Only choose this once the limits are settled.',
  'voice.policy.onKill': 'When voice is switched off',
  'voice.policy.onKillEnd': 'End calls in progress',
  'voice.policy.onKillFinish': 'Let calls in progress finish',
  'voice.policy.onKillHint': 'What the ordinary Switch off does. The kill switch always ends calls in progress.',
  'voice.policy.aiProvider': 'Voice assistant provider',
  'voice.policy.aiProviderHint': 'Its key is set under Settings, Integrations.',
  'voice.policy.aiStub': 'Test stand-in (no real assistant)',
  'voice.policy.aiVoice': 'Default voice',
  'voice.policy.aiVoiceHint': 'The provider\'s name for the voice. Empty uses the provider\'s default.',
  'voice.policy.aiModel': 'Model',
  'voice.policy.aiModelHint': 'Empty uses the provider\'s default.',
  'voice.policy.retentionMax': 'Longest a recording may be kept (days)',
  'voice.policy.retentionMaxHint': 'No organisation can keep recordings longer than this.',
  'voice.policy.retentionDefault': 'Recordings kept by default (days)',
  'voice.policy.retentionDefaultHint': 'What an organisation gets until it chooses.',
  'voice.policy.countries': 'Countries calls may go to',
  'voice.policy.countriesHint': 'Two-letter codes, separated by commas. South Africa is ZA.',
  'voice.policy.prefixes': 'Number ranges that are always refused',
  'voice.policy.prefixesHint': 'Prefixes in international form, such as +2786. Premium-rate and satellite ranges belong here.',
  'voice.nodes.title': 'Call engines',
  'voice.nodes.hint': 'The servers that carry the calls. With none connected, no call can arrive.',
  'voice.nodes.empty': 'No call engine is attached',
  'voice.nodes.emptyHint': 'Voice is deployed without one until it is ready to be switched on.',
  'voice.nodes.connected': 'Connected',
  'voice.nodes.disconnected': 'Not connected',
  'voice.nodes.draining': 'Draining',
  'voice.nodes.seen': 'last seen {{when}}',
  'voice.nodes.calls': '{{live}} of {{capacity}} calls',
  'voice.tenants.title': 'Organisations',
  'voice.tenants.hint': 'Every organisation that has asked for voice. Switch one on to let it use voice, or suspend it with a reason it will be shown.',
  'voice.tenants.empty': 'No organisation has asked for voice',
  'voice.tenants.emptyHint': 'They appear here once voice is on and they accept the terms.',
  'voice.tenants.organisation': 'Organisation',
  'voice.tenants.status': 'Status',
  'voice.tenants.dailyCap': 'Daily cap',
  'voice.tenants.updated': 'Updated',
  'voice.tenants.activate': 'Switch on',
  'voice.tenants.suspend': 'Suspend',
  'voice.tenants.turnOff': 'Turn off',
  'voice.tenants.outboundBlocked': 'Outgoing calls are blocked',
  'voice.status.off': 'Off',
  'voice.status.applied': 'Waiting for approval',
  'voice.status.active': 'On',
  'voice.status.suspended': 'Suspended',
  'voice.audit.title': 'Audit trail',
  'voice.audit.hint': 'Every switch change, refusal and suspension, newest first. It cannot be edited.',
  'voice.audit.empty': 'Nothing has happened yet',
  'voice.audit.emptyHint': 'Changes to the switches and refused calls are listed here.',
  'voice.audit.severity.info': 'Note',
  'voice.audit.severity.warning': 'Warning',
  'voice.audit.severity.critical': 'Critical',
  'voice.reason.label': 'Reason',
  'voice.reason.hint': 'It goes on the audit trail with your name.',
  'voice.reason.hintSuspend': 'The organisation is shown this, so write it for them.',
  'voice.reason.title.on': 'Switch voice on?',
  'voice.reason.title.off': 'Switch voice off?',
  'voice.reason.title.kill': 'Use the kill switch?',
  'voice.reason.title.suspend': 'Suspend this organisation\'s voice?',
  'voice.reason.body.on': 'Organisations that are switched on will be able to use the parts that are on.',
  'voice.reason.body.off': 'Voice is hidden from every organisation and new calls are refused. Calls in progress follow the setting under Policy.',
  'voice.reason.body.kill': 'Voice is switched off now and every call in progress is ended.',
  'voice.reason.body.suspend': 'Its calls are refused until you switch it on again.',
  'voice.reason.confirm.on': 'Switch on',
  'voice.reason.confirm.off': 'Switch off',
  'voice.reason.confirm.kill': 'End all calls and switch off',
  'voice.reason.confirm.suspend': 'Suspend',
  'voice.toast.saved': 'Voice settings saved',
  'voice.toast.failed': 'That could not be done',
  'voice.toast.on': 'Voice is on',
  'voice.toast.off': 'Voice is off',
  'voice.toast.killed': 'Voice is off. Calls ended: {{count}}',
  'voice.toast.tenant': 'The organisation\'s voice was changed',
  'voice.policy.termsUrl': 'Where the terms can be read',
  'voice.policy.termsUrlHint': 'The web address of the voice terms, starting with https://. Organisations are given this link before they accept.',
  'billing.skuRates.category.voice': 'Voice',
  'billing.skuRates.unit.minute': 'per minute',
  'billing.skuRates.unit.number': 'per number',
  'billing.skuRates.unit.month': 'per month',
  'billing.skuRates.unit.session': 'per session',
  'billing.skuRates.unit.code': 'per code',
  'voice.calls.title': 'Calls',
  'voice.calls.hint': 'Calls across every organisation. Ending a call here hangs up both sides at once; it is still charged for the time it ran.',
  'voice.calls.show.live': 'In progress',
  'voice.calls.show.recent': 'Recent',
  'voice.calls.empty': 'No calls yet',
  'voice.calls.emptyLive': 'No calls in progress',
  'voice.calls.emptyHint': 'This list refreshes by itself.',
  'voice.calls.from': 'From',
  'voice.calls.to': 'To',
  'voice.calls.started': 'Started',
  'voice.calls.state': 'State',
  'voice.calls.perMinute': 'Price a minute',
  'voice.calls.charged': 'Charged',
  'voice.calls.seconds': '{{seconds}} s',
  'voice.calls.unknown': 'Unknown caller',
  'voice.calls.end': 'End call',
  'voice.calls.endedNotice': 'The call was ended.',
  'voice.calls.status.ringing': 'Ringing',
  'voice.calls.status.active': 'In progress',
  'voice.calls.status.ended': 'Ended',
  'voice.calls.ended.completed': 'Completed',
  'voice.calls.ended.caller_hung_up': 'Caller hung up',
  'voice.calls.ended.no_credit': 'Credits ran out',
  'voice.calls.ended.max_length': 'Longest length reached',
  'voice.calls.ended.kill_switch': 'Kill switch',
  'voice.calls.ended.engine_lost': 'Engine lost',
  'voice.calls.ended.failed': 'Failed (not charged)',
  'voice.calls.ended.ended_by_platform': 'Ended from here',
  'voice.fraud.title': 'Stolen-line protection',
  'voice.fraud.hint': 'A stolen line is used in bursts, to many different numbers, in calls too short to be conversations. Any one of these stops that organisation\'s outgoing calls at once and writes a critical line on the trail. Incoming calls carry on. A person lifts the block in the list below.',
  'voice.fraud.max_outbound_per_10_min': 'Outgoing calls in ten minutes',
  'voice.fraud.max_outbound_per_10_minHint': 'The most outside calls one organisation may start in ten minutes.',
  'voice.fraud.max_destinations_per_hour': 'Different numbers in an hour',
  'voice.fraud.max_destinations_per_hourHint': 'The most different outside numbers one organisation may call in an hour.',
  'voice.fraud.max_short_calls_per_10_min': 'Very short calls in ten minutes',
  'voice.fraud.max_short_calls_per_10_minHint': 'How many answered calls shorter than the length beside this are allowed in ten minutes.',
  'voice.fraud.short_call_seconds': 'A very short call is under (seconds)',
  'voice.fraud.short_call_secondsHint': 'Answered calls shorter than this count towards the limit beside it.',
  'voice.tenants.liftBlock': 'Lift outgoing block',
  'voice.tenants.blockLifted': 'Outgoing calls are allowed again.',
  'voice.bans.title': 'Banned addresses',
  'voice.bans.hint': 'The call engine stops listening to an address after repeated failed sign-ins, for a day. While banned, even the right password gets no answer. Phone providers are never banned.',
  'voice.bans.empty': 'No address is banned',
  'voice.bans.emptyHint': 'This list refreshes by itself.',
  'voice.bans.banned': 'Banned',
  'voice.bans.line': '{{failures}} failed sign-ins · banned {{when}}',
  'voice.bans.lift': 'Lift ban',
  'voice.bans.liftedNotice': 'The ban was lifted. The call engine drops it within seconds.',
  'voice.bans.liftedLine': '{{address}}: lifted by {{by}} on {{when}}',
  'voice.calls.ended.no_answer': 'No answer',
  'voice.numbersPolicy.title': 'Numbers we supply',
  'voice.numbersPolicy.hint': 'A rental is paid only from credits an organisation bought. When it cannot be paid the number is first marked unpaid, then stops being answered, then is taken back.',
  'voice.numbersPolicy.rental_grace_days': 'Days unpaid before it stops being answered',
  'voice.numbersPolicy.rental_grace_daysHint': 'The number keeps working for this long after a rental could not be paid.',
  'voice.numbersPolicy.release_after_days': 'Days unpaid before it is taken back',
  'voice.numbersPolicy.release_after_daysHint': 'After this the number returns to the inventory. Always later than the days before.',
  'voice.numbersPolicy.application_hold_days': 'Days an application\'s fee stays held',
  'voice.numbersPolicy.application_hold_daysHint': 'Decide within this time. After it the hold runs out and approval is refused until they top up. 30 at most.',
  'voice.supply.save': 'Save',
  'voice.supply.edit': 'Edit',
  'voice.supply.remove': 'Remove',
  'voice.supply.kind.national': 'National (087)',
  'voice.supply.kind.geographic': 'Geographic',
  'voice.supply.status.submitted': 'Waiting',
  'voice.supply.status.approved': 'Approved',
  'voice.supply.status.rejected': 'Rejected',
  'voice.supply.status.withdrawn': 'Withdrawn',
  'voice.supply.applications.title': 'Applications for a number',
  'voice.supply.applications.hint': 'The registration fee has been held from the applicant\'s bought credits since they applied. Approving takes it and assigns the number in one step; rejecting gives it back and shows them your reason.',
  'voice.supply.applications.empty': 'No applications',
  'voice.supply.applications.emptyHint': 'Applications appear here when Applications for a number is switched on above.',
  'voice.supply.applications.contact': 'Contact and purpose',
  'voice.supply.applications.kind': 'Kind',
  'voice.supply.applications.fee': 'Fee held',
  'voice.supply.applications.assign': 'Choose a number to assign',
  'voice.supply.applications.approve': 'Approve',
  'voice.supply.applications.approved': 'Approved. {{number}} was assigned and the fee taken.',
  'voice.supply.applications.reject': 'Reject',
  'voice.supply.applications.rejected': 'Rejected. The held fee was given back.',
  'voice.supply.applications.rejectTitle': 'Reject this application',
  'voice.supply.applications.rejectBody': 'The held fee goes back to the organisation at once.',
  'voice.supply.applications.rejectHint': 'The organisation is shown this, so write it for them.',
  'voice.supply.carriers.title': 'Our carriers',
  'voice.supply.carriers.hint': 'The companies the numbers we supply come from. Calls for those numbers are accepted only from a carrier\'s own addresses.',
  'voice.supply.carriers.add': 'Add a carrier',
  'voice.supply.carriers.edit': 'Change a carrier',
  'voice.supply.carriers.empty': 'No carriers yet',
  'voice.supply.carriers.emptyHint': 'Add the carrier before the numbers held with it.',
  'voice.supply.carriers.off': 'Off',
  'voice.supply.carriers.status.new': 'Checking',
  'voice.supply.carriers.status.ok': 'Connected',
  'voice.supply.carriers.status.failing': 'Not connected',
  'voice.supply.carriers.noLimit': 'No spend limit confirmed',
  'voice.supply.carriers.mode.forward': '{{host}} sends calls to us',
  'voice.supply.carriers.mode.register': 'We sign in to {{host}}',
  'voice.supply.carriers.saved': 'Carrier saved.',
  'voice.supply.carriers.removed': 'Carrier removed.',
  'voice.supply.carriers.form.name': 'Name',
  'voice.supply.carriers.form.nameHint': 'The carrier\'s name, for this list.',
  'voice.supply.carriers.form.mode': 'How it connects',
  'voice.supply.carriers.form.mode.forward': 'It sends calls to our address',
  'voice.supply.carriers.form.mode.register': 'We sign in with a username and password',
  'voice.supply.carriers.form.modeHint.forward': 'The usual way for a wholesale trunk: the carrier trusts our address and we trust theirs.',
  'voice.supply.carriers.form.modeHint.register': 'For a carrier that gives an account to sign in with.',
  'voice.supply.carriers.form.host': 'Server',
  'voice.supply.carriers.form.hostHint': 'The carrier\'s SIP server, a host name or an address.',
  'voice.supply.carriers.form.port': 'Port',
  'voice.supply.carriers.form.portHint': '5060 unless the carrier says otherwise.',
  'voice.supply.carriers.form.transport': 'Connection type',
  'voice.supply.carriers.form.transportHint': 'UDP unless the carrier says otherwise.',
  'voice.supply.carriers.form.username': 'Username',
  'voice.supply.carriers.form.usernameHint': 'From the carrier.',
  'voice.supply.carriers.form.password': 'Password',
  'voice.supply.carriers.form.passwordHint': 'Stored encrypted and never shown again.',
  'voice.supply.carriers.form.passwordKeep': 'Leave empty to keep the password already saved.',
  'voice.supply.carriers.form.sources': 'Addresses its calls come from',
  'voice.supply.carriers.form.sourcesHint': 'One per line, such as 196.10.20.0/24. These are also the only addresses the firewall should allow on port 5060.',
  'voice.supply.carriers.form.limited': 'The account is prepaid or has a credit limit',
  'voice.supply.carriers.form.limitedHint': 'Switch on only once the carrier has confirmed it. It is the one ceiling on a phone bill that holds if everything else fails.',
  'voice.supply.carriers.form.active': 'Carrier is on',
  'voice.supply.carriers.form.activeHint': 'Off, its numbers are not answered and nothing is sent to it.',
  'voice.supply.inventory.title': 'Numbers we hold',
  'voice.supply.inventory.hint': 'The numbers held with our carriers. An available number can be assigned to an application; a retired one is kept out of sight.',
  'voice.supply.inventory.add': 'Add numbers',
  'voice.supply.inventory.added': '{{added}} added, {{skipped}} already held or in use.',
  'voice.supply.inventory.empty': 'No numbers held',
  'voice.supply.inventory.emptyHint': 'Add the numbers a carrier has given you.',
  'voice.supply.inventory.number': 'Number',
  'voice.supply.inventory.carrier': 'Carrier',
  'voice.supply.inventory.carrierHint': 'The carrier these numbers are with.',
  'voice.supply.inventory.kindHint': 'What kind these numbers are. Applicants choose a kind.',
  'voice.supply.inventory.numbers': 'Numbers',
  'voice.supply.inventory.numbersHint': 'One per line, in any usual form, up to 500. Numbers already held or in use are skipped.',
  'voice.supply.inventory.status.available': 'Available',
  'voice.supply.inventory.status.assigned': 'Assigned',
  'voice.supply.inventory.status.retired': 'Retired',
  'voice.supply.inventory.retire': 'Retire',
  'voice.supply.inventory.restore': 'Make available',
  'voice.readiness.title': 'Go-live checklist',
  'voice.readiness.hintEnforced': 'Worked out by the voice service. A line marked "Must fix" stops voice, or that part of it, being switched on. The others say what will not work yet.',
  'voice.readiness.hintAdvisory': 'Worked out by the voice service. On this installation it is advice only; in production a line marked "Must fix" stops voice, or that part of it, being switched on.',
  'voice.readiness.complete': 'Everything is in place',
  'voice.readiness.ready': '{{count}} to look at',
  'voice.readiness.blocked': 'Not ready',
  'voice.readiness.item.ok': 'In place',
  'voice.readiness.item.missing': 'Missing',
  'voice.readiness.item.blocking': 'Must fix',
  'voice.attested.title': 'What you have confirmed',
  'voice.attested.hint': 'Three things the service cannot see for itself. The checklist above reads them.',
  'voice.attested.attackTests': 'Attack tests passed on',
  'voice.attested.attackTestsHint': 'The date scripts/standalone/voice-attack.sh last printed ATTACK TESTS PASSED, as 2026-10-02. Outgoing calls cannot be switched on without it.',
  'voice.attested.loadTested': 'Calls at once the server carried well',
  'voice.attested.loadTestedHint': 'From scripts/standalone/voice-load.sh run against the real server. Set the limits above no higher.',
  'voice.attested.certificate': 'The call engine has a real certificate',
  'voice.attested.certificateHint': 'Switch on once the certificate for the SIP host name is installed. Without it no real phone can sign in.',
  'voice.calls.quality': 'Line',
  'voice.calls.quality.good': 'Good',
  'voice.calls.quality.fair': 'Fair',
  'voice.calls.quality.poor': 'Poor',
  'password.errors.breached': 'That password is one of the most common leaked passwords. Choose one that is your own.',
  'password.errors.tooShort': 'A password needs at least 12 characters.',
  'partners.withdrawals.fullNumberInFile': 'The full account number is only in the bank file. Each download is recorded in the partner’s history.',
  'partners.event.bank_file_downloaded': 'Bank file downloaded (holds the full account number)',
  'billing.tax.underFloor.title': '{{count}} rates would fall under their floor',
  'billing.tax.underFloor.body': 'Once the {{pct}}% VAT inside them is taken out, {{count}} rates would be under their cost plus the free plan markup. Raise them to the floor and turn VAT on, or leave VAT off and change them yourself. Raising a price for customers needs the notice the terms promise.',
  'billing.tax.underFloor.row': '{{sku}}: {{now}}, floor {{floor}}',
  'billing.tax.underFloor.raise': 'Raise them and turn VAT on',
  'billing.tax.underFloor.leave': 'Leave VAT off',
  'system.alerts.recipients.title': 'Who receives production alerts',
  'system.alerts.recipients.body': 'Every alert from the server goes by email to the addresses below. Critical ones (a disk nearly full, a failed backup, a service down) also go by WhatsApp to the number below, from the platform’s own WhatsApp number using the approved template. A change applies to the next alert.',
  'system.alerts.recipients.email': 'Alert email',
  'system.alerts.recipients.emailHint': 'One or more addresses, separated by commas. Leave blank to send no alert email.',
  'system.alerts.recipients.whatsapp': 'Alert WhatsApp number',
  'system.alerts.recipients.whatsappHint': 'In international form, starting with + and the country code. Leave blank to send no WhatsApp alerts.',
  'system.alerts.recipients.template': 'WhatsApp template',
  'system.alerts.recipients.templateHint': 'An approved utility template on the platform’s number. Its body takes two values: what happened, and the detail.',
  'system.alerts.recipients.language': 'Template language',
  'system.alerts.recipients.languageHint': 'The language code it was approved in, such as en.',
  'system.alerts.recipients.saved': 'Alert recipients saved.',
  'system.alerts.recipients.badEmail': 'Each entry must be an email address. Separate several with commas.',
  'system.alerts.recipients.badNumber': 'Give the number in international form, starting with + and the country code.',
  'system.alerts.recipients.badTemplate': 'A template name is lower-case letters, digits and underscores.',
  'system.alerts.channel.email': 'Email',
  'system.alerts.channel.whatsapp': 'WhatsApp',
  'voice.numbersPolicy.required_documents': 'Documents an application must carry',
  'voice.numbersPolicy.required_documentsHint': 'One per line, in the words the applicant sees. Each needs its own upload before an application can be sent (RICA). Leave it empty to ask for none.',
  'voice.supply.applications.document': 'Document',
  'voice.supply.applications.open': 'Open',
  'nav.item.contentCompany': 'Company details',
  'content.company.title': 'Company details',
  'content.company.description': 'What the website, the terms and invoices show about the company. A change shows on the website within a minute and on the next invoice; no release is needed.',
  'content.company.legalName': 'Registered name',
  'content.company.legalNameHint': 'As registered with CIPC.',
  'content.company.registration': 'Registration number',
  'content.company.registrationHint': 'The CIPC number, e.g. 2021/123456/07. Leave blank to show none.',
  'content.company.address': 'Street address',
  'content.company.addressHint': 'Shown on the contact page, under every legal page and on invoices. Leave blank to show only South Africa.',
  'content.company.phone': 'Phone and WhatsApp number',
  'content.company.phoneHint': 'In international form. Leave blank to hide the line on the website.',
  'content.company.saved': 'Company details saved.',
  'content.company.badPhone': 'Give the number in international form, starting with + and the country code.',
  'nav.item.contentAgreements': 'Agreements',
  'content.agreements.title': 'Agreements',
  'content.agreements.description': 'What customers accept before they can use the console, today the data processing agreement. The wording is the website\'s legal page; publishing a version here asks every organisation to accept again.',
  'content.agreements.current': 'Current: {{version}}',
  'content.agreements.acceptedCount': '{{accepted}} of {{total}} active organisations have accepted the current version.',
  'content.agreements.versionLine': '{{version}}, published {{date}}, accepted by {{count}}',
  'content.agreements.publishWarning': 'Publish only after the new wording is live on the website. Every organisation is stopped at the console until someone there accepts it.',
  'content.agreements.version': 'New version',
  'content.agreements.versionHint': 'The date the wording on {{path}} shows as last updated, such as 2026-10-02.',
  'content.agreements.summary': 'What changed',
  'content.agreements.summaryHint': 'Shown to customers when they are asked to accept again. One or two plain sentences.',
  'content.agreements.publish': 'Publish version',
  'content.agreements.published': 'Published. Organisations are asked to accept it the next time they open the console.',
  'tenants.view.agreements.title': 'Agreements',
  'tenants.view.agreements.accepted': 'Accepted {{version}}',
  'tenants.view.agreements.notAccepted': 'Not accepted ({{version}})',
  'tenants.view.agreements.none': 'Nothing accepted yet.',
  'tenants.view.agreements.line': '{{version}} accepted {{when}} by {{who}} from {{ip}}',
  'tenants.view.users.columns.role': 'Role',
  'tenants.view.users.role.owner': 'Owner',
  'tenants.view.users.role.admin': 'Admin',
  'tenants.view.users.role.agent': 'Agent',
  'tenants.closing.closes': 'Closes {{date}}',
  'tenants.closing.closed': 'Closed {{date}}',
  'tenants.closing.refund': 'Refund of unused credit asked for',
  'voice.switch.whatsapp': 'WhatsApp calls',
  'voice.switch.whatsappHint': 'Customers may call a business\'s WhatsApp number; Meta sends the call to the call engine and it is answered like a phone call. Charged at voice.wa.minute.',
  'voice.policy.waSources': 'Meta\'s addresses for WhatsApp calls',
  'voice.policy.waSourcesHint': 'The addresses or networks Meta\'s calls come from, one per line (Meta lists them with its webhook addresses). Only these reach the engine as WhatsApp calls; with none, no WhatsApp call can arrive.',
  'nav.item.metaStatus': 'Meta status',
  'metaStatus.title': 'Meta status',
  'metaStatus.description': 'What Meta has registered and verified for the platform, asked live: business verification, the app and its token, webhook fields, WhatsApp accounts and numbers. The same check runs every six hours and any change is emailed to you.',
  'metaStatus.checkAgain': 'Check again',
  'metaStatus.loadError': 'Meta\'s status could not be read',
  'metaStatus.summary.attention': '{{count}} lines need attention.',
  'metaStatus.summary.ok': 'Nothing needs attention.',
  'metaStatus.checkedAt': 'Checked {{when}}.',
  'metaStatus.state.ok': 'OK',
  'metaStatus.state.attention': 'Needs attention',
  'metaStatus.state.unknown': 'Not known',
  'metaStatus.state.later': 'Needed later',
  'metaStatus.coexistence.title': 'Coexistence (keep the WhatsApp Business app)',
  'metaStatus.coexistence.on': 'On',
  'metaStatus.coexistence.off': 'Off',
  'metaStatus.coexistence.ready': 'Everything Meta needs for coexistence is in place.',
  'metaStatus.coexistence.notReady': 'Before switching coexistence on:',
  'metaStatus.coexistence.missing.business_verified': 'Meta has to verify the business.',
  'metaStatus.coexistence.missing.business_app': 'The Meta app has to be a Business app.',
  'metaStatus.coexistence.missing.tech_provider': 'Meta has to approve the platform as a Tech Provider or Solution Partner (confirm it under Settings, Integrations, WhatsApp).',
  'metaStatus.coexistence.missing.credentials': 'Set the WhatsApp credentials under Settings, Integrations.',
  'metaStatus.coexistence.missing.webhook': 'Subscribe the webhook field {{field}} in the App Dashboard.',
  'metaStatus.coexistence.switchHint': 'The switch is the coexistence field on the WhatsApp integration.',
  'metaStatus.coexistence.switchLink': 'Open Integrations',
  'dashboard.meta.title': 'Meta needs attention',
  'dashboard.meta.body': '{{count}} lines on the Meta status page need attention.',
  'dashboard.meta.action': 'Open Meta status',
  'gateways.wba.step.history_requested': 'Contacts and chat history asked for',
  'metaStatus.summary.attentionOne': '1 line needs attention.',
  'content.company.showOnSite': 'Show on the website',
  'content.company.showHint': 'The legal name is always shown. A value switched off here still appears on invoices; the website leaves it out.',
  'nav.item.assistantTranscripts': 'Build with AI',
  'assistantTranscripts.title': 'Build with AI conversations',
  'assistantTranscripts.description': 'What customers asked the flow assistant in the last 30 days and what it answered, newest first. Use it to see where the assistant falls short. Conversations older than 30 days are deleted.',
  'assistantTranscripts.onlyProblems': 'Only with problems',
  'assistantTranscripts.refresh': 'Refresh',
  'assistantTranscripts.loadError': 'The conversations could not be loaded',
  'assistantTranscripts.summary': '{{count}} turns in the last 30 days (the newest 200 at most); {{problems}} proposed a flow with problems.',
  'assistantTranscripts.empty.title': 'No conversations',
  'assistantTranscripts.empty.description': 'Nobody has used Build with AI in the last 30 days, or none matches the filter.',
  'assistantTranscripts.proposed': 'Proposed a flow',
  'assistantTranscripts.talkedOnly': 'Answered in words',
  'assistantTranscripts.problemCount': '{{count}} problems',
  'assistantTranscripts.tokens': '{{in}} in, {{out}} out',
  'assistantTranscripts.asked': 'Conversation',
  'assistantTranscripts.answered': 'Assistant answer',
  'assistantTranscripts.problems': 'Problems in the proposed flow',
  'assistantTranscripts.summaryOne': '1 turn in the last 30 days; {{problems}} proposed a flow with problems.',
  'assistantTranscripts.problemOne': '1 problem',
  'auth.mfaSetup.title': 'Set up two-factor sign-in',
  'auth.mfaSetup.intro': 'Platform staff sign in with a second step. Add this key to an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password and the like), then type the six-digit code it shows.',
  'auth.mfaSetup.secretLabel': 'Key for your authenticator app',
  'auth.mfaSetup.code': 'Code from the app',
  'auth.mfaSetup.codeHint': 'Six digits. It changes every 30 seconds.',
  'auth.mfaSetup.submit': 'Switch on and sign in',
  'auth.mfaSetup.wrongCode': 'That code did not match. Check the time on your phone and try the newest code.',
  'auth.mfaSetup.expired': 'This setup has expired or had too many wrong codes. Sign in again to start over.',
  'auth.mfaSetup.noSetup': 'There is no setup waiting. Sign in first; if your account needs two-factor sign-in, you are brought back here.',
  'roles.invite.title': 'Invite platform staff',
  'roles.invite.intro': 'They get an email with a link to set a password here, valid for 7 days. Before their first sign-in they must set up two-factor sign-in. Platform staff can see and change every organisation.',
  'roles.invite.email': 'Email address',
  'roles.invite.name': 'Name',
  'roles.invite.nameHint': 'Optional. Shown in the console and on the security feed.',
  'roles.invite.send': 'Send invitation',
  'roles.invite.sent': 'Invitation sent to {{email}}.',
  'roles.invite.alreadyStaff': 'This person is already platform staff.',
  'roles.invite.emailInUse': 'This address belongs to a customer\'s account. Platform staff need an address of their own.',
  'roles.invite.invalidEmail': 'Give a valid email address.',
  'users.view.security.status': 'Account',
  'users.view.security.statusOn': 'Switched on',
  'users.view.security.statusOff': 'Switched off',
  'users.view.security.actions.enable': 'Switch on',
  'users.view.security.disableTitle': 'Switch this account off?',
  'users.view.security.enableTitle': 'Switch this account back on?',
  'users.view.security.disableBody': '{{email}} is signed out everywhere and cannot sign in until the account is switched back on. Nothing they own is deleted.',
  'users.view.security.enableBody': '{{email}} can sign in again with their password (and two-factor, if they have it).',
  'users.view.security.disabled': '{{email}} is switched off and signed out.',
  'users.view.security.enabled': '{{email}} can sign in again.',
  'users.view.security.lastAdmin': 'This is the only platform operator who can sign in, so it stays on.',
  'users.view.security.notSelf': 'You cannot switch off your own account.',
  'users.view.security.disableExplainer': 'Switching off signs the person out everywhere within a few minutes and is recorded on the security feed with your name.',
  'users.view.platformTeam': 'RepletoryReach platform team',
  'billing.skuRates.category.sms': 'SMS',
  'billing.skuRates.category.email': 'Email',
  'billing.skuRates.category.ussd': 'USSD',
  'settings.integrations.usedBy.sms_routing': 'choosing the SMS provider and its backup for every SMS: broadcasts, a flow\'s SMS step, the WhatsApp fallback and team notifications',
  'settings.integrations.usedBy.bulksms': 'SMS for customers when chosen as the active or backup SMS provider',
  'settings.integrations.usedBy.clickatell': 'SMS for customers when chosen as the active or backup SMS provider',
  'settings.integrations.usedBy.smsportal': 'SMS for customers when chosen as the active or backup SMS provider',
  'settings.integrations.sms.title': 'SMS',
  'settings.integrations.sms.subtitle': 'Customer SMS through the provider chosen under SMS routing',
  'settings.integrations.sms.available': 'Available',
  'settings.integrations.sms.unavailable': 'Not available',
  'settings.integrations.sms.unavailableHint': 'SMS cannot be sent: choose a provider under SMS routing and switch on its row. Until then customers see no SMS options.',
  'settings.integrations.sms.loadError': 'Could not load the SMS status.',
  'settings.integrations.sms.active': 'Provider',
  'settings.integrations.sms.backup': 'Backup provider',
  'settings.integrations.sms.none': 'None',
  'settings.integrations.sms.last24': 'Last 24 hours',
  'settings.integrations.sms.noneSent': 'No SMS sent',
  'settings.integrations.sms.statusCount': '{{status}}: {{count}}',
  'settings.integrations.sms.lastWebhook': 'Last callback',
  'settings.integrations.sms.webhook': '{{provider}}, {{when}}',
  'settings.integrations.sms.webhookWithOutcome': '{{provider}}, {{when}} ({{outcome}})',
  'settings.integrations.sms.noWebhook': 'None received yet',
  'settings.integrations.sms.status.queued': 'Queued',
  'settings.integrations.sms.status.sent': 'Sent',
  'settings.integrations.sms.status.delivered': 'Delivered',
  'settings.integrations.sms.status.failed': 'Failed',
  'settings.integrations.sms.status.refused': 'Refused',
  'settings.integrations.sms.test.number': 'Test number',
  'settings.integrations.sms.test.numberHint': 'With the country code, for example +27821234567. The test is charged to nobody.',
  'settings.integrations.sms.test.invalid': 'Enter the number with its country code, starting with +.',
  'settings.integrations.sms.test.send': 'Send a test SMS',
  'settings.integrations.sms.test.confirmTitle': 'Send a test SMS?',
  'settings.integrations.sms.test.confirmBody': 'A real SMS goes to {{number}} through {{provider}}. The platform pays for it; no organisation is charged.',
  'settings.integrations.sms.test.result': 'Result: {{status}}, {{segments}} part(s)',
  'nav.item.ussd': 'USSD',
  'shell.breadcrumb.ussd': 'USSD',
  'settings.integrations.usedBy.email_routing': 'choosing the email provider, its backup and the inbound provider for every customer email: broadcasts, a flow\'s email step, the WhatsApp fallback, Inbox replies and mail customers send to businesses',
  'settings.integrations.usedBy.sendgrid': 'customer email when chosen as the active, backup or inbound provider under Email routing, and businesses\' own sending domains',
  'settings.integrations.usedBy.mailgun': 'customer email when chosen as the active, backup or inbound provider under Email routing, and businesses\' own sending domains',
  'settings.integrations.usedBy.postmark': 'customer email when chosen as the active, backup or inbound provider under Email routing, and businesses\' own sending domains (with the account token)',
  'settings.integrations.usedBy.aws_ses': 'the platform\'s own mail (sign-up verification, password resets, wallet alerts, a flow\'s Notify your team step), and customer email when Amazon SES is chosen under Email routing',
  'settings.integrations.usedBy.ussd_routing': 'choosing the USSD aggregator that answers every business\'s codes, and the token in the callback address given to it',
  'settings.integrations.usedBy.africastalking': 'USSD sessions when chosen under USSD routing; the keys check the account',
  'settings.integrations.email.title': 'Email',
  'settings.integrations.email.subtitle': 'Customer email through the provider chosen under Email routing',
  'settings.integrations.email.available': 'Available',
  'settings.integrations.email.unavailable': 'Not available',
  'settings.integrations.email.unavailableHint': 'Email cannot be sent: choose a provider under Email routing and switch on its row. Until then customers see no email options.',
  'settings.integrations.email.loadError': 'Could not load the email status.',
  'settings.integrations.email.active': 'Provider',
  'settings.integrations.email.backup': 'Backup provider',
  'settings.integrations.email.inbound': 'Inbound provider',
  'settings.integrations.email.domain': 'Sending domain',
  'settings.integrations.email.none': 'None',
  'settings.integrations.email.last24': 'Last 24 hours',
  'settings.integrations.email.noneSent': 'No email sent',
  'settings.integrations.email.statusCount': '{{status}}: {{count}}',
  'settings.integrations.email.lastWebhook': 'Last callback',
  'settings.integrations.email.webhook': '{{provider}}, {{when}}',
  'settings.integrations.email.webhookWithOutcome': '{{provider}}, {{when}} ({{outcome}})',
  'settings.integrations.email.noWebhook': 'None received yet',
  'settings.integrations.email.suppressions': 'Suppressed addresses',
  'settings.integrations.email.suppressionCount': '{{count}} in all, {{platform}} for every business',
  'settings.integrations.email.status.queued': 'Queued',
  'settings.integrations.email.status.sent': 'Sent',
  'settings.integrations.email.status.delivered': 'Delivered',
  'settings.integrations.email.status.bounced': 'Bounced',
  'settings.integrations.email.status.complained': 'Marked as spam',
  'settings.integrations.email.status.failed': 'Failed',
  'settings.integrations.email.status.refused': 'Refused',
  'settings.integrations.email.test.address': 'Test address',
  'settings.integrations.email.test.addressHint': 'Where the test email goes. The test is charged to nobody.',
  'settings.integrations.email.test.invalid': 'Enter an email address, such as you@example.co.za.',
  'settings.integrations.email.test.send': 'Send a test email',
  'settings.integrations.email.test.confirmTitle': 'Send a test email?',
  'settings.integrations.email.test.confirmBody': 'A real email goes to {{address}} through {{provider}}. The platform pays for it; no organisation is charged.',
  'settings.integrations.email.test.result': 'Result: {{status}}',
  'ussd.eyebrow': 'Messaging',
  'ussd.title': 'USSD',
  'ussd.description': 'Codes businesses apply for and answer with their flows: the switch, the shared code, the applications waiting, the codes given out and the last day\'s sessions.',
  'ussd.loadFailed': 'Could not load USSD.',
  'ussd.toast.failed': 'That did not work.',
  'ussd.copy': 'Copy',
  'ussd.copied': 'Copied',
  'ussd.master.on': 'USSD is on',
  'ussd.master.off': 'USSD is off',
  'ussd.master.badgeOn': 'On',
  'ussd.master.badgeOff': 'Off',
  'ussd.master.onHint': 'Businesses can apply for codes once the aggregator and the prices below are in place, and live codes answer.',
  'ussd.master.offHint': 'No business can apply for a code and no code answers: a dial ends at once with a polite message.',
  'ussd.master.changed': 'Last changed {{when}} by {{who}}',
  'ussd.master.switchOn': 'Switch USSD on',
  'ussd.master.switchOff': 'Switch USSD off',
  'ussd.master.confirmOnTitle': 'Switch USSD on?',
  'ussd.master.confirmOnBody': 'Businesses can apply for codes (once the aggregator and all five prices exist), and every live code starts answering with its business\'s flows.',
  'ussd.master.confirmOffTitle': 'Switch USSD off?',
  'ussd.master.confirmOffBody': 'Every code stops answering at once and no business can apply. Rentals are still charged on the 1st.',
  'ussd.master.switchedOn': 'USSD switched on.',
  'ussd.master.switchedOff': 'USSD switched off.',
  'ussd.ready.routing': 'Aggregator',
  'ussd.ready.routingOn': 'Set up: {{provider}}',
  'ussd.ready.routingOff': 'Not set up',
  'ussd.ready.integrations': 'Settings, Integrations',
  'ussd.ready.prices': 'Prices',
  'ussd.ready.pricedAt': '{{name}}: {{amount}}',
  'ussd.ready.notPriced': '{{name}}: no price',
  'ussd.ready.priceUnknown': '{{name}}: billing did not answer',
  'ussd.ready.pricesHint': 'Applications stay closed until every USSD price exists. Add the missing ones under',
  'ussd.ready.pricesUnknownHint': 'Billing did not answer, so whether the prices exist is not known. Check them under',
  'ussd.ready.skuRates': 'Billing, SKU rates',
  'ussd.ready.callback': 'Callback address',
  'ussd.ready.callbackHint': 'Give this to the aggregator as each code\'s callback URL, with the webhook token from USSD routing in place of <webhook token>.',
  'ussd.sku.ussd.session': 'Session',
  'ussd.sku.ussd.code.setup.extension': 'Extension set-up',
  'ussd.sku.ussd.code.setup.dedicated': 'Dedicated code set-up',
  'ussd.sku.ussd.code.month.extension': 'Extension rental',
  'ussd.sku.ussd.code.month.dedicated': 'Dedicated code rental',
  'ussd.settings.title': 'Codes, rentals and sessions',
  'ussd.settings.hint': 'The shared code extensions are given on, the terms businesses accept, what happens when a rental goes unpaid, and how long a session may run.',
  'ussd.settings.shared': 'Shared code',
  'ussd.settings.sharedHint': 'Dialled like *120*1234#. Extensions are given on it, such as *120*1234*56#. It cannot change while extensions on it are in use.',
  'ussd.settings.terms': 'Terms version',
  'ussd.settings.termsHint': 'The version of the USSD terms businesses accept when they apply. Up to 40 characters.',
  'ussd.settings.grace': 'Grace days',
  'ussd.settings.graceHint': 'Days an unpaid rental may run before the code is suspended (1 to 60).',
  'ussd.settings.release': 'Release after (days)',
  'ussd.settings.releaseHint': 'Days unpaid before the code is taken back for good. More than the grace days, at most 365.',
  'ussd.settings.limits': 'Session limits',
  'ussd.settings.maxChars': 'Characters on a screen',
  'ussd.settings.maxCharsHint': '60 to 182. Most networks show 160 to 182.',
  'ussd.settings.timeout': 'Time for a turn (ms)',
  'ussd.settings.timeoutHint': 'How long a flow may take to answer one screen, 1 000 to 8 000 milliseconds.',
  'ussd.settings.maxTurns': 'Screens in a session',
  'ussd.settings.maxTurnsHint': '2 to 100. The session ends politely after this many.',
  'ussd.settings.saved': 'USSD settings saved.',
  'ussd.kind.extension': 'Extension on the shared code',
  'ussd.kind.dedicated': 'Dedicated code',
  'ussd.col.organisation': 'Organisation',
  'ussd.col.status': 'Status',
  'ussd.applications.title': 'Applications',
  'ussd.applications.hint': 'Those waiting come first. Approving gives the code and takes the set-up fee held since the business applied, then charges the rest of this month. Rejecting gives the fee back and shows the business your reason.',
  'ussd.applications.empty': 'No applications yet',
  'ussd.applications.emptyHint': 'Applications appear here when a business applies for a code on its Channels page.',
  'ussd.applications.purpose': 'What it is for',
  'ussd.applications.preferred': 'Preferred digits: {{digits}}',
  'ussd.applications.fees': 'Fees',
  'ussd.applications.setup': 'Set-up {{amount}}',
  'ussd.applications.monthly': 'Then {{amount}} a month',
  'ussd.applications.held': 'Fee held',
  'ussd.applications.status.submitted': 'Waiting',
  'ussd.applications.status.approved': 'Approved',
  'ussd.applications.status.rejected': 'Rejected',
  'ussd.applications.status.withdrawn': 'Withdrawn',
  'ussd.applications.extension': 'Extension to give',
  'ussd.applications.extensionPlaceholder': 'Digits after {{shared}}',
  'ussd.applications.extensionHint': 'Up to six digits, not starting with 0.',
  'ussd.applications.code': 'Code to give',
  'ussd.applications.codeHint': 'As it is dialled, such as *120*5678#.',
  'ussd.applications.approve': 'Approve',
  'ussd.applications.approved': 'Approved: {{code}} is live.',
  'ussd.applications.approvedUnpaid': 'Approved: {{code}} is live, but this month\'s rental could not be charged yet.',
  'ussd.applications.reject': 'Reject',
  'ussd.applications.rejectTitle': 'Reject this application?',
  'ussd.applications.rejectBody': 'The held set-up fee goes back to the business, and they are shown your reason.',
  'ussd.applications.reason': 'Reason',
  'ussd.applications.reasonHint': 'In words the business will read, such as what to change before applying again.',
  'ussd.applications.rejected': 'Application rejected. The held fee was given back.',
  'ussd.codes.title': 'Codes',
  'ussd.codes.hint': 'Every code given out. Suspending stops a code answering but its rental is still charged; releasing takes it back for good.',
  'ussd.codes.empty': 'No codes given out yet',
  'ussd.codes.emptyHint': 'A code appears here when an application is approved.',
  'ussd.codes.code': 'Code',
  'ussd.codes.rental': 'Next rental',
  'ussd.codes.status.live': 'Live',
  'ussd.codes.status.suspended': 'Suspended',
  'ussd.codes.status.released': 'Released',
  'ussd.codes.overdue': 'Rental unpaid since {{when}}',
  'ussd.codes.approvedOn': 'Live since {{when}}',
  'ussd.codes.releasedOn': 'Released on {{when}}',
  'ussd.codes.suspend': 'Suspend',
  'ussd.codes.suspendTitle': 'Suspend {{code}}?',
  'ussd.codes.suspendBody': 'The code stops answering at once. Its rental is still charged until it is resumed or released.',
  'ussd.codes.suspendReason': 'Reason (optional)',
  'ussd.codes.suspendReasonHint': 'Shown to the business. Up to 200 characters.',
  'ussd.codes.suspended': '{{code}} suspended.',
  'ussd.codes.resume': 'Resume',
  'ussd.codes.resumed': '{{code}} answers again.',
  'ussd.codes.release': 'Release',
  'ussd.codes.releaseTitle': 'Release {{code}}?',
  'ussd.codes.releaseBody': '{{code}} stops answering for good and is no longer charged. This cannot be undone; the business would have to apply again.',
  'ussd.codes.released': '{{code}} released.',
  'ussd.sessions.title': 'The last day\'s sessions',
  'ussd.sessions.hint': 'Sessions started in the last 24 hours: how they ended, what they were charged and the busiest codes.',
  'ussd.sessions.count': 'Sessions',
  'ussd.sessions.people': 'People',
  'ussd.sessions.turns': 'Screens',
  'ussd.sessions.charged': 'Charged',
  'ussd.sessions.byState': 'By state',
  'ussd.sessions.byOutcome': 'How they ended',
  'ussd.sessions.byCode': 'Busiest codes',
  'ussd.sessions.lastWebhook': 'Last callback',
  'ussd.sessions.webhook': '{{provider}}, {{when}}',
  'ussd.sessions.noWebhook': 'None received yet',
  'ussd.sessions.none': 'None',
  'ussd.sessions.countOf': '{{what}}: {{count}}',
  'ussd.sessions.noOutcome': 'Still open',
  'ussd.sessions.unknownCode': 'A code no longer listed',
  'ussd.sessions.state.active': 'Active',
  'ussd.sessions.state.ended': 'Ended',
  'ussd.sessions.state.refused': 'Refused',
  'ussd.sessions.state.expired': 'Expired',
  'ussd.sessions.outcome.waiting_input': 'Waiting for an answer',
  'ussd.sessions.outcome.done': 'Finished',
  'ussd.sessions.outcome.no_flow': 'No flow answers',
  'ussd.sessions.outcome.timeout': 'Too slow',
  'ussd.sessions.outcome.error': 'Error',
  'ussd.sessions.outcome.no_credit': 'No credit',
  'ussd.sessions.outcome.suspended': 'Code suspended',
  'ussd.sessions.outcome.unpriced': 'No price',
  'ussd.sessions.outcome.billing': 'Billing did not answer',
  'ussd.sessions.outcome.max_turns': 'Too many screens',
  'broadcasts.channel.whatsapp': 'WhatsApp',
  'broadcasts.channel.sms': 'SMS',
  'broadcasts.channel.email': 'Email',
  'broadcasts.columns.broadcast': 'Broadcast',
  'broadcasts.columns.delivered': 'Delivered',
  'broadcasts.columns.read': 'Read',
  'broadcasts.cell.channelThrottle': '{{channel}} · {{tps}} per second',
  'broadcasts.cell.delivered': '{{count}} ({{pct}}%)',
  'broadcasts.cell.read': '{{count}} read ({{pct}}%)',
  'broadcasts.drawer.estimate': 'Quoted cost',
  'broadcasts.drawer.estimateValue': '{{amount}} credits',
  'broadcasts.drawer.failureCode': 'Failed as a whole',
  'broadcasts.filters.channel': 'Channel',
  'broadcasts.filters.channelPlaceholder': 'Any channel',
  'broadcasts.progressDoneLabel': '{{sent}} of {{total}} sent, {{pct}}% done',
  'billing.plans.limit.own_whatsapp_account': 'Own WhatsApp account (business pays Meta)',
  'billing.plans.edit.ownAccountHint': 'On: the business may connect its own WhatsApp account and Meta bills it directly (saved with no limit). Off: not included, saved as 0.',
  'gateways.numbers.columns.metaBilledTo': 'Meta billed to',
  'gateways.numbers.metaBilledToHint': 'Who Meta charges for this number\'s messages. Our account: the shared account, which we pay and charge back in credits. The business: its own WhatsApp account, billed by Meta directly.',
  'gateways.numbers.payer.platform': 'Our account',
  'gateways.numbers.payer.customer': 'The business',
  'gateways.numbers.payer.unknown': 'Not recorded',
  'gateways.numbers.filters.anyPayer': 'Either account',
  'gateways.numbers.columns.nameStatus': 'Display name review',
  'gateways.numbers.nameStatus.APPROVED': 'Approved',
  'gateways.numbers.nameStatus.AVAILABLE_WITHOUT_REVIEW': 'Usable without review',
  'gateways.numbers.nameStatus.PENDING_REVIEW': 'Waiting for Meta',
  'gateways.numbers.nameStatus.DECLINED': 'Declined',
  'gateways.numbers.nameStatus.EXPIRED': 'Expired',
  'gateways.numbers.nameStatus.NONE': 'Not submitted',
  'gateways.numbers.nameStatus.unchecked': 'Not checked',
  'gateways.numbers.nameRejection': 'Reason: {{reason}}',
  'gateways.numbers.columns.nameRequest': 'New name asked for',
  'gateways.numbers.nameRequest.byWhen': '{{who}}, {{when}}',
  'gateways.numbers.nameRequest.when': 'Asked {{when}}',
  'gateways.numbers.nameRequest.list': 'New name asked for: {{name}}',
  'gateways.numbers.openWhatsAppManager': 'Open WhatsApp Manager',
  'gateways.numbers.nameRequests.filter': 'Name requests',
  'gateways.numbers.nameRequests.openOnly': 'Waiting to be made',
  'gateways.numbers.nameRequests.bannerTitle': 'Display names to change: {{count}}',
  'gateways.numbers.nameRequests.bannerBody': 'Businesses on our shared account asked for a new display name. Make each change in WhatsApp Manager; Meta then reviews it and the review status here follows.',
  'gateways.numbers.nameRequests.show': 'Show them',
  'gateways.numbers.nameRequests.showAll': 'Show all numbers',
  'gateways.cards.numbers.nameRequests': 'Name requests: {{count}}',
  'gateways.cards.numbers.ownAccounts': 'Own accounts: {{count}}',
  'billing.plans.edit.moduleLimits': 'Bookings and tickets',
  'billing.plans.limit.booking_staff': 'Bookable staff',
  'billing.plans.limit.booking_services': 'Bookable services',
  'billing.plans.limit.ticket_types': 'Ticket types',
  'billing.plans.limit.ticket_automations': 'Ticket automations',
  'billing.plans.limit.calendar_sync': 'Calendar sync for bookings',
  'billing.plans.edit.calendarSyncHint': 'On: bookings may sync with an outside calendar (saved with no limit). Off: not included, saved as 0.',
  'settings.integrations.category.notifications': 'NOTIFICATIONS',
  'settings.integrations.category.calendar': 'CALENDAR',
  'billing.renewal.title': 'When a plan\'s card is declined',
  'billing.renewal.body': 'Paid plans are charged to the business\'s card on the 1st. These settings decide what happens when that charge fails, and when the business is told a charge is coming. They apply to every business.',
  'billing.renewal.saved': 'Saved. The next renewal run uses the new settings.',
  'billing.renewal.grace_days.label': 'Grace period (days)',
  'billing.renewal.grace_days.hint': 'Days after the 1st a plan stays on with its card declined, before it ends and the business moves to the free plan. From {{min}} to {{max}}.',
  'billing.renewal.retry_hours.label': 'Try the card again every (hours)',
  'billing.renewal.retry_hours.hint': 'Hours between charges on a declined card. A new card is tried within the hour. From {{min}} to {{max}}.',
  'billing.renewal.reminder_days.label': 'Remind the business every (days)',
  'billing.renewal.reminder_days.hint': 'Days between emails saying the card was declined, until it is paid or the grace runs out. From {{min}} to {{max}}.',
  'billing.renewal.notice_days.label': 'Notice before the 1st (days)',
  'billing.renewal.notice_days.hint': 'Days before the 1st the business is told its card will be charged, or that it needs one. 0 sends no notice. From {{min}} to {{max}}.',
  'billing.subscriptions.flag.house': 'House account',
  'billing.subscriptions.flag.noCard': 'No card',
  'billing.subscriptions.flag.pending': 'To {{plan}} on {{date}}',
  'billing.subscriptions.flag.ends': 'Ends {{date}}',
  'tenants.view.wallet.house.label': 'House account',
  'tenants.view.wallet.house.hint': 'The platform\'s own business. Marked in the lists, but its plan is charged to its card like any other, so it serves as a live test.',
  'tenants.view.wallet.house.on': 'Marked as the house account.',
  'tenants.view.wallet.house.off': 'No longer the house account.',
  'nav.item.billingAiModels': 'AI models',
  'shell.breadcrumb.aiModels': 'AI models',
  'billing.overview.cards.aiModels.title': 'AI models',
  'billing.overview.cards.aiModels.hint': 'Choose which AI models businesses may use, and test each one first.',
  'billing.skuRates.unit.1k_chars': 'per 1 000 characters',
  'billing.skuRates.unit.image': 'per picture',
  'billing.aiModels.eyebrow': 'Billing',
  'billing.aiModels.title': 'AI models',
  'billing.aiModels.description': 'The models businesses may choose in the AI API and on the AI tools page, each charged to their credits at their plan\'s rate. Switch one on only after Test has had it answer.',
  'billing.aiModels.openPrices': 'Prices in SKU rates',
  'billing.aiModels.stat.on': 'Models on',
  'billing.aiModels.stat.onHint': 'Of {{total}}. Businesses see only the ones that are on.',
  'billing.aiModels.stat.calls': 'Calls, last 30 days',
  'billing.aiModels.stat.callsHint': 'Calls that finished and were charged, across every model.',
  'billing.aiModels.stat.charged': 'Charged, last 30 days',
  'billing.aiModels.stat.chargedHint': 'To businesses\' credits, at their plans\' rates.',
  'billing.aiModels.stat.outside': 'On outside South Africa',
  'billing.aiModels.stat.outsideOnHint': 'Prompts and pictures sent to these are processed outside South Africa.',
  'billing.aiModels.stat.outsideHint': 'None on. {{count}} marked as running outside South Africa.',
  'billing.aiModels.ratesError': 'The price book could not be read, so prices are not shown.',
  'billing.aiModels.showingCount': '{{on}} of {{total}} on',
  'billing.aiModels.empty': 'No AI models yet',
  'billing.aiModels.emptyHint': 'The models come from the flows-llm migration of 14 October 2026. An empty list means that migration has not run on this server.',
  'billing.aiModels.denied': 'Your account does not have permission to see the AI models.',
  'billing.aiModels.loadError': 'Failed to load the AI models.',
  'billing.aiModels.kindCount': '{{on}} of {{total}} on',
  'billing.aiModels.kind.text': 'Text',
  'billing.aiModels.kind.speech': 'Speech',
  'billing.aiModels.kind.transcribe': 'Transcription',
  'billing.aiModels.kind.image': 'Pictures',
  'billing.aiModels.kindHint.text': 'Write, summarise and answer, and some read pictures. Test calls the model once before you switch it on.',
  'billing.aiModels.kindHint.speech': 'Turn text into speech with Amazon Polly. Test reads a short sentence aloud; the platform pays the few characters.',
  'billing.aiModels.kindHint.transcribe': 'Turn a recording into text with Amazon Transcribe. Needs an S3 bucket in its region, set under Edit. Test stores, transcribes and deletes a two-second recording.',
  'billing.aiModels.kindHint.image': 'Make pictures from a description. Test makes one picture, which the platform pays for, so it asks first.',
  'billing.aiModels.pricesNote': 'Prices are the Free plan\'s list price per unit, from the price book. Every other plan pays its own rate, never under cost. Change them under Billing → SKU rates.',
  'billing.aiModels.columns.model': 'Model',
  'billing.aiModels.columns.provider': 'Provider',
  'billing.aiModels.columns.runsOn': 'Runs on',
  'billing.aiModels.columns.price': 'Price (Free plan)',
  'billing.aiModels.columns.usage': 'Last 30 days',
  'billing.aiModels.columns.offered': 'Offered',
  'billing.aiModels.flag.pictures': 'Reads pictures',
  'billing.aiModels.flag.outside': 'Outside South Africa',
  'billing.aiModels.modelId.platform': 'The platform\'s Bedrock text model',
  'billing.aiModels.region.platform': 'The platform\'s region',
  'billing.aiModels.price.readWritten': '{{read}} read / {{written}} written {{unit}}',
  'billing.aiModels.price.readWrittenUnits': '{{read}} read {{readUnit}} / {{written}} written {{writtenUnit}}',
  'billing.aiModels.price.readOnly': '{{read}} read {{unit}}',
  'billing.aiModels.price.writtenOnly': '{{written}} written {{unit}}',
  'billing.aiModels.price.unit': '{{price}} {{unit}}',
  'billing.aiModels.price.none': 'No price set',
  'billing.aiModels.price.missing': 'No rate in force for {{skus}}',
  'billing.aiModels.price.costTitle': 'The Free plan\'s list price; every other plan pays its own rate. Cost to the platform: {{cost}}.',
  'billing.aiModels.usage.calls': '{{count}} calls',
  'billing.aiModels.usage.callsCharged': '{{count}} calls · {{charged}} charged',
  'billing.aiModels.state.on': 'On',
  'billing.aiModels.state.off': 'Off',
  'billing.aiModels.switchLabel': 'Offer {{name}} to businesses',
  'billing.aiModels.outcome.ok': 'Last test: answered in {{ms}} ms',
  'billing.aiModels.outcome.stub': 'Last test: stand-in only',
  'billing.aiModels.outcome.failed': 'Last test: no answer',
  'billing.aiModels.toast.on': '{{name}} is on. Businesses can choose it now.',
  'billing.aiModels.toast.off': '{{name}} is off. Businesses can no longer choose it.',
  'billing.aiModels.toast.failed': 'Could not switch {{name}}',
  'billing.aiModels.on.title': 'Switch on {{name}}?',
  'billing.aiModels.on.body': 'Businesses can choose {{name}} ({{code}}) in the AI API and on the AI tools page from now on. Every call is charged to their credits at their plan\'s rate.',
  'billing.aiModels.on.outsideTitle': 'Runs outside South Africa',
  'billing.aiModels.on.outsideBody': '{{name}} runs in {{region}}. Every prompt and picture a business sends to it will be processed outside South Africa. Switch it on only once the data processing agreement says so.',
  'billing.aiModels.on.outsideBodyNoRegion': '{{name}} is marked as running outside South Africa. Every prompt and picture a business sends to it will be processed outside South Africa. Switch it on only once the data processing agreement says so.',
  'billing.aiModels.on.unpriced': 'This model names no SKU in the price book, so its calls cannot be charged.',
  'billing.aiModels.on.noPrice': 'The price book has no rate in force for {{skus}}, so its calls cannot be charged. Add the rate under Billing → SKU rates first.',
  'billing.aiModels.on.untested': 'You have not tested it in this visit. Run Test first: a model that does not answer gives every business that chooses it an error.',
  'billing.aiModels.on.testFailed': 'The last test did not answer ({{error}}). Businesses that choose it will get errors until it does.',
  'billing.aiModels.on.testedStub': 'The last test answered with the stand-in, because Bedrock is not configured here, so the model itself has not been proven.',
  'billing.aiModels.on.tested': 'It answered the test in {{ms}} ms.',
  'billing.aiModels.on.cannotTest': 'This kind of model cannot be tested here yet, so check the model id and region by hand first.',
  'billing.aiModels.on.confirm': 'Switch on',
  'billing.aiModels.on.confirmOutside': 'Switch on outside South Africa',
  'billing.aiModels.off.title': 'Switch off {{name}}?',
  'billing.aiModels.off.body': 'Businesses can no longer choose {{name}}. Every call that names it ({{code}}) in the AI API, a saved skill or the AI tools page is refused with "model unknown" until it is switched on again.',
  'billing.aiModels.off.usage': 'It answered {{count}} calls in the last 30 days, {{charged}} charged to businesses\' credits.',
  'billing.aiModels.off.confirm': 'Switch off',
  'billing.aiModels.test.actionFor': 'Test {{name}}',
  'billing.aiModels.test.title': 'Test {{name}}',
  'billing.aiModels.test.running': 'Asking {{name}} to reply with one word…',
  'billing.aiModels.test.couldNotRun': 'The test could not run',
  'billing.aiModels.test.answered': '{{name}} answered',
  'billing.aiModels.test.answeredBody': 'The model id, the region and the AWS account\'s access all work. The platform paid for these few tokens, not a business.',
  'billing.aiModels.test.stubTitle': 'Stand-in answer',
  'billing.aiModels.test.stubBody': 'Bedrock is not configured here, so the platform\'s stand-in answered instead of {{name}}. The model itself was not called, so this proves nothing about it.',
  'billing.aiModels.test.failed': '{{name}} did not answer',
  'billing.aiModels.test.failedHint': 'Check the model id and region, and that the AWS account has been given access to this model in Bedrock.',
  'billing.aiModels.test.answer': 'Answer',
  'billing.aiModels.test.emptyAnswer': 'It answered with nothing.',
  'billing.aiModels.test.time': 'Time',
  'billing.aiModels.test.ms': '{{ms}} ms',
  'billing.aiModels.test.read': 'Read',
  'billing.aiModels.test.written': 'Written',
  'billing.aiModels.test.tokens': '{{count}} tokens',
  'billing.aiModels.test.modelId': 'Model id',
  'billing.aiModels.test.again': 'Test again',
  'billing.aiModels.test.switchOn': 'Switch on',
  'billing.aiModels.edit.actionFor': 'Edit {{name}}',
  'billing.aiModels.edit.title': 'Edit {{name}}',
  'billing.aiModels.edit.intro': '{{kind}} model. Businesses name it {{code}} in the AI API. Its price is in the price book, under Billing → SKU rates.',
  'billing.aiModels.edit.name': 'Name',
  'billing.aiModels.edit.nameHint': 'What businesses see in the model list. 1 to 80 characters.',
  'billing.aiModels.edit.nameInvalid': 'The name is 1 to 80 characters.',
  'billing.aiModels.edit.description': 'Description',
  'billing.aiModels.edit.descriptionHint': 'One line on what the model is good at, shown to businesses beside its name. Leave empty for none.',
  'billing.aiModels.edit.modelId': 'Model id',
  'billing.aiModels.edit.modelIdHint': 'The Bedrock model id or inference profile, such as amazon.nova-lite-v1:0. Leave empty to use the platform\'s Bedrock text model.',
  'billing.aiModels.edit.modelIdHintOther': 'What the AWS service is asked for: a Bedrock model id, or the Polly or Transcribe engine (neural or standard).',
  'billing.aiModels.edit.region': 'Region',
  'billing.aiModels.edit.regionHint': 'The AWS region it runs in, such as af-south-1 (Cape Town) or us-east-1. Leave empty for the platform\'s Bedrock region.',
  'billing.aiModels.edit.regionInvalid': 'An AWS region code, such as af-south-1 or us-east-1.',
  'billing.aiModels.edit.maxOutput': 'Longest answer (tokens)',
  'billing.aiModels.edit.maxOutputHint': 'The most one answer may run to, {{min}} to {{max}} tokens. A business asking for more gets this much, and credit for this much is held before each call.',
  'billing.aiModels.edit.maxInvalid': 'A whole number from {{min}} to {{max}}.',
  'billing.aiModels.edit.acceptsImages': 'Reads pictures',
  'billing.aiModels.edit.acceptsImagesHint': 'Businesses may send pictures with a prompt. Switch on only for a model that reads them.',
  'billing.aiModels.edit.outside': 'Runs outside South Africa',
  'billing.aiModels.edit.outsideHint': 'Businesses are told before they use it, and switching it on asks you to confirm the data processing agreement covers it.',
  'billing.aiModels.edit.sortOrder': 'Order',
  'billing.aiModels.edit.sortOrderHint': 'Lower numbers are listed first, here and for businesses.',
  'billing.aiModels.edit.sortInvalid': 'A whole number.',
  'billing.aiModels.edit.saved': '{{name}} saved.',
  'billing.aiModels.edit.failed': 'Not saved',
  'billing.aiModels.edit.confirm.title': 'Save the changes to {{name}}?',
  'billing.aiModels.edit.confirm.moves': '{{name}} is on, so every business using it moves to the new model id or region from its next call. Run Test after saving to check it still answers.',
  'billing.aiModels.edit.confirm.leaves': '{{name}} is on. Marking it as running outside South Africa tells every business that their prompts and pictures are processed there, which the data processing agreement must already say. Switch it off first if it does not.',
  'billing.aiModels.edit.confirm.submit': 'Save changes',
  'billing.aiModels.on.noBucket': 'Speech to text needs its storage bucket before it can be switched on. Set it under Edit first; the service refuses otherwise.',
  'billing.aiModels.test.costsTitle': 'This test costs the platform a picture',
  'billing.aiModels.test.costsBody': 'It makes one picture, at the platform\'s cost of about {{cost}}. No business is charged.',
  'billing.aiModels.test.costsBodyUnpriced': 'It makes one picture, which the platform pays for. No business is charged.',
  'billing.aiModels.test.runPaid': 'Make one picture',
  'billing.aiModels.test.running.speech': 'Asking {{name}} to read a short sentence aloud…',
  'billing.aiModels.test.running.transcribe': 'Storing and transcribing a two-second recording with {{name}}. This can take up to a minute…',
  'billing.aiModels.test.running.image': 'Asking {{name}} for one picture…',
  'billing.aiModels.test.answeredBodyOther': 'The engine, the region and the AWS account\'s access all work. The platform paid for this test, not a business.',
  'billing.aiModels.test.failedHint.speech': 'Check the engine (neural or standard) and the region, and that the AWS account may use Polly there (polly:SynthesizeSpeech and polly:DescribeVoices).',
  'billing.aiModels.test.failedHint.transcribe': 'Check the region and the storage bucket, and that the AWS account may use Transcribe there and read, write and delete in the bucket.',
  'billing.aiModels.test.failedHint.image': 'Check the model id and region, and that the AWS account has been given access to this model in Bedrock.',
  'billing.aiModels.test.characters': 'Characters',
  'billing.aiModels.test.bucket': 'Storage bucket',
  'billing.aiModels.edit.bucket': 'Storage bucket',
  'billing.aiModels.edit.bucketHint': 'The S3 bucket, in the same region, where recordings wait while they are transcribed. Each one is deleted as soon as its text is back.',
  'billing.aiModels.edit.bucketInvalid': 'A bucket name is 3 to 63 lower-case letters, digits, dots and hyphens.',
  'tenants.view.credit.title': 'Add credit',
  'tenants.view.credit.open': 'Add credit',
  'tenants.view.credit.intro': '{{name}} has {{balance}}. One rand is one credit.',
  'tenants.view.credit.kind': 'What is it?',
  'tenants.view.credit.kind.payment': 'A payment they made',
  'tenants.view.credit.kind.gift': 'Free credit',
  'tenants.view.credit.kind.correction': 'A correction',
  'tenants.view.credit.kindHint.payment': 'Money they paid outside the platform, such as an EFT on a manual invoice. It is added as a top-up and they get the usual receipt.',
  'tenants.view.credit.kindHint.gift': 'Credit you give them, such as goodwill or a promise kept. Nothing was paid, so there is no receipt.',
  'tenants.view.credit.kindHint.correction': 'Takes credit back, such as credit added by mistake. Never more than they have free.',
  'tenants.view.credit.amount': 'Amount (R)',
  'tenants.view.credit.amountPaid': 'Amount paid (R)',
  'tenants.view.credit.amountTake': 'Amount to take back (R)',
  'tenants.view.credit.amountHint': 'In rand, with cents if needed. Prices include VAT, so a payment adds the full amount paid.',
  'tenants.view.credit.amountInvalid': 'Enter R1 to R1 000 000.',
  'tenants.view.credit.moreThanBalance': 'They only have {{balance}}.',
  'tenants.view.credit.method': 'How they paid',
  'tenants.view.credit.method.eft': 'EFT (bank transfer)',
  'tenants.view.credit.method.cash': 'Cash',
  'tenants.view.credit.method.other': 'Other',
  'tenants.view.credit.reference': 'Invoice number or payment reference',
  'tenants.view.credit.referenceHint': 'Shown on their ledger and receipt, so the payment can be matched to the bank line.',
  'tenants.view.credit.referenceInvalid': 'At least 2 characters.',
  'tenants.view.credit.receivedOn': 'Day the money arrived',
  'tenants.view.credit.dateFuture': 'That day has not come yet.',
  'tenants.view.credit.receipt': 'Email them the receipt',
  'tenants.view.credit.receiptHint': 'The same receipt a card top-up sends. Switch it off if you have sent one yourself.',
  'tenants.view.credit.reason': 'Reason',
  'tenants.view.credit.reasonHint.gift': 'They see it on their ledger, e.g. Welcome credit or Sorry for the outage on 12 October.',
  'tenants.view.credit.reasonHint.correction': 'They see it on their ledger, e.g. Credit added twice on 14 October.',
  'tenants.view.credit.reasonInvalid': 'At least 3 characters.',
  'tenants.view.credit.after': 'Their balance goes from {{before}} to {{after}}.',
  'tenants.view.credit.failed': 'The credit was not added',
  'tenants.view.credit.save.payment': 'Record payment',
  'tenants.view.credit.save.gift': 'Add credit',
  'tenants.view.credit.save.correction': 'Take credit back',
  'tenants.view.credit.done.payment': '{{amount}} recorded for {{name}}. Balance now {{balance}}.',
  'tenants.view.credit.done.gift': '{{amount}} added for {{name}}. Balance now {{balance}}.',
  'tenants.view.credit.done.correction': '{{amount}} taken back from {{name}}. Balance now {{balance}}.',
  'tenants.view.credit.done.receipt': 'Their receipt is on its way.',
  'tenants.view.credit.confirm.title.payment': 'Record a payment for {{name}}?',
  'tenants.view.credit.confirm.title.gift': 'Give {{name}} free credit?',
  'tenants.view.credit.confirm.title.correction': 'Take credit back from {{name}}?',
  'tenants.view.credit.confirm.payment': '{{amount}} paid by {{name}}, reference {{reference}}, is added as a top-up.',
  'tenants.view.credit.confirm.gift': '{{amount}} of free credit is added for {{name}}.',
  'tenants.view.credit.confirm.correction': '{{amount}} is taken from {{name}}\'s credit.',
  'tenants.view.credit.confirm.balance': 'Balance: {{before}} → {{after}}',
  'tenants.view.credit.confirm.receipt': 'They are emailed the receipt.',
  'tenants.view.credit.confirm.ledger': 'Their ledger says: {{reason}}',
  'tenants.view.credit.confirm.submit.payment': 'Record payment',
  'tenants.view.credit.confirm.submit.gift': 'Add credit',
  'tenants.view.credit.confirm.submit.correction': 'Take it back',
  'tenants.view.billing.kind.topup': 'Top-up',
  'tenants.view.billing.kind.grant': 'Free credit',
  'tenants.view.billing.kind.adjustment': 'Correction',
  'tenants.view.billing.kind.plan': 'Plan',
  'tenants.view.billing.kind.refund': 'Refund',
  'tenants.view.billing.kind.debit': 'Charge',
  'tenants.view.invoicePlan.title': 'Plan',
  'tenants.view.invoicePlan.badge': 'Pays by invoice',
  'tenants.view.invoicePlan.loadError': 'The plan could not be loaded. Refresh the page to try again.',
  'tenants.view.invoicePlan.switch': 'Pays by invoice',
  'tenants.view.invoicePlan.switchHint': 'Their plan fee is taken from the credit you add when their payment arrives (Add credit, a payment they made): on the 1st, and for an upgrade at once. Only you change their plan, here. Off: they pay for their plan by card and change it themselves.',
  'tenants.view.invoicePlan.switchedOn': '{{name}} now pays by invoice.',
  'tenants.view.invoicePlan.switchedOff': '{{name}} now pays for its plan by card.',
  'tenants.view.invoicePlan.state.none': 'No paid plan: they are on the free plan.',
  'tenants.view.invoicePlan.state.pastDue': '{{plan}}, waiting for payment since {{date}}. Add their payment with Add credit and it renews within the hour.',
  'tenants.view.invoicePlan.state.ends': '{{plan}}, ending on {{date}}.',
  'tenants.view.invoicePlan.state.changes': '{{plan}}, changing to {{next}} on {{date}}.',
  'tenants.view.invoicePlan.state.renews': '{{plan}}, renewing on {{date}}.',
  'tenants.view.invoicePlan.available': 'Bought credit a plan fee can come from: {{amount}}',
  'tenants.view.invoicePlan.choose': 'New plan',
  'tenants.view.invoicePlan.choosePlaceholder': 'Choose a plan',
  'tenants.view.invoicePlan.perMonth': '{{amount}} a month',
  'tenants.view.invoicePlan.free': 'free',
  'tenants.view.invoicePlan.credits': '{{count}} credits',
  'tenants.view.invoicePlan.change': 'Change plan',
  'tenants.view.invoicePlan.short': '{{amount}} is needed now and their bought credit is {{available}}. Add their payment with Add credit first.',
  'tenants.view.invoicePlan.quote.start': '{{plan}} starts now. {{amount}} for the rest of this month is taken from their credit and {{credits}} of plan credit is added. After that the fee is taken on the 1st of each month.',
  'tenants.view.invoicePlan.quote.upgrade': 'They move to {{plan}} now. {{amount}} for the rest of this month is taken from their credit, and {{credits}} of plan credit is added.',
  'tenants.view.invoicePlan.quote.downgrade': 'They move to {{plan}} on {{date}}. Until then they keep {{current}}, which is paid for.',
  'tenants.view.invoicePlan.quote.cancel': '{{current}} runs until {{date}}, then they are on {{plan}}. Nothing more is taken.',
  'tenants.view.invoicePlan.quote.keep': 'They stay on {{plan}}, and any booked change or cancellation is withdrawn.',
  'tenants.view.invoicePlan.quote.end_now': 'Their unpaid {{current}} plan ends now, and they are on {{plan}}.',
  'tenants.view.invoicePlan.quote.past_due': 'Their {{current}} plan is waiting for {{amount}}. Add their payment with Add credit and it renews within the hour, or choose the free plan to end it now.',
  'tenants.view.invoicePlan.quote.none': 'They are already on {{plan}}.',
  'tenants.view.invoicePlan.confirm.title': 'Move {{name}} to {{plan}}?',
  'tenants.view.invoicePlan.confirm.credit': 'Their bought credit before this: {{available}}.',
  'tenants.view.invoicePlan.confirm.email': 'Everyone on their team is told by email.',
  'tenants.view.invoicePlan.confirm.submit': 'Change plan',
  'tenants.view.invoicePlan.done.start': '{{plan}} has started.',
  'tenants.view.invoicePlan.done.upgrade': 'They are now on {{plan}}.',
  'tenants.view.invoicePlan.done.downgrade': '{{plan}} is booked for the 1st.',
  'tenants.view.invoicePlan.done.cancel': '{{current}} ends at the end of its month.',
  'tenants.view.invoicePlan.done.keep': 'They stay on {{plan}}.',
  'tenants.view.invoicePlan.done.end_now': 'Their unpaid plan has ended.',
  'tenants.view.invoicePlan.done.none': 'Nothing changed.',
  'tenants.view.invoicePlan.done.past_due': 'Nothing changed: the plan is waiting for payment.',
  'billing.subscriptions.flag.invoice': 'Pays by invoice',
  'billing.aiModels.edit.context': 'Context window (tokens)',
  'billing.aiModels.edit.contextHint': 'How much the model can read at once. Suggest flows with AI keeps each call under half of it. 0 means not known, read as 200 000.',
  'billing.aiModels.edit.contextInvalid': 'Use 0, or {{min}} to {{max}} tokens.',
  'billing.extraUse.title': 'Extra use (Growth+ and Scale+)',
  'billing.extraUse.body': 'A business on a plan that includes extra use can keep going after its credit runs out, charged to its saved card at its plan\'s prices, up to a monthly limit it sets. These settings apply to every business.',
  'billing.extraUse.saved': 'Saved. Businesses choose within the new limits straight away.',
  'billing.extraUse.offered.label': 'Offer extra use',
  'billing.extraUse.offered.hint': 'Off: no business can switch it on, and every business using it pauses. What is already owed is still charged.',
  'billing.extraUse.max_limit_minor.label': 'Highest monthly limit (R)',
  'billing.extraUse.max_limit_minor.hint': 'The most one business can owe in a month, and the highest limit a business may set. At least {{min}}.',
  'billing.extraUse.min_limit_minor.label': 'Lowest monthly limit (R)',
  'billing.extraUse.min_limit_minor.hint': 'The lowest monthly limit a business may set. At least {{min}}, and no more than the highest limit.',
  'billing.extraUse.min_step_minor.label': 'Smallest step or top-up (R)',
  'billing.extraUse.min_step_minor.hint': 'The least a business may be charged at a time, in steps or as an upfront top-up. At least {{min}}, and no more than the highest limit.',
  'billing.extraUse.invalid': 'Use {{min}} to {{max}}.',
  'billing.subscriptions.flag.extraUse': 'Extra use (+)',
  'billing.plans.limit.extra_use': 'Extra use (keep going after the credit runs out)',
  'billing.plans.edit.extraUseHint': 'On: a business on this plan with a saved card may keep going after its credit runs out, at the plan\'s prices, up to a monthly limit it sets (saved with no limit). Off: not included, saved as 0.',
  'tenants.view.extraUse.title': 'Extra use',
  'tenants.view.extraUse.loadError': 'Could not load extra use for this business.',
  'tenants.view.extraUse.badge.active': 'Active',
  'tenants.view.extraUse.badge.paused': 'Paused',
  'tenants.view.extraUse.badge.off': 'Off',
  'tenants.view.extraUse.badge.blocked': 'Blocked',
  'tenants.view.extraUse.onPlan': 'Included in {{plan}}',
  'tenants.view.extraUse.notOnPlan': 'Not included in {{plan}}',
  'tenants.view.extraUse.card': 'Card: {{card}}',
  'tenants.view.extraUse.noCard': 'No card saved',
  'tenants.view.extraUse.off': 'Not switched on.',
  'tenants.view.extraUse.settings': '{{how}}, up to {{limit}} a month.',
  'tenants.view.extraUse.mode.steps': 'In steps of {{amount}}',
  'tenants.view.extraUse.mode.upfront': 'Upfront, {{amount}} of credit when the balance drops under {{below}}',
  'tenants.view.extraUse.mode.month_end': 'On the 1st, with the plan',
  'tenants.view.extraUse.month': '{{month}}: used {{used}} of {{limit}}; owed {{owed}}; charged {{charged}}.',
  'tenants.view.extraUse.paused': 'Paused since {{date}}: {{reason}}.',
  'tenants.view.extraUse.reason.no_card': 'no card saved',
  'tenants.view.extraUse.reason.declined': 'a charge was declined',
  'tenants.view.extraUse.reason.past_due': 'the plan is past due',
  'tenants.view.extraUse.reason.blocked': 'blocked by the platform',
  'tenants.view.extraUse.reason.not_on_plan': 'the plan does not include it',
  'tenants.view.extraUse.reason.not_offered': 'extra use is not offered',
  'tenants.view.extraUse.declineReason': 'The card provider said: {{reason}}',
  'tenants.view.extraUse.blockedReason': 'Blocked because: {{reason}}',
  'tenants.view.extraUse.block': 'Block extra use for this business',
  'tenants.view.extraUse.blockHint': 'Blocked, extra use pauses and spending stops at zero again. What is already owed is still charged.',
  'tenants.view.extraUse.reasonLabel': 'Reason for a block (optional)',
  'tenants.view.extraUse.reasonHint': 'Kept with the block, for the platform team.',
  'tenants.view.extraUse.blocked': 'Extra use blocked for {{name}}.',
  'tenants.view.extraUse.unblocked': 'Extra use unblocked for {{name}}.',
  'tenants.view.extraUse.pausedNow': 'Paused: {{reason}}.',
  'common.words.add': 'Type and press Enter',
  'common.words.remove': 'Remove {{word}}',
  'common.words.full': 'That is the most: {{max}}',
  'nav.item.billingLanguages': 'Languages',
  'shell.breadcrumb.languages': 'Languages',
  'shell.breadcrumb.languageLines': 'Built-in lines',
  'billing.overview.cards.languages.title': 'Languages',
  'billing.overview.cards.languages.hint': 'Choose which languages chatbots may speak, and check the chatbot\'s fixed wording in each.',
  'billing.languages.eyebrow': 'Billing',
  'billing.languages.title': 'Languages',
  'billing.languages.description': 'The languages a chatbot can speak from one knowledge base. A business offers any language switched on here from Settings, Chatbot. A question in another language is put into the chatbot\'s main language by a small AI call, charged to the business as AI translation.',
  'billing.languages.openModels': 'AI models',
  'billing.languages.stat.on': 'Switched on',
  'billing.languages.stat.onHint': 'of {{total}} in the catalogue',
  'billing.languages.stat.offered': 'Offered by chatbots',
  'billing.languages.stat.offeredHint': '{{count}} chatbot choices in all',
  'billing.languages.stat.missing': 'Lines still in English',
  'billing.languages.stat.missingHint': 'Built-in lines not translated yet, of {{total}} in the languages switched on',
  'billing.languages.stat.review': 'Need a native speaker',
  'billing.languages.stat.reviewHint': 'Languages switched on whose quality is Check wording',
  'billing.languages.stat.staleHint': '{{count}} edited lines whose English has changed since',
  'billing.languages.showingCount': '{{on}} of {{total}} switched on',
  'billing.languages.empty': 'No languages',
  'billing.languages.emptyHint': 'The catalogue is filled by the flows-llm migrations. Run them, then reload.',
  'billing.languages.denied': 'Your account does not have permission to see the languages.',
  'billing.languages.loadError': 'The languages could not be loaded.',
  'billing.languages.region.south_africa': 'South Africa',
  'billing.languages.region.africa': 'Across Africa',
  'billing.languages.region.widely_used': 'Also widely used',
  'billing.languages.regionCount': '{{on}} of {{total}} on',
  'billing.languages.note': 'Meta codes are the WhatsApp template languages; none means templates cannot be in it, though the chatbot can still chat in it. Speech means voice notes in it can be turned into words.',
  'billing.languages.columns.language': 'Language',
  'billing.languages.columns.quality': 'Quality',
  'billing.languages.columns.meta': 'Meta codes',
  'billing.languages.columns.speech': 'Speech',
  'billing.languages.columns.bots': 'Chatbots',
  'billing.languages.columns.lines': 'Built-in lines',
  'billing.languages.columns.offered': 'Offered',
  'billing.languages.quality.strong': 'Strong',
  'billing.languages.quality.good': 'Good',
  'billing.languages.quality.review': 'Check wording',
  'billing.languages.meta.none': 'None',
  'billing.languages.lines.all': 'All {{total}}',
  'billing.languages.lines.inCode': 'Written in the code',
  'billing.languages.lines.count': '{{translated}} of {{total}}',
  'billing.languages.lines.detail': '{{edited}} edited · {{reviewed}} read by a native speaker',
  'billing.languages.lines.stale': '{{count}} edited, English changed since',
  'billing.languages.lines.missing': '{{count}} still in English',
  'billing.languages.lines.action': 'Built-in lines',
  'billing.languages.lines.actionFor': 'Built-in lines in {{name}}',
  'billing.languages.englishStays': 'English stays on: every chatbot falls back to it.',
  'billing.languages.state.on': 'On',
  'billing.languages.state.off': 'Off',
  'billing.languages.switchLabel': 'Offer {{name}}',
  'billing.languages.edit.actionFor': 'Edit {{name}}',
  'billing.languages.toast.on': '{{name}} is switched on. Businesses can offer it now.',
  'billing.languages.toast.off': '{{name}} is switched off.',
  'billing.languages.toast.failed': 'Could not change {{name}}',
  'billing.languages.off.title': 'Switch {{name}} off?',
  'billing.languages.off.body': 'Businesses can no longer offer {{name}} to their customers.',
  'billing.languages.off.bots': '{{count}} chatbots offer it now. They stop speaking it, and their customers get each chatbot\'s main language. A business cannot save its chatbot with it until it is on again.',
  'billing.languages.off.confirm': 'Switch off',
  'billing.languages.on.title': 'Switch {{name}} on?',
  'billing.languages.on.body': 'Every business can then offer {{name}} to its customers, who see it on the language menu as “{{own}}”.',
  'billing.languages.on.review': 'Its quality is Check wording: answers are fine, but have a native speaker read the built-in lines first.',
  'billing.languages.on.missing': '{{count}} of its {{total}} built-in lines are not translated yet, so customers see those in English until they are. Translate them from Built-in lines.',
  'billing.languages.on.confirm': 'Switch on',
  'billing.languages.edit.title': 'Edit {{name}}',
  'billing.languages.edit.intro': 'Code {{code}}. Only the fields you change are saved; each word list replaces the whole list.',
  'billing.languages.edit.name': 'English name',
  'billing.languages.edit.nameHint': 'How the platform team and the AI name it.',
  'billing.languages.edit.nameInvalid': 'Use 1 to {{max}} characters.',
  'billing.languages.edit.native': 'Its own name',
  'billing.languages.edit.nativeHint': 'Shown to customers on the language menu, which is a WhatsApp button. {{count}} of {{max}} characters.',
  'billing.languages.edit.nativeInvalid': 'Use 1 to {{max}} characters.',
  'billing.languages.edit.quality': 'Quality',
  'billing.languages.edit.qualityHint.strong': 'The AI writes it as well as English.',
  'billing.languages.edit.qualityHint.good': 'The AI writes it well; small slips are possible.',
  'billing.languages.edit.qualityHint.review': 'Answers are fine, but businesses are told to have a native speaker read the chatbot\'s fixed wording first.',
  'billing.languages.edit.meta': 'Meta template codes',
  'billing.languages.edit.metaHint': 'Meta\'s WhatsApp template language codes, such as zu, en_ZA or pt_BR. Leave empty when templates cannot be in this language.',
  'billing.languages.edit.metaInvalid': '{{code}} is not a Meta template language code (such as zu, en_ZA or pt_BR).',
  'billing.languages.edit.speech': 'Speech code',
  'billing.languages.edit.speechHint': 'AWS Transcribe\'s code, such as zu-ZA, when voice notes in this language can be turned into words. Leave empty if they cannot.',
  'billing.languages.edit.speechInvalid': 'Use AWS Transcribe\'s form, such as zu-ZA or sw-KE.',
  'billing.languages.edit.change_words': 'Change words',
  'billing.languages.edit.change_wordsHint': 'Words in this language that bring the language menu back, such as “ulimi”. Up to {{max}}.',
  'billing.languages.edit.greeting_words': 'Greetings',
  'billing.languages.edit.greeting_wordsHint': 'Greetings in this language, such as “sawubona”. A new customer who greets with one sees this language first on the menu. Up to {{max}}.',
  'billing.languages.edit.yes_words': 'Yes words',
  'billing.languages.edit.yes_wordsHint': 'Typed answers that mean yes to “Switch to …?”, besides tapping the button. Up to {{max}}.',
  'billing.languages.edit.no_words': 'No words',
  'billing.languages.edit.no_wordsHint': 'Typed answers that mean no to “Switch to …?”. Up to {{max}}.',
  'billing.languages.edit.sortOrder': 'Sort order',
  'billing.languages.edit.sortOrderHint': 'Lower comes first, in this list and in every business\'s list.',
  'billing.languages.edit.sortInvalid': 'Use a whole number.',
  'billing.languages.edit.saved': '{{name}} saved.',
  'billing.languages.edit.failed': 'Not saved',
  'billing.languages.lines.title': '{{name}}: built-in lines',
  'billing.languages.lines.titleShort': 'Built-in lines',
  'billing.languages.lines.description': 'The chatbot\'s own fixed wording in {{name}}: the language menu, the switch question, hand-over notices and buttons, beside the English. Lines are machine-translated once at the platform\'s cost; correct any line here and mark it as read by a native speaker.',
  'billing.languages.lines.back': 'All languages',
  'billing.languages.lines.translate': 'Translate missing lines now',
  'billing.languages.lines.remake.action': 'Remake machine lines',
  'billing.languages.lines.remake.title': 'Remake every machine line in {{name}}?',
  'billing.languages.lines.remake.body': 'Every machine-translated line in {{name}} is translated again, and their read-by-a-native-speaker marks are cleared. Lines you edited are kept. The platform pays, a few cents for the language.',
  'billing.languages.lines.remake.confirm': 'Remake',
  'billing.languages.lines.translated': '{{count}} lines written in {{name}}.',
  'billing.languages.lines.translateFailed': 'Could not translate',
  'billing.languages.lines.inCodeNote': 'English and Afrikaans lines are written in the code; change them there.',
  'billing.languages.lines.costNote': 'Translating is paid by the platform, a few cents for a language, never by a business.',
  'billing.languages.lines.reviewNote': '{{name}} is marked Check wording: have a native speaker read these lines before businesses rely on them.',
  'billing.languages.lines.offNote': '{{name}} is switched off, so no chatbot uses these lines yet.',
  'billing.languages.lines.notFound': 'There is no language with the code {{code}}.',
  'billing.languages.lines.loadError': 'The built-in lines could not be loaded.',
  'billing.languages.lines.filter.all': 'All ({{count}})',
  'billing.languages.lines.filter.missing': 'Still in English ({{count}})',
  'billing.languages.lines.filter.unread': 'Not read yet ({{count}})',
  'billing.languages.lines.filter.stale': 'English changed ({{count}})',
  'billing.languages.lines.empty': 'No built-in lines',
  'billing.languages.lines.emptyFiltered': 'No lines match this filter.',
  'billing.languages.lines.showAll': 'Show all lines',
  'billing.languages.lines.source.machine': 'Machine',
  'billing.languages.lines.source.edited': 'Edited',
  'billing.languages.lines.source.missing': 'Still in English',
  'billing.languages.lines.source.built_in': 'Written in the code',
  'billing.languages.lines.staleBadge': 'English changed',
  'billing.languages.lines.readBadge': 'Read by a native speaker',
  'billing.languages.lines.button': 'Button, {{max}} characters',
  'billing.languages.lines.english': 'English',
  'billing.languages.lines.in': 'In {{name}}',
  'billing.languages.lines.counter': '{{count}} of {{max}} characters.',
  'billing.languages.lines.missingHint': 'Customers see the English until this is translated.',
  'billing.languages.lines.tooLong': 'This is a button: at most {{max}} characters.',
  'billing.languages.lines.lostParts': 'Keep {{parts}} exactly as the English has it.',
  'billing.languages.lines.extraParts': 'The English has no {{parts}}; take it out.',
  'billing.languages.lines.saved': '{{key}} saved.',
  'billing.languages.lines.undo.action': 'Use the machine translation',
  'billing.languages.lines.undo.title': 'Take your edit back?',
  'billing.languages.lines.undo.body': 'Your wording of {{key}} in {{name}} is removed. Customers see the English until the line is translated again by machine.',
  'billing.languages.lines.undo.confirm': 'Take it back',
  'billing.languages.lines.readNeedsText': 'Translate the line first.',
  'billing.languages.lines.markRead': 'Mark as read by a native speaker',
  'billing.languages.lines.markUnread': 'Mark as not read'
};

// Translation is parked backlog for the internal admin console: the picker
// works (and persists), every string falls back to English.
const af: Catalog = {
  'billing.aiModels.edit.context': 'Konteksvenster (tokens)',
  'billing.aiModels.edit.contextHint': 'Hoeveel die model op een slag kan lees. Stel vloeie voor met KI hou elke oproep onder die helfte daarvan. 0 beteken onbekend, gelees as 200 000.',
  'billing.aiModels.edit.contextInvalid': 'Gebruik 0, of {{min}} tot {{max}} tokens.',
  'billing.extraUse.title': 'Ekstra gebruik (Growth+ en Scale+)',
  'billing.extraUse.body': '’n Besigheid op ’n plan wat ekstra gebruik insluit, kan aanhou nadat sy krediet op is, gehef op sy gestoorde kaart teen sy plan se pryse, tot by ’n maandelikse limiet wat hy stel. Hierdie instellings geld vir elke besigheid.',
  'billing.extraUse.saved': 'Gestoor. Besighede kies dadelik binne die nuwe limiete.',
  'billing.extraUse.offered.label': 'Bied ekstra gebruik aan',
  'billing.extraUse.offered.hint': 'Af: geen besigheid kan dit aanskakel nie, en elke besigheid wat dit gebruik, word onderbreek. Wat reeds verskuldig is, word steeds gehef.',
  'billing.extraUse.max_limit_minor.label': 'Hoogste maandelikse limiet (R)',
  'billing.extraUse.max_limit_minor.hint': 'Die meeste wat een besigheid in ’n maand kan skuld, en die hoogste limiet wat ’n besigheid kan stel. Minstens {{min}}.',
  'billing.extraUse.min_limit_minor.label': 'Laagste maandelikse limiet (R)',
  'billing.extraUse.min_limit_minor.hint': 'Die laagste maandelikse limiet wat ’n besigheid kan stel. Minstens {{min}}, en nie meer as die hoogste limiet nie.',
  'billing.extraUse.min_step_minor.label': 'Kleinste stap of aanvulling (R)',
  'billing.extraUse.min_step_minor.hint': 'Die minste wat ’n besigheid op ’n slag gehef kan word, in stappe of as ’n vooruit-aanvulling. Minstens {{min}}, en nie meer as die hoogste limiet nie.',
  'billing.extraUse.invalid': 'Gebruik {{min}} tot {{max}}.',
  'billing.subscriptions.flag.extraUse': 'Ekstra gebruik (+)',
  'billing.plans.limit.extra_use': 'Ekstra gebruik (hou aan nadat die krediet op is)',
  'billing.plans.edit.extraUseHint': 'Aan: ’n besigheid op hierdie plan met ’n gestoorde kaart kan aanhou nadat sy krediet op is, teen die plan se pryse, tot by ’n maandelikse limiet wat hy stel (gestoor sonder limiet). Af: nie ingesluit nie, gestoor as 0.',
  'tenants.view.extraUse.title': 'Ekstra gebruik',
  'tenants.view.extraUse.loadError': 'Kon nie ekstra gebruik vir hierdie besigheid laai nie.',
  'tenants.view.extraUse.badge.active': 'Aktief',
  'tenants.view.extraUse.badge.paused': 'Onderbreek',
  'tenants.view.extraUse.badge.off': 'Af',
  'tenants.view.extraUse.badge.blocked': 'Geblokkeer',
  'tenants.view.extraUse.onPlan': 'Ingesluit by {{plan}}',
  'tenants.view.extraUse.notOnPlan': 'Nie ingesluit by {{plan}} nie',
  'tenants.view.extraUse.card': 'Kaart: {{card}}',
  'tenants.view.extraUse.noCard': 'Geen kaart gestoor nie',
  'tenants.view.extraUse.off': 'Nie aangeskakel nie.',
  'tenants.view.extraUse.settings': '{{how}}, tot {{limit}} per maand.',
  'tenants.view.extraUse.mode.steps': 'In stappe van {{amount}}',
  'tenants.view.extraUse.mode.upfront': 'Vooruit, {{amount}} krediet wanneer die saldo onder {{below}} daal',
  'tenants.view.extraUse.mode.month_end': 'Op die 1ste, saam met die plan',
  'tenants.view.extraUse.month': '{{month}}: {{used}} van {{limit}} gebruik; {{owed}} verskuldig; {{charged}} gehef.',
  'tenants.view.extraUse.paused': 'Onderbreek sedert {{date}}: {{reason}}.',
  'tenants.view.extraUse.reason.no_card': 'geen kaart gestoor nie',
  'tenants.view.extraUse.reason.declined': '’n heffing is geweier',
  'tenants.view.extraUse.reason.past_due': 'die plan is agterstallig',
  'tenants.view.extraUse.reason.blocked': 'deur die platform geblokkeer',
  'tenants.view.extraUse.reason.not_on_plan': 'die plan sluit dit nie in nie',
  'tenants.view.extraUse.reason.not_offered': 'ekstra gebruik word nie aangebied nie',
  'tenants.view.extraUse.declineReason': 'Die kaartverskaffer het gesê: {{reason}}',
  'tenants.view.extraUse.blockedReason': 'Geblokkeer omdat: {{reason}}',
  'tenants.view.extraUse.block': 'Blokkeer ekstra gebruik vir hierdie besigheid',
  'tenants.view.extraUse.blockHint': 'Geblokkeer, word ekstra gebruik onderbreek en stop besteding weer by nul. Wat reeds verskuldig is, word steeds gehef.',
  'tenants.view.extraUse.reasonLabel': 'Rede vir ’n blokkering (opsioneel)',
  'tenants.view.extraUse.reasonHint': 'Word saam met die blokkering gehou, vir die platformspan.',
  'tenants.view.extraUse.blocked': 'Ekstra gebruik geblokkeer vir {{name}}.',
  'tenants.view.extraUse.unblocked': 'Ekstra gebruik ontblokkeer vir {{name}}.',
  'tenants.view.extraUse.pausedNow': 'Onderbreek: {{reason}}.',
  'common.words.add': 'Tik en druk Enter',
  'common.words.remove': 'Verwyder {{word}}',
  'common.words.full': 'Dis die meeste: {{max}}',
  'nav.item.billingLanguages': 'Tale',
  'shell.breadcrumb.languages': 'Tale',
  'shell.breadcrumb.languageLines': 'Ingeboude reëls',
  'billing.overview.cards.languages.title': 'Tale',
  'billing.overview.cards.languages.hint': 'Kies watter tale kletsbotte mag praat, en kontroleer die kletsbot se vaste bewoording in elkeen.',
  'billing.languages.eyebrow': 'Fakturering',
  'billing.languages.title': 'Tale',
  'billing.languages.description': 'Die tale wat ’n kletsbot uit een kennisbasis kan praat. ’n Besigheid bied enige taal wat hier aangeskakel is aan vanuit Instellings, Kletsbot. ’n Vraag in ’n ander taal word deur ’n klein KI-oproep in die kletsbot se hooftaal omgesit, aan die besigheid gehef as KI-vertaling.',
  'billing.languages.openModels': 'KI-modelle',
  'billing.languages.stat.on': 'Aangeskakel',
  'billing.languages.stat.onHint': 'van {{total}} in die katalogus',
  'billing.languages.stat.offered': 'Deur kletsbotte aangebied',
  'billing.languages.stat.offeredHint': '{{count}} kletsbotkeuses altesaam',
  'billing.languages.stat.missing': 'Reëls nog in Engels',
  'billing.languages.stat.missingHint': 'Ingeboude reëls nog nie vertaal nie, van {{total}} in die aangeskakelde tale',
  'billing.languages.stat.review': 'Moedertaalspreker nodig',
  'billing.languages.stat.reviewHint': 'Aangeskakelde tale met die gehalte Kontroleer bewoording',
  'billing.languages.stat.staleHint': '{{count}} gewysigde reëls waarvan die Engels sedertdien verander het',
  'billing.languages.showingCount': '{{on}} van {{total}} aangeskakel',
  'billing.languages.empty': 'Geen tale nie',
  'billing.languages.emptyHint': 'Die katalogus word deur die flows-llm-migrasies gevul. Voer hulle uit en laai dan weer.',
  'billing.languages.denied': 'Jou rekening het nie toestemming om die tale te sien nie.',
  'billing.languages.loadError': 'Die tale kon nie gelaai word nie.',
  'billing.languages.region.south_africa': 'Suid-Afrika',
  'billing.languages.region.africa': 'Oor Afrika heen',
  'billing.languages.region.widely_used': 'Ook wyd gebruik',
  'billing.languages.regionCount': '{{on}} van {{total}} aan',
  'billing.languages.note': 'Meta-kodes is die WhatsApp-sjabloontale; geen beteken sjablone kan nie daarin wees nie, al kan die kletsbot steeds daarin klets. Spraak beteken stemnotas daarin kan in woorde omgesit word.',
  'billing.languages.columns.language': 'Taal',
  'billing.languages.columns.quality': 'Gehalte',
  'billing.languages.columns.meta': 'Meta-kodes',
  'billing.languages.columns.speech': 'Spraak',
  'billing.languages.columns.bots': 'Kletsbotte',
  'billing.languages.columns.lines': 'Ingeboude reëls',
  'billing.languages.columns.offered': 'Aangebied',
  'billing.languages.quality.strong': 'Sterk',
  'billing.languages.quality.good': 'Goed',
  'billing.languages.quality.review': 'Kontroleer bewoording',
  'billing.languages.meta.none': 'Geen',
  'billing.languages.lines.all': 'Al {{total}}',
  'billing.languages.lines.inCode': 'In die kode geskryf',
  'billing.languages.lines.count': '{{translated}} van {{total}}',
  'billing.languages.lines.detail': '{{edited}} gewysig · {{reviewed}} deur ’n moedertaalspreker gelees',
  'billing.languages.lines.stale': '{{count}} gewysig, Engels sedertdien verander',
  'billing.languages.lines.missing': '{{count}} nog in Engels',
  'billing.languages.lines.action': 'Ingeboude reëls',
  'billing.languages.lines.actionFor': 'Ingeboude reëls in {{name}}',
  'billing.languages.englishStays': 'Engels bly aan: elke kletsbot val daarop terug.',
  'billing.languages.state.on': 'Aan',
  'billing.languages.state.off': 'Af',
  'billing.languages.switchLabel': 'Bied {{name}} aan',
  'billing.languages.edit.actionFor': 'Wysig {{name}}',
  'billing.languages.toast.on': '{{name}} is aangeskakel. Besighede kan dit nou aanbied.',
  'billing.languages.toast.off': '{{name}} is afgeskakel.',
  'billing.languages.toast.failed': 'Kon nie {{name}} verander nie',
  'billing.languages.off.title': 'Skakel {{name}} af?',
  'billing.languages.off.body': 'Besighede kan {{name}} nie meer aan hul kliënte aanbied nie.',
  'billing.languages.off.bots': '{{count}} kletsbotte bied dit nou aan. Hulle hou op om dit te praat, en hul kliënte kry elke kletsbot se hooftaal. ’n Besigheid kan sy kletsbot nie daarmee stoor voordat dit weer aan is nie.',
  'billing.languages.off.confirm': 'Skakel af',
  'billing.languages.on.title': 'Skakel {{name}} aan?',
  'billing.languages.on.body': 'Elke besigheid kan dan {{name}} aan sy kliënte aanbied, wat dit op die taalkieslys as “{{own}}” sien.',
  'billing.languages.on.review': 'Sy gehalte is Kontroleer bewoording: antwoorde is goed, maar laat ’n moedertaalspreker eers die ingeboude reëls lees.',
  'billing.languages.on.missing': '{{count}} van sy {{total}} ingeboude reëls is nog nie vertaal nie, so kliënte sien dié in Engels totdat hulle is. Vertaal hulle vanuit Ingeboude reëls.',
  'billing.languages.on.confirm': 'Skakel aan',
  'billing.languages.edit.title': 'Wysig {{name}}',
  'billing.languages.edit.intro': 'Kode {{code}}. Net die velde wat jy verander, word gestoor; elke woordelys vervang die hele lys.',
  'billing.languages.edit.name': 'Engelse naam',
  'billing.languages.edit.nameHint': 'Hoe die platformspan en die KI dit noem.',
  'billing.languages.edit.nameInvalid': 'Gebruik 1 tot {{max}} karakters.',
  'billing.languages.edit.native': 'Sy eie naam',
  'billing.languages.edit.nativeHint': 'Aan kliënte gewys op die taalkieslys, wat ’n WhatsApp-knoppie is. {{count}} van {{max}} karakters.',
  'billing.languages.edit.nativeInvalid': 'Gebruik 1 tot {{max}} karakters.',
  'billing.languages.edit.quality': 'Gehalte',
  'billing.languages.edit.qualityHint.strong': 'Die KI skryf dit net so goed soos Engels.',
  'billing.languages.edit.qualityHint.good': 'Die KI skryf dit goed; klein foute is moontlik.',
  'billing.languages.edit.qualityHint.review': 'Antwoorde is goed, maar besighede word aangeraai om eers ’n moedertaalspreker die kletsbot se vaste bewoording te laat lees.',
  'billing.languages.edit.meta': 'Meta-sjabloonkodes',
  'billing.languages.edit.metaHint': 'Meta se WhatsApp-sjabloontaalkodes, soos zu, en_ZA of pt_BR. Laat leeg wanneer sjablone nie in hierdie taal kan wees nie.',
  'billing.languages.edit.metaInvalid': '{{code}} is nie ’n Meta-sjabloontaalkode nie (soos zu, en_ZA of pt_BR).',
  'billing.languages.edit.speech': 'Spraakkode',
  'billing.languages.edit.speechHint': 'AWS Transcribe se kode, soos zu-ZA, wanneer stemnotas in hierdie taal in woorde omgesit kan word. Laat leeg as dit nie kan nie.',
  'billing.languages.edit.speechInvalid': 'Gebruik AWS Transcribe se vorm, soos zu-ZA of sw-KE.',
  'billing.languages.edit.change_words': 'Veranderwoorde',
  'billing.languages.edit.change_wordsHint': 'Woorde in hierdie taal wat die taalkieslys terugbring, soos “ulimi”. Tot {{max}}.',
  'billing.languages.edit.greeting_words': 'Groete',
  'billing.languages.edit.greeting_wordsHint': 'Groete in hierdie taal, soos “sawubona”. ’n Nuwe kliënt wat met een groet, sien hierdie taal eerste op die kieslys. Tot {{max}}.',
  'billing.languages.edit.yes_words': 'Ja-woorde',
  'billing.languages.edit.yes_wordsHint': 'Getikte antwoorde wat ja beteken op “Verander na …?”, benewens om die knoppie te tik. Tot {{max}}.',
  'billing.languages.edit.no_words': 'Nee-woorde',
  'billing.languages.edit.no_wordsHint': 'Getikte antwoorde wat nee beteken op “Verander na …?”. Tot {{max}}.',
  'billing.languages.edit.sortOrder': 'Sorteervolgorde',
  'billing.languages.edit.sortOrderHint': 'Laer kom eerste, in hierdie lys en in elke besigheid se lys.',
  'billing.languages.edit.sortInvalid': 'Gebruik ’n heelgetal.',
  'billing.languages.edit.saved': '{{name}} gestoor.',
  'billing.languages.edit.failed': 'Nie gestoor nie',
  'billing.languages.lines.title': '{{name}}: ingeboude reëls',
  'billing.languages.lines.titleShort': 'Ingeboude reëls',
  'billing.languages.lines.description': 'Die kletsbot se eie vaste bewoording in {{name}}: die taalkieslys, die veranderingsvraag, oorhandigingskennisgewings en knoppies, langs die Engels. Reëls word een keer op die platform se koste masjienvertaal; korrigeer enige reël hier en merk dit as deur ’n moedertaalspreker gelees.',
  'billing.languages.lines.back': 'Alle tale',
  'billing.languages.lines.translate': 'Vertaal ontbrekende reëls nou',
  'billing.languages.lines.remake.action': 'Maak masjienreëls oor',
  'billing.languages.lines.remake.title': 'Maak elke masjienreël in {{name}} oor?',
  'billing.languages.lines.remake.body': 'Elke masjienvertaalde reël in {{name}} word weer vertaal, en hul gelees-deur-’n-moedertaalspreker-merke word verwyder. Reëls wat jy gewysig het, bly. Die platform betaal, ’n paar sent vir die taal.',
  'billing.languages.lines.remake.confirm': 'Maak oor',
  'billing.languages.lines.translated': '{{count}} reëls in {{name}} geskryf.',
  'billing.languages.lines.translateFailed': 'Kon nie vertaal nie',
  'billing.languages.lines.inCodeNote': 'Engelse en Afrikaanse reëls is in die kode geskryf; verander hulle daar.',
  'billing.languages.lines.costNote': 'Vertaling word deur die platform betaal, ’n paar sent vir ’n taal, nooit deur ’n besigheid nie.',
  'billing.languages.lines.reviewNote': '{{name}} is gemerk Kontroleer bewoording: laat ’n moedertaalspreker hierdie reëls lees voordat besighede daarop staatmaak.',
  'billing.languages.lines.offNote': '{{name}} is afgeskakel, so geen kletsbot gebruik hierdie reëls nog nie.',
  'billing.languages.lines.notFound': 'Daar is geen taal met die kode {{code}} nie.',
  'billing.languages.lines.loadError': 'Die ingeboude reëls kon nie gelaai word nie.',
  'billing.languages.lines.filter.all': 'Alles ({{count}})',
  'billing.languages.lines.filter.missing': 'Nog in Engels ({{count}})',
  'billing.languages.lines.filter.unread': 'Nog nie gelees nie ({{count}})',
  'billing.languages.lines.filter.stale': 'Engels verander ({{count}})',
  'billing.languages.lines.empty': 'Geen ingeboude reëls nie',
  'billing.languages.lines.emptyFiltered': 'Geen reëls pas by hierdie filter nie.',
  'billing.languages.lines.showAll': 'Wys alle reëls',
  'billing.languages.lines.source.machine': 'Masjien',
  'billing.languages.lines.source.edited': 'Gewysig',
  'billing.languages.lines.source.missing': 'Nog in Engels',
  'billing.languages.lines.source.built_in': 'In die kode geskryf',
  'billing.languages.lines.staleBadge': 'Engels verander',
  'billing.languages.lines.readBadge': 'Deur ’n moedertaalspreker gelees',
  'billing.languages.lines.button': 'Knoppie, {{max}} karakters',
  'billing.languages.lines.english': 'Engels',
  'billing.languages.lines.in': 'In {{name}}',
  'billing.languages.lines.counter': '{{count}} van {{max}} karakters.',
  'billing.languages.lines.missingHint': 'Kliënte sien die Engels totdat dit vertaal is.',
  'billing.languages.lines.tooLong': 'Dit is ’n knoppie: hoogstens {{max}} karakters.',
  'billing.languages.lines.lostParts': 'Hou {{parts}} presies soos die Engels dit het.',
  'billing.languages.lines.extraParts': 'Die Engels het geen {{parts}} nie; haal dit uit.',
  'billing.languages.lines.saved': '{{key}} gestoor.',
  'billing.languages.lines.undo.action': 'Gebruik die masjienvertaling',
  'billing.languages.lines.undo.title': 'Neem jou wysiging terug?',
  'billing.languages.lines.undo.body': 'Jou bewoording van {{key}} in {{name}} word verwyder. Kliënte sien die Engels totdat die reël weer deur die masjien vertaal is.',
  'billing.languages.lines.undo.confirm': 'Neem terug',
  'billing.languages.lines.readNeedsText': 'Vertaal eers die reël.',
  'billing.languages.lines.markRead': 'Merk as deur ’n moedertaalspreker gelees',
  'billing.languages.lines.markUnread': 'Merk as nie gelees nie'
};
const zu: Catalog = {};
const xh: Catalog = {};
const st: Catalog = {};

export const CATALOGS: Record<Locale, Catalog> = { en, af, zu, xh, st };
